/**
 * The one user-interface string lookup (piece 1, decision 0013). A stable key
 * maps to English text and nothing else: no translation framework, no locale
 * negotiation, no second catalogue. A key is added here the first time the
 * string that needs it is written. Keys are `area.name` in dotted lower camel case.
 */
import { REHEARSAL_OVERDUE_DAYS } from './files/rehearsalDays';
import { ASSET_STRINGS } from './assets/assetStrings';
import { PLANT_FEED_STRINGS } from './assets/plantFeedStrings';
import { WORK_STRINGS } from './documents/workStrings';
import { FIELD_SERVICE_STRINGS } from './documents/fieldServiceStrings';
import { SCHEDULE_CALENDAR_STRINGS } from './work/scheduleCalendarStrings';
import { JOB_TYPE_STRINGS } from './work/jobTypeStrings';
import { PART_SOURCE_STRINGS } from './work/partSourceStrings';
import { VISIT_REPORT_STRINGS } from './documents/visitReportStrings';
import { WORK_ORDER_PROGRESS_STRINGS } from './documents/workOrderProgressStrings';
import { CHECKLIST_STRINGS } from './checklist/checklistStrings';
import { KNOWLEDGE_STRINGS } from './knowledge/knowledgeStrings';
import { MAIL_STRINGS } from './mail/mailStrings';
import { IMPORT_STRINGS } from './import/importStrings';
import { PRINT_GUIDE_STRINGS } from './print/printGuideStrings';
import { QUARANTINE_STRINGS } from './files/quarantineStrings';
import { HR_STRINGS } from './hr/hrStrings';
import { ACCOUNTING_SETUP_STEPS_STRINGS } from './accounting/accountingSetupStepsStrings';
import { BANK_STEPS_STRINGS } from './accounting/bankStepsStrings';
import { LOGIN_STEPS_STRINGS } from './identity/loginStepsStrings';
import { TEXT_THREAD_STRINGS } from './identity/textThreadStrings';
import { CONCEPT_TOPIC_STRINGS } from './help/conceptTopicStrings';
import { SETUP_GUIDES_STRINGS } from './settings/setupGuidesStrings';
import { PLAIN_LABEL_STRINGS } from './settings/plainLabelStrings';
import { DUPLICATE_STRINGS } from './records/duplicateStrings';
import { UNDO_MERGE_STRINGS } from './records/undoMergeStrings';
import { COMPANY_OWNERSHIP_STRINGS } from './records/companyOwnershipStrings';
import { JOB_BILLING_STRINGS } from './documents/jobBillingStrings';
import { QUOTE_LINK_STRINGS } from './documents/quoteLinkStrings';
import { FILE_ROW_STRINGS } from './files/fileRowStrings';
import { BACKUP_ALERT_STRINGS } from './files/backupAlertStrings';
import { MOBILE_STRINGS } from './mobile/mobileStrings';
import { NOTES_FILES_STRINGS } from './mobile/notesFilesStrings';
import { ADD_RECORD_STRINGS } from './mobile/addRecordStrings';
import { MAPS_STRINGS } from './mobile/mapsStrings';
import { WORK_DOCUMENTS_STRINGS } from './mobile/workDocumentsStrings';
import { CRM_READS_STRINGS } from './mobile/crmReadsStrings';
import { LOCK_STRINGS } from './mobile/lockStrings';
import { CRM_EDIT_STRINGS } from './mobile/crmEditStrings';
import { CRM_NOTES_STRINGS } from './mobile/crmNotesStrings';
import { DRAWER_STRINGS } from './mobile/drawerStrings';
import { EXPENSE_PHONE_STRINGS } from './mobile/expenseStrings';
import { BANKING_PHONE_STRINGS } from './mobile/bankingStrings';
import { KNOWLEDGE_PHONE_STRINGS } from './mobile/knowledgeStrings';
import { SERVICE_REPORT_PHONE_STRINGS } from './mobile/serviceReportStrings';
import { PICTURE_STRINGS } from './mobile/pictureStrings';
import { PUSH_STRINGS } from './mobile/pushStrings';
import { CALL_STRINGS } from './calls/callStrings';
import { PHONE_SYSTEM_STRINGS } from './calls/phoneSystemStrings';
import { PHONE_PAGES_STRINGS } from './calls/phonePagesStrings';
import { PHONE_SCREEN_STRINGS } from './calls/phoneScreenStrings';
import { APP_CALLING_STRINGS } from './calls/appCallingStrings';
import { PHONE_CALL_STRINGS } from './calls/phoneCallStrings';
import { RECORD_CHECK_STRINGS } from './records/recordSuggestionStrings';
import { LIST_MARK_STRINGS } from './records/listMarkStrings';
import { SITE_TYPE_STRINGS } from './records/siteTypeStrings';
import { SITE_TYPE_DEFAULT_STRINGS } from './records/siteTypeDefaultStrings';
import { CONFIDENTIALITY_STRINGS } from './permissions/confidentialityStrings';
import { CAPABILITY_STRINGS } from './permissions/capabilityStrings';
import { DOCUMENT_ACTION_STRINGS } from './mobile/documentActionStrings';
import { CRM_DAY_STRINGS } from './mobile/crmDayStrings';
import { CRM_CONTACTS_STRINGS } from './mobile/crmContactsStrings';
import { CRM_DEALS_STRINGS } from './mobile/crmDealsStrings';
import { EQUIPMENT_PHOTO_STRINGS } from './deals/equipmentPhotoStrings';
import { PHOTO_STANDARD_STRINGS } from './files/photoStandardStrings';
import { CRM_LEADS_STRINGS } from './mobile/crmLeadsStrings';
import { CRM_LISTS_STRINGS } from './mobile/crmListsStrings';
import { SYNC_STRINGS } from './mobile/syncStrings';
import { COMPANY_SUCCESSOR_STRINGS } from './records/companySuccessorStrings';

/**
 * 🔨 The table is typed by its keys with plain `string` texts, not `as const` (piece 1, found while building 2026-10-31):
 * the literal type of every English sentence had grown the emitted declaration past the length the compiler will
 * serialize (TS7056), so the shared package stopped building. `t()` returns a `string` either way.
 */
const STRING_TABLE = {
  'app.name': 'BMS2',
  // Piece 28: the asset register's strings, kept beside the register.
  ...ASSET_STRINGS,
  // Piece 28, decision 12503: the Site page's Plant Feed tile.
  ...PLANT_FEED_STRINGS,
  // Piece 29: the work-order side's strings, kept beside its rules.
  ...WORK_STRINGS,
  ...FIELD_SERVICE_STRINGS,
  ...WORK_ORDER_PROGRESS_STRINGS,
  // Piece 29, decision 2920: a technician's calendar events on the Schedule view.
  ...SCHEDULE_CALENDAR_STRINGS,
  // Piece 29 (T1710): the job types editor, the pick on a Draft job and the job's planned parts.
  ...JOB_TYPE_STRINGS,
  // Piece 29, decision 10320 (T3487): the Add Part window's van, shop and Another Site, and the Vans card.
  ...PART_SOURCE_STRINGS,
  // Piece 22, decision 2650: the Quote link's page, the acceptance line and the optional lines.
  ...QUOTE_LINK_STRINGS,
  ...VISIT_REPORT_STRINGS,
  // Piece 29 (T389): the typed checklist items, on the job and in their template editor.
  ...CHECKLIST_STRINGS,
  // Piece 30: the knowledge base's strings, kept beside its rules.
  ...KNOWLEDGE_STRINGS,
  ...MAIL_STRINGS,
  // Piece 31: the importer's strings, kept beside its rules.
  ...IMPORT_STRINGS,
  // Piece 27: the print templates pages' About This Page window (T557; decision 1282), kept beside the print rules.
  ...PRINT_GUIDE_STRINGS,
  // Piece 9, decisions 1180 to 1182: the scanner's and the quarantine's strings, kept beside their rules.
  ...QUARANTINE_STRINGS,
  // Piece 34: the HR module's strings, kept beside its rules.
  ...HR_STRINGS,
  // Piece 24, decision 1770: the Connect a bank stepper, kept beside its rules.
  ...ACCOUNTING_SETUP_STEPS_STRINGS,
  ...BANK_STEPS_STRINGS,
  // Piece 21, decision 1840: the Turn on Logins stepper, kept beside its rules.
  ...LOGIN_STEPS_STRINGS,
  ...TEXT_THREAD_STRINGS,
  // Piece 35, decisions 3420 and 3421: the phone app's own words, kept beside its rules.
  ...MOBILE_STRINGS,
  ...NOTES_FILES_STRINGS,
  ...ADD_RECORD_STRINGS,
  ...MAPS_STRINGS,
  ...WORK_DOCUMENTS_STRINGS,
  ...CRM_READS_STRINGS,
  ...LOCK_STRINGS,
  ...CRM_EDIT_STRINGS,
  ...CRM_NOTES_STRINGS,
  ...DRAWER_STRINGS,
  ...EXPENSE_PHONE_STRINGS,
  // Decision 8060 (row T3197): the phone's Banking screen.
  ...BANKING_PHONE_STRINGS,
  ...KNOWLEDGE_PHONE_STRINGS,
  ...SERVICE_REPORT_PHONE_STRINGS,
  ...PICTURE_STRINGS,
  ...PUSH_STRINGS,
  // Decision 5130: a call through the office number, its page and the note it leaves.
  ...CALL_STRINGS,
  // Decisions 11780 to 11783: the Twilio card's numbers, voicemail words, hours and inbound call address, kept beside the phone system.
  ...PHONE_SYSTEM_STRINGS,
  ...PHONE_PAGES_STRINGS,
  // Decision 11880: the phone's Phone section, Recents, Voicemail and Texts.
  ...PHONE_SCREEN_STRINGS,
  ...APP_CALLING_STRINGS,
  ...PHONE_CALL_STRINGS,
  ...RECORD_CHECK_STRINGS,
  ...LIST_MARK_STRINGS,
  ...SITE_TYPE_STRINGS,
  ...SITE_TYPE_DEFAULT_STRINGS,
  // T2352: Settings › People › Confidentiality Roles and the Person page's tile.
  ...CONFIDENTIALITY_STRINGS,
  // Decision 10300 (T769): the role editor's Capabilities section, a label per seeded key.
  ...CAPABILITY_STRINGS,
  ...DOCUMENT_ACTION_STRINGS,
  ...CRM_DAY_STRINGS,
  ...CRM_CONTACTS_STRINGS,
  ...CRM_DEALS_STRINGS,
  ...EQUIPMENT_PHOTO_STRINGS,
  ...PHOTO_STANDARD_STRINGS,
  ...CRM_LEADS_STRINGS,
  ...CRM_LISTS_STRINGS,
  ...SYNC_STRINGS,
  // Pieces 22, 31 and 9, decision 1841: the three concept windows, each held word for word to its piece.
  ...CONCEPT_TOPIC_STRINGS,
  // Piece 15, decision 1842: Setup and How-To, kept beside its list.
  ...SETUP_GUIDES_STRINGS,
  // Batch LBL (T1620): the plain words a Settings page draws for a stored member.
  ...PLAIN_LABEL_STRINGS,
  // Piece 10, decision 1484: the Duplicates report and its merge, kept beside the report's rules.
  ...DUPLICATE_STRINGS,
  // Decision 7500 (T3098): the Merged view, the Undo Merge window and the import's earlier-run line.
  ...UNDO_MERGE_STRINGS,
  // Decision 7501 (T3102): Owned By, the Add Owner and End Ownership windows, the Subsidiaries tile's Past and the two History labels.
  ...COMPANY_OWNERSHIP_STRINGS,
  // Pieces 22 and 29, decisions 2591 to 2594: time and materials billed from the job, kept beside its rules.
  ...JOB_BILLING_STRINGS,
  // Piece 9, decisions 3010 to 3014: the one File row, kept beside its rules.
  ...FILE_ROW_STRINGS,
  // Piece 9, decisions 4180 and 4181: the backup alert, kept beside its rules.
  ...BACKUP_ALERT_STRINGS,
  // Decision 7690 (T3141): a Bought Out company's successor, the banner sentences, the two History lines and the refusals.
  ...COMPANY_SUCCESSOR_STRINGS,
  'home.title': 'BMS2',
  'home.body': 'The application is being built from its plan, one piece at a time. Nothing here is a finished screen yet.',
  'home.pieceOne': 'Piece 1, design tokens and layout primitives, is the foundation every later screen draws from.',
  'harness.title': 'Component Test Page',
  'harness.intro': 'Every component built so far, in every documented state. The visual-regression and accessibility runs point here.',
  'harness.section.tokens': 'Color tokens',
  'harness.section.status': 'Status groups',
  'harness.section.neutrals': 'Neutrals',
  'harness.section.sizing': 'Sizing tokens',
  'harness.section.type': 'Type scale',
  'harness.section.icons': 'Icon reference',
  'harness.section.breakpoints': 'Breakpoint scale',
  'harness.section.components': 'Components',
  'harness.column.token': 'Token',
  'harness.column.value': 'Value',
  'harness.column.meaning': 'Meaning',
  'harness.column.icon': 'Lucide Name',
  'harness.column.breakpoint': 'Breakpoint',
  'harness.column.width': 'Width',
  'harness.state': 'State',
  'harness.note.width': 'A state that is a width shows its cap only where the viewport is wider than the cap. Resize to compare.',
  'harness.container.content': 'Steps with the breakpoints: full width on a phone, then 720, 960, 1200 and 1440px from md up.',
  'harness.container.wide': 'Caps at 1600px and fills the viewport below it.',
  'harness.container.full': 'Never caps.',
  'harness.grid.narrow': 'Columns at least 200px wide, as many as fit the row.',
  'harness.grid.regular': 'Columns at least 280px wide, as many as fit the row.',
  'harness.grid.wide': 'Columns at least 360px wide, as many as fit the row.',
  'harness.sample.text': 'The quick brown fox jumps over the lazy dog.',
  'harness.sample.pictureAlt': 'Condenser fan with the guard removed',
  'harness.sample.pictureCaption': 'Figure 2: the condenser fan, guard off',
  'harness.sample.short': 'Sample',
  'harness.status.text': 'Text on background',
  'harness.status.fill': 'Dark fill: the text hex as a background',
  'harness.status.fill.error': 'The one dark fill that carries text: the destructive dialog header, white over it.',
  'harness.status.fill.good': 'No text over it: the Toggle Switch track when on.',
  'harness.status.fill.inactive': 'No text over it: the Toggle Switch track when off.',
  'harness.status.fill.warning': 'Nothing in the plan draws this fill yet.',
  'harness.status.label.good': 'GOOD',
  'harness.status.label.warning': 'WARNING',
  'harness.status.label.error': 'ERROR',
  'harness.status.label.inactive': 'INACTIVE',
  'layout.sample.item': 'Item',
  'dev.title': 'Test pages',
  'dev.intro': 'Every page built so far on this test environment, the piece that built it, and what to look at. A piece adds its row as it lands.',
  'dev.section.pages': 'Pages',
  'dev.column.piece': 'Piece',
  'dev.column.page': 'Page',
  'dev.column.lookFor': 'What to Look At',
  'dev.pieceTwo': 'Foundational data types and the seams',
  'dev.page.schema.lookFor': 'Under Server below: the database is reachable, three migrations are applied, and the seeded counts read 130 unit families, 1733 units, 9 status rows and 6 departments.',
  'dev.server.title': 'Server',
  'dev.server.loading': 'Asking the API…',
  'dev.server.unreachable': 'The API did not answer. On the test environment that is a fault; in a local preview with no API running it is expected.',
  'dev.server.envKind': 'Environment',
  'dev.server.commit': 'Commit',
  'dev.server.database': 'Database',
  'dev.server.reachable': 'Reachable',
  'dev.server.databaseUnreachable': 'Not reachable from the API',
  'dev.server.migrations': 'Migrations applied',
  'dev.server.latestMigration': 'Latest migration',
  'dev.server.seeded': 'Seeded: unit families · units · status rows · departments',
  'dev.server.none': 'none',
  // Decision 1455: the counts are an identified request's alone.
  'dev.server.platform': 'Platform: files · audit events · open failures',
  'dev.server.counts': 'Counts',
  'dev.server.signInForCounts': 'Sign in to read the migration, seed and platform counts.',
  'control.on': 'On',
  'control.off': 'Off',
  'control.yes': 'Yes',
  'control.no': 'No',
  'field.required': 'Required',
  'field.requiredMark': '*',
  'field.errorIcon': 'This field has an error',
  'button.moreActions': 'More actions',
  'pill.confidential': 'Confidential',
  'redacted.lock': 'Redacted; the full value is composed on the server',
  // T731: the Copy Field, a read-only value with a Copy button that reads Copied for a moment.
  'copyField.copy': 'Copy',
  'copyField.copied': 'Copied',
  'copyField.selected': 'Selected, press Ctrl+C',
  'password.show': 'Show password',
  'password.hide': 'Hide password',
  'password.capsLock': 'Caps Lock is on',
  'password.strength': 'Password strength',
  'dropdown.addNew': 'Add New',
  // Decision 1451: the window a page's + New opens is titled with the button's own words.
  'quickCreate.new': 'New',
  'dropdown.empty': 'No matches',
  'dropdown.loading': 'Searching…',
  'dropdown.toggle': 'Show suggestions',
  'dropdown.typeToSearch': 'Type to search, at least',
  'chips.remove': 'Remove',
  'chips.count': 'selected',
  'date.zoneNote': 'Shown in your time zone',
  'date.recordZone': 'The record keeps',
  'date.rangeStart': 'From',
  'date.rangeEnd': 'To',
  'date.rangeOutOfOrder': 'The end is before the start.',
  'numeric.invalid': 'Enter a number',
  'banner.dismiss': 'Dismiss',
  'dialog.confirm': 'Confirm',
  'dialog.cancel': 'Cancel',
  'dialog.destructive.title': 'Delete',
  'dialog.destructive.body': 'This permanently deletes',
  'dialog.destructive.instruction': 'To confirm, type the phrase exactly:',
  'dialog.destructive.confirm': 'Delete permanently',
  'dialog.destructive.bodyTrash': 'This deletes, to the Trash where an administrator restores it,',
  'dialog.destructive.confirmTrash': 'Delete to the Trash',
  'dialog.destructive.phraseLabel': 'Confirmation phrase',
  'watermark.viewOnly': 'VIEW ONLY',
  'harness.sample.save': 'Save',
  'harness.sample.saveDraft': 'Save as Draft',
  'harness.sample.send': 'Send',
  'harness.sample.deleteDeal': 'Delete the Deal',
  'harness.sample.deleteSubject': 'the Deal "Acme roof replacement"',
  'harness.sample.deletePhrase': 'delete Acme roof replacement',
  'harness.sample.fieldLabel': 'Field label',
  'harness.sample.placeholder': 'Type here',
  'harness.sample.fieldError': 'Enter a valid phone number',
  'harness.sample.phone': 'Phone',
  'harness.sample.ein': 'Employer identification number',
  'harness.sample.copyShort': 'Account code',
  'harness.sample.copyLong': 'Callback address',
  'harness.sample.ssn': 'Social security number',
  'harness.sample.routing': 'Bank routing number',
  'harness.sample.password': 'Password',
  'harness.sample.newPassword': 'New password',
  'harness.sample.taxId': 'Tax ID',
  'harness.sample.note': 'Internal note',
  'harness.sample.noteText': 'Called the site contact; the panel is scheduled for Tuesday.',
  'harness.sample.amount': 'Amount',
  'harness.sample.quantity': 'Quantity',
  'harness.sample.margin': 'Margin',
  'harness.sample.count': 'Count',
  'harness.sample.pill': 'Pill',
  'harness.sample.checkbox': 'Include archived records',
  'harness.sample.radioLegend': 'Delivery method',
  'harness.sample.radioA': 'Pick up',
  'harness.sample.radioB': 'Deliver',
  'harness.sample.radioC': 'Ship',
  'harness.sample.toggleName': 'Notifications',
  'harness.sample.segmentLegend': 'Transactions',
  'harness.sample.segmentAll': 'All',
  'harness.sample.segmentCategorized': 'Categorized',
  'harness.sample.segmentUncategorized': 'Uncategorized',
  'harness.sample.segmentUncategorizedCount': '3 Uncategorized',
  'harness.sample.favorite': 'Favorite this record',
  'harness.sample.documentBody': 'A document page sits under the watermark; select or click this text to see the overlay intercept nothing.',
  'harness.sample.entityItem': 'Item',
  'harness.sample.entityPerson': 'Person',
  'harness.sample.item': 'Item',
  'harness.sample.attendees': 'Attendees',
  'harness.sample.chipA': 'Jordan Ramirez',
  'harness.sample.chipB': 'Sam Okafor',
  'harness.sample.date': 'Start date',
  'harness.sample.dateTime': 'Starts at',
  'harness.sample.dateRange': 'Audit window',
  'harness.sample.dateError': 'The start date is after the end date',
  'harness.sample.banner.good': 'Every line balances.',
  'harness.sample.banner.warning': 'The rate on this line is older than the price list.',
  'harness.sample.banner.error': 'The bank feed stopped answering an hour ago.',
  'harness.sample.banner.inactive': 'This Deal is under a live NDA.',
  'harness.sample.banner.info': 'The next action on this record is Negotiate Terms, due 10/02/2026.',
  'harness.sample.banner.review': 'Review the rate',
  'harness.sample.saved': 'Saved. The site\u2019s hours are updated.',
  'harness.sample.progress': 'This page\u2019s rows read',
  'harness.sample.saveNow': 'Save',
  'harness.sample.banner.retry': 'Retry now',
  'harness.sample.dialogTitle': 'Close the period',
  'harness.sample.dialogBody': 'Closing September locks every journal entry dated inside it; a correction after this needs a reversing entry.',
  'harness.sample.dialogConfirm': 'Close the period',
  'harness.sample.openDialog': 'Open the Confirmation',
  'harness.sample.dropdownOpen': 'Open with a query typed: two-line rows, then the Add New row.',
  'harness.sample.dropdownEmpty': 'A query nothing matches: the empty-state row, then the Add New row.',
  'harness.sample.dropdownLoading': 'A query still out: the loading row, then the Add New row.',
  'harness.sample.destructiveNote': 'Click it: the typed-phrase dialog opens and the action runs only from inside it.',
  'harness.sample.zoneRecord': 'The record keeps Pacific time; a viewer elsewhere sees the note.',
  'field.enterAll': 'Enter all',
  'field.digits': 'digits',
  'password.rules': 'What the bar counts',
  'password.rule.length8': 'At least 8 characters',
  'password.rule.length14': 'At least 14 characters',
  'password.rule.twoKinds': 'Two kinds of character',
  'password.rule.allKinds': 'Lower case, upper case, a digit and a symbol',
  'harness.sample.errorNote': 'Marked after a submit attempt with digits missing: the message clears as the tenth digit is typed, with no second blur.',
  'harness.sample.confidentialTin': 'Away from focus the field shows the four-digit tail a TIN\'s disclosure row allows; click in to edit the digits.',
  'harness.sample.confidentialEin': 'Away from focus the field shows no tail: the EIN\'s row carries zero until a super admin raises it.',
  'harness.sample.dropdownSelected': 'A row chosen: the field shows its first line, and typing searches afresh.',
  'async.loading': 'Still loading…',
  'async.empty': 'Nothing here yet.',
  'async.failed': 'This did not load.',
  'async.retry': 'Retry',
  'avatar.needsAction': 'awaiting reply',
  'avatar.accepted': 'accepted',
  'avatar.declined': 'declined',
  'avatar.tentative': 'tentative',
  'tree.expand': 'Expand',
  'tree.collapse': 'Collapse',
  'grid.toggleRow': 'Toggle every permission in this row',
  'grid.toggleColumn': 'Toggle this permission for every row',
  'drag.grabbed': 'Picked up',
  'drag.instructions': 'Press Space to pick an item up, the arrow keys to move it, Space to drop it and Escape to put it back.',
  'drop.prompt': 'Drop files here, or',
  'drop.choose': 'Choose Files',
  // Decision 1450: the one-file face of the same drop area, for a field that takes one file and sends it on a later press.
  'drop.promptOne': 'Drop a file here, or',
  'drop.chooseOne': 'Choose File',
  'drop.picked': 'Picked:',
  'drop.progressOf': 'Upload progress for',
  'drop.remove': 'Remove',
  'drop.retry': 'Retry',
  'drop.status.queued': 'Queued',
  'drop.status.uploading': 'Uploading',
  'drop.status.done': 'Done',
  'drop.status.failed': 'Failed',
  'drop.status.stopped': 'Stopped',
  'viewer.download': 'Download',
  'viewer.print': 'Print',
  'viewer.viewOnly': 'View only',
  /** 🔨 Kept: the document view page's own download chevron reads it too (decision 0686 retired the modal that used to share this key with it). */
  'pdf.viewer.download': 'Download',
  'address.line1': 'Address',
  'address.line2': 'Address line 2',
  'address.addLine2': '+ Add Line 2',
  'address.locality': 'City',
  'address.pending': 'Checking…',
  'address.reverify': 'Re-Verify Address',
  'address.required': 'Required',
  'address.pattern': 'Not the format this country uses; for example',
  'address.suggestion.title': 'Suggested correction',
  'address.suggestion.pick': 'Close addresses \u2014 pick the right one',
  'address.suggestion.accept': 'Use This Address',
  'address.suggestion.keep': 'Keep as Typed',
  'address.failed': 'Verification is unavailable. The record still saves; the address stays unverified.',
  'address.busy': 'The map service is busy right now. Wait a moment, then press Verify Again.',
  'address.verifyAgain': 'Verify Again',
  'address.heldUntil': 'Verify Address is held until {time} after a failed attempt at {failedAt}.',
  'address.retryLater': 'A retry opens an hour after the failure.',
  'address.verify': 'Verify Address',
  'address.verifying': 'Verifying…',
  'address.verified': 'The address is verified.',
  // Decision 7560 (T3117): a Verified or Estimated Location with no coordinates says so on its page and offers Verify Address again.
  'address.coordinatesMissing': 'The address is verified, but its coordinates were not taken; press Verify Address again.',
  'address.verifiedTyped': 'The address verifies as typed; it is marked Verified once saved.',
  'address.fromCache': 'Answered from an earlier check of the same address.',
  'address.notApplicable': 'This location is named rather than addressed, so there is no address to verify. Type a street on it to verify one.',
  // Decision 0674: Pin here, Clear pin, and why a browser's location reading gave no point.
  'location.pin.title': 'Pin',
  'location.pin.here': 'Pin Here',
  'location.pin.clear': 'Clear Pin',
  'location.pin.reading': 'Reading Your Location…',
  'location.pin.none': 'No pin. Pin here stores where you are standing now, and the map reads it before the address.',
  'location.pin.by': 'pinned by',
  'location.pin.saved': 'Pinned where you are standing.',
  'location.pin.notPinned': 'Nothing was pinned.',
  'location.pin.cleared': 'Pin cleared; the map reads the verified address again.',
  'location.pin.fromLink': 'from a pasted Maps link',
  'mapsLink.label': 'Paste a Maps link',
  'mapsLink.hint': 'A Google Maps link a customer sent fills the fields from the place it names, for you to check.',
  'mapsLink.reading': 'Reading the link…',
  'mapsLink.placeholder': 'https://maps.app.goo.gl/…',
  'mapsLink.notMaps': 'That is not a Google Maps link, so nothing was filled.',
  'mapsLink.noPoint': 'That Maps link names no point, so nothing was filled.',
  'mapsLink.read': 'Filled from the link; check each field before you save. The point is saved as the pin.',
  'mapsLink.pinFailed': 'The location was saved and its pin was not; press Save to try the pin again.',
  'mapsLink.failed': 'The link could not be read just now, so nothing was filled.',
  'mapsLink.unread.expansion': 'The short link could not be opened.',
  'mapsLink.unread.point': 'The link carries no point.',
  'mapsLink.unread.address': 'No map service answered the address; type the street.',
  'mapsLink.unread.stateAndCounty': 'The Census Bureau did not answer the state and county.',
  'mapsLink.source.openstreetmap': 'Address from OpenStreetMap',
  'mapsLink.source.google': 'Address from Google Maps',
  'mapsLink.source.geoapify': 'Address from Geoapify',
  'mapsLink.source.census': 'State and county from the Census Bureau',
  'mapsLink.placePicked': 'Picked the place on file at that address.',
  'mapsLink.placeNew': 'No place on file at that address, so Add Site opens with the building filled.',
  'geolocation.denied': 'Location permission was refused. Allow location for this site in the browser, then try again. On an iPhone, turn on Settings › Privacy & Security › Location Services, set Location under Settings › Apps › Safari (Settings › Safari before iOS 18) to Ask Next Time Or When I Share or While Using the App, and choose Allow in the page menu under Website Settings › Location.',
  'geolocation.insecure': 'This page was not opened at a secure address, and a browser reads its location only over one. Open the application’s https:// address, then try again.',
  'geolocation.unavailable': 'The browser could not tell where you are. Step outside or turn on location services, then try again.',
  'geolocation.timeout': 'Reading your location took too long. Try again.',
  'geolocation.unsupported': 'This browser cannot read its location.',
  'address.unverifiable': 'The address could not be confirmed. It is marked Unverifiable.',
  'address.unverifiable.none': 'The map service holds nothing like this address. Check the street, the city and the postal code.',
  'address.unverifiable.street': 'The street and the postal code are real; the house number is not one the map service holds. Check the number.',
  'address.unverifiable.postalArea': 'The postal area is real; the street is not one the map service holds. Check the street name.',
  'address.unverifiableTyped': 'The address could not be confirmed; it is marked Unverifiable once saved.',
  'address.correction.field': 'Field',
  'address.correction.typed': 'As typed',
  'address.correction.offered': 'Suggested',
  'address.accepted': 'The suggestion was accepted and the address is Verified.',
  'address.acceptedTyped': 'The suggestion was accepted; it is marked Verified once saved.',
  'address.kept': 'Kept as typed; the address is marked Needs review.',
  'address.keptTyped': 'Kept as typed; it is marked Needs review once saved.',
  'address.stepHint': 'Check the typed address with the address service before saving.',
  'address.administrativeArea': 'State or province',
  'address.postalCode': 'Postal code',
  'address.countryCode': 'Country',
  'address.dependentLocality': 'District',
  'address.municipality': 'Municipality',
  'address.mailingCity': 'Mailing city',
  'address.county': 'County',
  // Decision 0737: the County waits for a United States state to pick from.
  'location.county.stateFirst': 'Enter the state first',
  'list.view': 'View',
  'list.records': 'records',
  'list.record': 'record',
  'site.inForce.none': 'No requirements or policies',
  'site.inForce.requirement': '{count} requirement',
  'site.inForce.requirements': '{count} requirements',
  'site.inForce.policy': '{count} policy',
  'site.inForce.policies': '{count} policies',
  'list.page': 'page',
  'list.of': 'of',
  'list.selected': 'selected',
  'list.selectAll': 'Select all',
  'list.matching': 'matching',
  'list.actions': 'Selection actions',
  'list.clear': 'Clear',
  // Decision 7780 (T3156): Select all N matching on a list page, and the actions that cannot take it yet.
  'list.selection.shape': 'A selection names the list read and the rows left out.',
  'list.selection.tooMany': 'This selection matches {count} rows, and an action over every matching row takes {max} at most. Narrow the filter, then try again.',
  'list.selection.cap': 'Over {max} rows: narrow the filter.',
  'list.export.action': 'Export',
  'list.export.capped': 'Export stopped at {max} rows; {count} more rows match. Narrow the filter to export the rest.',
  'list.export.failed': 'The export could not be written. Try again.',
  'list.selectPage': 'Select every row on this page',
  'list.selectRow': 'Select this row',
  // T3240: the compact head's select-page box counts the rows it would select.
  'list.onPage': 'on this page',
  'list.open': 'Open',
  'list.pageSize': 'Rows per page',
  'list.previous': 'Previous page',
  'list.next': 'Next page',
  'list.layout.list': 'List',
  'list.layout.map': 'Map',
  'map.zoomIn': 'Zoom In',
  'map.zoomOut': 'Zoom Out',
  'map.unplaced': 'rows have no coordinates yet; a verified address earns them.',
  'map.empty': 'No row on this list has coordinates yet. A verified address earns them.',
  'map.off': 'No map service is chosen. Pick one under',
  'map.settingsLink': 'Settings › Integrations › Maps',
  'map.attribution': '© OpenStreetMap contributors',
  'map.alt': 'Map',
  'map.dragHint': 'Drag to move the map; a pin opens its record.',
  'item.column.item': 'Item',
  'item.column.description': 'Description',
  'item.column.qty': 'Qty',
  'item.column.budget': 'Budget (Hours)',
  'item.column.unit': 'Unit',
  'item.column.rate': 'Rate',
  'item.column.amount': 'Amount',
  'item.column.account': 'Account',
  'item.column.tax': 'Tax',
  'item.column.discount': 'Discount',
  'item.entityItem': 'Item',
  'item.entityAccount': 'Account',
  'item.entityTax': 'Tax category',
  'item.freeText': 'Free text',
  'item.remove': 'Remove line',
  'item.reference.list': 'List',
  'item.reference.purchase': 'Purchase',
  'item.discountKind': 'Discount kind',
  'item.discount.percentage': '%',
  'item.discount.flat': 'Flat',
  'item.addRow': '+ Add Row',
  'item.addFreeText': 'Add Free-Text Row',
  'item.addDivider': 'Add Group Divider',
  'item.dividerName': 'Group name',
  'item.moveUp': 'Move up',
  'item.moveDown': 'Move down',
  'item.dragHandle': 'Drag to reorder',
  'item.subtotal': 'Sub Total',
  // Decision 0644: the totals box under a sales document's item table, worked out live while the form is typed.
  'tax.preview.tax': 'Tax',
  'tax.preview.salesTax': 'sales tax',
  'tax.preview.noState': 'No ship-to state, so no tax is worked out',
  'tax.preview.noRegistration': 'No registration in force for',
  'tax.preview.onThisDate': 'on this date',
  'tax.preview.noRate': 'No sales tax rate is entered for',
  'tax.preview.exempt': 'The customer is exempt from sales tax in',
  'tax.preview.categoryNotTaxable': 'does not tax these lines',
  'tax.preview.noStatewideTax': 'No statewide sales tax in',
  'tax.preview.failed': 'The tax could not be worked out just now. The figures shown are the last ones worked out, and saving works the tax out again.',
  // Decision 0736: a row per jurisdiction, and the grey line a case headed for the review report carries.
  'tax.preview.on': 'on',
  // Decision 1001: a reduced-rate category's tax reads on its own row, naming the category.
  'tax.preview.reducedRate': 'reduced rate',
  'tax.preview.countyNotEntered': 'Only the state tax is charged: the ship-to has no county entered, and local tax is owed by county in',
  'tax.preview.jurisdictionNotConfirmed': 'has local taxes this table does not hold every row for, so only what the ship-to matched is charged until its jurisdiction is confirmed on the site.',
  'tax.preview.onReport': 'It is on the review report.',
  // Decision 7530: a company that is not Active starts no new accounting document and holds its drafts.
  'company.inactive.newDocument': '{company} is {status}, so a new document cannot name it.',
  'company.inactive.hold': '{company} is {status}, so this {document} cannot be {act}.',
  // Decision 7460: a document whose ship-to is only Estimated is held until the Location is Verified.
  'tax.hold.sentence': 'Local tax waits for the address {address} to be confirmed. Press Verified by User on the Location.',
  'tax.hold.generic': 'Local tax waits for the address to be confirmed. Press Verified by User on the Location.',
  'tax.hold.notFinal': 'The tax shown is the state alone and is not final.',
  'tax.hold.openLocation': 'Open the Location',
  'tax.hold.refreshed': 'Local tax was read again at the first send: {from} became {to}',
  'tax.hold.reopen': 'The local tax for this address has changed since the document was saved. Reopen it and save it so the tax recalculates, then send it.',
  'history.audit.documentTaxRefreshed': 'Tax worked out again at the first send',
  // Decision 0904: a line charged over a Default treatment, or over no answer at all, says so in the same grey line.
  'tax.preview.chargedAtStandard': 'Charged at the standard rate:',
  'tax.preview.in': 'in',
  'tax.preview.nobodyAnswered': 'nobody has answered how',
  'tax.preview.taxes': 'taxes',
  'tax.preview.onReviewReport': 'On the review report.',
  // Decision 0904: the four treatments a state gives a tax category, the one list every state answers from.
  'tax.treatment.notTaxable': 'Not taxable',
  'tax.treatment.default': 'Default',
  'tax.treatment.taxable': 'Taxable',
  'tax.treatment.custom': 'Custom',
  'tax.treatment.review': 'review',
  // Decision 0739: the site page's Tax Jurisdiction.
  'site.taxJurisdiction.loading': 'Looking up the tax jurisdiction…',
  'site.taxJurisdiction.combined': 'Combined',
  'site.taxJurisdiction.from': 'from',
  'site.taxJurisdiction.scheduled': 'scheduled',
  'site.taxJurisdiction.noRate': 'no rate entered',
  'site.taxJurisdiction.coverageNone': 'No local sales tax is owed in this state.',
  'site.taxJurisdiction.coveragePartial': 'The state has local taxes the rate table holds only some rows for, so a person confirms each address.',
  'site.taxJurisdiction.coverageComplete': 'Every local sales tax in this state is in the rate table.',
  'site.taxJurisdiction.confirmedOn': 'Jurisdiction confirmed on',
  'site.taxJurisdiction.warning.noState': 'The address names no state, so no tax is worked out for this site.',
  'site.taxJurisdiction.warning.countyNotEntered': 'County not entered: this site is charged the state rate alone and is on the review report.',
  'site.taxJurisdiction.warning.addressEstimated': 'The house number is only estimated. Verify the address by hand before the local tax is looked up.',
  'site.taxJurisdiction.warning.notConfirmed': 'Not confirmed: this site is charged the rows it matched and is on the review report until somebody confirms nothing else is owed here.',
  'site.taxJurisdiction.enterCounty': 'Enter the county on the location',
  'site.taxJurisdiction.confirm': 'Confirm Jurisdiction',
  'site.taxJurisdiction.confirmed': 'The jurisdiction is confirmed; the site leaves the review report.',
  'site.taxJurisdiction.notRegistered': 'No registration is in force in this state today, so nothing is charged here yet.',
  'site.taxJurisdiction.forms': 'Exemption forms this site needs',
  'site.taxJurisdiction.onFile': 'On file',
  'site.taxJurisdiction.notOnFile': 'Not on file',
  'site.taxJurisdiction.recordCertificate': 'Record a Certificate',
  'site.taxJurisdiction.municipalCertificate': 'its own certificate',
  'site.taxJurisdiction.library': 'Open the forms library',
  'invoice.new.shipTo': 'Ship to',
  'invoice.new.notChosen': 'Not chosen',
  'rte.toolbar': 'Formatting',
  'rte.heading': 'Style',
  'rte.bold': 'Bold',
  'rte.italic': 'Italic',
  'rte.underline': 'Underline',
  'rte.strike': 'Strikethrough',
  'rte.bullets': 'Bulleted list',
  'rte.numbers': 'Numbered list',
  'rte.alignLeft': 'Align left',
  'rte.alignCenter': 'Align center',
  'rte.alignRight': 'Align right',
  'rte.alignJustify': 'Justify',
  'rte.image': 'Insert image',
  'rte.font': 'Font',
  'rte.size': 'Size',
  'rte.size.points': '{n} pt',
  'rte.textColor': 'Text color',
  'rte.highlight': 'Highlight color',
  'rte.placeholder': 'Insert placeholder',
  'rte.undo': 'Undo',
  'rte.redo': 'Redo',
  'rte.outdent': 'Decrease indent',
  'rte.indent': 'Increase indent',
  'rte.lineSpacing': 'Line spacing',
  'rte.numbering': 'List numbering',
  'rte.numbering.continue': 'Continue Previous List',
  'rte.numbering.startAt': 'Start At…',
  'rte.numbering.none': 'Put the caret in a numbered list first',
  'rte.numbering.noPrevious': 'There is no numbered list before this one to continue',
  'rte.numbering.startLabel': 'Start at',
  'rte.numbering.apply': 'Set Start',
  'rte.quote': 'Quote',
  'rte.clearFormatting': 'Clear formatting',
  'rte.signature': 'Insert signature',
  'rte.more': 'More formatting',
  'rte.undo.none': 'Nothing to undo yet',
  'rte.redo.none': 'Nothing to redo',
  'rte.outdent.none': 'Put the caret in a list or an indented line first',
  'rte.clearFormatting.none': 'Select formatted words first',
  'item.changeItem': 'Change the item',
  'item.changeUnit': 'Change the unit',
  'item.unitChoose': 'Choose a unit',
  'item.perUnit': 'per',
  'item.qtyRoundedUp': 'Rounded up from {from}',
  'item.qtyRaised': 'Raised to the minimum of {minimum}',
  'item.qtyRoundedUpWhy': 'This item sells in steps of {step}.',
  'item.qtyRaisedWhy': 'This item sells no fewer than {minimum}.',
  'item.dividerNeedsName': 'Every group divider carries a name.',
  'tile.add': '+ Add',
  'tile.issue': 'Issue',
  'tile.collapse': 'Collapse this section',
  'tile.expand': 'Expand this section',
  /** 🔒 A tile row's running span reads to Present (decision 6701; T2973). */
  'tile.span.present': 'Present',
  // Decision 6211: the chevron on a list's group band and a Discovery card's group.
  'group.collapse': 'Collapse this group',
  'group.expand': 'Expand this group',
  'tile.shownOf': 'shown of',
  'tile.showMore': 'Show more',
  'tile.showFewer': 'Show fewer',
  'tile.edit': 'Edit layout',
  'tile.done': 'Done',
  'tile.arrange': 'Tile order',
  'tile.locked': 'Locked',
  'tile.columns': 'Width in columns',
  'tile.rows': 'Height in blocks',
  'tile.shorter': 'One block shorter',
  'tile.taller': 'One block taller',
  'tile.remove': 'Remove tile',
  'tile.columnsOf': 'columns',
  'tile.unlocked': 'Unlocked',
  'event.band': 'Events',
  'event.expand': 'Show the event',
  'event.collapse': 'Hide the event',
  'event.edit': 'Edit in the Event modal',
  'event.ends': 'Ends',
  'event.siteRequirements': 'Site requirements',
  'event.field.subject': 'Subject',
  'event.field.when': 'When',
  'event.field.location': 'Location',
  'event.field.meetingLink': 'Meeting link',
  'event.field.callNumber': 'Call number',
  'event.field.attendees': 'Attendees',
  'event.field.owner': 'Owner',
  'event.field.notes': 'Notes',
  'harness.sample.people': 'People',
  'harness.sample.viewAll': 'All people',
  'harness.sample.viewEmployees': 'Employees',
  'harness.sample.viewContacts': 'Contacts',
  'harness.sample.colName': 'Name',
  'harness.sample.colCompany': 'Company',
  'harness.sample.colTitle': 'Title',
  'harness.sample.colLastContact': 'Last contact',
  'harness.sample.colStatus': 'Status',
  'harness.sample.colOpenAmount': 'Open amount',
  'harness.sample.emptyPeople': 'Add a person from the People page, or import a list.',
  'harness.sample.serviceError': 'The People service answered 503.',
  'harness.sample.termContainer': 'Term Container',
  'harness.sample.assetTree': 'Asset register',
  'harness.sample.dashboard': 'Dashboard',
  'harness.sample.columns': 'Columns',
  'harness.sample.module': 'Module',
  'harness.sample.roleCaption': 'Role: Field technician',
  'harness.sample.moduleCompanies': 'Companies',
  'harness.sample.moduleAccounting': 'Accounting',
  'harness.sample.permRead': 'Read',
  'harness.sample.permWrite': 'Write',
  'harness.sample.permEdit': 'Edit',
  'harness.sample.permDelete': 'Delete',
  'harness.sample.tileStatus': 'Deal status',
  'harness.sample.tileDetails': 'Details',
  'harness.sample.tileNotes': 'Notes',
  'harness.sample.tileFiles': 'Files',
  'harness.sample.tileSites': 'Sites',
  'harness.sample.tileContacts': 'Contact details',
  'harness.sample.tileSummary': 'Summary',
  'harness.sample.tileTimeline': 'Timeline',
  'harness.sample.tileChecklist': 'Checklist',
  'harness.sample.lvStage': 'Stage',
  'harness.sample.lvValue': 'Value',
  'harness.sample.lvOwner': 'Owner',
  'harness.sample.lvClose': 'Close date',
  'harness.sample.lvBuilding': 'Building',
  'harness.sample.lvDesk': 'Desk',
  'harness.sample.taxStandard': 'Standard',
  'harness.sample.taxExempt': 'Exempt',
  'harness.sample.taxReduced': 'Reduced',
  'harness.detailList.badge': 'Active',
  'harness.detailList.action': 'Open',
  'harness.detailList.empty': 'No overrides have been issued.',
  'harness.card.title': 'Document Numbering',
  'harness.card.body': 'Each document kind counts on from its own prefix.',
  'harness.card.action': 'Add',
  'harness.composite.listWide': 'Two hundred rows in a fixed-height host: the header stays, the rows scroll, the footer pages.',
  'harness.composite.listTwoRows': 'Two rows in the same host: the identical footprint, and nothing shrink-wraps.',
  'harness.composite.listCompact': 'A host narrower than 320px: the band replaces the header row and the rows collapse to two lines.',
  'harness.composite.listGrouped': 'Grouped by status in the caller\'s order; a column sort reorders rows inside each group only.',
  'harness.composite.listSelected': 'The page ticked: the count, the action bar with Clear and nothing else, and Select all matching.',
  'harness.composite.canvasThree': 'Three tiles and the width for them: three columns, one block tall or two.',
  'harness.composite.canvasTwo': 'Two tiles on a wide host: two columns, never three with a gap.',
  'harness.composite.canvasOne': 'Below the collapse point every tile takes the full width whatever its span.',
  'harness.composite.canvasEdit': 'Edit mode: the drag list above, the padlocks on the tiles, and the keyboard reorders.',
  'harness.composite.eventNarrow': 'A host narrower than 432px: the Notes preview drops.',
  'harness.composite.addressLocal': 'A four-digit ZIP against the US row: the message names the example and no provider call is spent.',
  'harness.composite.fileView': 'A View link: no Download control exists, the watermark tiles the body, and Ctrl+P does nothing.',
  'harness.resolveLive.title': 'One thing is missing first',
  'harness.resolveLive.refusal': 'PA carries no rate in the Sales Tax Rate Table on 2026-09-01; enter the state’s rate there before registering',
  'harness.resolveLive.refused': 'a later row already stands for this state',
  'harness.resolveLive.confirm': 'Save it and carry on',
  'harness.resolveLive.field': 'State rate (%)',
  'harness.resolveLive.gathering': 'The refusal, the form that cures it, and a Primary that is dead until the form holds something.',
  'harness.resolveLive.refusedNote': 'The cure itself is refused: the modal stays open and swaps in what the server said, and the original act is never re-run.',
  'harness.resolveLive.writingNote': 'The write is left in flight, so the busy line shows and the Primary refuses a second press.',
  'harness.help.definition': 'A concept explained in prose: a lead, then the qualifications under it.',
  'harness.help.comparison': 'Two named actions compared in a table that scrolls sideways rather than widening the Body.',

  // Modal and overlay system (piece 5).
  'modal.close': 'Close',
  'addNew.which.title': 'Add a Record',
  'addNew.which.legend': 'Which kind of record',
  'addNew.which.next': 'Next',
  'addNew.kind.companyRecord': 'Company',
  'addNew.kind.personRecord': 'Person',
  'addNew.kind.locationRecord': 'Location',
  'addNew.kind.deal': 'Deal',
  'addNew.kind.item': 'Item',
  'addNew.kind.assetNode': 'Asset',
  'addNew.employee.title': 'Person Added',
  'addNew.employee.notice': 'is on file as a person. Somebody our own company employs is hired through HR before they can be picked here.',
  'addNew.employee.open': 'Open Their Page',
  'addNew.document.hint': 'It is raised on its own page, with its lines. Open a new one in another tab, save it there, then pick it here.',
  'addNew.document.open': 'Open a New',
  'addNew.unbuilt.taxCategory': 'No screen adds a tax category yet. The tax categories are reference data loaded with the install, so ask the administrator for a new one.',
  'addNew.unbuilt.checklistTemplate': 'A checklist template is written by the administrator on the Knowledge section’s Checklists tab, which only the administrator can open. Open it in another tab if you are the administrator, or ask them to add the template, then pick it here.',
  'addNew.checklistTemplate.open': 'Open Checklist Templates',
  'addNew.entity.taxCategory': 'Tax Category',
  'addNew.entity.checklistTemplate': 'Checklist Template',
  'addNew.account': 'Account',
  'modal.cancel': 'Cancel',
  // 🔒 Why a reason window's Primary is held while its reason is blank (piece 5, decision 0559).
  'modal.reason.needsReason': 'Type the reason first.',
  'modal.save': 'Save',
  'modal.deletePhrase': 'DELETE',

  // The resolve-live refusal (piece 5, decision 0442): a refusal the person in
  // front of the screen is allowed to cure opens a modal that gathers the cure.
  'resolveLive.heading': 'One thing is missing first',
  'resolveLive.hint': 'Fill this in here and the action you asked for runs straight afterwards. Nothing else is lost.',
  'resolveLive.writing': 'Saving what was missing…',
  'resolveLive.retrying': 'Running the original action…',

  // Help and About (piece 5, decision 0443): one explanation per concept,
  // opened from an information control beside the page title.
  'help.open': 'What is this?',
  // Distinct from the title bar's own Close, so the two controls in one dialog
  // do not read as the same control to somebody who cannot see them.
  'help.close': 'Close help',
  'help.subtitle': 'Help',
  // Piece 27, decision 0657 (T194): what a column set is, with the columns each set prints drawn from the sets themselves.
  'help.printColumnSet.title': 'Column Set',
  'help.printColumnSet.lead1': 'A column set is the list of columns the line table prints, in order. Choosing another set changes which columns the sheet draws; the rows beneath it then show, rename and size each column of the set chosen.',
  'help.printColumnSet.column.set': 'Column Set',
  'help.printColumnSet.column.prints': 'Columns It Prints',
  'help.printColumnSet.note1': 'Item and Description print as one cell, the item’s name over its SKU and its description, so a set naming both prints one column fewer than it names.',
  'help.printColumnSet.note2': 'Only the line table takes a column set. Every other region prints the same content whatever set it names, so the choice is offered on the line table alone.',
  'help.accountingPeriod.title': 'Accounting Period',
  'help.accountingPeriod.lead1':
    'An accounting period is one month of your fiscal year. The months are cut from the fiscal year start recorded for the organization, and a month is created the first time something needs it.',
  'help.accountingPeriod.lead2':
    'Closing a period locks it. Once a period is Closed nothing can post into it: a document dated into a closed month is refused when it is saved, and the refusal names the period that stopped it.',
  'help.accountingPeriod.note1':
    'A period closes only when every earlier period is already Closed, so there is never a gap in the closed run with a later month resting on top of it.',
  'help.accountingPeriod.note2':
    'There is no override that posts past the lock. An Admin reopens the period with a written reason, the entry posts, and the period is closed again. Every close and every reopen is recorded, and reopens are listed with their reasons and who gave them.',
  'help.accountingPeriod.note3':
    'A scheduled posting that would land in a closed month fails and waits on the blocked postings queue. Nothing ever quietly re-dates itself into the open month.',
  'help.chartOfAccounts.title': 'Chart of Accounts',
  'help.chartOfAccounts.lead1':
    'The chart of accounts is the list of accounts every document posts into. Each account carries a number, a name and a type: Asset, Liability, Equity, Revenue or Expense. The numbers run in bands, with assets in the 1000s, liabilities in the 2000s and operating expenses from the 6000s up.',
  'help.chartOfAccounts.lead2':
    'An account can sit under a parent account, so sub-accounts roll up into it. Each one also carries a subtype, and it is the subtype rather than the number that groups the balance sheet, separating current assets from fixed ones and current liabilities from long-term ones.',
  'help.chartOfAccounts.note1':
    'A fresh installation is seeded with the base set of accounts, so the system can post from the first day. Accounts are added to it as the business needs them.',
  'help.chartOfAccounts.note2':
    'An account is never deleted. Clearing Is Active hides it from new entries and keeps every posting already made against it.',
  // Decision 0708 (T230): the Sales tax page's explanations, transcribed from piece 25, behind the help glyph in its title bar.
  'help.salesTaxSettings.title': 'Sales tax: each state, its rates and returns, and the nexus warning',
  'help.salesTaxSettings.lead1': 'Every state is one line of the table, the ones we are registered in first. A line\u2019s count says how many of its returns are outstanding and how many of its charges wait for review. Open a state and its facts stand across the top: its registration and account number, when its tax becomes due, how often it files and the next return due, its threshold and sales, its state rate and its review lines. A fact with an act carries it beside the fact.',
  'help.salesTaxSettings.lead2': 'Under the facts, five tabs hold the rest, one open at a time. Rates holds the statewide series and the local rates. What it taxes holds one answer per tax category. Forms holds the exemption forms. Returns holds every return period for the state, paid ones included. Review holds the state\u2019s unsettled charges, each opening where it is settled. The tab last opened stays open as you move between states.',
  'help.salesTaxSettings.note1': 'Each state\u2019s rate is a dated series. A rate change is a new row from its own date, and the row it replaces is kept, so a credit note against an old invoice still reads the rate that invoice was issued under. Every rate names its source, and a rate the install seeded is marked until somebody enters the state\u2019s own figure over it. Check for updates prompts a review against each state\u2019s published figure and writes no rate.',
  'help.salesTaxSettings.note2': 'A local rate belongs to a county, a town or a special district, each a dated series of its own. The state\u2019s coverage says what a ship-to matching none of them owes: under Complete nothing more, under Partial the state alone and a place on the review report until somebody confirms its site\u2019s jurisdiction. A state with no rate cannot be registered.',
  'help.salesTaxSettings.note3': 'What each state taxes is one dated answer per tax category. A category with no answer, or one answered Default, is charged at the standard rate and stays on the review report until somebody answers it. Below the table, Returns due lists every return not yet paid, and the review report lists each state with lines to settle.',
  'help.salesTaxSettings.note4': 'The nexus Warning is a whole percentage of a state\u2019s published threshold, above nought and below a hundred, and it is a super admin\u2019s to change. The Critical fires the moment nexus is established, is never dismissed silently, and is emailed to the accounting group\u2019s shared mailbox where one is set under Documents Inbox rules.',
  // 🔒 T252 (decision 0724): the Intent card's four rows, one topic each, opened together from the card's head.
  'help.dealIntent.title': 'The Intent card, row by row',
  'help.dealBudget.title': 'Budget',
  'help.dealBudget.lead1': 'Budget is what the prospect means to spend: a Target, the price they are aiming at, and a Maximum, the most they will spend. The Maximum can be left empty.',
  'help.dealBudget.note1': 'The row turns green when a Target is saved; there is no box to tick. Every pair saved is kept in the Budget history with who saved it and when.',
  'help.dealBudget.note2': 'Deal Progress ticks Budget once a Target is saved, and Next Action lists Budget until then.',
  'help.dealBudget.note3': 'Where the Deal value stands above the Maximum, the banner warns that the budget is too low for the specified job, and Next Action leads with talking to the customer about the budget.',
  'help.dealAuthority.title': 'Authority',
  'help.dealAuthority.lead1': 'Authority is who on the customer\u2019s side decides: the people who sign for the work, the ones who sway it, and the part each plays.',
  'help.dealAuthority.note1': 'List the people who decide with Add person, or tag a contact as Authority. One tagged person is enough to complete the row. You can also tick the row yourself, which is taken with nobody listed.',
  'help.dealAuthority.note2': 'Mark Complete on Intent needs no note once somebody is tagged. A row you tick with nobody tagged needs a note, so write how the decision gets made.',
  'help.dealAuthority.note3': 'Deal Progress ticks Authority when somebody is tagged or the row is ticked, and Next Action lists Authority until then.',
  'help.dealNeed.title': 'Need',
  'help.dealNeed.lead1': 'Need is the problem the customer wants solved, written in their own words.',
  'help.dealNeed.note1': 'Tick the row once the need is stated. Mark Complete on Intent also needs a note on it.',
  'help.dealNeed.note2': 'Deal Progress ticks Need when the row is ticked, and Next Action lists Need until then.',
  'help.dealTiming.title': 'Timing',
  'help.dealTiming.lead1': 'Timing is when the customer needs the work: when they expect a quote, when the work starts and when it finishes.',
  'help.dealTiming.note1': 'The row turns green when an Expected quote is saved; there is no box to tick. The Deal\u2019s Expected close is its own date and does not follow the Expected quote.',
  'help.dealTiming.note2': 'Deal Progress ticks Timing once an Expected quote is saved, Next Action lists Timing until then, and the banner shows whichever dates are set.',
  'help.dealValue.title': 'Deal Value',
  'help.dealValue.lead1': 'The Deal value comes from the best information this Deal holds, taken in a fixed order.',
  'help.dealValue.lead2': 'A sent quote\u2019s total, before tax.',
  'help.dealValue.lead3': 'The bills of materials, each line at its item\u2019s minimum margin, where that stands above the value. A list raises the value and never lowers it.',
  'help.dealValue.lead4': 'The Budget Target.',
  'help.dealValue.lead5': 'The value as entered on the Deal.',
  'help.dealValue.thisQuote': 'This Deal\u2019s value comes from the sent quote\u2019s total.',
  'help.dealValue.thisBom': 'This Deal\u2019s value comes from the bill of materials at minimum margin.',
  'help.dealValue.thisBudget': 'This Deal\u2019s value comes from the Budget Target.',
  'help.dealValue.thisEntered': 'This Deal\u2019s value is the value as it stands, because nothing better is on the Deal yet or its bill of materials prices below it.',
  'help.dealValue.note1': 'When the value is above the Budget Maximum, the page shows Budget Too Low, and the first next action is to talk with the customer about the budget.',
  'help.writeOffVsVoid.title': 'Write Off and Void',
  'help.writeOffVsVoid.lead1':
    'Both of these end an invoice that is not going to be paid as it stands, and they say two different things about why. Write Off says the sale happened and the money cannot be collected. Void says the invoice should never have been raised.',
  'help.writeOffVsVoid.column.question': 'Question',
  'help.writeOffVsVoid.column.writeOff': 'Write Off',
  'help.writeOffVsVoid.column.void': 'Void',
  'help.writeOffVsVoid.row.when': 'When to use it',
  'help.writeOffVsVoid.when.writeOff': 'The customer will not pay and the debt is uncollectable.',
  'help.writeOffVsVoid.when.void': 'The invoice was raised in error: wrong customer, wrong goods, or it should not exist at all.',
  'help.writeOffVsVoid.row.posts': 'What it posts',
  'help.writeOffVsVoid.posts.writeOff': 'Debit 6950 Bad Debt Expense, credit 1100 Accounts Receivable, for what the invoice still owes.',
  'help.writeOffVsVoid.posts.void': 'One reversing entry against the original, swapping its debits and credits, dated today.',
  'help.writeOffVsVoid.row.invoice': 'The invoice afterwards',
  'help.writeOffVsVoid.invoice.writeOff': 'Stays on the books marked Written Off, carrying the reason and who authorized it.',
  'help.writeOffVsVoid.invoice.void': 'Reversed, and out of the documents figure along with the allocations against it.',
  'help.writeOffVsVoid.row.cash': 'The cash view',
  'help.writeOffVsVoid.cash.writeOff': 'Never shows it. The entry is accrual-only, because no cash moved.',
  'help.writeOffVsVoid.cash.void': 'Nothing to show: the original entry has been undone.',
  'help.writeOffVsVoid.row.undo': 'Undoing it',
  'help.writeOffVsVoid.undo.writeOff': 'A payment arriving later reverses the write-off first and then allocates.',
  'help.writeOffVsVoid.undo.void': 'Nothing to undo. Raise a fresh invoice if one is still owed.',
  'help.writeOffVsVoid.note1':
    'A written-off invoice is never voided, because the sale did happen. That is why an invoice already written off offers no Void.',
  'help.writeOffVsVoid.note2':
    "Where the state's sales tax registration is on the accrual-with-bad-debt-credit basis, the tax portion of a write-off goes back against that state's Sales Tax Payable account instead of Bad Debt Expense, and reduces the next return.",
  'help.writeOffVsVoid.note3': "A void is refused while today's accounting period is Closed, until an Admin reopens it.",

  'note.title': 'Add Note',
  'note.editTitle': 'Edit Note',
  'note.edit': 'Edit note',
  'note.showMore': 'Show more',
  'note.showLess': 'Show less',
  'note.edited': 'edited',
  'note.reply.add': 'Reply',
  'note.reply.one': 'reply',
  'note.reply.many': 'replies',
  // T2165: a counted noun's two words, read by `counted`, so no sentence builds a plural by appending an s.
  'unit.bomLine.one': 'BOM line',
  'unit.bomLine.other': 'BOM lines',
  'unit.rfq.one': 'RFQ',
  'unit.rfq.other': 'RFQs',
  'unit.releasedDocument.one': 'already-released document',
  'unit.releasedDocument.other': 'already-released documents',
  'unit.auditRow.one': 'audit row',
  'unit.auditRow.other': 'audit rows',
  'unit.earlierRequest.one': 'earlier request',
  'unit.earlierRequest.other': 'earlier requests',
  'unit.companyPair.one': 'exact company pair',
  'unit.companyPair.other': 'exact company pairs',
  'unit.company.one': 'company',
  'unit.company.other': 'companies',
  'unit.person.one': 'person',
  'unit.person.other': 'people',
  'unit.site.one': 'site',
  'unit.site.other': 'sites',
  'unit.line.one': 'line',
  'unit.line.other': 'lines',
  'unit.location.one': 'location',
  'unit.location.other': 'locations',
  'unit.minute.one': 'minute',
  'unit.minute.other': 'minutes',
  'unit.month.one': 'month',
  'unit.month.other': 'months',
  'unit.optionalLine.one': 'optional line',
  'unit.optionalLine.other': 'optional lines',
  'unit.stateRate.one': 'state rate',
  'unit.stateRate.other': 'state rates',
  'unit.transaction.one': 'transaction',
  'unit.transaction.other': 'transactions',
  'unit.value.one': 'value',
  'unit.value.other': 'values',
  'unit.workingDay.one': 'working day',
  'unit.workingDay.other': 'working days',
  'unit.hour.one': 'hour',
  'unit.hour.other': 'hours',
  'unit.day.one': 'day',
  'unit.day.other': 'days',
  'unit.invoice.one': 'invoice',
  'unit.invoice.other': 'invoices',
  // T2362: the accounts and deals the Sales Reporting Dashboard's metric panels count.
  'unit.account.one': 'account',
  'unit.account.other': 'accounts',
  'unit.deal.one': 'deal',
  'unit.deal.other': 'deals',
  'unit.creditNote.one': 'credit note',
  'unit.creditNote.other': 'credit notes',
  'unit.customerPayment.one': 'customer payment',
  'unit.customerPayment.other': 'customer payments',
  'unit.bill.one': 'bill',
  'unit.bill.other': 'bills',
  'unit.vendorCredit.one': 'vendor credit',
  'unit.vendorCredit.other': 'vendor credits',
  // Decision 12700: what goes with a deleted Deal, counted in its window.
  'unit.note.one': 'note',
  'unit.note.other': 'notes',
  'unit.file.one': 'file',
  'unit.file.other': 'files',
  'unit.contact.one': 'contact',
  'unit.contact.other': 'contacts',
  'note.reply.hide': 'Hide replies',
  'note.reply.label': 'Write a reply',
  'note.reply.send': 'Reply',
  'note.reply.delete': 'Delete reply',
  'note.reply.empty': 'Type a reply before sending it.',
  // 🔒 The composer every notes list opens with (decision 0910).
  'note.composer.label': 'New note',
  'note.composer.placeholder': 'Write a note…',
  'note.composer.add': 'Add',
  'note.composer.empty': 'Type a note before adding it.',
  'note.composer.saving': 'Adding the note…',
  'note.body': 'Note',
  // 🔒 Decision 7180: the note window's switch between the plain box and the Rich Text Editor.
  'note.richText': 'Rich text',
  'note.richTextOffHint': 'This note carries formatting. Turning Rich text off keeps the words and drops the bold, colors and lists.',
  'note.richTextHint': 'Rich text keeps the bold, colors and lists of a pasted email. Off, a paste keeps the words only.',
  'note.save': 'Save Note',
  'note.taggedRecord': 'Tagged record',
  'note.about': 'On',
  'note.alsoAbout': 'Also about',
  'note.alsoAboutHint': 'Other records this note concerns. It stays on the record above.',
  'note.writtenOn': 'Written on',
  'note.recordEntity': 'record',
  'note.issueFlag': 'Issue flag',
  'note.activityKind': 'Activity kind',
  'note.direction': 'Direction',
  'note.inbound': 'Inbound',
  'note.outbound': 'Outbound',
  'note.duration': 'Duration (minutes)',
  'note.confidentiality': 'Confidentiality',
  'note.none': 'None',
  'note.record': 'Record',
  'note.flagBanner': 'This note is flagged:',
  'event.title': 'Add Event',
  'event.editTitle': 'Edit Event',
  'event.save': 'Save Event',
  'event.cancel': 'Cancel meeting',
  'event.reinstate': 'Reinstate',
  'event.cancelTitle': 'Cancel this meeting?',
  'event.cancelBody': 'The meeting comes off the calendar and its attendees keep the record of it. You can reinstate it afterwards.',
  'event.cancelConfirm': 'Cancel the Meeting',
  'event.showCancelled': 'Show canceled',
  'calendar.today': 'Today',
  'calendar.month': 'Month',
  'calendar.week': 'Week',
  'calendar.previousMonth': 'The month before',
  'calendar.nextMonth': 'The month after',
  'calendar.more': '{n} more',
  'event.legend.inPerson': 'In-Person / Location',
  'event.legend.online': 'Online Meeting',
  'event.legend.call': 'Call',
  'event.legend.none': 'No Location Set',
  'event.legend.pto': 'Time Off',
  'event.legend.workOrder': 'Scheduled Work',
  'event.field.taggedRecord': 'Tagged record',
  'calendar.legendTitle': 'Event type legend',
  'calendar.addEvent': '+ Add Event',
  'calendar.previousWeek': 'The week before',
  'calendar.nextWeek': 'The week after',
  'calendar.travel': 'Travel',
  'calendar.addOnDay': '+ Add on This Day',
  'calendar.moreDots': '+{n}',
  'calendar.noEventsOn': 'Nothing on this day.',
  'calendar.pickDay': 'Tap a day to see what it holds.',
  'calendar.team': 'Team',
  'calendar.myCalendar': 'My Calendar',
  'calendar.alwaysShown': 'Always shown; cannot be unchecked',
  'calendar.directReports': 'Direct reports',
  'calendar.viewOnly': 'View Only',
  'calendar.overlay': 'Overlay',
  'calendar.notShown': 'Not shown',
  'calendar.viewing': 'Viewing',
  'calendar.calendarOf': "'s Calendar",
  'calendar.selected': '{n} selected',
  'calendar.backToMine': 'Back to my calendar',
  'calendar.noReports': 'Nobody reports to you, so no other calendar is offered here. The list is the people below you in the org chart.',
  'calendar.droppedSelection': 'The calendar is showing yours alone:',
  'calendar.droppedOne': 'one person you asked for is not yours to see.',
  'calendar.droppedMany': 'people you asked for are not yours to see.',
  'calendar.readOnly': 'Read only',
  'calendar.scheduledWork': 'A job’s Scheduled Work is changed on its Work Order.',
  'calendar.timeOff': 'Approved time off is changed or canceled through its request on My Info.',
  'calendar.viewControls': 'Calendar view',
  'event.cancelledPill': 'Canceled',
  'event.cancelledBanner': 'This meeting is canceled. Reinstating brings it back exactly as it was.',
  'event.thisEvent': 'this event',
  'event.subject': 'Title',
  'event.company': 'Company',
  'event.internalEvent': 'Internal event',
  'event.companyEntity': 'Company',
  'event.start': 'Start',
  'event.end': 'End',
  'event.allDay': 'All day',
  'event.type': 'Event type',
  'event.type.physical': 'Physical',
  'event.type.online': 'Online',
  'event.type.phone': 'Phone',
  'event.place': 'Place',
  'event.placeEntity': 'Place',
  'event.placePlaceholder': 'Type a site, an address or a company',
  'event.newSite': 'New site',
  'event.meetingLink': 'Meeting link',
  'event.callNumber': 'Call number',
  'event.callNumberExtension': 'Extension',
  'event.whichSite': 'This location is occupied by more than one company. Which site is meant?',
  'event.recurrence': 'Repeats',
  'event.recurrence.none': 'Does not repeat',
  'event.recurrence.daily': 'Daily',
  'event.recurrence.weekly': 'Weekly',
  'event.recurrence.monthly': 'Monthly',
  'event.recurrenceEnd': 'Repeat until',
  'event.zone': 'Times are in',
  'event.attendees': 'Attendees',
  'event.person': 'Person',
  'event.outsideContact': 'Outside contacts',
  'event.outsideName': 'Name',
  'event.outsideEmail': 'Email',
  'event.outsidePhone': 'Phone number',
  'event.outsideAdd': 'Add',
  'event.outsideRemove': 'Remove',
  'event.outsideHint': 'Somebody with no person record: a name, and the address or number the invitation reaches them on.',
  'event.outsideProblem.name': 'A guest carries a name.',
  'event.outsideProblem.email': 'That email address is not one an invitation could reach.',
  'event.outsideProblem.phone': 'That phone number does not place itself; give it with its area code.',
  'event.outsideProblem.duplicate': 'That guest is already on the list.',
  'event.notes': 'Notes',
  // Decision 1081: an event's reminder, the viewer's own.
  'event.reminder': 'Reminder',
  'event.reminder.off': 'Off',
  'event.reminder.lead15': '15 minutes before',
  'event.reminder.lead60': '1 hour before',
  'event.reminder.lead1440': '1 day before',
  'event.reminder.custom': 'Custom',
  'event.reminder.minutes': 'Minutes before',
  'event.reminder.minutesBefore': 'minutes before',
  'event.reminder.channel': 'Send by',
  'event.reminder.email': 'Email',
  'event.reminder.text': 'Text',
  'event.reminder.byEmail': 'by email',
  'event.reminder.byText': 'by text',
  'event.reminder.problem.leadWhole': 'A reminder is a whole number of minutes before the event, one or more.',
  'event.reminder.problem.leadTooLong': 'A reminder is thirty days before the event at the most.',
  'event.reminder.problem.channel': 'A reminder is sent by email or by text.',
  'event.reminder.problem.noTextService': 'Text reminders wait on a text-message service, and none is connected.',
  'event.reminder.problem.noMobile': 'Text reminders go to a mobile number, and none is on your record.',
  'event.scope': 'This change applies to',
  'event.scope.thisOne': 'This event',
  'event.scope.allOfThem': 'All events in the series',
  'event.scope.thisAndEveryFuture': 'This and every following event',
  'event.requirements': 'Site requirements',
  'event.bring': 'Bring',
  'event.expires': 'expires',
  'event.notHeld': 'not held',
  'attribute.title': 'Add Attribute',
  'attribute.save': 'Save Attribute',
  'attribute.parent': 'On',
  'attribute.type': 'Attribute',
  'attribute.value': 'Value',
  'attribute.unit': 'Unit',
  'attribute.base': 'Stored as',
  'attribute.notes': 'Notes',
  'attribute.visibility': 'Visibility',
  'upload.title': 'Upload File',
  'upload.save': 'Done',
  'upload.stop': 'Stop',
  'upload.busyHint': 'Each file starts as soon as it has a type. Done waits for the last one; Stop ends the upload.',
  'upload.send': 'Upload',
  'upload.heldHint': 'Check each file’s name and date, then press Upload. Stop ends the upload.',
  'upload.stoppedLanded': 'Stopped. These files had already landed:',
  'upload.stoppedNone': 'Stopped. No file had landed.',
  'upload.sameName.lead': 'A file named',
  'upload.sameName.already': 'is already on this record.',
  'upload.sameName.filedAs': 'Filed as:',
  'upload.sameName.ask': 'Is this a new version of it, or a separate file?',
  'upload.sameName.suggestVersion': 'Suggested: a new version, because the type you picked matches.',
  'upload.sameName.suggestSeparate': 'Suggested: a separate file, because the type you picked differs.',
  'upload.sameName.version': 'New Version',
  'upload.sameName.separate': 'Separate File',
  'upload.fileType': 'File type',
  'upload.pickAType': 'Pick a file type',
  /** 🔒 Decision 7850: the last entry of a type list an act cut, which reveals the rest of the types that apply. */
  'upload.otherType': 'Other Type',
  /** 🔒 Decision 9540: a standard form's own date, which its name's year takes, and its optional name, joined after the type. */
  'upload.formDate': 'Form Date',
  'upload.formDate.calendarDay': 'The form date is a calendar day, written as year, month and day.',
  'upload.formName.tooLong': 'The form name is {max} characters or fewer.',
  'upload.formDate.future': 'The form date is today or earlier; no standard form is signed in the future.',
  'upload.fileName': 'File Name',
  'upload.fileName.required': 'Each file needs a name.',
  'upload.fileName.tooLong': 'The file name is {limit} characters or fewer.',
  'upload.documentDate': 'Document Date',
  'upload.documentDate.future': 'The document date is today or earlier; no document is dated in the future.',
  'upload.formName': 'Form Name',
  'upload.formNameHint': 'Optional; joins the file name after the form type',
  /** 🔒 Decision 9680: an employment form's own date is the day it was signed, and its questions ride beside the file. */
  'upload.dateSigned': 'Date Signed',
  'upload.employment.confidentiality': 'Confidentiality Clause',
  'upload.employment.nonSolicit': 'Non-Solicit',
  'upload.employment.nonSolicitMonths': 'Months After Employment Ends',
  'upload.employment.returnOrDestroy': 'Documents Returned or Destroyed at the End',
  'upload.employment.choose': 'Choose',
  'upload.employment.writesAgreement': 'A Yes writes this person’s personal NDA, in force from the date signed.',
  'upload.class': 'Class',
  'upload.needsClassification': 'Needs classification',
  'upload.scanConfirm': 'Confirm the rotation and crop before this is processed.',
  'view.title': 'Add New View',
  'view.editTitle': 'Edit View',
  'view.save': 'Save View',
  'view.delete': 'Delete View',
  'view.name': 'Name',
  'view.favourite': 'Star this view',
  'view.default': 'Open this list on this view',
  'view.nameTaken': 'You already have a view with this name on this list.',
  'view.nameNeeded': 'Give the view a name to save it.',
  'view.band': 'View',
  'view.criteria': 'Show records where all of these hold',
  'view.field': 'Field',
  'view.pickField': 'Choose a field',
  'view.comparator': 'Comparator',
  'view.value': 'Value',
  'view.addRow': 'Add a condition',
  // Decision 8800 (T3276): the catalog's attribute types in the condition rows' field menu, on the Items list.
  'view.attributes': 'Attributes',
  'view.attributeGone': 'Attribute no longer offered',
  'view.unit': 'Unit',
  'view.removeRow': 'Remove condition',
  'view.problem.field': 'Choose a field for this condition.',
  'view.problem.value': 'Type what to look for, or remove this condition.',
  'view.noConditions.lead': 'All',
  'view.noConditions.tail': 'show. Add a condition to narrow the list.',
  'view.op.is': 'is',
  'view.op.isNot': 'is not',
  'view.op.contains': 'contains',
  'view.op.startsWith': 'starts with',
  'view.op.isMoreThan': 'is more than',
  'view.op.isLessThan': 'is less than',
  'view.op.isOn': 'is on',
  'view.op.isBefore': 'is before',
  'view.op.isAfter': 'is after',
  'view.op.isEmpty': 'is empty',
  'view.op.isNotEmpty': 'is not empty',
  'view.columns': 'Columns',
  'view.available': 'Available columns',
  'view.searchColumns': 'Search columns',
  'view.selected': 'Selected columns',
  'view.addColumn': 'Add column',
  'view.removeColumn': 'Remove column',
  'view.fixedColumn': 'The first column is fixed and always shows.',
  'view.columnLocked': 'That column is locked on this table and cannot be removed.',
  'view.sort.by': 'Sorted by',
  'view.sort.textAsc': 'A to Z',
  'view.sort.textDesc': 'Z to A',
  'view.sort.numberAsc': 'lowest first',
  'view.sort.numberDesc': 'highest first',
  'view.sort.dateAsc': 'oldest first',
  'view.sort.dateDesc': 'newest first',
  'view.sort.none': "In the list's own order",
  'view.sort.rows': 'rows a page',
  'view.saveFailed': 'The view was not saved. Try again.',
  'view.deleteFailed': 'The view was not deleted. Try again.',
  'view.opensHere': '(opens here)',
  'view.starred': 'Starred',
  'view.noMatch.lead': 'No',
  'view.noMatch.tail': 'matches this view.',
  'view.noMatch.rows': 'No row on this list matches this view.',
  // T3289: a Filter, a Series choice or a search that matches nothing; the lead is view.noMatch.lead, and Clear Filter is listFilter.clearAll.
  'filter.noMatch.tail': 'matches this filter.',
  'filter.noMatch.rows': 'No row on this list matches this filter.',
  // T3341: a search alone reads as a search, never as a filter.
  'filter.noMatch.searchTail': 'matches this search.',
  'filter.noMatch.searchRows': 'No row on this list matches this search.',
  'view.edit': 'Edit View',
  // 🔒 Built-in views and quick views (decision 2880).
  'view.builtIn.open': 'Open',
  'view.builtIn.closed': 'Closed',
  'view.builtIn.only': 'Only',
  // 🔒 A built-in view's Edit View (decision 2981): the name and the conditions read, the columns are the person's own.
  'view.builtIn.note': 'This is a built-in view. Its name and conditions are the list’s own; its columns are yours to arrange.',
  'view.builtIn.fixed': 'The list fixes this view’s conditions.',
  'view.star': 'Star',
  'view.unstar': 'Unstar',
  'view.starLimit': 'was not starred: five views are starred on this list, the most its bar holds. Unstar one first.',
  'view.quickViews': 'Quick views',
  'list.viewHeld': 'on this page match this view',
  'item.sku': 'SKU:',
  'richText.toolbar': 'Formatting',
  'richText.heading': 'Heading',
  'richText.bold': 'Bold',
  'richText.italic': 'Italic',
  'richText.underline': 'Underline',
  'richText.strikethrough': 'Strikethrough',
  'richText.bulletList': 'Bulleted list',
  'richText.numberList': 'Numbered list',
  'richText.image': 'Insert an image',
  'richText.alignLeft': 'Align left',
  'richText.alignCenter': 'Align center',
  'richText.alignRight': 'Align right',
  'richText.alignJustify': 'Justify',
  'richText.fontFamily': 'Font',
  'richText.fontSize': 'Font size',
  'richText.textColor': 'Text color',
  'richText.highlightColor': 'Highlight color',
  'email.title': 'Compose Email',
  'email.send': 'Send',
  'email.template': 'Template',
  'email.to': 'To',
  'email.from': 'From',
  'email.cc': 'Cc',
  'email.bcc': 'Bcc',
  'email.subject': 'Subject',
  'email.body': 'Message body',
  'email.chips': 'Merge fields',
  'email.attachment': 'The document goes out attached as a PDF.',
  'email.blocked': 'The send is held: these merge fields resolved to nothing —',
  'harness.section.modals': 'Modals',
  'harness.modal.shell': 'The three regions over a native dialog: the Title Bar and Footer pinned, the Body scrolling under them.',
  'harness.modal.scrolling': 'A Body taller than its region: the Title Bar and Footer hold still.',
  'harness.modal.stacked': 'A modal over a modal: the lower layer stays open and inert, and Escape closes the top one only.',
  'harness.modal.destructiveFooter': 'A Footer with one destructive-outlined secondary, whose action routes through the typed-phrase dialog.',
  'harness.openModal': 'Open',
  'harness.formFrame.window': 'The form in a window: the Primary named for the act, Cancel beside it, the refusal inside.',
  'harness.formFrame.section': 'The same form as a section of a page, with the same footer at its foot.',
  'harness.formFrame.refused': 'A refusal, written inside the frame above the fields, never behind it.',
  'harness.formFrame.heading': 'General Information',
  'harness.formFrame.field': 'Display Name',
  'harness.formFrame.refusal': 'That name is already on another company.',
  'dev.pieceSix': 'Reference data: units, attribute types, departments',
  'dev.pieceEight': 'The permission model: written, not enforced',
  'dev.page.permissions.lookFor': 'Under Server below: the seeded counts read 12 module default classes, 6 field visibility rules and 2 disclosure rows. Every gate answers true until enforcement is on.',
  'dev.pieceTen': 'Company, Person, Location: and the links that join them',
  'harness.sample.tileQueues': 'Your queues',
  'dev.pieceTwelve': 'Application shell and navigation: no login gate',
  'dev.pieceFourteen': 'Break-glass administrator and the local login',
  'dev.page.login.lookFor': 'A dedicated page, never a modal: email, password and the six-digit code on one form. Sign in as admin@localhost with the test host\u2019s break-glass password; the first sign-in shows the authenticator secret and enrolls on its first code. The header then names the administrator, and Sign out ends the session.',
  'dev.page.shell.lookFor': 'The frame itself: the 42px header with search and the avatar menu, the navigation bar and its collapsed rail, and the Dashboard at / with the work-queue counts. The bar carries Dashboard alone until later pieces seed their own rows.',
  'shell.productName': 'BMS',
  'shell.viewAsRefused': 'View as was refused for that employment.',
  'shell.groupOffline': 'The connection is down, so this part of the application could not load. Try again once you are back online.',
  'shell.groupFailed': 'This part of the application could not load. Try again, and reload the page if it fails again.',
  // T725 review: Exit whose clearing write failed keeps the banner and says so.
  'shell.viewAsExitFailed': 'Exit did not go through, so you are still viewing as this person. Press Exit again.',
  'session.signInLabel': 'Sign In',
  'session.enrolFirst': 'This login has no authenticator yet; open the Login Screen to enroll one.',
  'shell.navigation': 'Main navigation',
  'shell.menuCount.dispatch': '{count} jobs that need a crew',
  'shell.menuCount.myJobs': '{count} new jobs on your board',
  'shell.menuCount.approvals': '{count} approvals for you to decide',
  // T2251: the one-count readings, so a screen reader never hears "1 jobs".
  'shell.menuCount.dispatchOne': '1 job that needs a crew',
  'shell.menuCount.myJobsOne': '1 new job on your board',
  'shell.menuCount.approvalsOne': '1 approval for you to decide',
  'shell.openMenu': 'Open the menu',
  'shell.closeMenu': 'Close the menu',
  'shell.collapseNav': 'Collapse the navigation bar',
  'shell.collapse': 'Collapse',
  'shell.navNotSaved': 'Your navigation’s folding could not be saved; it will look as before on the next visit.',
  'shell.expandNav': 'Expand the navigation bar',
  'shell.searchLabel': 'Search everything',
  'shell.searchPlaceholder': 'Search companies, people, documents',
  'shell.searchLoading': 'Searching',
  'shell.searchEmpty': 'Nothing matches that yet.',
  // 🔒 Decision 2770 (T1616): the top bar's module chooser.
  'shell.searchModuleLabel': 'Choose What to Search',
  'shell.searchModuleAll': 'All',
  'shell.searchIn': 'Search in',
  // T3332: a search group's link to its filtered list, followed by the group's total.
  'shell.searchSeeAll': 'See all',
  'shell.accountSettings': 'Account Settings',
  // Decision 3180: the signed-in person's own employee file, beside Account Settings.
  'shell.myInfo': 'My Info',
  'shell.brandName': 'Wicked Edge',
  'shell.brandSuffix': 'automation',
  'shell.switchCompany': 'Switch company',
  'shell.viewAs': 'View as',
  'shell.signOut': 'Sign Out',
  'settings.title': 'Settings',
  'settings.subtitle': 'Every configurable value, in one index',
  // Decision 1397 (T582): the kind line's two links replace the separate way back, and the separator stands between them.
  'settings.crumbSeparator': '›',
  'settings.index.search': 'Search settings',
  'settings.index.searchHint': 'Try mileage or footer',
  'settings.index.noMatch': 'No setting matches what is typed. Try another word, or clear the box to see every setting.',
  'settings.index.empty': 'Nothing is configurable yet; an entry appears when the piece that owns it ships.',
  'settings.column.title': 'Setting',
  'settings.column.category': 'Category',
  'settings.save': 'Save',
  'settings.saved': 'Saved.',
  'settings.add': '+ Add',
  'settings.remove': 'Remove',
  'settings.end': 'End',
  'settings.deactivate': 'Deactivate',
  'settings.reactivate': 'Reactivate',
  'settings.retire': 'Retire',
  'settings.edit': 'Edit',
  'settings.cancel': 'Cancel',
  'settings.none': 'None',
  'settings.unreachable': 'The server did not answer.',
  // One row shape for a long Settings list (decision 2780; T1617).
  'settings.rowList.edit': 'Edit',
  'settings.rowList.unsaved': 'Not saved yet',
  'settings.rowList.nothingChanged': 'Nothing has changed yet.',
  'settings.rowList.discard': 'Discard Changes',
  // A typed value is not dropped silently: the question before a Settings page is left (decision 2781; T1612).
  'settings.leave.title': 'Leave Without Saving?',
  'settings.leave.body': 'These have changes that are not saved yet:',
  'settings.leave.stay': 'Stay',
  'settings.leave.discard': 'Discard',
  'settings.leave.save': 'Save',
  'settings.leave.saving': 'Saving…',
  'form.leave.body': 'What you typed here is not saved, and leaving drops it.',
  'form.leave.leave': 'Leave',
  'form.leave.keep': 'Keep Editing',
  'settings.security.title': 'Security',
  'settings.release.title': 'File Release',
  'settings.storage.title': 'Storage and Backups',
  'settings.storage.hint': 'Where your files are kept, and where their backup copies go. The main store serves every file; a mirror only holds copies, in case the main store is lost.',
  'settings.storage.targets': 'Storage targets',
  'settings.storage.primary': 'Primary',
  'settings.storage.mirror': 'Mirror',
  'settings.storage.inactive': 'inactive',
  'settings.storage.fromEnvironment': 'this server’s own store',
  'settings.storage.verifiedAt': 'Reached',
  'settings.storage.neverVerified': 'Not tested yet',
  'settings.storage.verifyFailed': 'Could not reach the store:',
  'settings.storage.verified': 'The store answered.',
  'settings.storage.of': 'of',
  'settings.storage.versionsCopied': 'file versions copied',
  'settings.storage.test': 'Test Connection',
  'settings.storage.makePrimary': 'Make Primary',
  'settings.storage.madePrimary': 'That store is now the Primary; every file is served from it.',
  'settings.storage.deactivate': 'Deactivate',
  'settings.storage.reactivate': 'Reactivate',
  'settings.storage.remove': 'Remove',
  'settings.storage.remove.title': 'Remove this store?',
  'settings.storage.remove.body': 'It stops receiving copies and leaves this list and the mirror schedule.',
  'settings.storage.remove.kept': 'Its backup copies stay as history:',
  'settings.storage.remove.keptNone': 'No backup copy points at it, so it is deleted.',
  'settings.storage.remove.copies': 'file versions copied',
  'settings.storage.removed.retired': 'The store was removed; its backup copies stay as history.',
  'settings.storage.removed.deleted': 'The store was removed.',
  'settings.storage.add': '+ Add Store',
  'settings.storage.noKey': 'This server has no key set aside for keeping an outside store’s password, so one cannot be saved. Ask whoever runs the server to set the key and restart it.',
  'settings.storage.name': 'Name',
  'settings.storage.endpoint': 'Endpoint URL',
  'settings.storage.bucket': 'Bucket',
  'settings.storage.region': 'Region (optional)',
  'settings.storage.accessKey': 'Access key',
  'settings.storage.secret': 'Secret key',
  'settings.storage.pathStyle': 'Path-style addressing (MinIO and most S3-compatible stores)',
  'settings.storage.save': 'Add a Store',
  'settings.storage.schedule': 'Backup schedule',
  'settings.storage.scheduleHint': 'Hours are read in the Organization’s time zone:',
  'settings.storage.enabled': 'Backups run on the clock',
  'settings.storage.mirrorHour': 'Mirror the changed files at hour',
  'settings.storage.dumpHour': 'Dump the database at hour',
  'settings.storage.hourHint': '0 to 23',
  'settings.storage.retention': 'Keep database dumps for days',
  'settings.storage.runs': 'Last runs',
  // Decision 1761: the Backups stepper at the top of Storage and backups, five legs through Setup Steps.
  'settings.storage.steps.title': 'Backups',
  'settings.storage.steps.hint': 'The seven legs of the backup, each read from what has run. A leg that is not done says what is missing and which runbook section does it.',
  'settings.storage.steps.runbook': 'Runbook:',
  // Decision 7540 (T3109): the prefix this server writes under, on the Primary's row and the stepper.
  'settings.storage.writesUnder': 'This server writes under {prefix}.',
  'settings.storage.prefixRule': 'It reads any file its database names, whatever the prefix, and mirrors, prunes and deletes only files under its own.',
  'settings.storage.steps.lastRun': 'last run',
  'settings.storage.steps.switchedOff': 'is switched off, so nothing is copied to it.',
  'settings.storage.steps.notRun': 'has not run yet.',
  'settings.storage.steps.office.title': 'The office NAS mirror',
  'settings.storage.steps.office.none': 'No Mirror on the office network or the tailnet is added yet.',
  'settings.storage.steps.office.body': 'Set the NAS up once, then add it under Add a store with its tailnet address as the endpoint, press Test connection, and press Mirror changes now under Last runs.',
  'settings.storage.steps.office.runbook': 'The office NAS: the first Mirror and the archive host, steps 1 to 5, in deploy/nas/README.md',
  'settings.storage.steps.cloud.title': 'The cloud mirror',
  'settings.storage.steps.cloud.none': 'No Mirror in a cloud bucket is added yet.',
  'settings.storage.steps.cloud.body': 'Add the cloud bucket under Add a store, press Test connection, and press Mirror changes now under Last runs. A Hyper Backup task from the NAS is not reported here.',
  'settings.storage.steps.cloud.runbook': '1. What runs, and where the copies are, in docs/runbooks/backup-and-restore.md',
  'settings.storage.steps.dump.title': 'The nightly database dump',
  'settings.storage.steps.dump.noMirror': 'No Mirror is active, so the dump has nowhere to go.',
  'settings.storage.steps.dump.never': 'No dump has run yet.',
  'settings.storage.steps.dump.notEvery': 'The last dump did not succeed on every active Mirror.',
  'settings.storage.steps.dump.body': 'The dump runs each night at the hour under Backup schedule. Press Dump the database now under Last runs to run it at once.',
  'settings.storage.steps.dump.runbook': '1. What runs, and where the copies are, in docs/runbooks/backup-and-restore.md',
  'settings.storage.steps.recovery.title': 'The recovery copy of the master key',
  'settings.storage.steps.recovery.noPassphrase': 'No recovery passphrase is set, so no copy of the master key is kept.',
  'settings.storage.steps.recovery.notWritten': 'The copy is sealed, and no dump has succeeded since, so no Mirror holds it yet.',
  'settings.storage.steps.recovery.written': 'Written beside the dump of',
  'settings.storage.steps.recovery.body': 'A super admin sets the passphrase under Recovery copy of the master key and presses Print for the safe. The next dump writes the copy beside it on every Mirror. The same key opens the stored files, so this copy is the way back to them too.',
  'settings.storage.steps.recovery.runbook': '5. The master key: custody, backup and rotation, in docs/runbooks/backup-and-restore.md',
  'settings.storage.steps.rehearsal.title': 'The monthly restore rehearsal',
  'settings.storage.steps.rehearsal.never': 'No rehearsal has passed yet.',
  'settings.storage.steps.rehearsal.passed': 'Last passed on',
  'settings.storage.steps.rehearsal.overdue': `That is more than ${REHEARSAL_OVERDUE_DAYS} days ago, so a rehearsal is due.`,
  'settings.storage.steps.rehearsal.body': 'Type the recovery passphrase under Monthly restore rehearsal and press Rehearse a restore now. It needs the dump and the recovery copy above, and a Mirror that has copied at least one file.',
  'settings.storage.steps.rehearsal.runbook': '3.1 The monthly rehearsal, from the page, in docs/runbooks/backup-and-restore.md',
  'settings.storage.steps.waitsMirror': 'Turn on the office or the cloud mirror, step 1 or 2, first.',
  'settings.storage.steps.rehearsal.waits': 'Finish the nightly dump and the recovery copy, steps 3 and 5, first.',
  'settings.storage.runMirror': 'Mirror Changes Now',
  'settings.storage.runDump': 'Dump the Database Now',
  'settings.storage.ran': 'Ran:',
  'settings.storage.noRuns': 'No run has happened yet.',
  'settings.storage.kindMirror': 'Mirror',
  'settings.storage.kindDump': 'Database dump',
  'settings.storage.running': 'running',
  'settings.storage.due': 'due',
  'settings.storage.done': 'done',
  'settings.storage.removed': 'removed',
  // T2876: a stopped Recover, Undo or rehearsal says one fixed sentence; the store's own message goes to the server log, never to the page.
  'settings.storage.recover.unexpectedRecover': 'Recover stopped on an error nobody expected, and nothing live changed; the server log holds it.',
  'settings.storage.recover.unexpectedUndo': 'Undo stopped on an error nobody expected, and nothing live changed; the server log holds it.',
  // T3069: the rehearsal's file step on an installation whose Mirror has copied nothing; the stepper's rehearsal step says it too.
  // T3077 (4): the stepper only guesses from each Mirror's latest run, so it words the warning as a guess; the rehearsal's own sentence above states the fact when it fails.
  'settings.storage.steps.rehearsal.mirrorGuess': 'No Mirror’s latest run is known to have copied a file, which is a guess from its outcome. If the rehearsal finds no file, run Mirror Changes Now, then Dump the Database Now, then rehearse again.',
  'settings.storage.rehearsal.mirrorEmpty': 'The Mirror has copied no file yet, so no file could be restored; run Mirror Changes Now, then Dump the Database Now, then rehearse again.',
  'settings.storage.rehearsal.restoreFailed': 'The newest dump that opened did not restore into an empty database; the server log holds the reason.',
  // Decision 1195: the master key's recovery copy, sealed under the owner's passphrase and printed for the safe.
  'settings.storage.recovery.title': 'Recovery copy of the master key',
  'settings.storage.recovery.hint': 'The master key opens every sealed value this server keeps. Its recovery copy is sealed under a passphrase only you hold, written beside every database dump on each Mirror, and printed once for the safe. With the backups, the printed page and the passphrase, a fresh server opens everything again.',
  'settings.storage.recovery.none': 'No recovery passphrase is set yet, so a lost server key cannot be recovered from the backups.',
  'settings.storage.recovery.sealed': 'The copy seals master key',
  'settings.storage.recovery.sealedAt': 'and was last sealed',
  'settings.storage.recovery.setBy': 'The passphrase was set by',
  'settings.storage.recovery.on': 'on',
  'settings.storage.recovery.fakeKey': 'This server holds no master key of its own, so the copy seals a key the server forgets when it restarts. Ask whoever runs the server to set its storage key first.',
  'settings.storage.recovery.passphrase': 'Recovery passphrase',
  'settings.storage.recovery.confirm': 'Type the passphrase again',
  'settings.storage.recovery.passphraseHint': 'Use at least 16 characters, several words you will not forget. The passphrase is never stored, so nobody can tell it to you again.',
  'settings.storage.recovery.set': 'Set Passphrase',
  'settings.storage.recovery.change': 'Change Passphrase',
  'settings.storage.recovery.saved': 'The master key is sealed under the new passphrase. Print the new copy for the safe now, and destroy the old page.',
  'settings.storage.recovery.superAdminOnly': 'Only a super admin sets or changes the recovery passphrase.',
  'settings.storage.recovery.print': 'Print for the Safe',
  'settings.storage.recovery.printNeedsPassphrase': 'Set the passphrase first, and then the copy can be printed.',
  'settings.storage.recovery.sheetTitle': 'BMS2 master key recovery copy',
  'settings.storage.recovery.sheetKey': 'Master key',
  'settings.storage.recovery.sheetSealed': 'Sealed',
  'settings.storage.recovery.sheetPrinted': 'Printed',
  'settings.storage.recovery.sheetKeep': 'Keep this page in the safe. With the passphrase it recovers the key that opens every sealed value, and without the passphrase it opens nothing.',
  'settings.storage.recovery.sheetNever': 'Never write the passphrase on this page or keep the two together.',
  'settings.storage.recovery.sheetStale': 'A page printed before the master key was last sealed recovers an older key, so reprint after every change of passphrase or key.',
  'settings.storage.recovery.sheetCode': 'The QR code carries the same text as the groups below it.',
  'settings.storage.recovery.sheetPrint': 'Print',
  'settings.storage.recovery.sheetClose': 'Close',
  // Decision 4360 (T2432): the Dump Encryption card, its sheet, and the Backups stepper's encryption leg.
  'settings.storage.dumpKey.title': 'Dump Encryption',
  'settings.storage.dumpKey.hint': 'Every database dump is encrypted to this key before it reaches a Mirror, so a stolen NAS or bucket holds nothing anybody can read. The private key is printed once for the safe and kept nowhere else.',
  'settings.storage.dumpKey.none': 'No dump key is set yet, so the dumps reach the Mirrors in plain text.',
  'settings.storage.dumpKey.set': 'Dumps are encrypted to the key with fingerprint',
  'settings.storage.dumpKey.madeBy': 'made by',
  'settings.storage.dumpKey.on': 'on',
  'settings.storage.dumpKey.signing': 'Every dump is also signed with this server’s own key, which no Mirror holds, and Recover checks the signature before it restores.',
  'settings.storage.dumpKey.notSigning': 'This server holds no storage key of its own, so its dumps carry no signature. Ask whoever runs the server to set its storage key.',
  'settings.storage.dumpKey.lost': 'A lost private key cannot be replaced: every dump encrypted under it is then unreadable.',
  'settings.storage.dumpKey.generate': 'Generate Key',
  'settings.storage.dumpKey.superAdminOnly': 'Only a super admin makes the dump key.',
  'settings.storage.dumpKey.generated': 'The key was made. Print the sheet now: once it is closed, the private key cannot be shown again.',
  'settings.storage.dumpKey.again.title': 'Generate a New Dump Key',
  'settings.storage.dumpKey.again.body': 'The new key encrypts only the dumps made from now on. Every dump made under the current key still needs the current printed sheet, for as long as the Mirrors keep it.',
  'settings.storage.dumpKey.again.keep': 'Keep the current sheet in the safe until the last dump made under it is pruned.',
  'settings.storage.dumpKey.sheetTitle': 'BMS2 dump private key',
  'settings.storage.dumpKey.sheetFingerprint': 'Fingerprint',
  'settings.storage.dumpKey.sheetMade': 'Made',
  'settings.storage.dumpKey.sheetOnly': 'This is the only copy of the private key. It is not stored anywhere, and the application cannot show it again.',
  'settings.storage.dumpKey.sheetKeep': 'Keep this page in the safe beside the recovery copy of the master key. Recover asks for this key to open a dump encrypted under it.',
  'settings.storage.dumpKey.sheetLost': 'If this page is lost, every dump encrypted under this key is unreadable, and nobody can open it.',
  'settings.storage.dumpKey.sheetAge': 'A dump also opens outside the application with the age tool: type the key into a file of its own, run age -d -i with that file, and delete the file afterwards.',
  'settings.storage.dumpKey.sheetPrint': 'Print',
  'settings.storage.dumpKey.sheetClose': 'Close',
  'settings.storage.steps.encryption.title': 'Dumps are encrypted',
  'settings.storage.steps.encryption.none': 'No dump key is set, so the dumps reach the Mirrors in plain text.',
  'settings.storage.steps.encryption.set': 'Dumps are encrypted to the key with fingerprint',
  'settings.storage.steps.encryption.signed': 'Every dump is signed with this server’s own key.',
  'settings.storage.steps.encryption.unsigned': 'This server holds no storage key of its own, so its dumps carry no signature.',
  'settings.storage.steps.encryption.body': 'A super admin presses Generate Key under Dump Encryption and prints the private key for the safe. A lost private key leaves every dump made under it unreadable.',
  'settings.storage.steps.encryption.runbook': '1. What runs, and where the copies are, in docs/runbooks/backup-and-restore.md',
  'settings.storage.steps.encryption.link': 'Go to Dump Encryption',
  // 🔒 Decision 7420 (T3083; E4b): the seventh leg, the stored files that should be encrypted and are not.
  'settings.storage.steps.files.title': 'Stored files are encrypted',
  'settings.storage.steps.files.none': 'No stored file that should be encrypted is plain.',
  'settings.storage.steps.files.one': '1 file that should be encrypted is not encrypted.',
  'settings.storage.steps.files.many': '{count} files that should be encrypted are not encrypted.',
  'settings.storage.steps.files.noKey': 'This server holds no master key that lasts past a restart, so these files stay plain until one is set.',
  'settings.storage.steps.files.running': 'Encrypting them now; this leg reads again when the pass finishes.',
  'settings.storage.steps.files.body': 'Every file with a confidentiality role, and every Accounting and HR file, is encrypted under the master key before it is stored. A nightly pass encrypts them too, so no file stays plain for more than a day. Press Encrypt Now to do it at once. A lost master key and a lost recovery copy leave every encrypted file unreadable.',
  'settings.storage.steps.files.runbook': '5. The master key: custody, backup and rotation, in docs/runbooks/backup-and-restore.md',
  'settings.storage.steps.files.encrypt': 'Encrypt Now',
  'settings.storage.steps.files.list': 'Not encrypted yet',
  'settings.storage.steps.files.noRecord': 'On no record',
  'settings.storage.steps.files.more': 'Show {count} More',
  'settings.storage.steps.files.unlisted': 'And {count} more not listed here.',
  'settings.storage.steps.files.started': 'Encrypting the files now.',
  'settings.storage.steps.files.alreadyRunning': 'A pass is already encrypting the files.',
  'settings.storage.steps.files.notMirrored': 'The Mirror has not succeeded a run since the last pass encrypted files, so it lacks their encrypted copies and no dump is safe to restore from yet. Press Mirror Changes Now under Last runs.',
  // Decision 1196: the monthly restore rehearsal, run with the passphrase typed.
  'settings.storage.rehearsal.title': 'Monthly restore rehearsal',
  'settings.storage.rehearsal.hint': 'A rehearsal restores the newest dump and one file from a Mirror into an empty database and store, opens the recovery copy with your passphrase, and opens the restored sealed values under the key it gives back. Nothing the server already holds is used, and nothing live is touched.',
  'settings.storage.rehearsal.never': 'No backup has been taken yet, so there is nothing to rehearse.',
  'settings.storage.rehearsal.lastGreen': 'The last rehearsal that passed ran',
  'settings.storage.rehearsal.noneGreen': 'No rehearsal has passed yet; the first dump was taken',
  'settings.storage.rehearsal.daysAgo': 'days ago.',
  'settings.storage.rehearsal.overdueAfter': `A rehearsal is overdue after ${REHEARSAL_OVERDUE_DAYS} days.`,
  'settings.storage.rehearsal.overdue': 'Overdue',
  'settings.storage.rehearsal.current': 'Current',
  'settings.storage.rehearsal.passphrase': 'Recovery passphrase',
  'settings.storage.rehearsal.privateKey': 'Dump private key',
  'settings.storage.rehearsal.privateKeyHint': 'Optional. Type the private key from the Dump Encryption sheet, and the rehearsal proves it opens the newest dump. Without it, the rehearsal passes over the encrypted dumps.',
  'settings.storage.rehearsal.run': 'Rehearse a Restore Now',
  'settings.storage.rehearsal.running': 'The rehearsal is restoring the backup; this takes a minute or more on a large database.',
  'settings.storage.rehearsal.passed': 'The rehearsal passed.',
  'settings.storage.rehearsal.failed': 'The rehearsal failed, and every super admin has a to-do about it.',
  'settings.storage.kindRehearsal': 'Restore rehearsal',
  'settings.storage.rehearsal.took': 'took',
  'settings.storage.rehearsal.seconds': 'seconds',
  'settings.storage.rehearsal.opened': 'opened',
  // Piece 21, decision 4040 (T2393): Recover the database, its card, Prepare and the comparison.
  'settings.storage.kindRecoverPrepare': 'Recover: prepare',
  'settings.storage.kindRecover': 'Recover',
  'settings.storage.kindRecoverFiles': 'Recover: files copied back',
  'settings.storage.kindRecoverUndo': 'Recover: undo',
  'settings.storage.recover.title': 'Recover the database',
  'settings.storage.recover.hint': 'Prepare restores one of a Mirror’s dumps into a copy beside the live database, brings it up to this release and shows what it holds against the live data. Nothing live changes.',
  'settings.storage.recover.from': 'Recover from',
  'settings.storage.recover.to': 'Recover to',
  'settings.storage.recover.passphrase': 'Recovery passphrase',
  'settings.storage.recover.newest': 'newest',
  'settings.storage.recover.noMirror': 'No active Mirror is set up, so there is nothing to recover from.',
  'settings.storage.recover.noDumps': 'This Mirror holds no dump yet.',
  'settings.storage.recover.keyNote': 'A dump taken before the master key was rotated cannot be recovered on this server.',
  'settings.storage.recover.encrypted': 'encrypted',
  'settings.storage.recover.unsigned': 'unsigned',
  'settings.storage.recover.privateKey': 'Dump private key',
  'settings.storage.recover.privateKeyHint': 'This dump is encrypted. Type the private key from the printed Dump Encryption sheet; it is used for this Prepare alone and never kept.',
  'settings.storage.recover.acceptUnsigned': 'This dump carries no signature, so nothing proves the Mirror left it unchanged, and a changed dump can run commands on the database server while it is restored. Prepare it anyway.',
  'settings.storage.recover.signedDump': 'The dump’s signature matched its bytes.',
  'settings.storage.recover.unsignedDump': 'The dump carried no signature, and it was prepared on your confirmation.',
  'settings.storage.recover.confirm.unsigned': 'This copy came from a dump with no signature, so nothing proves the Mirror left it unchanged.',
  'settings.storage.recover.prepare': 'Prepare',
  'settings.storage.recover.cancel': 'Cancel',
  'settings.storage.recover.discard': 'Discard',
  'settings.storage.recover.preparing': 'Preparing a copy from',
  'settings.storage.recover.stepDone': 'done',
  'settings.storage.recover.stepNow': 'under way',
  'settings.storage.recover.step.openCopy': 'Opening the recovery copy',
  'settings.storage.recover.step.fetchDump': 'Fetching the dump',
  'settings.storage.recover.step.checkPostgres': 'Checking the PostgreSQL version',
  'settings.storage.recover.step.restore': 'Restoring the copy',
  'settings.storage.recover.step.bringForward': 'Bringing the schema forward',
  'settings.storage.recover.step.checkFiles': 'Checking a file',
  'settings.storage.recover.step.checkSealed': 'Checking the sealed values',
  'settings.storage.recover.step.compare': 'Comparing with the live data',
  'settings.storage.recover.ready': 'The copy is prepared. Nothing live has changed.',
  'settings.storage.recover.expires': 'The prepared copy is dropped at',
  'settings.storage.recover.expired': 'The last prepared copy expired and was dropped at',
  'settings.storage.recover.failedAt': 'Prepare failed while',
  'settings.storage.recover.switchFailedAt': 'Recover failed while',
  'settings.storage.recover.dumpTaken': 'The dump was taken at',
  'settings.storage.recover.record': 'Record',
  'settings.storage.recover.recovered': 'Recovered copy',
  'settings.storage.recover.live': 'Live',
  'settings.storage.recover.lost': 'Will be lost',
  'settings.storage.recover.newestRow': 'newest',
  'settings.storage.recover.madeAfter': 'made after the dump',
  'settings.storage.recover.changedAfter': 'changed after the dump',
  'settings.storage.recover.unknownTime': 'the dump’s time is unknown',
  'settings.storage.recover.fileVersions': 'File versions',
  'settings.storage.recover.check.masterKey': 'Master key opened',
  'settings.storage.recover.check.restored': 'Dump restored',
  'settings.storage.recover.check.fileOpened': 'A file’s bytes matched',
  'settings.storage.recover.check.sealedOpened': 'Sealed values opened',
  'settings.storage.recover.migrations': 'Migrations applied to the copy:',
  'settings.storage.recover.noMigrations': 'The copy needed no migration.',
  'settings.storage.recover.recover': 'Recover',
  'settings.storage.recover.confirm.title': 'Recover the Database',
  'settings.storage.recover.confirm.signedOut': 'Every person is signed out, and the application is unavailable for a few minutes.',
  'settings.storage.recover.confirm.lost': 'Everything made or changed in the live data after the dump’s time is gone from it:',
  'settings.storage.recover.confirm.lostNone': 'Nothing in the live data was made or changed after the dump’s time.',
  'settings.storage.recover.confirm.safety': 'A safety dump of the current data is taken first. Undo the Recovery is then offered for',
  'settings.storage.recover.confirm.days': 'days.',
  'settings.storage.recover.confirm.byHand': 'Where the API is run by hand, with nothing to restart it, start it again once it stops.',
  'settings.storage.recover.confirm.typeWord': 'Type this word in capitals to confirm:',
  'settings.storage.recover.confirm.word': 'Confirmation word',
  'settings.storage.recover.confirm.code': 'Authenticator code',
  'settings.storage.recover.confirm.codeHint': 'A fresh six-digit code from your own authenticator app.',
  'settings.storage.recover.confirm.waiting': 'Type the word and a six-digit code first.',
  'settings.storage.recover.undo.title': 'Undo the Recovery',
  'settings.storage.recover.undo.body': 'The database from before the recovery becomes the live data again. The recovered database is set aside under its own name, with everything made since the recovery.',
  'settings.storage.recover.recovering': 'Recovering. The application closes for every person once the safety dump is taken.',
  'settings.storage.recover.switch.check': 'Checking the prepared copy',
  'settings.storage.recover.switch.safetyDump': 'Taking the safety dump',
  'settings.storage.recover.switch.maintenance': 'Closing the application',
  'settings.storage.recover.switch.connections': 'Stopping other connections',
  'settings.storage.recover.switch.switch': 'Switching the databases',
  'settings.storage.recover.recoveredBanner': 'The database was recovered, and every person must sign in again.',
  'settings.storage.recover.undoneBanner': 'The recovery was undone, and every person must sign in again.',
  'settings.storage.recover.usedDump': 'Dump used:',
  'settings.storage.recover.takenAt': 'taken',
  'settings.storage.recover.recoveredBy': 'Recovered by',
  'settings.storage.recover.undoneBy': 'Undone by',
  'settings.storage.recover.someone': 'a super admin',
  'settings.storage.recover.safetyDump': 'Safety dump:',
  'settings.storage.recover.noSafetyDump': 'none recorded',
  'settings.storage.recover.previousDatabase': 'Database from before, kept on the server as',
  'settings.storage.recover.setAside': 'Recovered database, set aside as',
  'settings.storage.recover.filesBack': 'Files copied back:',
  'settings.storage.recover.filesOf': 'of',
  'settings.storage.recover.filesFailed': 'failed',
  'settings.storage.recover.undoUntil': 'Undo the Recovery is offered until',
  'settings.storage.recover.undoGone': 'The database from before is gone or past its keep days, so the recovery cannot be undone here.',
  'settings.storage.recover.undo': 'Undo the Recovery',
  'settings.storage.recover.done': 'Done',
  'settings.security.retention': 'Audit read retention (months)',
  'settings.security.retentionHint': 'How long the record of who opened what is kept. Routine reads older than this are removed; reads of money, HR and protected records are always kept. Lowering it removes the older entries as soon as you save.',
  'settings.security.retentionConfirm': 'Lower the window and delete the rows it names?',
  'settings.security.coordinateRetention': 'Check-in coordinate retention (days)',
  'settings.security.coordinateTolerance': 'Check-in distance tolerance (meters)',
  'settings.security.coordinateToleranceHint': 'A check-in farther than this from its site reads in doubt on the Site page. Leave it blank and no visit is called into doubt.',
  'settings.security.sessionLifetime': 'Session lifetime (minutes)',
  'settings.security.sessionIdle': 'Session idle limit (minutes)',
  'settings.security.deviceSessionDays': 'Phone app session (days)',
  'settings.security.deviceSessionDaysHint': 'How long a phone with Face ID or a PIN stays signed in. Each open of the app restarts the count.',
  // Piece 35, Stage 0 (T2466): Settings › Mobile, the five phone keys; the session days keep the words Security gave them.
  'settings.mobile.title': 'Mobile',
  'settings.mobile.hint': 'How long the phone app keeps its work without signal, and how often it checks the server.',
  'settings.mobile.offlineStoreExpiryDays': 'Phone store expiry (days)',
  'settings.mobile.offlineStoreExpiryDaysHint': 'A phone that has not reached the server for this many days clears the work it holds.',
  'settings.mobile.syncWarningDays': 'Sync warning (days)',
  'settings.mobile.syncWarningDaysHint': 'After this many days without a sync, the person is emailed and asked to open the app. It is fewer than the expiry.',
  'settings.mobile.foregroundSyncSeconds': 'Sync while open (seconds)',
  'settings.mobile.foregroundSyncSecondsHint': 'How often an open app asks the server what changed.',
  'settings.mobile.minimumSupportedBuild': 'Lowest supported build',
  'settings.mobile.minimumSupportedBuildHint': 'A phone app below this build is asked to update before it syncs.',
  'settings.security.alertEmail': 'Break-glass alert address',
  'settings.security.alertEmailHint': 'Unset, the alert goes to every login holding the Admin role.',
  'settings.security.shareLinkExpiry': 'Share link expiry (days)',
  'settings.security.disclosures': 'Confidential field disclosure',
  'settings.security.disclosuresHint': 'How much of each confidential field is shown, such as its last four digits. Only a super admin changes one, and every change is kept on record.',
  'settings.security.revealTail': 'Reveal tail',
  // Decision 1397 (T582 C5): each disclosure row read as words from the label list beside its key, never as the record and field names.
  'settings.security.disclosure.companyEin': 'Company · Tax ID',
  'settings.security.disclosure.vendorTin': 'Vendor tax details · TIN',
  'settings.security.disclosure.unnamed': 'A confidential field with no label yet',
  'settings.security.rowsDue': 'rows would go',
  'settings.departments.title': 'Departments',
  'settings.roles.title': 'Roles',
  'trash.title': 'Trash',
  'trash.hint': 'Records and files that were deleted. Restore any of them here in one click. The Trash keeps a file or a record for 120 days, then clears it for good, and it stays listed here, marked as cleared.',
  'trash.filter': 'Record type',
  'trash.allTypes': 'All types',
  'trash.type.file': 'File',
  'trash.type.fileVersion': 'File version',
  'trash.type.company': 'Company',
  'trash.type.person': 'Person',
  'trash.type.location': 'Location',
  'trash.type.deal': 'Deal',
  'trash.type.workOrder': 'Work Order',
  'trash.type.visit': 'Visit',
  'trash.deletedBy': 'Deleted by',
  'trash.purgedOn': 'Purged',
  'trash.restore': 'Restore',
  'trash.restored': 'Restored:',
  'trash.runPurge': 'Run Purge Now',
  'trash.purged': 'Purge ran; versions whose bytes left:',
  'trash.empty': 'The trash is empty. A record deleted from its own page waits here until it is restored.',
  'settings.logins.title': 'Logins',
  'settings.logins.hint': 'Who can sign in. Invite somebody to give them a login for their job here; the login belongs to that job, so when the job ends its sign-in is turned off. Each login reads Invited, Active or Deactivated.',
  'settings.logins.invite': '+ Invite New User',
  'settings.logins.inviteTitle': 'Invite New User',
  'settings.logins.empty': 'No login beyond the break-glass administrator yet.',
  'settings.logins.locked': 'Locked',
  'settings.logins.internal': 'Internal',
  'settings.logins.portal': 'Portal ·',
  'settings.logins.mfaOn': 'Authenticator enrolled',
  'settings.logins.mfaOff': 'No authenticator',
  'settings.logins.pending': 'Invitation pending by',
  'settings.logins.resend': 'Resend Invite',
  'settings.logins.unlock': 'Unlock',
  'settings.logins.lastSignIn': 'Last signed in',
  'settings.logins.neverSignedIn': 'Never signed in',
  'settings.logins.noAttempts': 'No sign-in attempts yet.',
  'settings.logins.noAddress': 'no address',
  'settings.logins.outcome.success': 'Signed in',
  'settings.logins.outcome.badPassword': 'Wrong password',
  'settings.logins.outcome.badFactor': 'Wrong authenticator code',
  'settings.logins.outcome.refused': 'Refused',
  'settings.logins.resetMfa': 'Reset MFA',
  'settings.logins.forgetDevices': 'Forget Devices',
  // 🔒 Decision 0476: an administrator revokes access and moves a login to another role.
  'settings.logins.revoke': 'Revoke Access',
  // 🔨 Decision 0483: a reminder rule's subject, audience, channel and stop condition are keys, and these are what a person reads.
  'reminders.subject.operationFailure': 'Something the system tried failed',
  'reminders.subject.fileVersionBlocked': 'A file was blocked by the virus scan',
  'reminders.subject.rfqVendorResponse': 'A vendor has not answered a request for quotation',
  'reminders.audience.internal': 'Goes to your own people',
  'reminders.audience.customer': 'Goes to the customer',
  'reminders.audience.vendor': 'Goes to the vendor',
  'reminders.channel.email': 'by email',
  'reminders.channel.inApp': 'as a to-do in the app',
  'reminders.channel.push': 'as a phone notification',
  'reminders.stop.subjectClosed': 'Stop once it is dealt with',
  'reminders.stop.acknowledged': 'Stop once somebody acknowledges it',
  'reminders.stop.never': 'Keep going until the limit below',
  // 🔒 Decision 0482: the sign-in history read by device, not as a run of identical lines.
  'settings.logins.device.signIns': 'sign-ins',
  'settings.logins.device.lastIn': 'Last got in',
  'settings.logins.device.neverIn': 'never got in',
  'settings.logins.device.failures': 'failed attempts',
  'settings.logins.device.unknown': 'Unknown device',
  'settings.logins.device.noAddress': 'no address recorded',
  // 🔒 Decision 0482: the temporary password is shown once, in a window that says to write it down.
  'settings.logins.handOverTitle': 'Write This Password Down Now',
  'settings.logins.handOverWhat': 'This is the only time it is shown. Copy it or write it down before you close this window, then give it to the person in person — it will not be shown again, and nobody can look it up later.',
  'settings.logins.handOverDone': 'I Have Written It Down',
  'settings.logins.restore': 'Restore Access',
  'settings.logins.revokeTitle': 'Revoke Access',
  'settings.logins.revokeWhy': 'Why access is being revoked',
  'settings.logins.revokeWhat': 'They are signed out everywhere at once, their trusted devices are forgotten and any unredeemed invitation is canceled. Their records and their history stay exactly as they are. You can restore access later.',
  'settings.logins.revoked': 'Access revoked.',
  'settings.logins.restored': 'Access restored.',
  'settings.logins.changeRole': 'Change Role',
  'settings.logins.changeRoleTitle': 'Change Role',
  'settings.logins.changeRoleWhat': 'What somebody can see and do comes from their role. The change takes effect the next time they load a page.',
  'settings.logins.currentRole': 'Holds now',
  'settings.logins.newRole': 'Move to',
  'settings.logins.roleChanged': 'Role changed.',
  'settings.logins.passkeys': 'Passkeys',
  'settings.logins.noPasskeys': 'No passkeys',
  'settings.logins.removePasskey': 'Remove Passkey',
  'settings.logins.unnamedPasskey': 'Unnamed passkey',
  // Decision 4652 (3a): a login's phones, each revoked and held by an administrator or allowed again.
  'settings.logins.phones': 'Phones',
  'settings.logins.noPhones': 'No phone has signed in with this login.',
  'settings.logins.unnamedPhone': 'Unnamed phone',
  'settings.logins.phoneBuild': 'build',
  'settings.logins.phoneLastContact': 'last contact',
  'settings.logins.phoneState.live': 'Live',
  'settings.logins.phoneState.revoked': 'Revoked',
  'settings.logins.phoneState.held': 'Held',
  'settings.logins.revokePhone': 'Revoke Phone',
  'settings.logins.allowPhone': 'Allow Again',
  'settings.logins.phoneRevoked': 'Phone revoked. It stays signed out until it is wiped or allowed again.',
  'settings.logins.phoneAllowed': 'Phone allowed again. It signs in again with its person’s password.',
  'settings.logins.trustedDevices': 'trusted devices',
  'settings.logins.trustedDeviceDays': 'Days a trusted device skips the code (0 turns it off)',
  'settings.logins.handOver': 'Hand this over once; it is not shown again:',
  'settings.logins.invited': 'Invited.',
  'settings.logins.lockoutTitle': 'Lockout and recovery',
  'settings.maps.title': 'Maps',
  'settings.maps.hint': 'The map service behind addresses and travel time. OpenStreetMap needs no key and is shared with everyone; Google Maps needs a key from your Google Cloud project.',
  'settings.maps.provider': 'Map service',
  'settings.maps.off': 'Off — no travel time is computed',
  'settings.maps.openStreetMap': 'OpenStreetMap (free, shared servers)',
  'settings.maps.googleMaps': 'Google Maps',
  'settings.maps.googleKey': 'Google Maps API key',
  'settings.maps.googleKeyHint': 'Stored sealed; it is never shown again. Enter a new key to replace the held one.',
  'settings.maps.keyHeld': 'A key is held, ending in',
  'settings.maps.keyHeldNoTail': 'A key is held.',
  'settings.maps.noKey': 'No key is held.',
  'settings.maps.removeKey': 'Remove the held key',
  'settings.maps.padding': 'Travel buffer padding, minutes',
  'settings.maps.paddingHint': 'Added to every computed drive time. Changing it never recalculates an event already saved.',
  'settings.maps.testAddress': 'Address to test',
  'settings.maps.test': 'Test the Service',
  'settings.maps.testFound': 'Found it at',
  'settings.maps.testFailed': 'The service refused:',
  'settings.maps.save': 'Save',
  /** Decision 1648: a sealed row's read-back, shared by every step that holds a secret. */
  'settings.sealed.hint': 'Held sealed and never shown again. Leave it empty to keep the one held; type a new value to replace it.',
  'settings.sealed.held': 'Held, ending in',
  'settings.sealed.heldNoTail': 'A value is held.',
  'settings.sealed.none': 'Nothing is held.',
  'settings.sealed.remove': 'Remove the held value',
  /** Decisions 1648 and 1651: Settings › Integrations, a card per vendor, and the seams each card switches. */
  'settings.integrations.title': 'Integrations',
  'settings.integrations.hint': 'Every outside service this installation talks to, one card each. The line under a name says where its setup stands; its button opens the steps.',
  'settings.integrations.open': 'Open the Steps',
  'settings.integrations.loading': 'Reading where each setup stands…',
  'settings.integrations.vendor.stripe': 'Stripe',
  'settings.integrations.vendor.plaid': 'Plaid',
  'settings.integrations.vendor.zohoBooks': 'Zoho Books',
  'settings.integrations.vendor.maps': 'Maps',
  'settings.integrations.vendor.singleSignOn': 'Single sign-on',
  'settings.integrations.vendor.malwareScanner': 'Malware scanner',
  'settings.integrations.vendor.eSignature': 'Electronic signature',
  'settings.integrations.vendor.anthropic': 'Anthropic',
  'settings.integrations.vendor.peopleData': 'People Data',
  'settings.integrations.vendor.twilio': 'Twilio',
  'settings.integrations.serves.stripe': 'Card and bank payments on the portal, and bank feeds through Financial Connections.',
  'settings.integrations.serves.plaid': 'Bank feeds from your bank accounts and cards.',
  'settings.integrations.serves.zohoBooks': 'The old books the first cutover imports from.',
  'settings.integrations.serves.maps': 'Addresses, maps and travel time, through Google Maps or OpenStreetMap.',
  'settings.integrations.serves.singleSignOn': 'Signing in through your identity provider.',
  'settings.integrations.serves.malwareScanner': 'Scanning every uploaded file, through ClamAV.',
  'settings.integrations.serves.eSignature': 'Sending documents out to be signed.',
  'settings.integrations.serves.anthropic': 'The AI seam, which reads text and answers in a fixed shape, through Claude, and the lookups that read a company’s own website and search the web through it.',
  'settings.integrations.serves.peopleData': 'Lookups of a person or a company in a licensed people-data provider.',
  'settings.integrations.serves.twilio': 'Text messages and phone calls from the company number.',
  'settings.integrations.standing.notSetUp': 'Not set up',
  'settings.integrations.standing.step': 'Step',
  'settings.integrations.standing.of': 'of',
  'settings.integrations.standing.connected': 'Connected',
  'settings.integrations.standing.since': 'since',
  'settings.integrations.standing.attention': 'Needs attention:',
  'settings.integrations.seams': 'What it serves',
  'settings.integrations.seam.payments': 'Payments',
  'settings.integrations.seam.bankFeed': 'Bank feed',
  'settings.integrations.seam.maps': 'Map service',
  'settings.integrations.seam.booksSource': 'Books source',
  'settings.integrations.seam.malwareScan': 'File scanning',
  'settings.integrations.seam.signIn': 'Sign-in',
  'settings.integrations.seam.ai': 'AI seam',
  'settings.integrations.seam.enrichmentPeopleData': 'People-data lookups',
  'settings.integrations.seam.enrichmentWebsite': 'Website lookups',
  'settings.integrations.seam.enrichmentSearch': 'Web search lookups',
  'settings.integrations.seam.on': 'On',
  'settings.integrations.seam.off': 'Off',
  'settings.integrations.stepLater': 'Finish the step above first.',
  /** Decision 1648: Stripe's five steps. */
  'settings.integrations.stripe.title': 'Stripe',
  'settings.integrations.stripe.hint': 'Five steps take this installation from no Stripe account to a test payment landed. Each step says what to do in the Stripe dashboard and what to paste here.',
  'settings.integrations.stripe.mode.title': 'Choose the mode',
  'settings.integrations.stripe.mode.body': 'Test mode moves no real money, and its keys begin sk_test_ and pk_test_. Live mode takes real cards. Start in Test mode, take the test payment in step 5, then come back with the live keys.',
  'settings.integrations.stripe.mode.legend': 'Which keys will you enter?',
  'settings.integrations.stripe.mode.test': 'Test mode',
  'settings.integrations.stripe.mode.live': 'Live mode',
  'settings.integrations.stripe.mode.dashboard': 'Open the API keys page for this mode',
  'settings.integrations.stripe.mode.continue': 'Continue',
  'settings.integrations.stripe.mode.read': 'Read from the secret key held:',
  'settings.integrations.stripe.mode.unknown': 'The secret key held names no mode.',
  'settings.integrations.stripe.keys.title': 'Enter the keys',
  'settings.integrations.stripe.keys.body': 'On the API keys page, copy the secret key and the publishable key. The same Stripe account takes card and bank payments and serves Financial Connections, so these keys are entered once.',
  'settings.integrations.stripe.secret': 'Secret key',
  'settings.integrations.stripe.publishable': 'Publishable key',
  'settings.integrations.stripe.publishableHint': 'Public by design: it is sent to every browser that opens the bank sign-in, so it is held in the clear and shown back to you. It begins pk_.',
  'settings.integrations.stripe.publishableLooksSecret': 'That looks like a secret key rather than a publishable one. A publishable key begins pk_ and is safe to send to a browser; a secret key is not.',
  'settings.integrations.stripe.otherMode': 'That publishable key is from the other mode than the secret key held. Enter the pair from the same mode.',
  'settings.integrations.stripe.keys.save': 'Save the Keys',
  'settings.integrations.stripe.keys.check': 'Check the Keys',
  'settings.integrations.stripe.keys.checking': 'Asking Stripe…',
  'settings.integrations.stripe.keys.checkOk': 'Stripe answered for the account',
  'settings.integrations.stripe.keys.checkRefused': 'Stripe refused:',
  'settings.integrations.stripe.keys.summary': 'Secret key held; publishable key',
  'settings.integrations.stripe.webhook.title': 'Register the webhook',
  'settings.integrations.stripe.webhook.body': 'In the dashboard, under Developers, Webhooks, add an endpoint with the address below and choose the event payment_intent.succeeded. Stripe then shows a signing secret beginning whsec_; paste it here.',
  'settings.integrations.stripe.webhook.url': 'Endpoint address',
  'settings.integrations.stripe.webhook.dashboard': 'Open the webhooks page',
  'settings.integrations.stripe.webhookSecret': 'Signing secret',
  'settings.integrations.stripe.webhook.save': 'Save the Signing Secret',
  'settings.integrations.stripe.webhook.needsSecret': 'Paste the signing secret from Stripe, then save it.',
  'settings.integrations.stripe.webhook.waiting': 'Waiting for the first event. Send a test event from the dashboard, or take the test payment in step 5.',
  'settings.integrations.stripe.webhook.landed': 'The last event landed',
  'settings.integrations.stripe.rails.title': 'Rails and surcharge',
  'settings.integrations.stripe.rails.body': 'Choose how a customer may pay on the portal. A rail turned off is offered nowhere.',
  'settings.integrations.stripe.rails.card': 'Card',
  'settings.integrations.stripe.rails.bank': 'Bank debit (ACH)',
  'settings.integrations.stripe.rails.save': 'Save the Rails',
  'settings.integrations.stripe.rails.needs': 'Save the secret and publishable keys on step 2 first.',
  'settings.integrations.stripe.rails.surcharge': 'A credit card adds the card surcharge, now',
  'settings.integrations.stripe.rails.surchargeLink': 'Open Card Surcharge',
  'settings.integrations.stripe.rails.on': 'On:',
  'settings.integrations.stripe.rails.neither': 'neither rail',
  'settings.integrations.stripe.test.title': 'Take a test payment',
  'settings.integrations.stripe.test.body': 'Charges one dollar to Stripe’s own test card. Nothing real moves and no invoice is paid. The step is done once the webhook for it lands.',
  'settings.integrations.stripe.test.take': 'Take a One-Dollar Test Payment',
  'settings.integrations.stripe.test.taking': 'Charging the test card…',
  'settings.integrations.stripe.test.sent': 'The charge was taken; waiting for its webhook to land.',
  'settings.integrations.stripe.test.landed': 'The test payment landed',
  'settings.integrations.stripe.test.amount': 'Amount',
  'settings.integrations.stripe.test.fee': 'Stripe’s fee',
  'settings.integrations.stripe.test.net': 'Reached the bank',
  'settings.integrations.stripe.test.live': 'Live mode takes real money, so no test charge is offered here. Pay a small invoice on the portal with a real card instead, and refund it from the payment.',
  'settings.integrations.stripe.test.needs': 'Offered in Test mode once the keys and the webhook are in.',
  /** Decision 5380 (T2736): Anthropic's four steps. */
  'settings.integrations.anthropic.title': 'Anthropic',
  'settings.integrations.anthropic.hint': 'Four steps connect this installation to Anthropic. Every call is logged and priced, and one monthly cap covers every outside lookup.',
  'settings.integrations.anthropic.key.title': 'Enter the key',
  'settings.integrations.anthropic.key.body': 'Create a key in the Anthropic console and paste it here. It is held sealed, and this page only ever shows its last four characters.',
  'settings.integrations.anthropic.key.console': 'Open the Anthropic console’s keys page',
  'settings.integrations.anthropic.key.label': 'Anthropic key',
  'settings.integrations.anthropic.key.save': 'Save the Key',
  'settings.integrations.anthropic.key.typeFirst': 'Type a key first.',
  'settings.integrations.anthropic.model.title': 'Choose the model',
  'settings.integrations.anthropic.model.body': 'Type the model id exactly as Anthropic publishes it on its models page. The three prices below cost each call in the log.',
  'settings.integrations.anthropic.model.label': 'Model id',
  'settings.integrations.anthropic.model.inputPrice': 'Cents per million tokens in',
  'settings.integrations.anthropic.model.outputPrice': 'Cents per million tokens out',
  'settings.integrations.anthropic.model.searchPrice': 'Cents per web search',
  'settings.integrations.anthropic.model.pricesHint': 'Each price is a whole number of cents. A blank price goes back to its starting figure.',
  'settings.integrations.anthropic.model.save': 'Save the Model',
  'settings.integrations.anthropic.spending.title': 'Spending',
  'settings.integrations.anthropic.spending.body': 'The cap covers every outside lookup, AI and people data alike. Once the month reaches it, every lookup stops until the month turns.',
  'settings.integrations.anthropic.spending.cap': 'Monthly cap',
  'settings.integrations.anthropic.spending.capHint': 'Fifty dollars while none is saved.',
  'settings.integrations.anthropic.spending.spent': 'has spent',
  'settings.integrations.anthropic.spending.of': 'of',
  'settings.integrations.anthropic.spending.none': 'Nothing has been spent this month.',
  'settings.integrations.anthropic.spending.save': 'Save the Cap',
  'settings.integrations.anthropic.feature.testConnection': 'Test Connection',
  'settings.integrations.anthropic.feature.personSearch': 'Person search',
  'settings.integrations.anthropic.feature.companySearch': 'Company search',
  'settings.integrations.anthropic.feature.websiteReading': 'Website reading',
  'settings.integrations.anthropic.feature.personLookup': 'Person lookup',
  'settings.integrations.anthropic.feature.companyLookup': 'Company lookup',
  // Decision 5530: Ask Claude on the Discovery card, priced on the same cap.
  'settings.integrations.anthropic.feature.askClaude': 'Ask Claude',
  // Decision 6003: Make Report on the Discovery card, priced on the same cap.
  'settings.integrations.anthropic.feature.makeReport': 'Make Report',
  'settings.integrations.anthropic.test.title': 'Test the connection',
  'settings.integrations.anthropic.test.body': 'Makes the smallest call the model answers. It is logged and priced like any other call, and counts against the cap.',
  'settings.integrations.anthropic.test.press': 'Test Connection',
  'settings.integrations.anthropic.test.testing': 'Testing…',
  'settings.integrations.anthropic.test.connected': 'Connected. The model',
  'settings.integrations.anthropic.test.replied': 'replied',
  'settings.integrations.anthropic.refusal.noKey': 'No key is held. Enter one in step 1.',
  'settings.integrations.anthropic.refusal.noModel': 'No model is chosen. Type one in step 2.',
  'settings.integrations.anthropic.refusal.refusedKey': 'Anthropic refused the key. Check it in the console and enter it again.',
  'settings.integrations.anthropic.refusal.rateLimited': 'Anthropic is limiting calls just now. Try again in a minute.',
  'settings.integrations.anthropic.refusal.providerUnavailable': 'Anthropic did not answer. Check the model id, then try again later.',
  'settings.integrations.anthropic.refusal.capReached': 'This month’s cap is reached, so nothing was sent. A To-Do stands for the administrator.',
  /** Decisions 5381 and 5410 (T2737): the people-data provider's three steps. */
  'settings.integrations.peopleData.title': 'People Data',
  'settings.integrations.peopleData.hint': 'Three steps connect this installation to a licensed people-data provider. Every lookup is logged and priced, and counts against the one monthly cap on the Anthropic card.',
  'settings.integrations.peopleData.vendor': 'The provider is',
  'settings.integrations.peopleData.key.title': 'Enter the key',
  'settings.integrations.peopleData.key.body': 'Create a key in the provider’s dashboard and paste it here. It is held sealed, and this page only ever shows its last four characters.',
  'settings.integrations.peopleData.key.console': 'Open the provider’s keys page',
  'settings.integrations.peopleData.key.label': 'People data key',
  'settings.integrations.peopleData.key.save': 'Save the Key',
  'settings.integrations.peopleData.key.typeFirst': 'Type a key first.',
  'settings.integrations.peopleData.price.title': 'Set the price and the ceiling',
  'settings.integrations.peopleData.price.body': 'What one lookup costs on your plan, in whole cents. Leave it blank while the provider’s free lookups last, and each lookup is logged at nought cents. A lookup the provider finds nothing for costs nothing.',
  'settings.integrations.peopleData.ceiling.label': 'Lookups a month',
  'settings.integrations.peopleData.ceiling.body': 'The most lookups a month before the next one is refused. Leave it blank and the provider’s free hundred is the ceiling.',
  'settings.integrations.peopleData.ceiling.usage': 'lookups this month',
  'settings.integrations.peopleData.ceiling.of': 'of',
  'settings.integrations.peopleData.price.label': 'Cents per lookup',
  'settings.integrations.peopleData.price.unit': 'cents a lookup',
  'settings.integrations.peopleData.price.save': 'Save Price and Ceiling',
  'settings.integrations.peopleData.test.title': 'Test the connection',
  'settings.integrations.peopleData.test.body': 'Looks up your own company by its name and website. It is logged and priced like any other lookup, and counts against the cap.',
  'settings.integrations.peopleData.test.press': 'Test Connection',
  'settings.integrations.peopleData.test.testing': 'Testing…',
  'settings.integrations.peopleData.test.connected': 'Connected. The provider found your company:',
  'settings.integrations.peopleData.test.connectedNoMatch': 'Connected. The provider answered and found nothing for your company.',
  'settings.integrations.peopleData.test.noMatch': 'The provider found nothing for your company.',
  'settings.integrations.peopleData.field.fullName': 'Full name',
  'settings.integrations.peopleData.field.title': 'Title',
  'settings.integrations.peopleData.field.email': 'Email',
  'settings.integrations.peopleData.field.companyName': 'Company',
  'settings.integrations.peopleData.field.phone': 'Phone',
  'settings.integrations.peopleData.field.name': 'Name',
  'settings.integrations.peopleData.field.website': 'Website',
  'settings.integrations.peopleData.field.address': 'Address',
  // Decision 6180: what a whole answer finds beside the five fields.
  'settings.integrations.peopleData.field.linkedIn': 'LinkedIn page',
  'settings.integrations.peopleData.field.experience': 'Job',
  'settings.integrations.peopleData.field.education': 'School',
  'settings.integrations.peopleData.field.skill': 'Skill',
  'settings.integrations.peopleData.field.seniority': 'Seniority (assumed)',
  'settings.integrations.peopleData.field.jobRole': 'Role (assumed)',
  'settings.integrations.peopleData.field.jobSubRole': 'Sub-role (assumed)',
  'settings.integrations.peopleData.field.yearsExperience': 'Years of experience (assumed)',
  'settings.integrations.peopleData.field.salaryBand': 'Salary band (assumed)',
  'settings.integrations.peopleData.field.industry': 'Industry',
  'settings.integrations.peopleData.field.employerWebsite': "Employer's website",
  'settings.integrations.peopleData.field.employerAddress': "Employer's address",
  'settings.integrations.peopleData.field.employerLinkedIn': "Employer's LinkedIn page",
  'settings.integrations.peopleData.field.employerIndustry': "Employer's industry",
  'settings.integrations.peopleData.field.employerSize': "Employer's size",
  'settings.integrations.peopleData.field.employerFounded': 'Employer founded',
  'settings.integrations.peopleData.field.size': 'Size',
  'settings.integrations.peopleData.field.founded': 'Founded',
  'settings.integrations.peopleData.refusal.noKey': 'No key is held. Enter one in step 1.',
  'settings.integrations.peopleData.refusal.refusedKey': 'The provider refused the key. Check it and the plan’s credits in its dashboard, and enter it again.',
  'settings.integrations.peopleData.refusal.rateLimited': 'The provider is limiting lookups just now. Try again in a minute.',
  'settings.integrations.peopleData.refusal.providerUnavailable': 'The provider did not answer. Try again later.',
  'settings.integrations.peopleData.refusal.callCeilingReached': 'This month’s lookup ceiling is reached, so nothing was sent. A To-Do stands for the administrator.',
  'settings.integrations.peopleData.refusal.capReached': 'This month’s cap is reached, so nothing was sent. A To-Do stands for the administrator.',
  /** Decision 5820 (T2801): Twilio's card, its account and Test Connection on Setup, the two seam lines and the inbound address on Status. */
  'settings.integrations.twilio.title': 'Twilio',
  'settings.integrations.twilio.hint': 'Two steps connect this installation to your Twilio account. Texts and calls leave from the company number, and nothing reaches a phone until all three values are held.',
  'settings.integrations.twilio.tabs': 'Twilio sections',
  'settings.integrations.twilio.tab.setup': 'Setup',
  'settings.integrations.twilio.tab.status': 'Status',
  'settings.integrations.twilio.account.title': 'Enter the account',
  'settings.integrations.twilio.account.body': 'Copy the account SID and the auth token from the Account Info panel on your Twilio console’s home page. The token is held sealed, and this page only ever shows its last four characters.',
  'settings.integrations.twilio.account.console': 'Open the Twilio console',
  'settings.integrations.twilio.account.sid': 'Account SID',
  'settings.integrations.twilio.account.token': 'Auth token',
  'settings.integrations.twilio.account.tokenHint': 'The auth token checks that a text or call posted to us came from Twilio. Texts and calls we send sign in with the API key once it is saved.',
  'settings.integrations.twilio.account.number': 'Company number',
  'settings.integrations.twilio.account.numberHint': 'The Twilio number texts and calls leave from, written with the plus and the country code, such as +15735550100.',
  'settings.integrations.twilio.account.save': 'Save the Account',
  'settings.integrations.twilio.account.typeFirst': 'Type or change a value first.',
  'settings.integrations.twilio.account.inForce': 'Account SID, auth token and company number are all in force.',
  'settings.integrations.twilio.account.notInForce': 'The account SID, the auth token and the company number are each needed before anything is sent.',
  'settings.integrations.twilio.source.environment': 'The server’s environment supplies this while nothing is saved here.',
  'settings.integrations.twilio.test.title': 'Test the connection',
  'settings.integrations.twilio.test.body': 'Reads your account through Twilio’s API with the SID and the token in force. It sends nothing and costs nothing.',
  'settings.integrations.twilio.test.press': 'Test Connection',
  'settings.integrations.twilio.test.testing': 'Asking Twilio…',
  'settings.integrations.twilio.test.connected': 'Connected. Twilio answered for the account',
  'settings.integrations.twilio.test.refused': 'Twilio refused:',
  'settings.integrations.twilio.test.refusedNoWords': 'Twilio refused the account SID or the auth token. Check both in the console and enter them again.',
  'settings.integrations.twilio.test.notSetUp': 'No account SID or auth token is in force. Enter both in step 1.',
  'settings.integrations.twilio.test.unreachable': 'Twilio did not answer. Try again later.',
  'settings.integrations.twilio.status.lines': 'What answers texts and calls',
  'settings.integrations.twilio.status.inbound.title': 'Inbound texts',
  'settings.integrations.twilio.status.inbound.body': 'In the Twilio console, open the company number and paste this address under Messaging, A message comes in, as a webhook using HTTP POST. A text sent to the number is then filed here.',
  'settings.integrations.twilio.status.inbound.url': 'Inbound text address',
  /** Decision 1648: Plaid's three steps. */
  'settings.integrations.plaid.title': 'Plaid',
  'settings.integrations.plaid.hint': 'Three steps connect this installation to Plaid and prove it with one bank.',
  'settings.integrations.plaid.keys.title': 'Enter the keys',
  'settings.integrations.plaid.keys.body': 'From the Plaid dashboard, under Team Settings, Keys. The client ID identifies this installation and the secret authenticates it. Each Plaid environment issues its own secret.',
  'settings.integrations.plaid.clientId': 'Plaid client ID',
  'settings.integrations.plaid.secret': 'Plaid secret',
  'settings.integrations.plaid.keys.save': 'Save the Keys',
  'settings.integrations.plaid.keys.summary': 'Client ID',
  'settings.integrations.plaid.environment.title': 'Choose the environment',
  'settings.integrations.plaid.environment': 'Plaid environment',
  'settings.integrations.plaid.sandbox': 'Sandbox — test institutions, no real bank',
  'settings.integrations.plaid.production': 'Production — real bank accounts',
  'settings.integrations.plaid.environment.save': 'Save the Environment',
  'settings.integrations.plaid.connect.title': 'Connect one bank',
  'settings.integrations.plaid.connect.body': 'Open Banking, pick an account and press Connect, then choose Plaid and sign in to the bank on Plaid’s own screen. In the sandbox, Plaid’s test bank takes user_good and pass_good.',
  'settings.integrations.plaid.connect.open': 'Open Banking',
  'settings.integrations.plaid.connect.done': 'A bank account is connected through Plaid.',
  /** Decision 1648: the signature provider's two steps. */
  'settings.integrations.eSignature.title': 'Electronic Signature',
  'settings.integrations.eSignature.hint': 'Sending a document out to be signed is an optional module. No signature vendor is connected yet.',
  'settings.integrations.eSignature.module.title': 'Turn on electronic signature',
  'settings.integrations.eSignature.module.body': 'Electronic signature is switched on Settings › Modules.',
  'settings.integrations.eSignature.module.on': 'The module is on.',
  'settings.integrations.eSignature.module.off': 'The module is off.',
  'settings.integrations.eSignature.module.open': 'Open Modules',
  'settings.integrations.eSignature.vendor.title': 'The signature service',
  'settings.integrations.eSignature.vendor.body': 'No signature vendor’s adapter is built yet. Envelopes go through the built-in stand-in, which sends nothing outside this installation. A document is still signed the usual way: sent, and returned signed.',
  /** Decision 1648: the three pages that became steps keep their fields; these are the steps' own words. */
  'settings.maps.step.service': 'Choose the map service',
  'settings.maps.step.serviceSummary': 'Service:',
  'settings.maps.step.key': 'Enter the service keys',
  'settings.maps.step.keyNotNeeded': 'No Google key is needed for the service chosen.',
  'settings.maps.step.test': 'Test an address',
  'settings.maps.saveKey': 'Save the Key',
  'settings.maps.step.keyWaits': 'Choose the map service and save it on step 1 first.',
  'settings.maps.step.keyNeeded': 'Type the Google Maps API key, then press Save the Key.',
  /** Decision 3120: the Postal Service's credentials, beside the Google key on step 2. */
  /** Decision 3121: how many address services must agree, on step 1 beside the service. */
  'settings.maps.agreement': 'Address services that must agree',
  'settings.maps.agreementHint': 'If three services agree, the address is most likely correct; if only one comes back, it needs review. A whole number from 1 to 3; three that agree always verify.',
  'settings.maps.usps.title': 'USPS Addresses API',
  'settings.maps.usps.help': 'The consumer key and secret come from the USPS developer portal, and getting them needs a USPS Business Customer Gateway account. Both are stored sealed and never shown again.',
  'settings.maps.usps.consumerKey': 'USPS consumer key',
  'settings.maps.usps.consumerSecret': 'USPS consumer secret',
  'settings.maps.usps.save': 'Save the USPS Keys',
  'settings.maps.usps.remove': 'Remove the USPS keys',
  'settings.maps.usps.needed': 'Type the USPS consumer key or secret, then press Save the USPS Keys.',
  'settings.maps.usps.held': 'held',
  'settings.maps.usps.missing': 'not held',
  'settings.maps.usps.asked': 'USPS is asked first for every United States address.',
  'settings.maps.usps.notAsked': 'USPS is not asked. Enter both the consumer key and the consumer secret to ask it first for United States addresses.',
  // Decision 7320 (T3062): Geoapify's key and daily limit, and the Address services list with a switch for each service.
  'settings.maps.geoapifyService': 'Geoapify (map, geocoding and routing, needs a key)',
  'settings.maps.step.geoapifyKeyNeeded': 'Type the Geoapify API key, then press Save Geoapify.',
  // Decision 7510 (T3103): Geoapify draws the map's tiles, and its terms ask for both names under it.
  'map.attributionGeoapify': '© OpenStreetMap contributors · Powered by Geoapify',
  // T3068 (1): choosing Geoapify retires the wildcard's other rows, so the countries the United States rows do not cover are asked of Geoapify alone.
  'settings.maps.geoapify.chosenNote': 'With Geoapify chosen, Google Maps and OpenStreetMap are no longer asked for an address outside the United States, and Geoapify answers United States, Canadian and Mexican addresses only. An address in any other country is not checked until another map service is chosen.',
  'settings.maps.geoapify.title': 'Geoapify Geocoding API',
  'settings.maps.geoapify.help': 'The key comes from the Geoapify dashboard at myprojects.geoapify.com, where a free project allows 3,000 requests a day. It is stored sealed and never shown again.',
  'settings.maps.geoapify.key': 'Geoapify API key',
  'settings.maps.geoapify.limit': 'Geoapify daily limit',
  'settings.maps.geoapify.limitHint': 'Geoapify is not asked once today’s use reaches this many credits; its free plan allows 3,000 a day. A whole number; 0 means no limit.',
  'settings.maps.geoapify.limitProblem': 'The Geoapify daily limit is a whole number from 0, and 0 means no limit.',
  'settings.maps.geoapify.save': 'Save Geoapify',
  'settings.maps.geoapify.remove': 'Remove the Geoapify Key',
  'settings.maps.geoapify.needed': 'Type the Geoapify API key or change the daily limit, then press Save Geoapify.',
  'settings.maps.geoapify.asked': 'Geoapify is asked beside the other services for every United States, Canadian and Mexican address.',
  'settings.maps.geoapify.notAsked': 'Geoapify is not asked. Enter its key to ask it beside the other services for United States, Canadian and Mexican addresses.',
  'settings.maps.services.title': 'Address services',
  'settings.maps.services.hint': 'A service switched off is never asked. Switch it on again and it is asked at the next press of Verify Address. With every service off, no United States address is checked; each reads Unverifiable until one is on again.',
  'settings.maps.services.lastOn': 'With every service off, no United States address is checked; each reads Unverifiable until one is on again.',
  'settings.maps.services.loading': 'Loading the address services.',
  'settings.maps.services.none': 'The United States is bound to no address service.',
  'settings.maps.services.failed': 'The address services could not be loaded. Reload the page to try again.',
  'settings.maps.services.name.usps': 'USPS',
  'settings.maps.services.name.google': 'Google',
  'settings.maps.services.name.openstreetmap': 'OpenStreetMap',
  'settings.maps.services.name.census': 'Census Bureau',
  'settings.maps.services.name.geoapify': 'Geoapify',
  'settings.maps.services.keyHeld': 'Key held',
  'settings.maps.services.keyMissing': 'No key: not asked',
  'settings.maps.services.keyNone': 'Needs no key',
  'settings.maps.services.switch': 'Ask {name}',
  'settings.maps.services.usage': '{used} of {limit} credits today',
  'settings.maps.services.usageNoLimit': '{used} credits today, no limit',
  'settings.maps.testNeedsAddress': 'Type an address to test, then press Test the Service.',
  'settings.sso.step.register': 'Register this application with your provider',
  'settings.sso.step.registerSummary': 'Issuer',
  'settings.sso.step.check': 'Check the issuer',
  'settings.sso.step.on': 'Turn it on',
  'settings.sso.step.onSummary': 'Single sign-on is on.',
  'settings.sso.step.onWaits': 'Save the issuer, the client ID and the client secret on step 1 first.',
  'settings.sso.saveSwitch': 'Save the Switch',
  'scanner.step.address': 'Name the scanner’s address',
  'scanner.step.addressSummary': 'Scanner at',
  'scanner.step.check': 'Check it',
  'settings.sso.title': 'Single Sign-On',
  'settings.sso.hint': 'Sign in through your identity provider over OpenID Connect. The provider owns the second factor: with the switch on, an internal login signs in on the provider’s proof alone and no local code is asked for. The break-glass administrator and every portal login keep their password.',
  'settings.sso.enabled': 'Single sign-on is on',
  'settings.sso.enabledHint': 'With the switch on, an internal login’s password is refused with a message naming the button below, unless the overlap is on.',
  'settings.sso.providerName': 'Provider name, as the button reads it',
  'settings.sso.issuerUrl': 'Issuer URL',
  'settings.sso.issuerHint': 'The https address the provider names as its issuer; the discovery document is read from its well-known path.',
  'settings.sso.clientId': 'Client id',
  'settings.sso.clientSecret': 'Client secret',
  'settings.sso.clientSecretHint': 'Stored sealed; it is never shown again. Enter a new secret to replace the held one.',
  'settings.sso.secretHeld': 'A secret is held, ending in',
  'settings.sso.secretHeldNoTail': 'A secret is held.',
  'settings.sso.noSecret': 'No secret is held.',
  'settings.sso.removeSecret': 'Remove the held secret',
  'settings.sso.emailClaim': 'Email claim',
  'settings.sso.emailClaimHint': 'The claim on the identity token that carries the login email; email for most providers, preferred_username for some.',
  'settings.sso.passwordOverlap': 'Password overlap: accept passwords beside the provider',
  'settings.sso.passwordOverlapHint': 'The window for turning single sign-on off: people set a password while they can still sign in through the provider, then the switch goes off.',
  'settings.sso.redirectUri': 'Redirect URI to register with the provider:',
  'settings.sso.check': 'Check the Issuer',
  'settings.sso.checkFound': 'The provider answered as',
  'settings.sso.checkFailed': 'The check failed:',
  'settings.sso.save': 'Save',
  'settings.sso.problem.issuerUrl': 'The issuer is an https address with no query or fragment.',
  'settings.sso.problem.clientId': 'The switch needs a client id.',
  'settings.sso.problem.clientSecret': 'The switch needs a client secret.',
  'settings.sso.problem.emailClaim': 'The claim is one name, letters, digits, dots and underscores.',
  'settings.sso.problem.providerName': 'The button needs a provider name.',
  'settings.logins.method.sso': 'single sign-on',
  'settings.logins.lastSsoSignIn': 'Last signed in with single sign-on',
  'settings.reading.title': 'Document Reading',
  'settings.reading.hint': 'How an uploaded certificate is read. A fillable PDF reads from its own fields and a PDF with text from its text layer, with no key. A scan or a photo needs Google Cloud Vision, with a key from your Google Cloud project.',
  'settings.reading.googleKey': 'Google Cloud Vision API key',
  'settings.reading.googleKeyHint': 'Stored sealed; it is never shown again. Enter a new key to replace the held one.',
  'settings.reading.keyHeld': 'A key is held, ending in',
  'settings.reading.keyHeldNoTail': 'A key is held.',
  'settings.reading.noKey': 'No key is held: a scanned or photographed certificate is not read, the certificate window says the key is missing, and the person types what it says.',
  'settings.reading.removeKey': 'Remove the held key',
  'settings.reading.save': 'Save',
  // Decision 0701 (T212): the reading order and its defaults, stated on the page as built-in facts.
  'settings.reading.defaultsTitle': 'How a document is read',
  'settings.reading.defaultsHint': 'Built in and ready on a fresh install: these are how the reader works, shown here and not settings. The Google Cloud Vision key below is the one thing to fill, and only scans and photographs need it.',
  'settings.reading.order': 'Reading order',
  'settings.reading.step.fields': '1. A fillable PDF’s own form fields — no key needed',
  'settings.reading.step.text': '2. A PDF’s text layer, or a plain-text file — no key needed',
  'settings.reading.step.picture': '3. A scan or a photograph, through Google Cloud Vision — only with the key below',
  'settings.reading.kinds': 'File kinds read',
  'settings.reading.sizeCap': 'Size cap',
  'settings.reading.sizeCapUpload': 'for any upload',
  'settings.reading.sizeCapCertificate': 'for a certificate a customer sends through its one-time link',
  'settings.reading.firstWins': 'The first step that reads the document wins; a later step is tried only where the one before it read nothing.',
  'settings.priceList.title': 'Import Price List',
  'settings.priceList.hint': 'Update your items’ prices from a vendor’s price list. Upload the list, check every change it would make, then confirm. Each item is matched by the manufacturer’s part number, and its buy and sell prices keep their usual gap.',
  // Decision 7570 (T3121): the importer's five steps.
  'settings.priceList.file.title': 'Choose the File and the Manufacturer',
  'settings.priceList.file.label': 'Price List File',
  'settings.priceList.file.hint': 'One sheet saved as CSV is one run. A list with several sheets is imported one sheet at a time.',
  'settings.priceList.file.rows': 'rows',
  'settings.priceList.file.workbook': 'That file is a workbook. Save the sheet you want as CSV and choose that file.',
  'settings.priceList.file.empty': 'That file has no header row to read.',
  'settings.priceList.file.unreadable': 'That file could not be read.',
  'settings.priceList.file.hold': 'Choose a CSV file and the manufacturer first.',
  'settings.priceList.manufacturer': 'Manufacturer',
  'settings.priceList.manufacturerEntity': 'manufacturer',
  'settings.priceList.continue': 'Continue',
  'settings.priceList.map.title': 'Map the Columns',
  'settings.priceList.map.hint': 'Pick the file’s column for each field. The part number, description and list price are required.',
  'settings.priceList.map.part': 'Manufacturer Part Number',
  'settings.priceList.map.description': 'Description',
  'settings.priceList.map.list': 'List Price',
  'settings.priceList.map.buy': 'Buy Price',
  'settings.priceList.map.vendorPart': 'Vendor Part Number',
  'settings.priceList.map.notInFile': 'Not in this file',
  'settings.priceList.map.choose': 'Choose a column',
  'settings.priceList.map.presetName': 'Save Preset As',
  'settings.priceList.map.presetHint': 'The mapping is saved under this name for the manufacturer when you review, and the next run for that manufacturer offers it.',
  'settings.priceList.map.presetFilled': 'Filled from the manufacturer’s saved preset.',
  'settings.priceList.map.preset': 'Preset',
  'settings.priceList.map.hold': 'Choose the part number, description and list price columns, and name the preset.',
  'settings.priceList.choices.title': 'Choose How This Run Prices',
  'settings.priceList.choices.newBuy': 'Buy % of List for a New Item',
  'settings.priceList.choices.newBuyHint': 'Asked once. A new item’s sell price comes from that buy price through the default margin.',
  'settings.priceList.choices.runBuy': 'Buy % of List for Every Item',
  'settings.priceList.choices.runBuyHint': 'Leave blank to keep each item’s own spread. A mapped buy column always wins.',
  'settings.priceList.choices.create': 'Create an item for each unmatched row',
  'settings.priceList.choices.category': 'Category',
  'settings.priceList.choices.unit': 'Unit',
  'settings.priceList.choices.taxCategory': 'Tax Category',
  'settings.priceList.choices.status': 'Status',
  'settings.priceList.choices.choose': 'Choose one',
  'settings.priceList.choices.createHold': 'Choose the category, unit, tax category and status a new item takes.',
  'settings.priceList.choices.review': 'Review Changes',
  'settings.priceList.choices.creates': 'Unmatched rows create items.',
  'settings.priceList.choices.rejects': 'Unmatched rows are rejected.',
  'settings.priceList.review.title': 'Review the Changes',
  'settings.priceList.review.hint': 'Nothing changes until you accept. Adjust any row first; Accept All applies every increase, drop and new item.',
  'settings.priceList.review.accepting': 'Accepting',
  'settings.priceList.review.of': 'of',
  'settings.priceList.result.title': 'See What Changed',
  'settings.priceList.result.repriced': 'repriced',
  'settings.priceList.result.created': 'created',
  'settings.priceList.result.refused': 'refused',
  'settings.priceList.result.refusedHeading': 'Refused by the Margin Floor',
  'settings.priceList.result.rejectedHeading': 'Rejected Rows',
  'settings.priceList.result.again': 'Start Another Run',
  'settings.priceList.result.stopped': 'Accept All stopped at a refused batch after {done} of {total} rows were sent, and {left} were never sent. Run the same file again to finish: a row already applied is not applied twice.',
  'settings.priceList.file.notUtf8': 'That file is not UTF-8 text, so its accents and symbols would be lost. Save it as CSV UTF-8 and choose it again.',
  'settings.priceList.file.duplicateHeader': 'The file names two columns {name}, and only the last is read. Rename one in the file and choose it again.',
  'settings.priceList.map.duplicateColumn': 'The column {name} is chosen for more than one field. Each field takes a column of its own.',
  'settings.priceList.manufacturer.refused': 'The list is empty because your role may not read Companies. Ask an administrator for read on the Company module.',
  'settings.priceList.gate': 'Shown to a viewer holding write on the Item module, never to a role by name.',
  'settings.logins.lockoutThreshold': 'Failed attempts before lockout (1 to 20)',
  'settings.logins.lockoutWindow': 'Lockout window (minutes)',
  'settings.developerRoutes.title': 'Developer Routes',
  'settings.developerRoutes.hint': 'Tools for testing only: reset the test data, and read every email the server would have sent, password reset links included. They work only while this switch is on, only for a super admin, and never on the live server.',
  'settings.developerRoutes.enabled': 'Serve the developer routes',
  'settings.developerRoutes.resetLink': 'Reset to fixtures',
  'settings.developerRoutes.resetLinkLine': 'Empties the test database and loads the test companies again, behind a typed confirmation.',
  'settings.developerRoutes.nothingLink': 'Reset to nothing',
  'settings.developerRoutes.nothingLinkLine': 'Empties the test database and leaves the seeded developer and the reference seed alone, behind the same typed confirmation.',
  'settings.developerRoutes.resetLinkLast': 'Last reset: {date}',
  'settings.developerRoutes.resetLinkNever': 'Last reset: never',
  'settings.developerRoutes.enabledHint': 'A reset leaves this switch on; every change to it is written to the audit trail.',
  'settings.logins.clockLockoutTitle': 'Clock lockout',
  'settings.logins.clockLockoutHint': 'Refused PINs or badges at a clock or at a site gate lock that worker out of every clock for the window. The threshold runs from 1 to 20.',
  'settings.logins.clockLockoutThreshold': 'Refused punches before lockout (1 to 20)',
  'settings.logins.clockLockoutWindow': 'Clock lockout window (minutes)',
  'settings.logins.resetLinkMinutes': 'Reset link lifetime (minutes)',
  'settings.logins.resetRequestsPerHour': 'Reset requests per hour, per login',
  'settings.logins.employment': 'Employment',
  'settings.logins.pickEmployment': 'Choose an employment with no login yet',
  'share.landing.reading': 'Opening the shared file…',
  'share.landing.signIn': 'Every shared file opens only for somebody signed in, so this link asks you to sign in first. Sign in and the file opens here. With no login, ask for access below.',
  'share.landing.refusedSignIn': 'If you hold a login, sign in: this link may open for you once you are signed in.',
  'share.landing.signInAction': 'Sign in',
  'share.landing.refused': 'This link does not open this file.',
  'share.landing.askSender': 'Ask whoever sent the link to share the file again.',
  'share.landing.askLead': 'Or ask whoever sent it to give you access. They get a to-do; nothing is granted by asking.',
  'share.landing.askEmail': 'Where to reach you',
  'share.landing.askAction': 'Ask for Access',
  'share.needsLoginNote': 'This link opens only for somebody signed in. If they have no login, invite them under Settings › People › Logins, or let them ask from the link itself — the request comes back to you as a to-do.',
  'share.landing.asked': 'Your request has gone to whoever sent the link. They will be in touch.',
  'share.landing.unreachable': 'The file could not be reached. Try the link again.',
  'share.landing.cannotDraw': 'This file cannot be drawn in the browser. Use Download if the link allows it.',
  'settings.logins.company': 'Company',
  'settings.logins.pickCompany': 'Pick a company…',
  'settings.logins.pickCompanyFirst': 'Pick a company first',
  'settings.logins.ourOwn': '(ours)',
  'settings.logins.willBeInternal': 'This will be an internal login.',
  'settings.logins.willBePortal': 'This will be a portal login for',
  'settings.logins.noCandidates': 'Every open employment already has a login.',
  'settings.logins.hiddenExternal': 'employment(s) at other companies are not offered: this role cannot create an external login.',
  'settings.logins.hiddenInternal': 'employment(s) at your own company are not offered: this role cannot create a login here.',
  'settings.logins.viewingAs': 'You are viewing as somebody else, so this list answers for their role and not yours. Leave View as to invite.',
  'settings.logins.noOrganization': 'The organization is not bound to its own company yet, so no employment counts as internal. Set the company under Settings › Organization.',
  'settings.logins.email': 'Login email',
  'settings.logins.role': 'Role',
  'settings.logins.delivery': 'Delivery',
  'settings.logins.channel.workEmail': 'Link to the work email',
  'settings.logins.channel.personalEmail': 'Link to a personal email',
  'settings.logins.channel.mobile': 'Code by text message',
  'settings.logins.channel.inPerson': 'Code handed over in person',
  'settings.logins.channel.temporaryPassword': 'Temporary password',
  'settings.logins.mobile': 'Mobile number',
  'settings.logins.personalEmail': 'Personal email',
  'settings.logins.linkHint': 'The link works once and expires in seven days. A personal address is only where the link goes; it is never the login.',
  'settings.logins.hint.mobile': 'The code works once and expires in 24 hours; the number becomes the recovery channel.',
  'settings.logins.hint.inPerson': 'The code is shown to you once to hand over; it sets no password and grants no session.',
  'settings.logins.hint.temporaryPassword': 'The weakest method: the password is shown to you once and must be changed at the first sign-in.',
  'settings.logins.send': 'Send Invitation',
  // Decision 6880: a super admin issues a temporary password to an existing login, and Resend takes a channel.
  'settings.logins.issueTemporaryPassword': 'Issue Temporary Password',
  'settings.logins.resendTitle': 'Resend Invitation',
  'settings.logins.resendWhat': 'The new invitation replaces the one still waiting. The last channel is chosen to start; a personal address or a number is asked only when the channel changes to one.',
  // T3007: an invitation on a channel whose provider is not set up is refused, naming the two ways that work now.
  'settings.logins.mailNotSetUp': 'Company mail is not set up yet, so no invitation goes by email. Its Sending step on Settings › Email › Mailboxes is not done. Hand the login over with a code in person or a temporary password instead.',
  'settings.logins.textNotSetUp': 'No text provider is connected, so no code goes by text message. Hand the login over with a code in person or a temporary password instead.',
  'settings.logins.problem.pick': 'Choose an employment and a login email.',
  'settings.logins.problem.destination': 'Name where the link or code goes.',
  // Decision 0575: the organisation's one logo, uploaded here and drawn in Region 1 of every print template.
  'settings.logo.title': 'Logo',
  'settings.logo.intro': 'One logo for the whole organization. It is drawn at the top left of every document that prints, and on the document pages you read on screen.',
  'settings.logo.none': 'No logo is set, so the top left of every printed sheet is empty.',
  'settings.logo.alt': 'Organization logo',
  'settings.logo.choose': 'Choose a logo',
  'settings.logo.upload': 'Upload Logo',
  'settings.logo.replace': 'Replace Logo',
  'settings.logo.remove': 'Remove Logo',
  'settings.logo.rule': 'A PNG, JPEG or SVG picture, no larger than 2 MB. It is stored as you send it and never re-drawn.',
  'settings.logo.uploaded': 'The logo is saved.',
  'settings.logo.removed': 'The logo is removed.',
  // Decisions 1393 to 1396: the Organization profile page, its seven sections and its two confirmations.
  'settings.organization.title': 'Organization Profile',
  'settings.organization.intro': 'The organization’s own facts: what prints on every document, the zone and formats every screen reads, and the defaults a new document starts from.',
  'settings.organization.identity': 'Identity',
  'settings.organization.names': 'Names',
  'settings.organization.legalName': 'Legal name',
  'settings.organization.tradingName': 'Trading name',
  'settings.organization.namesHint': 'The legal name prints at the top of every quote, invoice, statement and credit note. The trading name is our company record’s name, which screens and lists show; it is changed on that record.',
  'settings.organization.confirmNameTitle': 'Change the Legal Name',
  'settings.organization.confirmNameBody': 'Every document printed or reprinted from now on carries “{name}” at the top, old ones included. The change is recorded in the audit trail.',
  'settings.organization.confirmZoneTitle': 'Change the Time Zone',
  'settings.organization.confirmZoneBody': 'Printed documents read their dates in this zone, so an old document reprinted from now on shows its dates in {zone}. The change is recorded in the audit trail.',
  'settings.organization.confirm': 'Change It',
  'settings.organization.saved': 'The organization profile is saved.',
  'settings.organization.address': 'Address and Contact',
  'settings.organization.printedAddress': 'Printed address',
  'settings.organization.noAddress': 'Our company record names no building yet, so nothing prints under the logo.',
  'settings.organization.mainPhone': 'Main phone',
  'settings.organization.website': 'Website',
  'settings.organization.notRecorded': 'Not recorded',
  'settings.organization.editOnRecord': 'Edit on our company record',
  'settings.organization.addressHint': 'Read from our own company record, which is where they are changed. The address is the one every document prints.',
  'settings.organization.registration': 'Registration',
  'settings.organization.ein': 'Tax ID',
  'settings.organization.duns': 'DUNS number',
  'settings.organization.onFile': 'On file',
  'settings.organization.registrationHint': 'Both numbers are kept encrypted on our company record, so this page shows only whether each is on file. State sales-tax registrations are on Tax › Sales Tax.',
  'settings.organization.salesTaxLink': 'Open Sales tax',
  'settings.organization.regional': 'Regional',
  'settings.organization.timeZone': 'Time zone',
  'settings.organization.timeZoneHint': 'The one zone the organization runs in. Printed documents and the day calculations use it, and so does anybody who has not chosen their own zone under Account settings.',
  'settings.organization.currency': 'Currency',
  'settings.organization.currencyHint': 'Every amount is held in this one currency, and the application converts none. It is fixed at install, so it is shown here and never changed.',
  'settings.organization.fiscal': 'Fiscal',
  'settings.organization.fiscalYearStart': 'Fiscal year start',
  'settings.organization.fiscalLocked': 'Fixed now that accounting periods exist, because each period was cut from this day.',
  'settings.organization.fiscalOpen': 'No accounting period exists yet, so the day the fiscal year starts can still move.',
  'settings.organization.periodLength': 'Accounting period length',
  'settings.organization.periodsLink': 'Open Accounting periods',
  'settings.organization.coverage': 'Data coverage from',
  'settings.organization.coverageHint': 'How far back the imported history reaches. It records the past, so it is not edited.',
  'settings.organization.salesDefaults': 'Sales Defaults',
  'settings.organization.poThreshold': 'Customer PO required above',
  'settings.organization.poHint': 'An order above this amount needs the customer’s purchase order number, as does any order for a customer whose own record asks for one.',
  'settings.organization.numberSeriesHint': 'Document numbers and their prefixes are set on Number series.',
  'settings.organization.numberSeriesLink': 'Open Number series',
  'settings.logo.problem.pick': 'Choose a picture to upload.',
  'settings.logo.problem.type': 'A logo is a PNG, a JPEG or an SVG picture.',
  'settings.logo.problem.size': 'A logo is no larger than 2 MB.',
  'settings.domains.title': 'Email Domains',
  'settings.domains.subtitle': 'The domains an internal login may sit on',
  'settings.domains.intro': 'The email domains your own staff use, such as yourcompany.com. Somebody can join as staff only with a work address on a domain listed here, so add yours before inviting anybody.',
  'settings.domains.domain': 'Domain',
  'settings.domains.added': 'Added',
  'settings.domains.current': 'Current',
  'settings.domains.none': 'No domain is registered yet.',
  'settings.domains.add': 'Register a Domain',
  'settings.domains.addTitle': 'Register an Email Domain',
  'settings.domains.makeCurrent': 'Make this the current domain',
  'settings.domains.makeCurrentHint': 'Every registered domain keeps matching, so nobody is locked out by a change. The current one is where new work addresses are expected.',
  'settings.domains.saved': 'The domain is registered.',
  'settings.domains.problem.blank': 'Type the domain to register.',
  'settings.domains.check': 'Check Records',
  'settings.domains.checkTitle': 'Check the Domain’s Records',
  'settings.domains.checkHint': 'Looks up the SPF record, the DMARC record and a DKIM key, and stores what DNS answered. Leave the selector blank to try the common ones.',
  'settings.domains.selector': 'DKIM Selector',
  'settings.domains.checked': 'The check is stored.',
  'settings.domains.notChecked': 'Records not checked yet',
  'settings.domains.lastCheck': 'Last check',
  'settings.domains.overall': 'Overall',
  'settings.domains.dkimNotSetUp': 'Not Set Up for DKIM',
  'settings.domains.dkimAdds': 'DKIM signs the mail you send, so receivers can tell it came from you and fewer messages land in spam. Mail sends without it.',
  'settings.logins.pickRole': 'Pick a role…',
  'settings.logins.problem.role': 'Choose the role this login holds. A login whose job carries no role can read nothing.',
  'settings.logins.noRoleWarning': 'This login holds no role, so it sees nothing until a role is set on Settings › People › Logins.',
  'settings.logins.sendWithoutRole': 'Send Without a Role',
  'settings.logins.noPortalRole': 'No role is marked as usable by a login outside your own company yet. Mark one under Settings › People › Roles, then come back.',
  'settings.roles.portalTitle': 'Outside logins',
  'settings.roles.portalEligible': 'A login outside your own company may hold this role',
  'settings.roles.portalHint': 'Leave this off for a role meant for your own staff. An invitation for somebody at another company offers only the roles marked here, and refuses any other whatever the screen showed.',
  // Decision 3460: the one gate that holds a role to its book of business; off, the role reads its modules whole.
  'settings.roles.reachTitle': 'Who they see',
  'settings.roles.restricted': 'Only the companies, Deals and jobs assigned to them',
  'settings.roles.reachHint': 'Leave this off and a person in this role sees every company’s records in the modules ticked above. Tick it and they see the companies assigned to them, the Deals whose team they are on and the jobs their own lines name.',
  // Decision 6004 (T2780 batch 2): Ask Claude, Resubmit and Make Report are a permission a role carries.
  'settings.roles.askClaude': 'May press Ask Claude, Resubmit and Make Report',
  'settings.classes.title': 'Document Classes by Role',
  'settings.tags.title': 'Tags',
  'settings.customFields.title': 'Custom Fields',
  'settings.departments.addButton': '+ Add Department',
  'settings.departments.addTitle': 'Add a Department',
  'settings.departments.name': 'Name',
  'settings.departments.parent': 'Parent department',
  'settings.departments.head': 'Head of department',
  'settings.departments.positions': 'positions',
  'settings.departments.hint': 'Your organization’s departments and who heads each. An approval that goes to a department’s head reaches the person set here; a department in use is turned off rather than deleted.',
  'settings.roles.pick': 'Role',
  'settings.roles.grid': 'Module permissions',
  'settings.roles.caption': 'The four independent rights per Module',
  'settings.roles.classes': 'Document classes',
  'settings.roles.classesCaption': 'Which document classes each role opens',
  'settings.roles.name': 'Role name',
  'settings.roles.nameNeeded': 'Give the role a name.',
  'settings.roles.newTitle': 'Add a Role',
  'settings.roles.addButton': '+ Add Role',
  'settings.roles.newHint': 'A new role starts with nothing granted. Pick it on the page, tick what it opens, and save before anybody is given it.',
  'settings.tags.uses': 'uses',
  'settings.tags.mergeInto': 'Merge into',
  'settings.tags.merge': 'Merge',
  'settings.tags.empty': 'No tags yet. A tag is made on a record, from its Tags field, and shows here once a record carries it.',
  'settings.tags.hint': 'Merging repoints every record carrying the retired label to the one kept. Changing a color recolors that label everywhere it is applied.',
  'settings.tags.colour': 'Color for this label',
  // Decision 2260: the Categories card and its editor on Settings › CRM › Tags (screen D).
  'settings.tags.category': 'Category',
  'settings.tags.colourFromCategory': 'Color from category',
  'settings.tags.mergeCategoryDiffers': 'The two labels sit in different categories; the label kept keeps its own.',
  'settings.tagCategories.title': 'Categories',
  'settings.tagCategories.addButton': 'Add Category',
  'settings.tagCategories.empty': 'No category yet. A category groups tags and gives them one icon and one color.',
  'settings.tagCategories.tag': 'tag',
  'settings.tagCategories.tags': 'tags',
  'settings.tagCategories.record': 'record',
  'settings.tagCategories.records': 'records',
  'settings.tagCategories.outOfUse': 'Out of use: its tags draw uncategorized, in their own colors.',
  'settings.tagCategories.moveUp': 'Move up',
  'settings.tagCategories.moveDown': 'Move down',
  'settings.tagCategories.editButton': 'Edit',
  'settings.tagCategories.add': 'Add Category',
  'settings.tagCategories.edit': 'Edit Category',
  'settings.tagCategories.save': 'Save',
  'settings.tagCategories.name': 'Name',
  'settings.tagCategories.inUse': 'In use',
  'settings.tagCategories.warning': 'Changing the color or icon changes every tag in this category, everywhere, straight away. A category taken out of use leaves its tags on their records, drawn uncategorized in their own colors.',
  'settings.tagCategories.sampleOne': 'First tag',
  'settings.tagCategories.sampleTwo': 'Second tag',
  'settings.customFields.recordType': 'Record type',
  'settings.customFields.name': 'Field name',
  'settings.customFields.kind': 'Kind',
  'settings.customFields.values': 'Values, one per line',
  'settings.customFields.required': 'Required',
  'settings.customFields.hint': 'Extra fields you add to companies, people, locations and sites. A field added here shows on every record of that kind; turning one off hides it and keeps what was filled in. Item and asset details are set under Catalog › Categories.',
  'settings.customFields.addButton': '+ Add Custom Field',
  'settings.customFields.addTitle': 'Add a Custom Field',
  'settings.certificates.title': 'Tax Exempt Certificates',
  'settings.siteRequirements.title': 'Company Site Requirements',
  'settings.assignments.title': 'Account Assignments',
  'file.details.title': 'File Details',
  'file.details.type': 'File type',
  'file.details.description': 'Description',
  'file.details.note': 'Add a note',
  'file.details.noteHint': 'A note on the file, kept with the file and shown on the record it is filed under.',
  'file.details.newVersion': 'Upload a new version',
  'file.details.versions': 'Versions',
  'file.details.versionLine': 'Version {number}: {name}, uploaded by {person} on {date}',
  'file.details.storedAs': 'Stored as {stored}',
  'file.details.unknownUploader': 'an unknown person',
  'file.details.delete': 'Delete File',
  'file.details.deleteTitle': 'Delete this file?',
  'file.details.deleteBody': 'The file goes to the trash and stays restorable by an administrator.',
  'file.details.deleteConfirm': 'Delete',
  'file.details.edit': 'File details',
  'file.details.saved': 'Saved.',
  'certificate.title': 'Add tax exempt certificate',
  'certificate.state': 'State',
  'certificate.reason': 'Exemption reason',
  'certificate.editTitle': 'Edit tax exempt certificate',
  'certificate.reviewTitle': 'Review tax exempt certificate',
  'certificate.reviewHint': 'A contact uploaded this through a request link. It exempts nothing until you check its facts against the file and save.',
  'changeRequest.action': 'Request Change',
  'changeRequest.title': 'Request a Change',
  'changeRequest.hint': 'Tell us what is wrong or out of date on your company’s record. Somebody on our side makes the change or says why not, and the answer shows on the Change requests tile.',
  'changeRequest.field': 'The field it concerns',
  'changeRequest.anyField': 'No particular field',
  'changeRequest.text': 'What needs to change',
  'changeRequest.send': 'Send Request',
  'changeRequest.filed': 'Your request is sent. Its answer shows on the Change requests tile.',
  'changeRequest.none': 'None yet',
  'changeRequest.accept': 'Accept',
  'changeRequest.decline': 'Decline',
  'changeRequest.declineTitle': 'Decline the Change Request',
  'changeRequest.declineNote': 'Why it is declined',
  'changeRequest.declineHint': 'The person who filed the request reads this note beside its status.',
  'changeRequest.accepted': 'The change request is accepted.',
  'changeRequest.declined': 'The change request is declined.',
  'changeRequest.acceptNotice': 'The change asked for:',
  'changeRequest.acceptField': 'It names the field',
  'changeRequest.filedBy': 'Filed by',
  'changeRequest.decidedBy': 'Decided by',
  'changeRequest.statusOpen': 'Open',
  'changeRequest.statusAccepted': 'Accepted',
  'changeRequest.statusDeclined': 'Declined',
  'certificateRequest.title': 'Certificate request links',
  'certificateRequest.none': 'No certificate has been requested from this site.',
  'certificateRequest.request': 'Request a Certificate',
  'certificateRequest.send': 'Send the Link',
  'certificateRequest.hint': 'The contact gets an email with a link to upload their certificate: one PDF or photo, no sign-in. The link works once and for 14 days, and what it brings waits for your review.',
  'certificateRequest.contact': 'Contact',
  'certificateRequest.pickContact': 'Choose the contact',
  'certificateRequest.noContacts': 'Nobody at this account has a work email; add one to a contact first.',
  'certificateRequest.sent': 'sent',
  'certificateRequest.until': 'open until',
  'certificateRequest.sentTo': 'The link was sent to',
  'certificateRequest.revoke': 'Revoke Link',
  'certificateRequest.revoked': 'The link is revoked; it opens nothing now.',
  'certificateRequest.state.open': 'Open',
  'certificateRequest.state.used': 'Used',
  'certificateRequest.state.expired': 'Expired',
  'certificateRequest.state.revoked': 'Revoked',
  'certificateUpload.title': 'Upload a tax exempt certificate',
  'certificateUpload.for': 'For',
  'certificateUpload.document': 'Document',
  'certificateUpload.hint': 'Upload one PDF or a photo of the signed certificate, up to 10 MB. The link works once.',
  'certificateUpload.choose': 'Choose File',
  'certificateUpload.noFile': 'No file chosen',
  'certificateUpload.send': 'Upload',
  'certificateUpload.sending': 'Uploading…',
  'certificateUpload.received': 'Thank you. Your certificate was received, and this link now closes.',
  'certificateUpload.reading': 'Checking the link…',
  'certificateUpload.tooLarge': 'The file is larger than 10 MB. Send a smaller scan or a PDF.',
  'certificate.getLibrary': '— or browse every form we hold in the',
  'certificate.library': 'forms library',
  'taxForms.title': 'Tax Exempt Forms',
  'taxForms.subtitle': 'Every state form we hold, and the two multistate forms',
  'taxForms.hint': 'Each form opens in your browser or downloads as the state published it. Settings › Tax › Sales Tax hides one the business does not use.',
  'taxForms.manageHint': 'Hide a form the business does not use: it leaves the forms library and the certificate window’s links, and still opens from here.',
  'taxForms.open': 'Open',
  'taxForms.download': 'Download',
  'taxForms.hide': 'Hide',
  'taxForms.show': 'Show',
  'taxForms.hidden': 'Hidden',
  'taxForms.none': 'Every form is hidden. Show one from Settings › Tax › Sales Tax.',
  'taxForms.unreachable': 'The forms library could not be read.',
  'certificate.formType': 'Form type',
  'certificate.pickFormType': 'Choose the form',
  'certificate.edit': 'Edit',
  'certificate.end': 'End',
  'certificate.view': 'View Certificate',
  'certificate.menu': 'Certificate actions',
  'certificate.card.reason': 'Reason',
  'certificate.card.form': 'Form',
  'certificate.card.effective': 'In force from',
  'certificate.card.expires': 'Expires',
  'certificate.card.noExpiry': 'No expiry on its file',
  'certificate.card.ended': 'Ended',
  'certificate.card.by': 'by',
  'certificate.endTitle': 'End This Certificate',
  'certificate.endConsequence': 'From the moment you end it, this certificate exempts nothing: a quote or invoice dated after today is taxed. Documents dated today or earlier keep their exemption, and the row stays on the site as history.',
  'certificate.ended': 'ended',
  // 🔒 Decision 7891 (T3181): a certificate no document names is removed or moved; one a document names is ended.
  'certificate.remove': 'Remove',
  'certificate.move': 'Move',
  'certificate.removeTitle': 'Remove This Certificate',
  'certificate.removeConsequence': 'Remove is for a certificate placed here by mistake. The row is deleted, so this site reads as if it never held it, and its file stays on the company\'s Files with its type.',
  'certificate.moveTitle': 'Move This Certificate',
  'certificate.moveSite': 'Site It Belongs To',
  'certificate.moveConsequence': 'The certificate and its file move to the site you pick, and both sites\' History say so.',
  'certificate.moveNone': 'This company has no other open site to move it to.',
  'certificate.movePick': 'Pick the site it belongs to first.',
  'certificate.openSite': 'Open the site\'s certificates',
  'certificate.fileStands': 'The file stays as it is. A different document is a renewal: end this certificate and add the new one.',
  'certificate.effectiveFrom': 'Effective from',
  'certificate.expiresOn': 'Expires on',
  'certificate.file': 'Certificate file',
  'certificate.pickFile': 'Choose the certificate before saving.',
  'certificate.getForm': 'Get the blank form:',
  'certificate.getStreamlined': 'or the multistate',
  'certificate.noForm': 'This state levies no general sales tax; a certificate here is for a local or special tax.',
  'certificate.add': '+ Add Certificate',
  'certificate.nameNotFound': 'The customer’s name was not found on the document; check it is the right certificate.',
  'certificate.nameFound': 'The customer’s name is on the document. The dates read from it are filled in below; check them before saving.',
  'certificate.unread': 'The document could not be read; enter the dates by hand.',
  'certificate.noVisionKey': 'This is a picture or a scan, and no Google Cloud Vision key is held, so it was not read. An administrator enters one in Settings › Documents › Document Reading; until then, enter the dates by hand.',
  'certificate.reading': 'Reading the document…',
  'certificate.chooseFile': 'Choose File',
  'certificate.noFile': 'No file chosen',
  'certificate.pickState': 'Choose the state',
  'certificate.pickReason': 'Choose the exemption reason',
  'certificate.municipality': 'Municipality',
  'certificate.statewide': 'Statewide',
  'certificate.otherMunicipality': 'Another municipality…',
  'certificate.municipalityName': 'Municipality name',
  'certificate.reasonOther': 'Reason, in the certificate\u2019s words',
  'certificate.preview': 'Certificate',
  'certificate.previewHint': 'Pick the file and it shows here, so what the reading missed can be read off the page.',
  'certificate.noPreview': 'This file type has no preview; open it from the Files tile after saving.',
  'certificate.readFields': 'Read from the form’s own fields:',
  'certificate.readText': 'Read from the document’s text:',
  'certificate.readPicture': 'Read from the picture:',
  'certificate.blanket': 'blanket certificate',
  'certificate.singlePurchase': 'single purchase',
  'certificate.purchaser': 'purchaser',
  'requirement.title': 'Add Site Requirement',
  'requirement.type': 'Requirement',
  'requirement.startDate': 'Required from',
  'requirement.end': 'End',
  'requirement.endTitle': 'End This Requirement',
  'requirement.endDate': 'Ends on',
  // 🔒 Decision 10501: a row the rule finds unused is removed, leaving no ended row.
  'requirement.remove': 'Remove',
  'requirement.removeTitle': 'Remove This Requirement',
  'requirement.removeConsequence': 'Remove deletes this requirement, so the site reads as if it never required it, and the History keeps a line saying it was removed. It is offered while no work has been sent to the site since the requirement started.',
  'requirement.ended': 'Ended',
  'requirement.pickType': 'Pick a requirement.',
  'requirement.notRequiredHere': 'Not required at this site',
  'requirement.notRequiredHint': 'Waives a Company default here: the type leaves the bring list for this site.',
  // Decision 0464: what this person is recorded as able to do, beneath a skill.
  'subSkill.title': 'Record a sub-skill',
  'subSkill.pick': 'Sub-skill',
  'subSkill.pickOne': 'Pick a sub-skill.',
  'subSkill.none': 'Nothing recorded yet',
  'subSkill.remove': 'Withdraw',
  'subSkill.hint': 'A statement somebody made, not a certificate: it proves nothing and expires on no date. A job that needs it warns when this person is dispatched without it.',
  'subSkill.notedBy': 'recorded by',
  // Decision 0614: a person holds a skill whole, and the whole covers every sub-skill beneath it.
  'skill.title': 'Record a Skill',
  'skill.pickSkill': 'Skill',
  'skill.pickSkillOne': 'Pick a skill.',
  'skill.pickWhat': 'What they can do',
  'skill.wholeSkill': 'The whole skill',
  'skill.wholeSkillOnly': 'This skill has no sub-skills, so it is held whole.',
  'skill.none': 'Nothing recorded yet',
  'skill.alreadyWhole': 'Already recorded as the whole skill.',
  'skill.hint': 'A statement somebody made, not a certificate: it proves nothing and expires on no date. A skill held whole covers every sub-skill under it, so a job asking for one of them warns about nobody who holds the skill.',
  'siteRole.title': 'Add Site Role',
  'siteRole.role': 'Role',
  'siteRole.pickRole': 'Pick a role.',
  'siteRole.startDate': 'From',
  'siteRole.billTo': 'Bill to',
  'siteRole.shipTo': 'Ship to',
  'siteRole.service': 'Service',
  'settings.requirementTypes.title': 'Site Requirement Types',
  'settings.requirementTypes.hint': 'The site requirements and protective equipment a customer site can ask for, such as a hard hat. Every site picks from this list; a requirement’s kind is set when it is added and does not change.',
  'settings.requirementTypes.name': 'Name',
  'settings.requirementTypes.kind': 'Kind',
  'settings.requirementTypes.kindEquipment': 'Equipment',
  'settings.requirementTypes.kindVerified': 'Verified',
  'settings.requirementTypes.requirementType': 'Verified against',
  'settings.requirementTypes.scope': 'Scope',
  'settings.requirementTypes.scopePersonal': 'Personal',
  'settings.requirementTypes.scopeShared': 'Shared',
  'settings.requirementTypes.serialised': 'Serialized instrument',
  'settings.requirementTypes.add': '+ Add Type',
  'settings.requirementTypes.retire': 'Retire',
  'settings.requirementTypes.retired': 'Retired',
  'settings.display.category': 'Organization',
  'settings.display.dateFormat': 'Date format',
  'settings.display.hint': 'Every date in the application reads in this order. Times keep each person’s own time zone.',
  'settings.display.tileListRows': 'Rows shown in a tile list',
  'settings.display.tileListRowsHint': 'How many rows a tile listing documents, people, files or events shows before Show more. Four when left empty. History keeps its own scrolling list.',
  'settings.company.pick': 'Company',
  'settings.company.pickEntity': 'company',
  'settings.certificates.hint': 'Certificates are held per site: a school is exempt at its campus and not at its commercial arm at the same address.',
  'settings.certificates.none': 'No certificate on this site.',
  'settings.certificates.state': 'State',
  'settings.siteRequirements.type': 'Requirement type',
  'settings.assignments.rep': 'Sales rep',
  'settings.assignments.hint': 'Which sales reps look after which companies. Pick a rep to add or remove their companies; a company can have several reps. This decides who sees an account, so it carries no job title.',
  'settings.assignments.assign': 'Assign',
  'settings.fileTypes.name': 'File type',
  'settings.fileTypes.class': 'Class',
  'settings.fileTypes.customerMayDownload': 'Customer may download',
  /** 🔒 Decision 9680: the switch that makes a type an employment form, offered by the Person page's Employment Documents. */
  'settings.fileTypes.employmentForm': 'Employment form',
  'settings.fileTypes.pendingPublic': 'Public is proposed; a second person approves it.',
  'settings.fileTypes.system': 'System',
  'settings.fileTypes.systemHint': 'The application asks for this type by name, so it cannot be renamed or retired.',
  // 🔒 Decision 13311: what the application files under a type it names, on the type's File Types and Document Photo Processing rows.
  'settings.fileTypes.purpose.visitNote': 'For visit photo uploads: every photo added to a visit’s notes, on the web or the phone, is filed under this type.',
  'settings.fileTypes.approvePublic': 'Approve Public',
  'settings.fileTypes.addButton': '+ Add File Type',
  'settings.fileTypes.addTitle': 'Add a File Type',
  'settings.fileTypes.hint': 'The kinds of document people file, such as a signed contract or a permit. Set each kind’s class, which decides who may open it, and whether it can be downloaded; making a kind Public takes a second person’s approval.',
  'settings.blocked.hint': 'File endings that can never be uploaded, such as .exe. Type one and press Enter to add it; a file ending in a listed one, even behind a second ending, is refused.',
  'settings.blocked.list': 'Blocked extensions',
  'settings.blocked.listTitle': 'Blocked Extensions',
  'settings.chips.add': 'Type a value and press Enter',
  'settings.chips.none': 'Nothing listed yet.',
  'settings.release.permission': 'Default release permission',
  'settings.release.downloadOffered': 'Download box offered on release',
  'settings.release.watermark': 'Watermark line under the viewer identity',
  'settings.release.approvalRule': 'Approval rule a release routes through',
  'settings.release.hint': 'The choices offered when somebody releases a file to a customer or a vendor. Whether to release is still decided file by file, and every change here is recorded.',
  'settings.classes.hint': 'Which kinds of document each role can open. Tick a class for a role to let everybody in that role open documents of that class.',
  'settings.terms.preamble': 'Preamble',
  'settings.terms.preambleNote': 'no number · no title',
  'settings.terms.addClause': '+ Add Clause',
  'settings.terms.addPreamble': '+ Add Preamble',
  'settings.terms.import': '+ Import Document',
  'settings.terms.clauses': 'clauses',
  'settings.terms.clause': 'clause',
  'settings.terms.titleField': 'Title',
  'settings.terms.body': 'Body',
  'settings.terms.kinds': 'Prints on',
  'settings.terms.kindsHint': 'The documents this section\u2019s clauses print on; a section prints on at least one, and one that should print nowhere is removed.',
  'settings.terms.addSection': '+ Add Section',
  'settings.terms.tabs': 'Terms & Conditions',
  'settings.terms.tabLibrary': 'Library',
  'settings.terms.tabSets': 'Sets',
  'settings.termsSets.title': 'Terms Sets',
  'settings.termsSets.hint': 'A set is a named pick of the library\u2019s sections. Each kind of document prints its client\u2019s set, else its class\u2019s default set, else the whole library. A set a confidentiality role adds prints after everything else, always.',
  'settings.termsSets.empty': 'No terms set yet: every document prints the whole library.',
  'settings.termsSets.add': '+ New Set',
  'settings.termsSets.new': 'New Terms Set',
  'settings.termsSets.edit': 'Edit Terms Set',
  'settings.termsSets.editAction': 'Edit',
  'settings.termsSets.name': 'Name',
  'settings.termsSets.role': 'Added by Confidentiality Role',
  'settings.termsSets.noRole': 'None: a Class or a Client Picks It',
  'settings.termsSets.roleHint': 'A set naming a role prints after the set and after a document\u2019s own terms wherever a confidential file of that role is attached.',
  'settings.termsSets.roleLocked': 'A class default or a client\u2019s choice takes no role; make a new set for the role.',
  'settings.termsSets.pickHint': 'Tick the sections the set prints. A section brings its sub-sections; a sub-section ticked alone prints beneath its section\u2019s heading.',
  'settings.termsSets.order': 'Printed Order',
  'settings.termsSets.nonePicked': 'No section picked yet.',
  'settings.termsSets.up': 'Up',
  'settings.termsSets.down': 'Down',
  'settings.termsSets.retire': 'Retire',
  'settings.termsSets.retireTitle': 'Retire This Terms Set',
  'settings.termsSets.retireBody': '{name} leaves the list and no document prints it again. The library keeps it as it was, and a document already sent keeps the terms it went out with.',
  'settings.termsSets.retired': 'Retired.',
  'settings.termsSets.sections': '{count} sections',
  'settings.termsSets.oneSection': '1 section',
  'settings.termsSets.addedBy': 'Added by {role}',
  'settings.termsSets.defaultFor': 'Default for {class}',
  'settings.termsSets.clients': '{count} clients',
  'settings.termsSets.oneClient': '1 client',
  'settings.termsSets.classes': 'Class Defaults',
  'settings.termsSets.classesHint': 'The set each class of document prints where its client names none. The Bill and the two payments are receipts and print no terms.',
  'settings.termsSets.class': 'Class',
  'settings.termsSets.kinds': 'Documents',
  'settings.termsSets.defaultSet': 'Default Set',
  'settings.termsSets.wholeLibrary': 'Whole Library',
  'settings.terms.editSection': 'Edit section',
  'settings.terms.section': 'Section',
  'settings.terms.sectionNumber': 'Number',
  'settings.terms.sectionNumberHint': 'Blank takes the next free number. Two sections never share one, and the section\u2019s clauses move with it.',
  'settings.terms.sectionTitle': 'Heading',
  'settings.terms.sectionKindsHint': 'The documents this section\u2019s clauses print on; each clause inherits them unless it narrows them. A section prints on at least one.',
  'settings.terms.untitledSection': 'Untitled section',
  'settings.terms.addHereClause': '+ Clause',
  'settings.terms.addHereSubSection': '+ Sub-Section',
  'settings.terms.addClauseTo': 'Add a clause to {name}',
  'settings.terms.addSubSection': 'Add a sub-section under {name}',
  'settings.terms.editSubSection': 'Edit sub-section',
  'settings.terms.subSectionNumberHint': 'Blank takes the next place after the section\u2019s last clause or sub-section. A clause and a sub-section never share a place, and the sub-section\u2019s clauses move with it.',
  'settings.terms.subSectionKindsHint': 'The documents this sub-section\u2019s clauses print on: its section\u2019s, or fewer of them.',
  'settings.terms.subSection': 'Sub-section',
  'settings.terms.order': 'Order',
  'settings.terms.inherit': 'Prints on every document its section does',
  'settings.terms.narrowHint': 'A clause narrows its section to fewer documents, never more.',
  'settings.terms.preambleKindsHint': 'The documents the Preamble prints on; it prints on at least one.',
  'settings.terms.noSection': 'Add a section first: every clause belongs to one.',
  'settings.terms.dragHint': 'Drag a clause onto another section to move it, or choose its section in its own editor.',
  'settings.terms.remove': 'Remove',
  'settings.terms.removeSectionTitle': 'Remove this section',
  'settings.terms.removeClauseTitle': 'Remove this clause',
  'settings.terms.removeSectionBody': '{name} and every clause in it stop printing on these documents: {kinds}. A document already sent keeps the terms its frozen copy carries, and a document with its own terms keeps them.',
  'settings.terms.removeClauseBody': '{name} stops printing on these documents: {kinds}. A document already sent keeps the terms its frozen copy carries, and a document with its own terms keeps them.',
  'settings.terms.removeConfirm': 'Remove',
  'settings.terms.removed': 'Removed.',
  'settings.terms.editClauseTitle': 'Save this change',
  'settings.terms.editSectionTitle': 'Save this section\u2019s change',
  'settings.terms.editBodySent': 'The library keeps {name} as it was and writes the change as its next version. Every draft prints the change. The sent documents listed keep the words they went out with.',
  'settings.terms.editBodyNone': 'The library keeps {name} as it was and writes the change as its next version. Every draft prints the change, and no sent document names it.',
  'settings.terms.editConfirm': 'Save',
  'settings.terms.overrides': 'Overrides',
  'settings.terms.overridesHint': 'Each override stands on its own line; removing one lets the clause fall back to the next level up.',
  'settings.terms.flagged': 'Flagged for review',
  'settings.terms.flaggedHint': 'The Global clause changed after this override was negotiated. Keep it, update it to match Global, or edit it further; there is no silent dismiss.',
  'settings.terms.keep': 'Keep as Override',
  'settings.terms.updateToGlobal': 'Update to Match Global',
  'settings.terms.addOverride': '+ Client Override',
  'settings.terms.overrideFor': 'Client',
  'settings.terms.editFlags': 'Saving this change flags every override pointing at this clause for review:',
  'settings.terms.importHint': 'A .docx splits into one container per numbered clause, keeping the source numbering. Nothing is written until the outline is confirmed.',
  'settings.terms.importFile': 'Terms document',
  'settings.terms.noFileChosen': 'No file chosen',
  'settings.terms.outline': 'Outline',
  'settings.terms.sections': 'sections',
  'settings.terms.noNumbering': 'The source carries no detectable numbering; nothing was written.',
  'settings.terms.preambleFound': 'Preamble found: keep it as the list\u2019s first row',
  'settings.terms.signatureFound': 'Signature block found: it sits outside the clause list and is not written as a clause',
  'settings.terms.confirmImport': 'Write the Clauses',
  'settings.terms.written': 'clauses written',
  'settings.terms.parties': 'Signature parties',
  'settings.terms.partiesHint': 'Who signs each kind of document. Add a signer for a document type to print a signature line for them; a document type with no signer prints no signature block.',
  'settings.terms.documentType': 'Document type',
  'settings.terms.partyLabel': 'Party label',
  'settings.terms.addPartyButton': '+ Add Signer',
  'settings.terms.addPartyTitle': 'Add a Signer',
  'settings.templates.name': 'Name',
  'settings.templates.subject': 'Subject',
  'settings.templates.bodyField': 'Body',
  'settings.templates.documentType': 'Document type',
  'settings.templates.hint': 'The emails the application sends with a document, such as an invoice. Each document type has one default template; write or change any template here and pick it when you send.',
  'settings.templates.from': 'From',
  'settings.templates.fromNone': 'Whoever sends it',
  'settings.templates.fromHint': 'Used on every message sent from this template; when empty, the sender\u2019s own address.',
  'settings.templates.fromInactive': 'no longer a sending address',
  'settings.templates.cc': 'Cc',
  'settings.templates.bcc': 'Bcc',
  'settings.templates.addAddress': 'Add',
  'settings.templates.removeAddress': 'Remove',
  'settings.photo.hint': 'Which kinds of uploaded file have their pictures shrunk as they arrive, and to what resolution in dots per inch.',
  'settings.photo.compress': 'Compress on upload',
  'settings.photo.dpi': 'Target DPI',
  'settings.notifications.hint': 'The reminders the application sends on its own, one line each: what it chases, who it reaches, how often and when it stops. Open one with its pencil to change its timing or its template, and preview or test-send it to yourself first.',
  'settings.notifications.subject': 'Subject',
  'settings.notifications.template': 'Template',
  'settings.notifications.offsets': 'Offsets (days, comma separated)',
  'settings.notifications.maxSends': 'Max sends',
  'settings.notifications.active': 'Active',
  'settings.notifications.preview': 'Preview',
  'settings.notifications.testSend': 'Test-Send to Me',
  'settings.notifications.sentTo': 'Sent to',
  'settings.email.footer': 'Legal footer (HTML)',
  'settings.email.footerTitle': 'Legal Footer (HTML)',
  'settings.email.footerHint': 'Appended after any signature on every message the application sends; empty is the normal state and appends nothing.',
  'settings.account.title': 'Account Settings',
  'settings.account.zone': 'Time zone',
  'settings.account.zoneHint': 'Every screen renders its times in this zone; the organization\u2019s own is the fallback.',
  'settings.account.organizationZone': 'Use the organization zone',
  // T575: the empty choice names the organization's zone by its city and offset, so it fits the Large box at either text size.
  'settings.account.organizationZoneNamed': 'Organization: {city} ({offset})',
  'settings.account.textSize': 'Text Size',
  'settings.account.textSizeNormal': 'Normal',
  'settings.account.textSizeLarge': 'Large',
  'settings.account.textSizeHint': 'Large draws every screen one step bigger. It changes the moment you choose it, and follows you to every device you sign in from.',
  'settings.account.textSizeRefused': 'Your text size was not saved, so the page is back at the size it had.',
  'settings.account.signOutEverywhere': 'Sign Out Everywhere',
  'settings.account.revoked': 'sessions ended',
  'settings.account.employment': 'Your employment',
  'settings.account.login': 'Your login',
  'settings.fileTypes.title': 'File Types',
  'settings.fileTypes.appliesEverywhere': 'Applies on every record:',
  'settings.fileTypes.appliesTo': 'Applies on:',
  'settings.photo.title': 'Document Photo Processing',
  'settings.templates.title': 'Email Template Manager',
  'settings.notifications.title': 'Reminder Rules',
  'settings.notifications.approvalTitle': 'Approval Reminders',
  'settings.notifications.approvalHint': 'A document routed for approval mails each person its step routes to once. While it waits, the reminder mails them again every so many days. Zero or blank turns the reminder off.',
  'settings.notifications.approvalDays': 'Remind Every (Days)',
  'settings.notifications.interval': 'Interval (days)',
  'settings.notifications.stop': 'Stops when',
  'settings.notifications.eventMode': 'On the event',
  'settings.notifications.empty': 'No reminder exists yet; each one appears here once the part of the application that sends it is built.',
  'settings.notifications.offsetsInvalid': 'Offsets are whole days, comma separated.',
  'settings.notifications.offsetsShort': 'Offsets (days)',
  'settings.notifications.inactive': 'Inactive',
  'settings.terms.title': 'Term Container Library',
  'settings.terms.importTitle': 'Import Document',
  'settings.terms.signatureTitle': 'Signature Block',
  'settings.terms.empty': 'No clause yet. Add one, add the Preamble, or import a document.',
  'settings.terms.noSignature': 'No party: this document type prints no Signature Block.',
  'settings.terms.partyFields': 'By · Printed Name · Date',
  'settings.account.activeEmployment': 'Your active employment is the one every write is signed with.',
  'settings.account.noEmployment': 'No active employment.',
  'settings.account.signOutEverywhereHint': 'Ends every session you hold, this one included, and takes you to the Login Screen.',
  'settings.account.noSession': 'The sandbox holds no session to end; sign in as a person to use this.',
  'settings.account.passkeys': 'Passkeys',
  'settings.account.passkeysHint': 'A passkey answers the second step of your sign-in in place of the code. The password step stays; add one on each device you sign in from.',
  'settings.account.passkeyLabel': 'Device name',
  'settings.account.addPasskey': 'Add a Passkey',
  'settings.account.removePasskey': 'Remove',
  'settings.account.noPasskeys': 'No passkeys yet.',
  'settings.account.passkeyAdded': 'Passkey added.',
  'settings.account.passkeyUnsupported': 'This browser offers no passkeys.',
  'settings.account.passkeyRefused': 'The passkey was not added.',
  'settings.account.passkeyAddedOn': 'added',
  'settings.account.passkeyLastUsed': 'last used',
  'settings.account.passkeyNeverUsed': 'never used',
  'settings.attributeTypes.title': 'Attribute Types',
  'settings.attributeTypes.hint': 'The details an item can record, such as voltage or color. A measured detail has a unit; a list detail has a set of choices. A detail nothing uses can be deleted; one in use is retired instead.',
  'settings.attributeTypes.name': 'Name',
  'settings.attributeTypes.kind': 'Kind',
  'settings.attributeTypes.family': 'Unit family',
  'settings.attributeTypes.defaultUnit': 'Default unit',
  'settings.attributeTypes.values': 'Values, one per line',
  'settings.attributeTypes.addValues': 'Add Values',
  'settings.attributeTypes.retired': 'retired',
  'settings.attributeTypes.editTitle': 'Edit Attribute Type',
  'settings.attributeTypes.save': 'Save Changes',
  'settings.attributeTypes.delete': 'Delete',
  'settings.attributeTypes.deleteTitle': 'Delete Attribute Type',
  'settings.attributeTypes.deleteConsequence': 'This attribute type and its values are deleted for good. Where something uses it, nothing is deleted and Retire is offered instead.',
  'settings.attributeTypes.deleteValueTitle': 'Remove Value',
  'settings.attributeTypes.deleteValueConsequence': 'This value is removed from its attribute type for good. Where an item carries it, nothing is removed and Retire is offered instead.',
  'settings.attributeTypes.valuesHeading': 'Values',
  'settings.attributeTypes.value': 'Value',
  'settings.attributeTypes.valueRetired': 'Retired',
  'settings.attributeTypes.moveUp': 'Move up',
  'settings.attributeTypes.moveDown': 'Move down',
  'settings.attributeTypes.firstAlready': 'This value is first already.',
  'settings.attributeTypes.lastAlready': 'This value is last already.',
  'settings.attributeTypes.removeValue': 'Remove value',
  'settings.attributeTypes.familyFixed': 'The unit family stays as the type was made; the default unit moves within it.',
  'settings.attributeTypes.choose': 'Choose…',
  'settings.attributeTypes.addButton': '+ Add Attribute Type',
  'settings.attributeTypes.addTitle': 'Add an Attribute Type',
  'settings.attributeTypes.addNeeds': 'Give the type a name, then a unit family and default unit, or at least one value.',
  'item.attribute.newType': 'New Attribute Type…',
  'settings.attributeTypes.none': 'No attribute types yet.',
  'settings.categories.title': 'Categories',
  'settings.categories.hint': 'The categories items are sorted into, and the details each category’s items carry. Pick a category to see or change its details; a category with none of its own uses its parent’s, and its badge says so.',
  'settings.categories.own': 'own',
  'settings.categories.inherits': 'inherits',
  'settings.categories.retired': 'retired',
  'settings.categories.deep': 'past three levels',
  'settings.categories.deepHint': 'Revisit the tree: real data has passed three levels.',
  'settings.categories.showRetired': 'Show retired',
  'settings.categories.new': '+ New Category',
  'settings.categories.newUnder': 'New category under',
  'settings.categories.newRoot': 'New category at the root',
  'settings.categories.name': 'Name',
  'settings.categories.rename': 'Rename',
  'settings.categories.items': 'items',
  'settings.categories.itemsStillPoint': 'items still point at it; new items no longer land there.',
  'settings.categories.noAttributes': 'no attribute list',
  'settings.categories.moveUnder': 'Move under',
  'settings.categories.root': 'the root',
  'settings.categories.previewMove': 'Preview Move',
  'settings.categories.moveConsequences': 'Three things change for',
  'settings.categories.vendorsChange': 'Which vendors are asked for its RFQ lines: repping follows the tree.',
  'settings.categories.attributesChange': 'Which attributes apply: the inherited list changes',
  'settings.categories.attributesStay': 'Which attributes apply: the category keeps its own list.',
  'settings.categories.confirmMove': 'Move',
  'settings.categories.attributes': 'Attributes',
  'settings.categories.defaultUnit': 'default',
  'settings.categories.inheritedFrom': 'inherited from',
  'settings.categories.required': 'required',
  'settings.categories.optional': 'optional',
  'settings.categories.makeRequired': 'Make Required',
  'settings.categories.makeOptional': 'Make Optional',
  'settings.categories.addAttribute': '+ Add attribute',
  'settings.categories.pick': 'Select a category to see its attributes.',
  'catalog.priceList.increases': 'increases',
  'catalog.priceList.decreases': 'decreases',
  'catalog.priceList.creates': 'new items',
  'catalog.priceList.unchanged': 'unchanged',
  'catalog.priceList.rejected': 'rejected',
  'catalog.priceList.part': 'Manufacturer part',
  'catalog.priceList.description': 'Description',
  'catalog.priceList.kind': 'Change',
  'catalog.priceList.list': 'List',
  'catalog.priceList.buy': 'Buy',
  'catalog.priceList.sell': 'Sell',
  'catalog.priceList.kind.increase': 'Increase',
  'catalog.priceList.kind.decrease': 'Drop',
  'catalog.priceList.kind.unchanged': 'Unchanged',
  'catalog.priceList.kind.create': 'New item',
  'catalog.priceList.kind.rejected': 'Rejected',
  'catalog.priceList.adjust': 'Adjust',
  'catalog.priceList.keep': 'Keep',
  'catalog.priceList.acceptAll': 'Accept All',
  'catalog.repricing.title': 'Purchase Price Rose',
  'catalog.repricing.accept': 'Accept Price',
  'catalog.repricing.decline': 'Leave as Is',
  'catalog.repricing.landed': 'Purchase price',
  'catalog.repricing.currentSell': 'Current sales price',
  'catalog.repricing.newSell': 'New sales price',
  'catalog.repricing.hint': 'The number you type is the final price; nothing applies until each item is answered.',
  'catalog.repricing.priceAboveZero': 'A price is above zero.',
  'dev.pieceSixteen': 'Item, the catalog and the stock ledger',
  'edit.none': 'None',
  'edit.addressLine2': 'Address line 2',
  'edit.addressLineN': 'Address line {n}',
  'edit.clearSite': 'Clear',
  'edit.companyEntity': 'company',
  'edit.companyChosen': 'Chosen',
  // An accounting document's two modes: the edit form and the print layout drawn on screen (decision 0409).
  'document.edit.header': 'Details',
  'document.edit.lines': 'Item table',
  'document.edit.saveDraft': 'Save as Draft',
  'document.edit.saveAndSend': 'Save and Send',
  'document.edit.saveAndSchedule': 'Save and Schedule',
  'document.edit.action': 'Edit',
  'document.footer.more': 'More',
  // Decision 0687: who writes an accounting document, and the reason the page says in place of the controls it withholds.
  'document.write.refusedExternal': 'An outside login reads its own company’s documents and writes none.',
  'document.write.refusedModule': 'This login holds no write on this document’s module.',
  'document.write.refusedNotOnAccount': 'Only a person in accounting, assigned to this account or on its deal’s team changes this document.',
  // Decision 1632: a document read refused to a viewer neither assigned to the account nor on the Deal’s team.
  'document.read.restricted': 'This record is restricted: you are not on the account or the Deal’s team.',
  // T114, decision 0681: every document's own date, the box on its form and the fact on its page.
  'document.edit.date': 'Date',
  'document.fact.date': 'Date',
  // T133, T134, T115, decision 0682: the Purchase Order's Bill To and delivery date, and how five documents ship.
  // T124, decision 0683: the notes Region 7 prints, typed under each form's header.
  'document.edit.notesToCustomer': 'Notes to the customer',
  'document.edit.notesToVendor': 'Notes to the vendor',
  'document.edit.notesHint': 'Printed on the first page of the sheet, under the addresses.',
  // 🔒 Decision 0684 (T125): the Terms window on a printed document's page, and a clause's kinds on its own editor.
  'document.terms.action': 'Terms',
  'document.terms.title': 'Terms & Conditions',
  'document.terms.loading': 'Reading the terms\u2026',
  'document.terms.unavailable': 'The terms could not be read.',
  'document.terms.inForce': 'Clauses in force for this document',
  'document.terms.none': 'No clause in the library prints on this kind of document.',
  'document.terms.replace': 'Replace the terms for this document alone',
  'document.terms.ownText': 'This document\u2019s own terms',
  'document.terms.ownHint': 'Printed in place of the clauses, as written here. Untick to print the clauses again.',
  'document.terms.asSent': 'Terms as sent {date}: the words this document went out with, whatever the library has said since.',
  'document.terms.fromClientSet': 'From the terms set {name}, this client\u2019s choice.',
  'document.terms.fromClassSet': 'From the terms set {name}, the default for this kind of document.',
  'document.terms.addition': 'Confidentiality Addition',
  'document.terms.additionHint': 'The clauses marked Confidentiality Addition print after this document\u2019s own terms as well, and replacing the terms never removes them.',
  'document.terms.predates': 'This document\u2019s copy was sent {date}, before a sent document kept its terms, so the clauses below are today\u2019s.',
  'document.copy.open': 'Open',
  'document.copy.title': 'The Copy as Sent',
  'document.copy.sent': 'Sent {date} by {name}',
  'document.copy.lines': 'Lines',
  'document.copy.total': 'Total',
  'document.copy.terms': 'Terms as sent',
  'document.copy.unavailable': 'The copy could not be read.',
  'document.edit.shipmentPreference': 'Shipment preference',
  'document.edit.shipmentPreferenceNone': 'Not chosen',
  'document.print.shipVia': 'Ship Via',
  'document.print.deliverBy': 'Deliver By',
  'po.form.billTo': 'Bill To',
  'po.form.billToHint': 'Our own billing address, from the organization’s default billing site.',
  'po.form.billToMissing': 'The organization has no billing site yet; add one on its own company page.',
  'po.form.deliveryDate': 'Delivery date',
  'settings.shipmentPreferences.title': 'Shipment Preferences',
  'settings.shipmentPreferences.hint': 'How a document’s goods travel. The Quote, Sales Order, Invoice, Purchase Order and Work Order each offer this list; one switched off is no longer offered, and a document that already names it keeps it.',
  'settings.shipmentPreferences.label': 'Label',
  'settings.shipmentPreferences.order': 'Order',
  'settings.shipmentPreferences.add': '+ Add Shipment Preference',
  'settings.shipmentPreferences.addTitle': 'Add a Shipment Preference',
  'settings.shipmentPreferences.off': 'Switched off',
  'settings.packageTypes.title': 'Package Types',
  'settings.packageTypes.hint': 'The kinds of box a package is packed in. A type retired here is no longer offered on a new package, and a package that already names it keeps it.',
  'settings.packageTypes.label': 'Label',
  'settings.packageTypes.order': 'Order',
  'settings.packageTypes.add': '+ Add Package Type',
  'settings.packageTypes.addTitle': 'Add a Package Type',
  'settings.packageTypes.off': 'Retired',
  'settings.packageTypes.used': 'Packages',
  /* T185: a site written from the document form, beside the picker it lands in. */
  'document.edit.siteEntity': 'Site',
  'document.edit.addSiteRereadFailed': 'The site was saved, but the account’s sites could not be read again, so it is not chosen here yet. Reload the page to pick it.',
  // Piece 22, decision 0612: the two views of one accounting document.
  'document.view.details': 'Details',
  'document.view.pdf': 'PDF',
  'document.view.openPdf': 'Open the PDF in a new tab',
  'document.view.legend': 'Which view of this document',
  'document.view.backToList': 'Back to the list',
  'document.view.closeGlyph': '\u00d7',
  'document.view.noSheet': 'This document has no printed sheet to show.',
  // 🔒 Decision 1401: a region outside the geometry rule warns, and the sheet still draws. The banner opens once, lists one line per region, and gives the fix once (T591).
  'document.view.sheetWarning': 'In template {template}, region {region}: {problem}.',
  'document.view.sheetWarningOpeningOne': 'The sheet is drawn, but one region falls outside the layout rule.',
  'document.view.sheetWarningOpeningMany': 'The sheet is drawn, but {count} regions fall outside the layout rule.',
  'document.view.sheetWarningFix': 'Fix the template under Settings › Documents › Print Templates.',
  'document.view.sheetWarningAction': 'Open Print Templates',
  // 🔒 How many pages the printer would produce, under the sheet the pane draws (T169).
  'document.sheet.onePage': '1 page',
  'document.sheet.pages': 'pages',
  'document.sheet.frame': 'Printed Sheet',
  'document.view.sheetInViewer': 'This document\u2019s sheet is drawn by the viewer. Press PDF above to open it.',
  'document.view.items': 'Items',
  // 🔨 Editing a sent Quote reopens it as the next revision (decision 0172), which
  // refuses a prompt. The control names its own consequence instead (decision 0474).
  'document.revise.action': 'Edit as revision',
  'document.frozen.why': 'Why this cannot be edited',
  'document.sheet.pageAlt': 'Page {page} of {pages}',
  'document.print.noLines': 'No lines on this document yet.',
  // Region 8's own eight column headings, and Region 1's wordmark where no logo file is set (piece 27).
  'document.print.column.lineNumber': '#',
  'document.print.column.item': 'Item',
  'document.print.column.description': 'Description',
  'document.print.column.mfrPart': 'MFR Part #',
  'document.print.column.qty': 'Qty',
  'document.print.column.unitPrice': 'Unit Price',
  'document.print.column.discount': 'Discount',
  'document.print.column.amount': 'Amount',
  'document.print.column.unitCost': 'Unit Cost',
  'document.print.column.extendedCost': 'Extended Cost',
  // Piece 27: the columns the other families and the optional Image column add to Region 8.
  'document.print.column.image': 'Image',
  'document.print.column.unit': 'Unit',
  'document.print.column.listPrice': 'List Price',
  'document.print.listPrice': 'List',
  'document.print.taxableMark': 'T',
  'document.print.taxableLegend': 'T = Taxable',
  'document.print.column.sku': 'SKU',
  'document.print.column.vendorPart': 'Vendor #',
  'document.print.column.mfrReference': 'MFR Reference',
  // T3775: a Sent payment's share that is no bill, read under the Bill heading in words and not as the stored kind.
  'document.print.nonBillShare': 'Credit Note Refund',
  'document.print.vendorPart': 'Vendor #:',
  'document.print.column.itemAndDescription': 'Item & Description',
  'document.print.column.asset': 'Asset',
  'document.print.column.hours': 'Hours',
  'document.print.column.level': 'Level',
  'document.print.column.rate': 'Rate',
  'document.print.column.invoice': 'Invoice',
  'document.print.column.settled': 'Settled',
  // Piece 27, decision 0223: the customer statement's own Region 8 headings.
  'document.print.column.entryDate': 'Date',
  'document.print.column.document': 'Document',
  'document.print.column.charge': 'Charge',
  'document.print.column.credit': 'Credit',
  'document.print.column.balance': 'Balance',
  'document.print.issuedBy': 'From',
  'document.print.billTo': 'Bill To',
  'document.print.shipTo': 'Ship To',
  'document.print.vendor': 'Vendor',
  'document.print.deliverTo': 'Deliver To',
  'document.print.customer': 'Customer',
  'document.print.site': 'Site',
  'document.print.total': 'Total',
  'document.print.subtotal': 'Subtotal',
  'document.print.discount': 'Discount',
  'document.print.subject': 'Subject',
  // Piece 27: the printed sheet's own furniture, headed the same way on the screen and on the page.
  'document.print.confidential': 'CONFIDENTIAL',
  // Decision 10632 (T887): the corner mark on a sent document printed after its send, with the day printed.
  'document.print.reprint': 'Reprint {date}',
  // 🔒 Decision 0568: the footer mark on a document that runs to more than one sheet; a document of one sheet carries none.
  'document.print.pageOfPages': 'Page {n} of {m}',
  'document.print.termsSheet': 'Terms',
  'document.print.termsAsSent': 'Terms as Sent {date}',
  'document.print.termsPredate': 'This copy was sent {date}, before a sent document kept its terms; the terms below are today\u2019s.',
  'document.print.signedBy': 'By',
  'document.print.printedName': 'Printed Name',
  'document.print.signatureDate': 'Date',
  'document.print.noLinesPrinted': 'No Lines',
  'document.print.region.scheduledDate': 'Scheduled',
  'document.print.region.technician': 'Technician',
  'document.print.region.ppe': 'PPE',
  'document.print.region.completionSignature': 'Completed By',
  // Piece 27, decision 0558: the check's own regions above ten. Each draws one value and no caption, because the stock pre-prints every caption.
  'document.print.region.amountInWords': 'Amount in Words',
  'document.print.region.memo': 'Memo',
  'document.print.region.payTo': 'Pay To',
  'document.print.region.checkDate': 'Date',
  'document.print.region.numericAmount': 'Amount',
  'document.print.paidBy': 'Paid By',
  // T3754: a Sent payment's sheet is addressed to the vendor it paid, and its table is headed by the bill it settled.
  'document.print.paidTo': 'Paid To',
  'document.print.column.bill': 'Bill',
  'document.print.column.billDated': 'Bill Date and Amount',
  'document.print.payTo': 'Pay to the Order of',
  'print.templates.title': 'Print Templates',
  'print.templates.intro': 'One saved layout per document type, positioned in inches from the top-left of the page. The preview is the same HTML the sheet is made from.',
  'print.templates.family': 'Family',
  'print.templates.documentType': 'Document type',
  'print.templates.name': 'Name',
  'print.templates.default': 'Default',
  'print.templates.customer': 'Customer',
  'print.templates.open': 'Open',
  'print.templates.region': 'Region',
  'print.templates.x': 'X (in)',
  'print.templates.y': 'Y (in)',
  'print.templates.width': 'Max width (in)',
  'print.templates.height': 'Max height (in)',
  'print.templates.enabled': 'Enabled',
  'print.templates.labelOverride': 'Label override',
  'print.templates.columnSet': 'Column set',
  // The column sets' names in the template editor's picker (piece 27, decision 0641).
  'print.columnSet.transactional': 'Transactional',
  'print.columnSet.transactionalWithImage': 'Transactional with image',
  'print.columnSet.transactionalWithReference': 'Transactional with MFR reference',
  'print.columnSet.packing': 'Packing slip',
  'print.columnSet.field': 'Field',
  'print.columnSet.receipt': 'Payment receipt',
  'print.columnSet.stub': 'Check stub',
  'print.columnSet.statement': 'Statement',
  'print.columnSet.request': 'Request for quotation',
  'print.columnSet.materials': 'Bill of materials',
  // Piece 27, decision 0656: a template's typeface, sizes, title colour, page number and content rows.
  'print.font.liberationSans': 'Liberation Sans',
  'print.font.serif': 'Serif (Liberation Serif, Times New Roman)',
  'print.font.monospace': 'Monospace (Liberation Mono, Courier New)',
  'print.titleColor.ink': 'Ink',
  'print.titleColor.charcoal': 'Charcoal',
  'print.titleColor.navy': 'Navy',
  'print.titleColor.green': 'Green',
  'print.titleColor.maroon': 'Maroon',
  'print.pageNumber.off': 'Off',
  'print.pageNumber.left': 'Left',
  'print.pageNumber.centre': 'Center',
  'print.pageNumber.right': 'Right',
  'print.row.date': 'Date',
  'print.row.expiryDate': 'Expiry Date',
  'print.row.dueDate': 'Due Date',
  'print.row.paymentTerms': 'Payment Terms',
  'print.row.customerPo': 'Customer PO',
  'print.row.deal': 'Deal',
  'print.row.quote': 'Quote',
  'print.row.salesOrder': 'Sales Order',
  'print.row.purchaseOrder': 'Purchase Order',
  'print.row.invoice': 'Invoice',
  'print.row.bill': 'Bill',
  'print.row.createdBy': 'Created By',
  'print.row.enteredBy': 'Entered By',
  'print.row.requestedBy': 'Requested By',
  'print.row.method': 'Method',
  'print.row.reason': 'Reason',
  'print.row.vendorInvoiceNumber': 'Vendor Invoice Number',
  'print.row.carrier': 'Carrier',
  'print.row.tracking': 'Tracking Number',
  'print.row.periodEnd': 'Period End',
  'print.row.period': 'Period',
  'print.row.openingBalance': 'Opening Balance',
  'print.row.sentAt': 'Sent',
  'print.row.responsesDue': 'Responses Due',
  'print.row.customer': 'Customer',
  'print.row.checkNumber': 'Check Number',
  'print.row.deliveryDate': 'Deliver By',
  'print.row.shipmentPreference': 'Ship Via',
  'print.row.notToExceed': 'Not to Exceed',
  'print.row.subtotal': 'Subtotal',
  'print.row.discount': 'Discount',
  'print.row.tax': 'Tax Lines',
  'print.row.total': 'Total',
  'print.row.paid': 'Paid',
  'print.row.open': 'Open',
  'print.row.settled': 'Settled',
  'print.row.notes': 'Notes',
  'print.row.clauses': 'Terms and Conditions',
  'print.templates.termsSheetsHint': 'The terms and conditions print on sheets of their own after the document\u2019s last sheet, at the body size unless a size is set here.',
  'print.templates.fontGroup': 'Font',
  'print.templates.fontFamily': 'Font',
  'print.templates.bodySize': 'Body size (pt)',
  'print.templates.titleSize': 'Title size (pt)',
  'print.templates.titleColor': 'Title color',
  'print.templates.pageNumber': 'Page Number',
  'print.templates.pageNumberHint': 'Printed on every sheet of a document that runs to more than one. Off prints none.',
  'print.templates.rowShow': 'Show',
  'print.templates.rowName': 'Row',
  'print.templates.rowLabel': 'Label',
  'print.templates.rowWidth': 'Width (%)',
  'print.templates.rowSize': 'Size (pt)',
  'print.templates.rowsFacts': 'Document Details',
  'print.templates.rowsColumns': 'Columns',
  'print.templates.rowsTotals': 'Total Rows',
  'print.templates.rowsSummary': 'Notes and Terms',
  'print.templates.rowsWidthHint': 'Widths are rough shares of the table: the shown columns are scaled to fill it, and a blank one shares what is left.',
  'print.templates.showUnit': 'Unit under the quantity',
  'print.templates.showListPrice': 'List price under the unit price, where they differ',
  'print.templates.totalAlways': 'The Total always prints.',
  'print.templates.rowsNone': 'This document type prints no rows here.',
  'print.templates.labelsGroup': 'Label Overrides',
  'print.templates.duplicate': 'Duplicate To…',
  'print.templates.duplicateTitle': 'Duplicate This Template',
  'print.templates.duplicateHint': 'The page, the margins, the font and sizes, the title color, the page number and every region’s place and on-or-off are copied. The line table’s columns, the document details rows and the label overrides start from the chosen type’s own defaults. Each copy is named after this template and its type, and is not that type’s default.',
  'print.templates.duplicateTarget': 'Copy onto',
  'print.templates.duplicateEvery': 'Every other type of this family',
  'print.templates.duplicateAction': 'Duplicate',
  'print.templates.duplicateNone': 'This family prints no other document type, so there is nothing to copy the layout onto.',
  'print.templates.duplicateSaveFirst': 'Save or cancel the changes first: a copy is made of the saved template.',
  'print.templates.duplicated': 'Copied onto',
  'print.templates.duplicatedTypes': 'document types.',
  'print.templates.labelsHint': 'The caption each region prints in place of its own. Leave a box empty to print the region’s own caption.',
  'print.templates.save': 'Save region',
  'print.templates.saved': 'Region saved.',
  'print.templates.preview': 'Live preview',
  'print.templates.previewTitle': 'The sheet this template produces',
  'print.templates.none': 'No print template has been saved yet.',
  'print.templates.bandRole': 'Band',
  'print.templates.reset': 'Reset Regions',
  'print.templates.resetConfirm': 'Every region on this template goes back to where its family seeds it, and any label override on it is dropped. No other template is touched.',
  'print.templates.resetDone': 'The regions are back at their defaults.',
  'print.templates.saveAll': 'Save Template',
  'print.templates.savedAll': 'The template is saved.',
  'print.templates.cancelDraft': 'Cancel',
  'print.templates.unsaved': 'This layout has changes nobody has saved.',
  'print.templates.regions': 'Regions',
  'print.templates.back': '‹ Print templates',
  'print.templates.notFound': 'That print template is not here.',
  'print.templates.company': 'Company',
  'print.templates.noCompany': 'The type’s own default',
  'print.templates.pageSize': 'Page size',
  'print.templates.newFormat': '+ New Customer Format',
  'print.templates.newFormatTitle': 'A Customer’s Own Format',
  'print.templates.newFormatHint': 'A second template for one account, starting from that document type’s default and used for that account in its place.',
  'print.templates.create': 'Create the Format',
  'print.templates.listHint': 'One saved layout per document type. Open a template to position its regions beside the sheet it produces.',
  'print.templates.printExample': 'Print example',
  'print.templates.templateNoun': 'Template',
  'print.templates.forCompany': 'for',
  'print.templates.page': 'Page',
  'print.templates.pageSizeLetter': 'Letter — 8.5 × 11 in',
  'print.templates.pageSizeA4': 'A4 — 8.27 × 11.69 in',
  'print.templates.marginTop': 'Top margin (in)',
  'print.templates.marginLeft': 'Left margin (in)',
  'print.templates.marginRight': 'Right margin (in)',
  'print.templates.marginBottom': 'Bottom margin (in)',
  'print.templates.pageHint': 'The sheet and the margins this template prints on, saved on this template alone. A region that no longer fits the sheet is refused until it is moved.',
  // Decision 0661 (T190): the fold marks, a page setting the template carries.
  'print.templates.foldMarks': 'Fold marks',
  'print.templates.foldMarksHint': 'A short gray dash at the left and right edge of every sheet where it is folded in thirds for a window envelope.',
  // Decision 0662 (T191): the envelope overlay on the editor's preview, and the names its cutouts carry.
  'print.templates.envelopeOverlay': 'Envelope overlay',
  // Decision 0696 (T203): every region's box on the preview, blue for the open section and grey for the rest.
  'print.templates.regionBoxes': 'Region boxes',
  'print.templates.envelopeProfile': 'Envelope',
  'print.templates.envelopeHint': 'Draws where the chosen envelope’s windows fall on the sheet, folded the way the envelope takes it, with its creases dashed. It is on this preview only and never printed.',
  'print.templates.envelopeNone': 'No envelope profile is set up for this template’s paper size, so there is nothing to lay over the sheet.',
  'print.envelope.returnWindow': 'Return window',
  'print.envelope.addressWindow': 'Address window',
  // Decision 0700 (T209): the check stock overlay on a check template's preview, and the names its marks carry.
  'print.templates.checkStockOverlay': 'Show the check stock',
  'print.templates.checkStockHint': 'The check is drawn over a picture of the pre-printed stock, with the six values where they print. The picture is on the screen only: the printer prints the six values onto the stock you load.',
  'print.checkStock.date': 'Date',
  'print.checkStock.amount': '$',
  'print.checkStock.signature': 'Authorized signature',
  // Decision 0698 (T207): the sheet and fold a profile is measured on, and the windowless envelope.
  'print.envelope.foldThirds': 'Folded in thirds',
  'print.envelope.foldThirdsThenHalf': 'Folded in thirds, then in half',
  'print.envelope.foldQuarters': 'Folded in quarters',
  'print.envelope.foldFlat': 'Flat, not folded',
  'print.envelope.noWindow': 'no window — nothing on this sheet shows through',
  // Decision 0663 (T193): a template made its type's global default from the gallery.
  'print.templates.makeDefault': 'Make Default',
  'print.templates.rename': 'Rename',
  'print.templates.delete': 'Delete',
  'print.templates.deleteTitle': 'Delete Template',
  'print.templates.deleteTemplate': 'Template',
  'print.templates.deleteType': 'Document Type',
  'print.templates.deleteWarning': 'Its layout, its rows and its regions are removed, and it cannot be brought back. A customer printing on it prints on the default from now on.',
  'print.templates.deleted': 'is deleted.',
  'print.templates.renameSave': 'Save Name',
  'print.templates.renameCancel': 'Cancel',
  'print.templates.nameGroup': 'Name',
  'print.templates.nameHint': 'What the gallery and this page call the template. It is not printed on any sheet, and another template of the same document type cannot carry it.',
  'print.templates.makeDefaultConfirm': 'Make this the default template for every',
  'print.templates.makeDefaultConfirmTail': 'printed for a customer without a format of their own?',
  'print.templates.madeDefault': 'is now the default for every',
  'print.templates.pageFixed': 'A check prints on pre-printed laser voucher stock, perforated at 3.5 and 7 inches, so this family is US Letter and the size is not a choice.',
  // Decision 0659 (T188): the one zoom control a framed sheet is read at, in the template editor's preview and every document's PDF view.
  'sheet.zoom.in': 'Zoom In',
  'sheet.zoom.out': 'Zoom Out',
  'sheet.zoom.fit': 'Fit Page',
  'sheet.zoom.alreadyFit': 'The whole page is already in view.',
  'sheet.zoom.largest': 'The sheet is already at its largest.',
  'sheet.zoom.smallest': 'The sheet is already at its smallest.',
  'print.templates.printExampleHint': 'The sheet this document type prints on today, so what is being copied is seen before it is copied.',
  // Decision 0615: the editor's sections, the gallery, and the controls in the sticky bar; decision 0697 adds Addresses.
  'print.templates.sectionGeneral': 'General',
  'print.templates.sectionPositions': 'Positions',
  'print.templates.positionSummary': '{x} across, {y} down, {width} by {height} inches',
  'print.templates.positionOff': 'turned off',
  'print.templates.editOnPositions': 'Edit on Positions',
  'print.templates.positionsEnvelopeRule': 'Regions 2 and 5 are mailing addresses, so Save refuses either one outside the envelope window: from {left} to {right} inches across, and from {top} inches down to the fold at {fold}.',
  'print.templates.sectionAddresses': 'Addresses',
  'print.templates.envelopeBandHint': 'The return address and Bill To show through a window envelope only inside this band, so the editor refuses either one outside it: from {left} to {right} inches across, and from {top} inches down to the fold at {fold}. Deliver To is not held to it.',
  'print.templates.sectionHeaderFooter': 'Header & Footer',
  'print.templates.sectionTransaction': 'Transaction Details',
  'print.templates.sectionTable': 'Table',
  'print.templates.sectionTotal': 'Total',
  'print.templates.sectionOther': 'Other Details',
  'print.templates.sections': 'Sections',
  'print.templates.position': 'Position',
  'print.templates.properties': 'Template Properties',
  'print.templates.refreshPreview': 'Refresh Preview',
  'print.templates.sectionEmpty': 'This template draws nothing in this section.',
  'print.templates.galleryHeading': 'Templates',
  'print.templates.galleryTypes': 'Document types',
  'print.templates.galleryHint': 'One card per saved layout of the chosen document type. Open a card to position its regions beside the sheet it produces.',
  'print.templates.galleryNoneForType': 'No template is saved for this document type yet.',
  'print.templates.newCard': 'New template',
  'print.templates.newCardHint': 'A second layout for one account, starting from this type’s default.',
  'print.templates.selectTemplate': 'Select',
  'print.templates.thumbnail': 'The first page of this template’s sample sheet',
  'print.defaults.title': 'Print Defaults',
  'print.defaults.intro': 'The margins a new print template starts with, in inches, from 0 to 2. An existing template keeps its own margins, changed on its own page.',
  'print.defaults.marginTop': 'Top margin',
  'print.defaults.marginLeft': 'Left margin',
  'print.defaults.marginRight': 'Right margin',
  'print.defaults.marginBottom': 'Bottom margin',
  'document.notFound': 'This document could not be read; it may have been voided or removed.',
  'document.print.account': 'Account',
  'document.print.date': 'Date',
  'document.print.terms': 'Terms',
  'document.print.reference': 'Reference',
  'document.print.saveFirst': 'The printed sheet appears once the document is saved.',
  // Piece 24: the Bill's own page, its print projection and the three-way match against its purchase order.
  'bill.escalation': 'Approval',
  'bill.fact.vendorInvoiceNumber': 'Vendor invoice number',
  'bill.fact.billDate': 'Bill date',
  'bill.fact.dueDate': 'Due',
  'bill.fact.purchaseOrder': 'Purchase order',
  'bill.fact.enteredBy': 'Entered by',
  // Piece 27, decision 0223: the customer statement's own title, facts and Region 8 column headings.
  // Decision 0613: the request for quotation's own Region 4 rows and its closing clause.
  'rfq.print.issued': 'Issued',
  'rfq.print.responsesDue': 'Responses due',
  'rfq.print.requestedBy': 'Requested by',
  'rfq.print.terms': 'Please return your unit price, lead time and any minimum quantity per line, quoting the request number above.',
  // Decision 0613: the statement on the one print renderer.
  'statement.fact.period': 'Period',
  'statement.fact.openingBalance': 'Opening balance',
  'statement.fact.closingBalance': 'Closing balance',
  'statement.fact.sentAt': 'Sent',
  'statement.kind.payment': 'Payment',
  'statement.column.date': 'Date',
  'statement.column.document': 'Document',
  'statement.column.charge': 'Charge',
  'statement.column.credit': 'Credit',
  'statement.column.balance': 'Balance',
  'statement.print.notice': 'This statement is a summary of the invoices, credit notes and payments on your account. Your invoices remain the record of what is owed.',
  'statement.live.empty': 'Nothing was invoiced, credited or paid in this period.',
  'statement.action.send': 'Send Statement',
  'statement.action.batch': 'Send Statements',
  'statement.action.download': 'View PDF',
  'statement.sent.heading': 'Statements sent',
  'statement.sent.empty': 'No statement has been sent to this customer yet.',
  'statement.hint': 'The figures are computed from this customer’s invoices, credit notes and payments each time you open this. Nothing is stored until you send it.',
  'statement.field.periodStart': 'From',
  'statement.field.periodEnd': 'To',
  'statement.noCustomer': 'Open a customer and choose Statements to see one.',
  'statement.notice.sent': 'The statement was sent and a copy of exactly what it said was kept.',
  'statement.notice.batchSent': 'Statements went to every customer carrying a balance for that period.',
  'record.statements': 'Statements',
  'statement.periodJoin': '—',
  // Piece 27, Customer signature: the manual path's modal and the panel that draws what was signed.
  'signature.manual.title': 'Record Signed Copy',
  'signature.manual.action': 'Record Signed Copy',
  'signature.manual.act': 'Record It',
  'signature.manual.file': 'The returned file',
  'signature.manual.noFiles': 'Upload the returned signed file to this document first, then record it here.',
  'signature.manual.signerName': 'Who signed',
  'signature.manual.signerEmail': 'Their email address',
  'signature.manual.signedAt': 'When they signed',
  'signature.manual.hint': 'This records that the customer signed and freezes the revision they signed. The signed file stays on this document as the evidence.',
  'signature.manual.convert': 'Also raise the Sales Order',
  'signature.recorded': 'The signed copy was recorded and the document is accepted.',
  'signature.recorded.converted': 'The signed copy was recorded, the document is accepted and the Sales Order was raised.',
  'signature.none': 'Nobody has signed this yet.',
  'signature.revision': 'revision',
  'signature.title': 'Signature',
  // Piece 27, Optional modules: Settings › Modules and Settings › Accounting › Budgets.
  'settings.modules.title': 'Modules',
  'settings.modules.hint': 'Three optional parts of the system, each off until you turn it on. Turning one off hides its screens and its reports and deletes nothing, so turning it back on finds everything where it was.',
  'settings.modules.note': 'No module ever blocks work. Turning Budgets on does not stop an over-budget purchase order being raised and sent.',
  'settings.modules.eSignatureCaveat': 'E-Signature: no signing vendor has been chosen, so turning this on does not get you electronic signing yet. Recording a returned signed copy by hand works either way.',
  'settings.modules.budgetsCaveat': 'Budgets: reveals Settings › Accounting › Budgets and the budget-versus-actual report.',
  'settings.modules.depreciationCaveat': 'Depreciation: the screens arrive with the asset register. Turning it on today changes nothing you can see.',
  'settings.budgets.title': 'Budgets',
  'settings.budgets.hint': 'One budgeted figure per account per period of a fiscal year, against the chart of accounts you already have.',
  'settings.budgets.neverGates': 'A budget never stops anybody spending. Going over one is a conversation, not a refusal.',
  'settings.budgets.moduleOff': 'The Budgets module is off, so these figures are hidden from the reports and the menus. They are still here and still editable, and nothing has been deleted.',
  'settings.budgets.fiscalYear': 'Fiscal year',
  'settings.budgets.account': 'Account',
  'settings.budgets.pickAccount': 'Choose an account',
  'settings.budgets.period': 'Period',
  'settings.budgets.amount': 'Amount',
  'settings.budgets.save': 'Save the Figure',
  'settings.budgets.saved': 'The figure was saved.',
  'settings.budgets.none': 'Nothing has been budgeted for this year yet.',
  // Piece 27, Vendor credit application: the Apply credit modal on a Bill's own page.
  'bill.credit.applyTitle': 'Apply Credit',
  'bill.credit.applyAct': 'Apply It',
  'bill.credit.openBalance': 'This bill still owes',
  'bill.credit.which': 'Which credit',
  'bill.credit.pick': 'Choose a credit',
  'bill.credit.column.credit': 'Credit',
  'bill.credit.column.dated': 'Dated',
  'bill.credit.column.remaining': 'Remaining',
  'bill.credit.viewTitle': 'Available Credit',
  'bill.credit.vendorHint': 'A credit is applied from one of this vendor’s bills once that bill is submitted and posted.',
  'bill.credit.amount': 'How much to apply',
  'bill.credit.atMost': 'At most',
  'bill.credit.partialHint': 'Applying part of a credit is ordinary. Whatever is left stays available for the next bill from this vendor.',
  'bill.credit.none': 'This vendor has no credit left to apply.',
  'bill.credit.applied': 'The credit was applied:',
  'bill.credit.applyAction': 'Apply credit',
  'bill.print.paid': 'Paid',
  'bill.print.open': 'Open',
  'bill.new.subtitle': 'The payable side of a purchase order, and of nothing at all where the electricity has no order behind it',
  'bill.edit.subtitle': 'Adjustable for what the vendor actually invoiced',
  'bill.field.vendor': 'Vendor',
  'bill.field.vendorInvoiceNumber': "Vendor's invoice number",
  'bill.field.billDate': 'Bill date',
  'bill.field.paymentTerms': 'Payment terms',
  'bill.field.purchaseOrder': 'Purchase order',
  'bill.action.submit': 'Save and Submit',
  'bill.action.approve': 'Approve',
  'bill.action.reject': 'Reject',
  'bill.action.resubmit': 'Resubmit',
  'bill.action.void': 'Void',
  'bill.action.createAsset': 'Create Asset',
  'bill.asset.from': 'From',
  'bill.asset.line': 'line',
  'bill.asset.linked': 'Asset',
  'bill.asset.created': 'The asset is in the register, and this line now names it.',
  'bill.action.buildFromPo': 'Build from purchase order',
  'bill.pill.flagged': 'Variance Flagged',
  'bill.variance.heading': 'Outside the tolerance',
  'bill.variance.tolerance': 'Tolerance',
  'bill.variance.notYetReceived': 'Quantity not yet comparable — nothing received against the order line',
  'bill.variance.noOrderLine': 'No order line behind this line',
  'bill.credit.heading': 'This vendor has credit available',
  'bill.credit.apply': 'Apply it against this bill before it is paid.',
  // 🔒 T740: a bill not ready for credit says when it applies and offers the list to read, never an Apply the route refuses.
  'bill.credit.applyOnceSubmitted': 'It applies once this bill is submitted and posted.',
  'bill.credit.nextBill': 'It stays available for this vendor’s next bill.',
  'bill.credit.viewAction': 'View Credits',
  'payBills.achRecordedHere': 'No provider is connected to the ACH rail yet, so this payment is initiated at the bank and recorded here. Nothing leaves the building when you save it.',
  'payBills.achNoExport': 'ACH is initiated through the payment seam and never through a bank file exported by hand; no file is produced here.',
  'payBills.title': 'Pay Bills',
  'payBills.subtitle': 'One payment against as many bills as it covers',
  'payBills.vendor': 'Vendor',
  'payBills.method': 'Method',
  'payBills.account': 'Paying account',
  'payBills.date': 'Payment date',
  'payBills.reference': 'Reference',
  'payBills.referenceHint': 'A wire is recorded as Other, with the rail named here.',
  'payBills.open': 'Open',
  'payBills.paying': 'Paying',
  'payBills.total': 'Total to pay',
  'payBills.pay': 'Pay',
  'payBills.none': 'This vendor has no open bills.',
  'payBills.paid': 'Paid; the bills are settled and the payment is recorded.',
  'payBills.withholding': 'Backup withholding',
  'payBills.withholdingHint': 'Withheld at 24% from the first dollar and posted to Backup Withholding Payable; the vendor is paid the remainder.',
  'payBills.openVendor': 'Open the Vendor',
  'payBills.reimbursements': 'Expense Reports to Reimburse',
  'payBills.reimbursementsHint': 'Approved expense reports still owed to the person who filed them. A check pays all or part of what is owed, from the paying account and on the payment date above.',
  'payBills.reimbursementsNone': 'No approved expense report is owed.',
  'payBills.reimbursementOwed': 'Owed',
  'payBills.checkAmount': 'Check amount',
  'payBills.payByCheck': 'Pay by Check',
  'payBills.chooseAccount': 'Choose the paying account above first.',
  'payBills.amountNeeded': 'Type a check amount above zero to pay this report.',
  'payBills.reimbursed': 'Check written; it waits under Checks to Print.',
  'banking.difference': 'Difference',
  'banking.amountInCardFeed': 'Amount in card feed',
  'banking.pull': 'Pull the Feed',
  'passkey.offer.body': 'You can sign in on this device with your fingerprint, face or screen lock instead of typing a code every time. Your authenticator app still works and is still required elsewhere.',
  'passkey.offer.add': 'Set Up a Passkey',
  'passkey.offer.dot': 'You have no passkey on this device yet',
  'banking.connect.signIn': 'Sign in to Your Bank',
  'banking.connect.signInNotStarted': 'The bank sign-in loaded but did not start. Try again.',
  'banking.connect.signInNotFinished': 'The bank sign-in did not finish. Try again.',
  'banking.connect.signInFailed': 'The bank sign-in could not be opened. Check the connection and try again.',
  'banking.connect.provider.scripted': 'Scripted Test Feed',
  'banking.connect.problem.noProvider': 'A feed connection names the provider it runs through. Choose the provider.',
  'banking.connect.problem.noReference': 'A feed connection carries the reference the provider issued for it. Paste the reference the provider gave when the connection was made.',
  'banking.connect.problem.unboundProvider': 'No bank feed provider \u2018{provider}\u2019 is bound in this environment. The bound ones are {bound}.',
  'banking.connect.problem.noneBound': 'none',
  'banking.connect.signInHint': 'Your bank\u2019s own sign-in opens in a window from the provider. Your password is typed there and never reaches this application.',
  'banking.connect.whichAccount': 'Which account is this feed?',
  'banking.connect.foundNone': 'The sign-in finished without attaching an account. Try again and pick one.',
  'banking.connect.sharedConnection': 'One sign-in covers every account behind it, so this feed will deliver all of them. Choosing above names it; it does not narrow what arrives.',
  'banking.importStatement': 'Import a statement',
  'banking.importAnyway': 'Import It Anyway',
  'banking.importAnyway.confirm': 'The statement names a different account number from this one. Importing it writes its transactions into this account, and taking them back out afterwards means excluding each one by hand.',
  'banking.pulled': 'The feed was pulled',
  'banking.notConnectedHint': 'No feed is connected to this account, so there is nothing to pull. Lines can still be recorded by anything that writes to the account, such as a card payment.',
  'banking.tabs': 'Transactions',
  'banking.tab.uncategorized': 'Uncategorized',
  'banking.tab.all': 'All Transactions',
  'banking.tab.checks': 'Checks to Print',
  'banking.noLines': 'No transactions have been delivered against this account yet.',
  'banking.noRegister': 'Nothing has posted to this account in the books and the feed has delivered nothing.',
  'banking.source.books': 'Books',
  'banking.source.feed': 'Feed',
  'banking.source.both': 'Books and Feed',
  'banking.reversal.reversed': 'Reversed',
  'banking.reversal.reverses': 'Reversal',
  'banking.count.books': 'postings in books',
  'banking.count.feed': 'lines in feed',
  // Decision 5940: the three tabs' one list table, its filter pills and the rule's mark.
  'banking.col.date': 'Date',
  'banking.col.details': 'Statement Details',
  'banking.col.deposits': 'Deposits',
  'banking.col.withdrawals': 'Withdrawals',
  'banking.col.category': 'Category',
  'banking.col.act': 'Action',
  'banking.lines.label': 'Transaction lines',
  // Decision 9760: the line under each of the account's three lists, Show more, and the Uncategorized tab's order.
  'banking.lines.count': '{shown} of {total} lines shown, {left} left',
  'banking.lines.showMore': 'Show More',
  'banking.lines.moreFailed': 'The next lines did not load. Try Show More again.',
  'banking.order': 'Order',
  'banking.order.newest': 'Newest First',
  'banking.order.oldest': 'Oldest First',
  'banking.filter': 'Show',
  'banking.filter.all': 'All',
  'banking.filter.recognized': 'Recognized',
  'banking.filter.excluded': 'Excluded',
  'banking.recognized': 'Recognized',
  'banking.noFilterLines': 'No line on this tab carries that mark.',
  'banking.open': 'Match',
  'banking.matchPanel': 'Match transactions',
  'banking.categorizePanel': 'Categorize manually',
  'banking.noCandidates': 'Nothing keyed in matches this line, so categorize it instead.',
  'banking.categoryAccount': 'Account',
  'banking.categorize': 'Categorize',
  'banking.categorized': 'The line was categorized',
  // Decision 8821: Split, several lines for one feed line, posted as one entry.
  'banking.split': 'Split',
  'banking.split.cancel': 'One Account',
  'banking.split.addLine': 'Add a Line',
  'banking.split.remove': 'Remove',
  'banking.split.amount': 'Amount',
  'banking.split.company': 'Company',
  'banking.split.companyEntity': 'Company',
  'banking.split.remaining': 'Still to assign',
  'banking.split.over': 'Over the line by',
  'banking.split.even': 'The lines sum to the feed line.',
  'banking.split.save': 'Save',
  'banking.split.waits': 'Save waits until every line names an account and an amount, and the lines sum to the feed line.',
  'banking.split.direction': 'Type each share as a positive amount; a withdrawal’s shares are taken out of the account.',
  'banking.confirmMatch': 'Confirm',
  'banking.matched': 'The line was matched',
  // Decision 1200: a deposit matched to an unpaid invoice with one press, and Unmatch.
  'banking.proposal': 'Proposed invoice',
  'banking.matchInvoice': 'Match',
  'banking.openOther': 'Other Matches',
  'banking.invoiceMatched': 'The payment was recorded against the invoice, and the line is matched.',
  'banking.unmatch': 'Unmatch',
  'banking.unmatch.confirm': 'Unmatching voids the payment the Match button recorded, reopens the invoice it paid and returns the deposit to Unmatched.',
  'banking.unmatched': 'The payment was voided, and the deposit is unmatched again.',
  // Decision 1541: a line the bank feed no longer reports is marked, never deleted, and a match on it names its payment.
  'banking.withdrawn': 'Withdrawn by the bank',
  'banking.withdrawn.matchedTo': 'still matched to',
  'banking.withdrawn.payment': 'a payment',
  'banking.exclude': 'Exclude as a Duplicate',
  'banking.excluded': 'The line was excluded',
  'banking.ruleMatched': 'A rule matches this line and posts it to',
  'banking.applyRule': 'Apply the Rule',
  // Decision 10901 (T1040): the Bank Rules tab on the Banking page, and Make a Rule from This Line on a feed line's panel.
  'banking.pageTabs': 'Banking',
  'banking.tab.accounts': 'Accounts',
  'banking.tab.rules': 'Bank Rules',
  'banking.rules.hint': 'A rule you write matches a feed line before the matcher judges it. The lowest priority number is tried first, and the first rule that matches wins.',
  'banking.rules.add': 'Add a Rule',
  'banking.rules.addTitle': 'Add a Bank Rule',
  'banking.rules.editTitle': 'Edit a Bank Rule',
  'banking.rules.makeTitle': 'Make a Bank Rule',
  'banking.rules.saveChanges': 'Save Changes',
  'banking.makeRule': 'Make a Rule from This Line',
  'banking.rules.edit': 'Edit',
  'banking.rules.deactivate': 'Deactivate',
  'banking.rules.activate': 'Activate',
  'banking.rules.moveUp': 'Move Up',
  'banking.rules.moveDown': 'Move Down',
  'banking.rules.empty': 'No bank rules yet. Add one to code the fuel card or the rent debit without judging it each month.',
  'banking.rules.inactive': 'Inactive',
  'banking.rules.priority': 'Priority',
  'banking.rules.postsTo': 'posts to',
  'banking.rules.payee': 'Payee',
  'banking.rules.everyAccount': 'Every bank account',
  'banking.rules.field': 'Match on',
  'banking.rules.operator': 'Compare',
  'banking.rules.value': 'Value',
  'banking.rules.valueHint': 'The words to look for.',
  'banking.rules.amount': 'Amount',
  'banking.rules.amountLow': 'Lowest Amount',
  'banking.rules.amountHigh': 'Highest Amount',
  'banking.rules.targetAccount': 'Post to Account',
  'banking.rules.bankAccount': 'Applies to',
  'banking.rules.chooseAccount': 'Choose an account',
  'banking.rules.invalid': 'Name what the rule compares and the account a matching line posts to.',
  'banking.rules.saved': 'The rule was saved',
  'banking.rules.moved': 'The rule order was saved',
  'banking.rules.field.description': 'Description',
  'banking.rules.field.amount': 'Amount',
  'banking.rules.field.counterparty': 'Counterparty',
  'banking.rules.operator.equals': 'Equals',
  'banking.rules.operator.contains': 'Contains',
  'banking.rules.operator.between': 'Between',
  'banking.reconcile': 'Reconcile',
  'banking.moneyOut': 'Money out',
  'banking.moneyOutHint': 'One bill is paid from the bill itself. These two cover the rest.',
  // Decision 1310: the section is Expenses, and the reports live inside it.
  'expenseReports.title': 'Expenses',
  'expenseReports.subtitle': 'Your mileage and expenses, and the reports they are filed on',
  'expenseReports.periodStart': 'Period start',
  'expenseReports.periodEnd': 'Period end',
  'expenseReports.new': '+ New Report',
  'expenseReports.newHint': 'The period the report covers; the expenses go on it once it is open.',
  'expenseReports.start': 'Start report',
  'expenseReports.starting': 'Starting…',
  'expenseReports.none': 'No expense reports have been raised yet.',
  'expenseReports.aging': 'Unmatched company-card items',
  'expenseReports.agingHint': 'Both directions: an expense with no charge behind it is somebody out of pocket, and a charge with no expense is a deduction that fails at audit.',
  'expenseReports.agingNone': 'Nothing is waiting on a match in either direction.',
  'expenseReports.days': 'days old',
  'expenseReports.noReceipt': 'No expense line filed for this charge',
  'expenseReports.chase': 'chase this one',
  // Decision 2961: whose card an unmatched item is.
  'expenseReports.cardHolder': 'card held by',
  'expenseReport.subtitle': 'One employee’s mileage and expenses for a period',
  'expenseReport.allReports': 'Back to Expenses',
  'expenseReport.to': 'to',
  'expenseReport.total': 'Total',
  'expenseReport.approvedBy': 'Approved by',
  'expenseReport.paidByCheck': 'Paid by check',
  'expenseReport.stillOwed': 'Still owed',
  'expenseReport.submit': 'Submit',
  'expenseReport.submitted': 'The report is submitted',
  'expenseReport.approve': 'Approve',
  'expenseReport.approved': 'The report is approved',
  'expenseReport.filerCannotApprove': 'You raised this report, so somebody else approves it and decides its flags.',
  'expenseReport.incurredOn': 'Date incurred',
  'expenseReport.category': 'Category',
  'expenseReport.fundingSource': 'Whose money',
  'expenseReport.companyCard': 'Company card',
  'expenseReport.reimbursable': 'Reimbursable',
  'expenseReport.description': 'What it was for',
  'expenseReport.amount': 'Amount',
  'expenseReport.miles': 'Miles',
  'expenseReport.milesHint': 'Fill in miles for a mileage line and the amount is computed from the rate in force on the day the travel happened. Fill in an amount for anything else.',
  'expenseReport.receiptHint': 'A line can be added without its receipt, but a category that asks for one keeps the report from being submitted until the line has it. Open the line to attach the receipt.',
  'expenseReport.receiptOwed': 'Receipt owed',
  'expenseReport.receiptsOwedList': 'These lines still owe a receipt, and the report cannot be submitted until each has one:',
  'expenseReport.addLine': 'Add the Line',
  'expenseReport.lineAdded': 'The line was added',
  'expenseReport.noLines': 'This report has no lines yet.',
  'expenseReport.flag.duplicateReceipt': 'Possible Duplicate',
  'expenseReport.flag.alcoholIncluded': 'Alcohol Included',
  'expenseReport.flag.exceedsPerPersonThreshold': 'Over the Per-Person Threshold',
  'expenseReport.flag.noLinkedBusinessContext': 'No Business Context',
  'expenseReport.flag.noJournalFound': 'No Journal Found',
  'expenseReport.carriedBy': 'Posted in the books by',
  'expenseReport.submitInZohoExpense': 'This report came in from Zoho Expense; submit it there, and the next pull brings its status.',
  'expenseReport.approveInZohoExpense': 'This report came in from Zoho Expense; approve it there, and the next pull brings its status.',
  'expenseReport.bankLine': 'bank line',
  'expenseReport.noBankLine': 'no bank line posted through it yet',
  'expenseReport.importedFromZohoExpense': 'This report came in from Zoho Expense, which approves it. It posts nothing here: each line links to the journal entry the Books pull wrote, or is flagged where none was found.',
  'expenseReport.pendingMatchHint': 'A company-card line is approved and attached to this report, and reaches the books only when its charge is matched on the card’s own feed. One spend arrives twice, and posting both would book it twice.',
  'expenseReport.flagApprove': 'Approve the Line',
  'expenseReport.flagReject': 'Reject the Line',
  'expenseReport.flagResolved': 'The flag was resolved',
  // Piece 24, decision 1185: the Edit Expense window, the receipt on the left and the fields on the right.
  'expenseEntry.title': 'Edit Expense',
  'expenseEntry.filingOnto': 'Filing onto',
  'expenseEntry.edit': 'Edit',
  'expenseEntry.report': 'Report',
  'expenseEntry.expenseDate': 'Expense Date',
  'expenseEntry.merchant': 'Merchant',
  'expenseEntry.merchantEntity': 'Company',
  'expenseEntry.category': 'Category',
  'expenseEntry.amount': 'Amount',
  'expenseEntry.paidWith': 'Paid With',
  // Decision 2961: the company card an expense was paid with, offered once Company card is chosen.
  'expenseEntry.card': 'Card',
  'expenseEntry.noCards': 'No company card is set up; an accountant adds one on the Banking page.',
  'expenseEntry.description': 'Description',
  'expenseEntry.customer': 'Customer',
  'expenseEntry.billable': 'Billable',
  'expenseEntry.project': 'Project',
  'expenseEntry.projectEntity': 'Deal',
  'expenseEntry.projectDeal': 'Deal',
  'expenseEntry.projectWorkOrder': 'Work Order',
  'expenseEntry.attendees': 'Attendees',
  'expenseEntry.attendeeEntity': 'Person',
  'expenseEntry.relationship': 'Relationship',
  'expenseEntry.place': 'Place',
  'expenseEntry.businessPurpose': 'Business Purpose',
  // Row T3407: a 1200 category draws the three a stock line requires.
  'expenseEntry.item': 'Item',
  'expenseEntry.itemEntity': 'Item',
  'expenseEntry.quantity': 'Quantity',
  'expenseEntry.site': 'Site',
  'expenseEntry.siteEntity': 'Site',
  'expenseEntry.stockItemNeeded': 'Choose the item this expense bought.',
  'expenseEntry.stockQuantityNeeded': 'Enter how many it bought.',
  'expenseEntry.stockSiteNeeded': 'Choose the site the stock landed at.',
  'expenseEntry.stockSiteNotOurs': 'The site is not one of our own open sites; choose the site the stock landed at.',
  'expenseEntry.none': 'Choose one',
  'expenseEntry.save': 'Save',
  'expenseEntry.cancel': 'Cancel',
  'expenseEntry.saved': 'The expense was saved.',
  'expenseEntry.noReceipt': 'No receipt is filed with this expense.',
  'expenseEntry.cannotDraw': 'The receipt cannot be drawn here; download it to read it.',
  'expenseEntry.reading': 'The receipt is loading.',
  'expenseEntry.perDiemByHand': 'The Per diem category is claimed on the phone’s Per Diem page, because its amount is its days at the rate; choose another category here.',
  'expenseEntry.rotate': 'Rotate',
  // Decision 1187: the window walks the Documents Inbox's unfiled receipts in arrival order.
  'expenseEntry.of': 'of',
  'expenseEntry.receiptOf': 'Receipt',
  'expenseEntry.skip': 'Skip',
  'expenseEntry.saveAndNext': 'Save and Next',
  'expenseEntry.saveAndClose': 'Save and Close',
  'documentsInbox.title': 'Documents Inbox',
  'documentsInbox.subtitle': 'Receipts you uploaded and drives on your calendar that no expense names yet, oldest first',
  'documentsInbox.upload': 'Upload',
  'documentsInbox.fileReceipts': 'File Receipts',
  'documentsInbox.fileAsExpense': 'File as Expense',
  'documentsInbox.none': 'Every receipt you uploaded is filed on an expense report.',
  'documentsInbox.walkDone': 'The walk reached the last receipt.',
  'documentsInbox.uploaded': 'Uploaded',
  'documentsInbox.uploadSubtitle': 'Each file lands in the Inbox until an expense names it.',
  'documentsInbox.fromReport': 'File receipts from the Documents Inbox',
  // Decision 2963: the drives the Inbox offers from the person's calendar.
  'documentsInbox.drives': 'Drives From Your Calendar',
  'documentsInbox.drove': 'Drove on',
  'documentsInbox.to': 'To',
  'documentsInbox.noPlace': 'No place on the event',
  // Decision 1188: the date, the merchant and the amount read off the receipt, marked until touched.
  'expenseEntry.readFromReceipt': 'Read from Receipt',
  'expenseEntry.noMerchantCompany': 'No Company carries the name read off the receipt.',
  'expenseEntry.createMerchant': 'Create the Company',
  // Decision 1186: Itemise opens the amount into lines that sum to the receipt's total.
  'expenseEntry.itemise': 'Itemize',
  'expenseEntry.addItem': 'Add a Line',
  'expenseEntry.removeItem': 'Remove',
  'expenseEntry.unassigned': 'Still to assign',
  'expenseEntry.overBy': 'Over the total by',
  'expenseEntry.itemisedEven': 'The lines sum to the total.',
  'expenseEntry.readOnly':'This report is already submitted, so its expenses are read here and not changed.',
  // Piece 24, decision 1311: + New Expense, the window opened on no line and no receipt.
  'expenseReports.newExpense': '+ New Expense',
  'expenseEntry.newTitle': 'New Expense',
  'expenseEntry.saveAndNew': 'Save and New',
  'expenseEntry.newReport': 'New Report',
  'expenseEntry.unreportedHint': 'The expense stands unreported until it goes on a report; pick a report here when you mean one.',
  'expenseEntry.newReportHint': 'None of your Draft reports covers today, so saving makes a new report for',
  'expenseEntry.chooseReport': 'Choose the report this expense is filed on.',
  'expenseEntry.madeFor': 'was made for',
  'expenseEntry.madeAndSaved': 'and the expense was saved on it.',
  'expenseEntry.savedOnto': 'The expense was saved on',
  'expenseEntry.theReport': 'the report',
  'expenseEntry.dropPrompt': 'Drop the receipt here, paste it, or choose the file.',
  'expenseEntry.chooseFile': 'Choose a File',
  'expenseEntry.fileType': 'File Type',
  'expenseEntry.uploading': 'The receipt is uploading.',
  'expenseEntry.retryUpload': 'Try Again',
  // Piece 24, decision 1312: below tablet width the New Expense window opens on the camera.
  'expenseEntry.cameraPrompt': 'Take a picture of the receipt, choose the file, or skip to the fields.',
  'expenseEntry.takePicture': 'Take a Picture',
  'expenseEntry.skipReceipt': 'Skip the Receipt',
  'expenseEntry.receiptWaiting': 'The receipt has not uploaded yet; choose its File Type, or press Try Again if the upload failed.',
  'expenseEntry.state.typeNeeded': 'Choose its File Type below: the receipt waits on it and uploads the moment you do.',
  'expenseEntry.state.uploadFailed': 'The receipt did not upload; press Try Again.',
  'expenseEntry.state.uploaded': 'The receipt is uploaded.',
  'expenseEntry.state.reading': 'The receipt is uploaded and is being read for its date, merchant and amount.',
  'expenseEntry.state.read': 'Read: the fields marked Read from Receipt came from it; check them against the picture.',
  'expenseEntry.state.noReader': 'The receipt is uploaded, but no receipt reader is configured, so nothing was filled in; type the fields from the picture.',
  'expenseEntry.state.readFailed': 'The receipt is uploaded, but it could not be read, so nothing was filled in; type the fields from the picture.',
  'expenseEntry.dropKeeps': 'A receipt uploaded here waits in the Documents Inbox if the expense is not saved.',
  // Piece 24, decision 1313: an expense can exist before its report.
  'expenseEntry.unreported': 'Unreported',
  'expenseEntry.savedUnreported': 'The expense was saved as unreported; it goes on a report when you add it to one.',
  'expenseReports.unreportedChase': 'on no report yet',
  // Piece 24, decision 1315: + Mileage, a drive at the rate of its own day.
  'expenses.newMileage': '+ Mileage',
  'mileage.title': 'Mileage',
  'mileage.subtitle': 'A drive in your own car, paid back at the rate for its day.',
  'mileage.date': 'Date',
  'mileage.event': 'From the Calendar',
  'mileage.noEvent': 'No event',
  'mileage.noEvents': 'No Physical event of yours falls on this day.',
  'mileage.from': 'From',
  'mileage.to': 'To',
  'mileage.miles': 'Miles',
  'mileage.purpose': 'Purpose',
  'mileage.rateOn': 'The rate on',
  'mileage.is': 'is',
  'mileage.centsAMile': ' a mile',
  'mileage.makes': 'which makes',
  'mileage.savedOnto': 'The mileage was saved onto',
  // Decision 2963: the miles read from the map, marked as such and still editable.
  'mileage.fromMap': 'From the map: the drive from',
  'mileage.fromMapPlain': 'From the map; change the miles if the drive went another way.',
  'mileage.noMap': 'The map could not measure this drive; type the miles.',
  'mileage.savedUnreported': 'The mileage was saved as unreported; it goes on a report when you add it to one.',
  // Piece 24, decision 1314: the Expenses tab, Add to Report and Delete.
  'expenses.tabs': 'Expenses or reports',
  'expenses.tab.expenses': 'Expenses',
  'expenses.tab.reports': 'Reports',
  'expenses.unreportedHeading': 'Unreported',
  'expenses.unreportedHint': 'These expenses are on no report yet, so nobody approves or pays them. Pick them and press Add to Report.',
  'expenses.noneUnreported': 'Every expense of yours is on a report.',
  'expenses.reportedHeading': 'On a Report',
  'expenses.shownOf': '{shown} of {total} shown',
  'expenses.showMore': 'Show More',
  'expenses.moreFailed': 'The next expenses did not load. Try Show More again.',
  'expenseReports.agingLeft': '{count} older items are not listed here. The oldest come first, so clearing these brings the next into view.',
  'expenses.noneReported': 'None of your expenses is on a report yet.',
  'expenses.pick': 'Pick',
  'expenses.drop.title': 'Drop receipts here',
  'expenses.drop.prompt': 'Drag receipt pictures or PDFs here, paste one, or choose several: each is read and becomes an unreported expense marked Needs review.',
  'expenses.drop.choose': 'Choose Receipts',
  'expenses.drop.noType': 'No active Receipt file type exists, so receipts cannot be filed here; an administrator restores it under the file types in Settings.',
  'expenses.drop.loadingType': 'The file types are still loading; choose receipts once they have.',
  'expenses.drop.ignoredBusy': 'That drop was ignored, because the receipts already dropped are still being filed; drop these again when they finish.',
  'expenses.drop.ignoredLoading': 'That drop was ignored, because the file types are still loading; drop the receipts again in a moment.',
  'expenses.drop.uploading': 'Uploading.',
  'expenses.drop.reading': 'Reading the receipt.',
  'expenses.drop.filedRead': 'Filed as an unreported expense that needs review:',
  'expenses.drop.filedUnread': 'Filed with the picture alone, because the receipt could not be read; it needs review.',
  'expenses.drop.filedNoReader': 'Filed with the picture alone, because no receipt reader is configured; it needs review.',
  'expenses.drop.duplicate': 'Not filed, because an expense of yours already has the same merchant, amount and date:',
  'expenses.drop.duplicateWaits': 'The receipt waits in the Documents Inbox in case both are meant.',
  'expenses.drop.failed': 'Not filed:',
  'expenses.drop.notReceipt': 'Skipped, because only a picture or a PDF is a receipt.',
  'expenses.drop.noReader': 'No receipt reader is configured, so a photographed receipt is filed with the picture alone; open each one to fill it in.',
  'expenses.needsReview': 'Needs review',
  'expenses.noCategory': 'No category',
  'expenses.noPaidWith': 'Paid With not chosen',
  'expenses.aReceipt': 'A receipt',
  'expenses.busy': 'The page is saving; wait for it to finish.',
  'expenses.addToReport': 'Add to Report',
  'expenses.delete': 'Delete',
  'expenses.deleteTitle': 'Delete these unreported expenses?',
  'expenses.deleteBody': 'The expenses are removed, and a receipt one of them named goes back to the Documents Inbox.',
  'expenses.deleted': 'unreported expenses were deleted.',
  'expenses.deletedOne': 'One unreported expense was deleted.',
  'expenses.milesShort': 'mi',
  // Decision 2181: the Rows and Tiles switch, the Receipt column and the receipt's thumbnail.
  'expenses.layout.legend': 'Show expenses as',
  'expenses.layout.rows': 'Rows',
  'expenses.layout.tiles': 'Tiles',
  'expenses.receiptColumn': 'Receipt column',
  'expenses.thumb.none': 'No receipt',
  'expenses.thumb.scanning': 'Being scanned',
  'expenses.thumb.blocked': 'Blocked by the scanner',
  'expenses.thumb.gone': 'Receipt removed',
  'expenses.thumb.pdf': 'PDF receipt',
  'expenses.thumb.loading': 'Loading',
  'expenses.thumb.unreadable': 'Cannot be shown',
  'expenses.thumb.open': 'Open the receipt',
  'reconciliation.title': 'Reconciliation',
  'reconciliation.subtitle': 'Prove one statement period against the bank',
  'reconciliation.openHint': 'Type the day the statement ends and the balance it ends at. Matching every line is not reconciling: only a statement balance proves the books against the bank.',
  'reconciliation.endDate': 'Statement ending date',
  'reconciliation.endingBalance': 'Statement ending balance',
  'reconciliation.open': 'Start the Reconciliation',
  'reconciliation.opened': 'The reconciliation is open',
  'reconciliation.statement': 'Statement to',
  'reconciliation.clearedBalance': 'Cleared book balance',
  'reconciliation.difference': 'Difference',
  'reconciliation.clearedCount': 'cleared',
  'reconciliation.unclearedCount': 'still uncleared',
  'reconciliation.clearAll': 'Clear All',
  'reconciliation.cleared': 'The rows were updated',
  'reconciliation.finish': 'Finish',
  'reconciliation.finished': 'The reconciliation is finished',
  'reconciliation.finishedBy': 'Finished by',
  'reconciliation.reopen': 'Reopen',
  'reconciliation.reopened': 'The reconciliation is open again',
  'reconciliation.reopen.title': 'Reopen This Reconciliation',
  'reconciliation.reopen.reason': 'Why is it being reopened?',
  'reconciliation.reopen.hint': 'The reason is recorded the way a period reopen is, so a statement that was proved and then changed is answerable.',
  'reconciliation.history': 'Earlier statements',
  'reconciliation.view': 'Open',
  'checks.title': 'Checks to Print',
  'checks.subtitle': 'Printed on pre-printed stock, and numbered from the paper afterwards',
  'checks.number': 'Number',
  'checks.col.payee': 'Payee',
  'checks.col.account': 'Account',
  'checks.col.number': 'Check Number',
  'checks.col.amount': 'Amount',
  'checks.none': 'No check is waiting to be printed.',
  'checks.noneInView': 'No check here yet.',
  'checks.view': 'Which Checks',
  'checks.view.toPrint': 'To Print',
  'checks.view.printed': 'Printed',
  'checks.view.voided': 'Voided',
  'checks.print': 'Print',
  'checks.void': 'Void',
  'checks.void.title': 'Void This Check',
  'checks.void.reason': 'Why is this check voided?',
  'checks.void.hint': 'The row and its number stay, and the payment behind it is reversed through its own path.',
  'checks.printed': 'Numbered and marked printed.',
  'checks.voided': 'Voided; the row and its number stay.',
  // Piece 24, decisions 0556 and 0558: the batch print and the dialog that numbers it from the paper.
  'checks.printHint': 'Tick the checks to print, press Print, and load the stock. Nothing is numbered until you confirm what came out of the printer.',
  'checks.select': 'Print this one',
  'checks.selectAll': 'Select All',
  'checks.clearAll': 'Clear All',
  'checks.download': 'View PDF',
  'checks.alignment': 'Print Alignment Sheet',
  'checks.confirm.title': 'Did these checks print correctly?',
  'checks.confirm.hint': 'Type the number on the first sheet that came out. Each check below takes the next number in turn. Untick any that did not print; it keeps its place in the queue with no number.',
  'checks.confirm.start': 'Number on the first sheet',
  'checks.confirm.direction': 'Numbers run',
  'checks.confirm.act': 'Confirm',
  'checks.confirm.ticked': 'Printed correctly',
  'checks.confirmed': 'Numbered from the paper.',
  'checks.confirm.voided': 'a voided check takes no number; write a new one',
  'checks.confirm.already': 'this check is numbered and printed already',
  'checks.sheet.voided': 'a voided check has no sheet to print',
  'checks.batch.empty': 'a batch names at least one check',
  'checks.batch.duplicate': 'a batch names each check once; one of these is named twice',
  'checks.batch.oneAccount': 'a batch is one bank account\u2019s stock, printed from one tray, and these checks are drawn on two:',
  'checks.batch.mixed': 'Tick checks on one bank account at a time; each account has its own stock and its own numbers.',
  'checks.batch.notWaiting': 'one of these checks is not waiting to be printed',
  'checks.start.required': 'the number on the first printed sheet is typed in before anything is confirmed',
  'checks.start.digits': 'a check number is digits, exactly as the stock carries it',
  'checks.start.tooLow': 'the numbers run below one before the batch ends; start higher or print fewer',
  'checks.direction.ascending': 'Up the list',
  'checks.direction.descending': 'Down the list',
  'checks.direction.problem': 'the numbering direction is either ascending or descending',
  'banking.title': 'Banking',
  'banking.subtitle': 'Every connected account, its ledger account and its balance',
  'banking.none': 'No bank or card account is set up yet.',
  'banking.kind.checking': 'Checking',
  'banking.kind.savings': 'Savings',
  'banking.kind.creditCard': 'Credit card',
  'banking.amountInBooks': 'Amount in books',
  // 🔒 Decision 0477: the Bank Summary chart, Figure 124's own panel.
  'banking.summary.title': 'Bank summary',
  'banking.summary.range': 'Date range',
  'banking.summary.feedLine': 'Bank feed balance',
  'banking.summary.booksLine': 'Books balance',
  'banking.summary.empty': 'Nothing has moved through this account yet, so there is no trend to draw.',
  'banking.summary.days7': 'Last 7 days',
  'banking.summary.days30': 'Last 30 days',
  'banking.summary.days90': 'Last 90 days',
  'banking.lastUpdated': 'Last feed update',
  'banking.neverUpdated': 'never pulled',
  'banking.amountInFeed': 'Amount in bank feed',
  'banking.connection': 'Connection',
  'banking.notConnected': 'Not connected',
  // Piece 24, decisions 0432 and 0433: opening an account, connecting its feed, and the nightly pull.
  'banking.newAccount': 'New Bank Account',
  'banking.newAccount.subtitle': 'It opens at Not connected; connecting a feed to it is the next step.',
  'banking.newAccount.name': 'Account name',
  'banking.newAccount.kind': 'Kind',
  'banking.newAccount.glAccount': 'Ledger account',
  'banking.newAccount.institution': 'Institution',
  'banking.newAccount.last4': 'Last four digits',
  'banking.newAccount.created': 'The account was opened',
  // Decision 2961 (T1055): the Banking page's Company cards section, its add and edit window, and Cancel Card.
  'companyCards.title': 'Company Cards',
  'companyCards.hint': 'Each card the company issues: who holds it and the card account its charges arrive on. A canceled card stays listed and takes no new expense.',
  'companyCards.add': '+ Add Card',
  'companyCards.none': 'No company card is set up yet.',
  'companyCards.addTitle': 'Add Company Card',
  'companyCards.editTitle': 'Edit Company Card',
  'companyCards.label': 'Card label',
  'companyCards.lastFour': 'Last four digits',
  'companyCards.issuer': 'Issuer',
  'companyCards.holder': 'Holder',
  'companyCards.cardAccount': 'Card account',
  'companyCards.chooseOne': 'Nobody named',
  'companyCards.noAccount': 'No card account',
  'companyCards.heldBy': 'held by',
  'companyCards.edit': 'Edit',
  'companyCards.cancel': 'Cancel Card',
  'companyCards.cancelHint': 'A canceled card takes no new expense; the expenses already paid with it keep it.',
  'companyCards.cancelledOn': 'Canceled on',
  'companyCards.reactivate': 'Reactivate',
  'companyCards.active': 'Active',
  'companyCards.canceled': 'Canceled',
  'companyCards.saved': 'The card was saved',
  'companyCards.canceledNotice': 'The card was canceled',
  'companyCards.reactivated': 'The card was reactivated',
  'banking.connect': 'Connect a Feed',
  'banking.connect.subtitle': 'Two facts: which provider, and the reference that provider issued for this connection.',
  'banking.connect.provider': 'Feed provider',
  'banking.connect.reference': 'Connection reference',
  'banking.connect.noCredentials': 'No bank username, password or security answer is stored here. This is the reference the provider issued, and nothing else.',
  'banking.connect.noProviders': 'No bank feed provider is bound in this environment, so nothing can be connected yet.',
  'banking.connected': 'The feed was connected',
  'banking.disconnect': 'Disconnect the Feed',
  'banking.disconnect.confirm': 'Disconnecting stops the next lines arriving and keeps every line already delivered.',
  'banking.disconnected': 'The feed was disconnected',
  'banking.close': 'Close Account',
  'banking.close.confirm': 'Close {name}? Its lines stay readable and it leaves every list of accounts to pay or deposit from.',
  'banking.closed': 'The account was closed',
  'banking.close.refused.feed': 'Disconnect the feed on {name} first: a connected feed is still delivering lines to it.',
  'banking.close.refused.unmatched': '{name} still has feed lines waiting to be matched, categorized or excluded. Settle them first.',
  'banking.close.refused.reconciliation': 'Finish the open reconciliation on {name} first.',
  'banking.close.already': '{name} is already closed.',
  'banking.reopen': 'Reopen Account',
  'banking.reopened': 'The account was reopened',
  'banking.reopen.already': '{name} is already open.',
  'banking.status.closed': 'Closed',
  'banking.feedProvider': 'Feed provider',
  'banking.nightlyPull': 'Connected feeds also pull themselves nightly, so these lines arrive without this button.',
  'payment.bankAccount': 'Bank account',
  'payment.bankAccount.unbanked': 'Not banked yet',
  'bill.credit.vendorCredit': 'Vendor credit',
  'bill.credit.prepayment': 'Prepayment',
  'bill.delivery.heading': 'These goods have not been received',
  'bill.delivery.body': 'Confirm the delivery on the purchase order, or bill first and receive when the goods land.',
  'bill.reject.reason': 'Why is this bill rejected?',
  'bill.void.reason': 'Why is this bill voided?',
  'bill.new.needsVendor': 'A bill names the vendor it came from.',
  'bill.new.needsInvoiceNumber': "A bill carries the vendor's own invoice number, so a payment matches what they sent.",
  'bill.duplicate.title': 'Vendor Invoice Already Entered',
  'bill.duplicate.warning': "{billNumber} already carries this vendor's invoice {number}.",
  'bill.duplicate.question': 'Keep both Bills, or cancel and check the number you typed.',
  'bill.duplicate.refusal': "{billNumber} already carries this vendor's invoice {number}; confirm that you mean to keep both Bills.",
  'bill.new.needsLines': 'A bill carries at least one line.',
  'bill.new.noPurchaseOrder': 'No purchase order — the electricity has none either',
  'bill.po.leftToBill': 'left to bill',
  'bill.po.readFailed': 'That purchase order could not be read for billing; open the order to see where it stands.',
  'bill.po.hint': 'Picking an order fills the lines with what is still left to bill on it.',
  'bill.po.none': 'This vendor has no purchase order with anything left to bill.',
  'bill.po.chooseVendor': 'Choose the vendor first; their open purchase orders are listed here.',
  'bill.notFound': 'No bill stands at this address.',
  'bill.lines.title': 'Lines',
  'bill.lines.total': 'Total',
  'bill.lines.paid': 'Paid',
  'bill.emptyLines': 'This bill carries no lines.',
  'bill.submitted.posted': 'Submitted. No approval was needed, so the bill is posted and now reads Received.',
  'bill.submitted.pending': 'Submitted for approval. The bill posts once it is approved, and it waits on the Pending Approvals queue until then.',
  'bill.submitted.clearedNotPosted': 'Submitted. No approval is needed, but posting a bill is for the Accounting and Admin roles, so it stays a Draft until one of them submits it.',
  'bill.approvedNotice': 'Approved and posted.',
  'bill.rejectedNotice': 'Rejected.',
  'bill.voidedNotice': 'Voided, and its postings reversed.',
  'bill.void.title': 'Void This Bill',
  'bill.void.hint': 'The bill keeps its number and its postings are reversed; nothing is deleted.',
  'bill.reject.title': 'Reject This Bill',
  'bill.rejection': 'Rejected',
  'bill.pill.void': 'Void',
  'bill.approval.pending': 'Held for approval',
  'bill.approval.rejected': 'Rejected',
  'bill.approval.approved': 'Approved',
  'bill.action.pay': 'Pay',
  'bill.pay.title': 'Pay This Bill',
  'bill.pay.printNow': 'Pay and Print Now',
  'bill.pay.printLater': 'Pay and Print Later',
  'bill.pay.sheetProblem': 'Paid. The check’s sheet did not open, so the check waits in Checks to Print.',
  'bill.pay.numberLater': 'Paid. The check waits in Checks to Print with no number.',
  'bill.pay.owing': 'Still owing',
  'bill.pay.unavailable': 'This bill is not open for payment. The vendor’s open bills do not include it, so there is nothing here to settle.',
  'bill.pay.severalHint': 'This pays one bill. To cover several of this vendor’s bills with one payment, use the Pay bills run from Banking.',
  'bill.paidNotice': 'Paid; the bill is settled and the payment is recorded.',
  'settings.billVariance.title': 'Bill Variance Tolerance',
  'settings.billVariance.hint': "How far a bill line may sit above its purchase order's price, or above the quantity received, before it is flagged and routed through the approval gate. A tolerance of zero flags every rounding difference.",
  'settings.billVariance.percent': 'Tolerance, percent',
  // Piece 24, decision 0556: which way the numbers run down a printed stack of checks.
  'settings.checkNumbering.title': 'Check Numbering Direction',
  'settings.checkNumbering.hint': 'Some printers take a stack of check stock face down, so the last sheet out of the tray carries the lowest number. This is the direction the numbering dialog starts on; a person can flip it for one batch without changing it here.',
  'settings.checkNumbering.direction': 'Numbers run',
  // Piece 24, decision 1620 (T858): the credit card's surcharge, and the two card types the networks hold at zero.
  'settings.cardSurcharge.title': 'Card Surcharge',
  'settings.cardSurcharge.hint': 'The percentage a credit card payment adds for the customer, shown beneath the amount where tax sits. The card networks cap it at 3 percent, and 0 turns the line off.',
  'settings.cardSurcharge.credit': 'Credit card, percent',
  'settings.cardSurcharge.debit': 'Debit card',
  'settings.cardSurcharge.prepaid': 'Prepaid card',
  'settings.cardSurcharge.forbiddenHeading': 'Never surcharged',
  'settings.cardSurcharge.forbidden': 'The card networks forbid a surcharge on this card, so it stays at 0 percent.',
  'settings.cardSurcharge.superAdminOnly': 'The card surcharge is a super admin\u2019s to change.',
  // Piece 24, decision 1621 (T858): the master mailbox's address, the dedicated addresses and the Subject Line Rules.
  'settings.documentsInbox.title': 'Documents Inbox Rules',
  'settings.documentsInbox.hint': 'Where bills and other accounting documents arrive, and how a document is pre-sorted before it is read. The subject rules start sorting mail once the mail client reads them.',
  'settings.documentsInbox.masterHeading': 'Master mailbox',
  'settings.documentsInbox.masterHint': 'The accounting group’s shared mailbox, which every bill and accounting document comes into and the books read. Changing it moves the mailbox that answers at the old address to the new one.',
  'settings.documentsInbox.masterAddress': 'Master mailbox address',
  'settings.documentsInbox.masterUnset': 'Not set yet, so no mail is read into the Documents Inbox and the tax warning email goes nowhere.',
  'settings.documentsInbox.masterNoMailbox': 'No mailbox answers at this address yet.',
  'settings.documentsInbox.masterMailbox': 'Read in the mailbox',
  'settings.documentsInbox.superAdminOnly': 'The master mailbox’s address is a super admin’s to change.',
  'settings.documentsInbox.addressesHeading': 'Dedicated addresses',
  'settings.documentsInbox.addressesHint': 'An address of its own for one kind of document, so a document mailed to it arrives already marked as that kind. A retired address keeps its history.',
  'settings.documentsInbox.addressesEmpty': 'No dedicated address yet.',
  'settings.documentsInbox.address': 'Address',
  'settings.documentsInbox.preFlags': 'Pre-flags as',
  'settings.documentsInbox.chooseOne': 'Choose one',
  'settings.documentsInbox.retire': 'Retire',
  'settings.documentsInbox.classification.customerPO': 'Customer PO',
  'settings.documentsInbox.classification.vendorBill': 'Vendor bill',
  'settings.documentsInbox.classification.vendorQuote': 'Vendor quote',
  'settings.documentsInbox.classification.receipt': 'Receipt',
  'settings.documentsInbox.classification.other': 'Other',
  'settings.documentsInbox.rulesHeading': 'Subject line rules',
  'settings.documentsInbox.rulesHint': 'A word or a bracketed reference in an incoming subject, and what it does. A new rule starts as a draft and matches nothing until it is switched on.',
  'settings.documentsInbox.rulesEmpty': 'No subject line rule yet.',
  'settings.documentsInbox.token': 'Token',
  'settings.documentsInbox.matches': 'Matches',
  'settings.documentsInbox.matchKind.bracketedReference': 'A bracketed reference',
  'settings.documentsInbox.matchKind.word': 'A word',
  'settings.documentsInbox.documentType': 'Document type',
  'settings.documentsInbox.anyDocument': 'Any document',
  'settings.documentsInbox.then': 'Then',
  'settings.documentsInbox.action.linkToRecord': 'Link to the record',
  'settings.documentsInbox.action.setPriorityFlag': 'Set the priority flag',
  'settings.documentsInbox.priority': 'Priority',
  'settings.documentsInbox.status.active': 'Active',
  'settings.documentsInbox.status.draft': 'Draft',
  'settings.documentsInbox.switchOn': 'Switch On',
  'settings.documentsInbox.switchOff': 'Switch Off',
  'settings.documentsInbox.remove': 'Remove',
  'settings.documentsInbox.addRule': '+ Add Rule',
  'settings.documentsInbox.addRuleTitle': 'Add a Rule as Draft',
  'settings.documentsInbox.addAddressButton': '+ Add Address',
  'settings.documentsInbox.addAddressTitle': 'Add an Address',
  'settings.expenseCategories.title': 'Expense Categories',
  'settings.expenseCategories.hint': 'The categories an expense is filed under, each tied to the account it is booked to. A retired category keeps its history and takes no new expense.',
  'settings.expenseCategories.name': 'Category name',
  'settings.expenseCategories.account': 'Posts to',
  'settings.expenseCategories.add': '+ Add Category',
  'settings.expenseCategories.addTitle': 'Add a Category',
  'settings.expenseCategories.retire': 'Retire',
  'settings.expenseCategories.restore': 'Restore',
  'settings.expenseCategories.receipt': 'receipt required',
  'settings.expenseCategories.mealThreshold': 'Per-person meal threshold',
  'settings.expenseCategories.inForce': 'Per-person threshold in force',
  'settings.mileageRate.title': 'Mileage Rate',
  'settings.mileageRate.hint': 'What a mile driven is paid at, in dollars. A trip takes the rate in force on the day it happened, so January travel filed in March takes January’s rate, and a later change moves no trip already filed.',
  'settings.mileageRate.cents': 'Rate a mile',
  'settings.mileageRate.inForce': 'In force today',
  'settings.perDiemRates.title': 'Per Diem Rates',
  'settings.perDiemRates.hint': 'What one day of travel allowance is paid at. A claim takes the rate in force on its first day. Ending a rate keeps it listed, because a claim may name it, and nothing here deletes one.',
  'settings.perDiemRates.add': '+ Add Rate',
  'settings.perDiemRates.addTitle': 'Add a Rate',
  'settings.perDiemRates.name': 'Name',
  'settings.perDiemRates.amount': 'Amount a day',
  'settings.perDiemRates.currency': 'Currency',
  'settings.perDiemRates.effectiveFrom': 'Effective from',
  'settings.perDiemRates.inForce': 'In Force',
  'settings.perDiemRates.from': 'from',
  'settings.perDiemRates.until': 'until',
  'settings.perDiemRates.runsOn': 'runs on',
  'settings.perDiemRates.end': 'End',
  'settings.perDiemRates.endTitle': 'End a Rate',
  'settings.perDiemRates.endedOn': 'Last day',
  'settings.perDiemRates.none': 'No per diem rate is set.',
  'settings.perDiemRates.addNeeds': 'Type a name, an amount above zero, a three-letter currency and the first day.',
  'settings.perDiemRates.endNeeds': 'Choose the last day.',
  // T3234: the sentences the per diem rate service refuses with, read by the page's tests as the server words them.
  'settings.perDiemRates.refuse.name': 'A per diem rate has a name.',
  'settings.perDiemRates.refuse.amount': 'A per diem rate is an amount of money a day, above zero.',
  'settings.perDiemRates.refuse.startDay': 'A per diem rate names the day it starts, as a calendar day.',
  'settings.perDiemRates.refuse.currency': 'A per diem rate names its currency by its three-letter code.',
  'settings.perDiemRates.refuse.overlap': 'The rate {name} already covers {from} to {to}, so a new rate starts after it ends.',
  'settings.perDiemRates.refuse.overlapOpen': 'no end day',
  'settings.perDiemRates.refuse.afterCurrent': 'A new rate starts after the rate in force, which began on {day}.',
  'settings.perDiemRates.refuse.notFound': 'No per diem rate {id} was found.',
  'settings.perDiemRates.refuse.alreadyEnded': 'The rate {name} has already ended.',
  'settings.perDiemRates.refuse.endDay': 'Ending a rate names the last day it is offered, as a calendar day.',
  'settings.perDiemRates.refuse.endBeforeStart': 'A rate ends on or after the day it started, {day}.',
  'settings.perDiemRates.refuse.version': 'Ending a rate names the version of the rate it read.',
  'settings.perDiemRates.conflict': 'This rate was changed by somebody else since it was opened; reopen it and end it again.',
  'settings.matchTiers.title': 'Match Confidence Tiers',
  'settings.matchTiers.hint': 'What counts as a High, Medium or Low match when the system suggests which payment a bank line is. Nothing posts on its own at any setting: High is one click a person makes, and Low is never posted automatically.',
  'settings.matchTiers.windowDays': 'Posting-date window, days',
  'settings.matchTiers.toleranceCents': 'Amount tolerance',
  'settings.matchTiers.similarityPercent': 'Name similarity, percent',
  'settings.form1099.title': '1099 Filing Threshold',
  'settings.form1099.hint': 'What one vendor has to be paid in a year before a 1099 is prepared for them. Each figure applies from its own date, so a 2027 run prepared in 2029 uses the 2027 figure. This keeps the figures and a record of what was filed; the filing itself is done elsewhere.',
  'settings.form1099.amount': 'Threshold amount',
  'settings.form1099.inForce': 'In force today',
  'vendorTax.title': 'Vendor',
  'vendorTax.entityType': 'Entity type',
  'vendorTax.entityTypeHint': 'As Form W-9 Line 3a records it. An LLC that elected corporate treatment is recorded as the corporation it elected.',
  'vendorTax.tin': 'Tax ID',
  // 🔒 T3185: the vendor's Tax ID keeps the W-9 line it is read from and the dash pattern its Entity Type offers.
  'vendorTax.tinHint': 'From Form W-9 Part I, in the pattern {pattern}.',
  'vendorTax.legalServices': 'Legal services',
  'vendorTax.legalServicesHint': 'Payments for legal services are reportable even to a corporation, so this overrides the corporate exemption.',
  'vendorTax.w9Status': 'W-9 status',
  'vendorTax.filingStatus': 'Filing status',
  'vendorTax.ytd': 'Year-to-date reportable',
  'vendorTax.excluded': 'Excluded',
  'vendorTax.threshold': 'Threshold',
  'vendorTax.backupWithholding': 'Backup withholding required',
  'vendorTax.override': 'Override',
  'vendorTax.override.title': 'Override the W-9 Requirement',
  'vendorTax.override.reason': 'Why is this vendor payable with no W-9?',
  'vendorTax.override.hint': 'The reason is shown wherever the status renders, and is logged with who set it and when.',
  'vendorTax.w9File': 'Or name one already on Company Files',
  'vendorTax.uploadW9': 'Upload W-9',
  'vendorTax.replaceW9': 'Replace',
  'vendorTax.w9Filed': 'Form W-9 on file:',
  'vendorTax.w9Uploaded': 'W-9 uploaded',
  'vendorTax.w9UploadHint': 'Upload the signed W-9 here. It is filed on Company Files under the W-9 type and waits for approval.',
  'vendorTax.w9ReplaceHint': 'Replace files another W-9 the same way. It drops the approval the old one carried.',
  'vendorTax.w9TypeMissing': 'Upload W-9 files under the W-9 file type, which is deactivated or missing here. Reactivate it, or add it, in Settings › Documents › File Types.',
  'vendorTax.entityTypeNeeded': 'Entity type needed',
  'vendorTax.attachW9': 'Name as the W-9',
  'vendorTax.approve': 'Approve the W-9',
  'vendorTax.w9Doubt': 'This file does not read as a Form W-9.',
  'vendorTax.approveDoubt.title': 'Approve Over the Doubt',
  'vendorTax.approveDoubt.hint': 'Approving records that you have read the file and confirm it is this vendor’s W-9. The doubt goes on the audit trail beside your approval.',
  'vendorTax.approveDoubt.act': 'Approve Anyway',
  'vendorTax.reject': 'Reject the W-9',
  'vendorTax.reject.title': 'Reject This W-9',
  'vendorTax.reject.reason': 'What is wrong with it?',
  'vendorTax.reject.hint': 'The vendor goes back to No W-9 on File and is asked to upload another; they cannot be marked active for payment until they do.',
  'vendorTax.prepare': 'Prepare the Return',
  'vendorTax.recordFiling': 'Record the Filing',
  'vendorTax.filing.title': 'Record How This Return Was Filed',
  'vendorTax.filing.method': 'Filed through',
  'vendorTax.filing.date': 'Filed on',
  'vendorTax.filing.datePlaceholder': 'YYYY-MM-DD',
  'vendorTax.needsCorrection': 'Needs Correction',
  'vendorTax.needsCorrection.title': 'Mark This Return as Needing Correction',
  'vendorTax.needsCorrection.reason': 'What is wrong with the filed return?',
  'vendorTax.save': 'Save',
  'vendorTax.notAVendor': 'This company is not marked a vendor, so it carries no vendor tax details.',
  'vendorTax.noProfile': 'No entity type is recorded for this vendor yet.',
  'vendorTax.exempt': 'Exempt from filing by entity type',
  'vendorTax.filing.filed': 'Filed',
  'workOrder.print.priority': 'Priority',
  'workOrder.print.status': 'Status',
  // The list-and-detail frame every list-backed record renders in (decision 0408).
  'listDetail.back': '← Back to the list',
  'listDetail.collapse': 'Collapse the list',
  'listDetail.expand': 'Expand the list',
  // Decision 7781 (T3163): the handle on the seam between the list pane and the record.
  'listDetail.resize': 'Resize the list',
  'listDetail.resizeHint': 'Drag to resize the list. Double-click to return it to a third.',
  'listDetail.pane': 'List',
  'item.list.offDefault': 'Off-default accounts',
  'item.list.taggedWith': 'Tagged',
  // Decision 7640: the Items list's Series menu.
  'item.list.series': 'Series',
  'item.list.allSeries': 'All series',
  'item.list.noManufacturer': 'No manufacturer',
  // Decision 8800 (T3276): the Items list's Filter, several conditions at once without a saved view.
  'listFilter.button': 'Filter',
  'listFilter.apply': 'Apply',
  'listFilter.clear': 'Clear',
  'listFilter.clearAll': 'Clear Filter',
  // T3341: the Clear beside the Filter button takes the conditions off alone; Clear Filter in the empty state takes the Series choice and the search too.
  'listFilter.clearConditions': 'Clear Conditions',
  'listFilter.saveAsView': 'Save as View',
  'listFilter.saveNeedsRows': 'Add a condition to save it as a view.',
  'listFilter.hint': 'Apply narrows the list now and saves nothing. Save as View keeps these conditions as a view of your own.',
  'item.image.title': 'Item Image',
  'item.image.set': 'Set Image',
  'item.image.replace': 'Replace Image',
  'item.image.remove': 'Remove Image',
  'item.image.removeTitle': 'Remove this image?',
  'item.image.removeBody': 'The image comes off the Item and its file goes to the trash.',
  'item.image.removeConfirm': 'Remove',
  'item.image.pick': 'Choose an image',
  'item.image.size': 'Printed size',
  'item.image.size.three': '3 in',
  'item.image.size.two': '2 in',
  'item.image.size.one': '1 in',
  'item.image.hint': 'The picture is scaled to fit the printed size at 300 DPI, never enlarged and never larger than three inches a side.',
  'item.image.storedAt': 'Stored at',
  'item.image.was': 'was',
  'item.image.none': 'No image yet.',
  'item.image.notImage': 'Pick a PNG, JPEG, WebP or GIF image.',
  'item.image.pickFirst': 'Choose an image before saving.',
  'item.image.alt': 'Item image',
  // Decision 0575: Region 1's picture, on the sheet and on the screen alike.
  'document.print.logoAlt': 'Organization Logo',
  'item.image.save': 'Save Image',
  'item.attribute.title': 'Add Attribute',
  'item.attribute.type': 'Attribute',
  // Decision 0559: Save is gray until a type is chosen, and says so.
  'item.attribute.pickType': 'Choose an attribute before saving.',
  'item.attribute.category': 'on this category',
  'item.attribute.value': 'Value',
  'item.attribute.unit': 'Unit',
  'item.supplier.title': 'Add Supplier',
  'item.supplier.editTitle': 'Edit Supplier',
  'item.supplier.edit': 'Edit',
  'item.supplier.company': 'Vendor',
  'item.supplier.pickCompany': 'Pick the vendor first.',
  'item.supplier.vendorPartNumber': 'Vendor part number',
  'item.supplier.minimum': 'Minimum order quantity',
  'item.supplier.status': 'Status',
  'item.supplier.status.primary': 'Primary',
  'item.supplier.status.active': 'Active',
  'item.supplier.status.notApplicable': 'Not applicable',
  'item.supplier.status.blacklisted': 'Blacklisted',
  'item.supplier.reopenHint': 'This row ended. Saving a status reopens it.',
  'item.stock.title': 'Stock at Site',
  'item.stock.hint': 'The row says the Item is stocked here and carries the site’s own low-stock judgment; the quantity is the ledger’s sum and is never typed.',
  'item.stock.site': 'Site',
  'item.stock.siteEntity': 'site',
  'item.stock.pickSite': 'Pick the site first.',
  'item.stock.threshold': 'Low stock threshold',
  'item.stock.reorder': 'Reorder quantity',
  'dev.pieceSeventeen': 'Item page — New/Edit, and the catalog list',
  'dev.page.items.lookFor': 'The catalog at /items with + New Item, the Off-default accounts view and Edit Selected over a selection, the accounts among its fields; an Item page with ten tiles, Stock by Site absent on a Service, and no Delete anywhere.',
  'dev.page.catalog.lookFor': 'Settings › Catalog › Categories: the tree with own/inherits badges and item counts on the left, the selected category’s attributes on the right; a move previews its three consequences. The Site page’s Stock tile lists what is stocked there with the derived on-hand.',
  'shell.settings': 'Settings',
  'dev.pieceFifteen': 'Settings and administration',
  'dev.page.settings.lookFor': 'The gear in the header opens the Index, one List Page grouped by category. Term Container Library draws the Preamble as the list\u2019s own first row and each Section with its clause count; Import Document splits a .docx into clauses and writes nothing until confirmed.',

  'login.title': 'Sign in',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.forgot': 'Forgot password?',
  'login.ssoButton': 'Sign in With',
  'login.ssoOr': 'or with your password',
  'login.ssoStateMismatch': 'The provider’s answer did not belong to this browser’s sign-in. Press the button again.',
  'login.ssoWorking': 'Finishing the sign-in with the provider…',
  'login.signIn': 'Sign In',
  'login.mfaHeading': 'Multi-factor verification',
  'login.trustDevice': 'Trust this device: skip the code here for',
  'login.factorTitle': 'Enter the code from your authenticator',
  'login.factorBody': 'The password held. The six-digit code your authenticator app shows now, or a recovery code, finishes the sign-in.',
  'login.verify': 'Verify',
  'login.usePasskey': 'Use a Passkey',
  'login.passkeyUnsupported': 'This browser offers no passkeys; enter the code instead.',
  'login.passkeyFailed': 'The passkey did not verify. Enter the code instead, or try again.',
  'login.enrolScan': 'Scan the code with your authenticator app, or type the secret into it.',
  'login.trustDays': 'days',
  'login.code': '6-digit code',
  'login.codePlaceholder': '– – – – – –',
  'login.noAccount': 'No account? Contact your Company\u2019s own Admin for an invite.',
  'login.enrolTitle': 'Enroll your authenticator',
  'login.enrolHint': 'Add this secret to an authenticator app, then type the code it shows. The secret is shown once.',
  'login.enrolSecret': 'Secret',
  'login.enrolUrl': 'Or open this link on the phone that holds the app',
  'login.enrolConfirm': 'Confirm Code',
  'login.changeTitle': 'Set a new password',
  'login.changeHint': 'The default credential can do exactly one thing: set a new password. Every other page waits until it is changed.',
  'login.newPassword': 'New password',
  'login.newPasswordAgain': 'New password again',
  'login.mismatch': 'The two passwords differ.',
  'login.tooShort': 'A password is at least twelve characters.',
  'login.isDefault': 'The documented default cannot be set again.',
  'login.changeSave': 'Set Password',
  'login.problem.email': 'Email is required.',
  'login.problem.password': 'Password is required.',
  'login.problem.code': 'The code is six digits.',
  'login.unreachable': 'The server did not answer. Nothing was sent.',
  'login.locked': 'Too many failed attempts. Try again in',
  'login.forgotTitle': 'Reset your password',
  'login.forgotBody': 'A reset is sent on the recovery channel your login holds. If it holds none, ask HR to re-issue your invitation.',
  'login.forgotSend': 'Send Reset',
  'login.forgotSent': 'If that address holds a login with a recovery channel, a reset was sent there. Otherwise ask HR to re-issue your invitation.',
  'login.back': 'Back to sign in',
  'login.haveCode': 'Have an activation code?',
  'login.codeTitle': 'Enter your activation code',
  'login.codeBody': 'The code you were handed or texted. It works once.',
  'login.activationCode': 'Activation code',
  'login.redeem': 'Continue',
  'login.activateTitle': 'Set your password',
  'login.activateHint': 'Choose the password you will sign in with. Twelve characters or more.',
  'login.activateSave': 'Set Password and Continue',
  'login.recoveryTitle': 'Your recovery codes',
  'login.recoveryHint': 'Each code signs you in once if your authenticator is lost. They are shown only now; keep them somewhere safe.',
  'login.recoveryContinue': 'I Have Saved Them',
  'shell.exitEmulation': 'Exit',
  'shell.viewAsSubtitle': 'Everything renders for the identity you pick, and every write records both of you.',
  'shell.viewAsConfirm': 'View as This Person',
  'shell.viewAsEmpty': 'There is nobody to view as yet.',
  'shell.viewAsSuperAdmin': 'a super admin, who cannot be emulated',
  'shell.accountSubtitle': 'Settings that are yours alone; nobody else sees the difference.',
  'shell.timeZone': 'Time zone',
  'shell.timeZoneHint': 'Every time in the application is drawn in this zone. A record held somewhere else names its own zone beside it.',
  'shell.timeZoneOrganization': 'Use the organization zone',
  'shell.timeZoneRefused': 'That is not a time zone the application knows.',
  'shell.testEnvironment': 'Test environment',
  'shell.version': 'Version',
  'shell.built': 'built',
  'shell.lastReset': 'reset',
  'shell.whatsNew': 'What’s New',
  'shell.whatsNewUnseen': 'There are changes you have not seen yet',
  'whatsNew.title': 'What’s New',
  'whatsNew.subtitle': 'Changes to the application, newest first',
  'whatsNew.empty': 'Nothing has been written in the change log yet.',
  // Decision 8700 (T3273): the release-notes page's own words — the area filter, the app tags and the Details control.
  'whatsNew.filter': 'Area',
  'whatsNew.filterAll': 'All',
  'whatsNew.app.web': 'Web',
  'whatsNew.app.phone': 'Phone',
  'whatsNew.details': 'Details',
  'whatsNew.hideDetails': 'Hide Details',
  'whatsNew.linePage': '{page}: ',
  'whatsNew.area.mobile': 'Phone App',
  'whatsNew.area.platform': 'Across the Application',
  'whatsNew.area.quote': 'Quotes',
  'whatsNew.area.salesOrder': 'Sales Orders',
  'whatsNew.area.invoice': 'Invoices and Credit Notes',
  'whatsNew.area.purchaseOrder': 'Purchase Orders and RFQs',
  'whatsNew.area.workOrder': 'Work Orders',
  'whatsNew.area.item': 'Catalog and Stock',
  'whatsNew.area.deal': 'Deals and Leads',
  'whatsNew.area.companyPerson': 'Companies and People',
  'whatsNew.area.expenses': 'Expenses and Mileage',
  'whatsNew.area.billsAndPayments': 'Bills and Payments',
  'whatsNew.area.banking': 'Banking',
  'whatsNew.area.ledger': 'Ledger',
  'whatsNew.area.tax': 'Tax',
  'whatsNew.area.files': 'Files and Printing',
  'whatsNew.area.auth': 'Sign-In and Access',
  'whatsNew.area.settings': 'Settings',
  'whatsNew.area.reports': 'Reports',
  'whatsNew.area.shell': 'Dashboard, Calendar and Navigation',
  'shell.editRecord': 'Edit this record',
  // 🔒 Decision 2805: the gear on a page a setting governs, which opens the menu of the entries that govern it.
  'shell.pageSettings': 'Settings for this page',
  'shell.titleBarExpand': 'Show the whole title bar',
  'shell.titleBarCollapse': 'Fold the title bar to its first line',
  'shell.offline': 'You are offline. Changes will not save until the connection is back.',
  'shell.dashboard.title': 'Dashboard',
  'shell.dashboard.noQueues': 'No queues are open to you yet.',
  'shell.dashboard.feedEmpty': 'Nothing has happened since you were last here.',
  'shell.accessDenied.title': 'Access Denied',
  'shell.accessDenied.body': 'This record is restricted. Ask an administrator if you need it.',
  'shell.notFound.title': 'Not Found',
  'shell.notFound.body': 'There is nothing at that address.',
  'shell.error.title': 'Something went wrong',
  'shell.error.body': 'The page could not be drawn. Reload, and tell an administrator if it happens again.',
  // Piece 9, decision 0822 (T271): the reference a fault's row carries, shown beside the failure so the owner can find it.
  'shell.maintenance.title': 'Back in a Few Minutes',
  'shell.maintenance.body': 'The database is being recovered from a backup. The application is unavailable for a few minutes, and everybody signs in again afterwards.',
  'shell.maintenance.reloads': 'This page reloads by itself once the application is back.',
  'shell.maintenance.byHand': 'Where the API is run by hand, start it again if it has not come back within a few minutes.',
  'shell.error.reference': 'Error reference',
  'dev.page.records.lookFor': 'Under Server below: the status counts read 21 rows, because this piece seeds one per member of its four status enums. The seventeen tables carry the overlap rule as a database constraint.',
  'presence.nobody': 'Nobody else is viewing',
  'presence.oneOther': 'other person viewing',
  'presence.others': 'other people viewing',
  'presence.list': 'Also viewing',
  'live.banner': 'just edited this record',
  'live.dismiss': 'Dismiss',
  'live.simulate': 'Simulate a Remote Save',
  'live.save': 'Save',
  'live.saved': 'Saved',
  'conflict.title': 'Changed by someone else while you were editing',
  'conflict.yours': 'Your value',
  'conflict.theirs': 'Their value',
  'conflict.keepMine': 'Keep Mine',
  'conflict.takeTheirs': 'Take Theirs',
  'harness.live.presenceOne': 'One other viewer: the avatar and the count the heartbeat answered.',
  'harness.live.presenceSeveral': 'Three other viewers in arrival order; the person looking is left out.',
  'harness.live.presenceStale': 'A viewer whose last ping is older than staleAfter is filtered out at read time, still stored.',
  'harness.live.banner': 'The dismissible banner naming the last remote editor.',
  'harness.live.conflict': 'One field edited on both sides: both values, and a choice.',
  'harness.live.record': 'A record with four fields: a remote save updates the clean fields in place with a highlight, and asks only about a field you have edited.',
  'harness.sample.remoteEditor': 'Jordan Ramirez',
  'dev.page.reference.lookFor': 'Under Server below: the seeded counts read 4 tax categories, 4 catalog attribute types, 2 requirement types, 6 site requirement types, 4 activity kinds and 3 address rules.',
  // Piece 9: the scan-confirm screen, the Share flow and the developer outbox.
  'dev.pieceNine': 'Files, versions, sharing, audit: outbound mail and the reminder engine',
  'dev.page.outbox.lookFor': 'The sends the recording fake behind the mail seam captured: sender, recipient, Cc, Bcc, subject, body and the time accepted, newest first. A fresh environment has sent nothing, so the list is empty.',
  'scan.title': 'Check the scan',
  'scan.intro': 'Check the rotation and the crop below, then Confirm.',
  'scan.photoAlt': 'The uploaded photo, drawn at the rotation shown',
  'scan.rotation': 'Rotation',
  'scan.rotateLeft': 'Rotate Left',
  'scan.rotateRight': 'Rotate Right',
  'scan.confirm': 'Confirm',
  'scan.retry': 'Retry',
  'scan.adjustManually': 'Adjust Manually',
  'scan.noBoundary': 'No document edge was found in this photo, so nothing has been cropped.',
  'scan.confirmBlocked': 'Confirm needs four crop corners. Adjust Manually places them.',
  'scan.cropHelp': 'Drag a corner handle, or move the focused one with the arrow keys.',
  'scan.corner.topLeft': 'Crop corner, top left',
  'scan.corner.topRight': 'Crop corner, top right',
  'scan.corner.bottomRight': 'Crop corner, bottom right',
  'scan.corner.bottomLeft': 'Crop corner, bottom left',
  'scan.processing': 'Running now',
  'scan.failedStep': 'This step failed',
  'scan.step.grossOrientation': 'Gross orientation',
  'scan.step.fineDeskew': 'Fine deskew',
  'scan.step.autoCrop': 'Auto crop',
  'scan.step.grayscale': 'Grayscale',
  'scan.step.downsample': 'Downsample',
  'share.title': 'Share File',
  'share.recipient': 'Recipient',
  'share.recipientEntity': 'Person',
  'share.recipientPlaceholder': 'Search people',
  'share.permission': 'Permission',
  'share.view': 'View',
  'share.download': 'Download',
  'share.viewOnlyNote': 'A View link offers the reader no download and no print control.',
  'share.expires': 'Expires',
  'share.address': 'Email address',
  'share.loginNote': 'Whoever opens the link signs in first, and the file gate runs again for them.',
  'share.deliver': 'Deliver',
  'share.email': 'Email',
  'share.copy': 'Copy link',
  'share.qr': 'Show QR',
  'share.working': 'Creating the link',
  'share.confidentialNeeds': 'A confidential file is shared with a named recipient and an expiry date. Both are needed here.',
  'share.needsRecipient': 'Recipient',
  'share.needsExpiry': 'Expiry date',
  'share.needsAddress': 'Email needs the recipient address or one typed here.',
  'share.link': 'Link',
  'share.qrAlt': 'A QR code holding the share link',
  'share.deliveredEmail': 'Emailed',
  'share.deliveredCopy': 'Copied',
  'share.deliveredQr': 'Shown as a QR code',
  'share.done': 'Done',
  'outbox.title': 'Developer outbox',
  'outbox.intro': 'What the recording fake behind the mail seam captured, newest first \u2014 sender, recipient, copies, subject and body. This page reads those sends and sends nothing itself.',
  'outbox.column.from': 'Sender',
  'outbox.column.to': 'Recipient',
  'outbox.column.cc': 'Cc',
  'outbox.column.bcc': 'Bcc',
  'outbox.column.subject': 'Subject',
  'outbox.column.body': 'Body',
  'outbox.column.accepted': 'Accepted',
  'outbox.column.status': 'Status',
  'outbox.noValue': '\u2014',
  'outbox.loading': 'Asking the server what the fake recorded.',
  'outbox.unreachable': 'The server did not answer, so there is nothing to list here. This page shows sends only where the API is running behind it.',
  'outbox.empty': 'Nothing has been sent yet.',
  'outbox.notRecording': 'A real transport is bound behind the seam, and it records nothing here.',
  'outbox.back': 'Back to the test pages',
  'dev.pieceEleven': 'Sandbox identity: one seeded developer, checks return true',
  'dev.page.reset.lookFor': 'The date of the last reset, the seeded developer admin@localhost, and the four counts of what the database holds now. Pressing Reset to fixtures empties the database and reruns install, seed and fixtures, and the confirmation says so before it runs.',
  'reset.title': 'Reset to fixtures',
  'reset.intro': 'Empties the test database and reruns the install script, the reference seed and the fixture load. Anything typed since the last reset is lost at the next one.',
  'reset.back': 'Back to the test pages',
  'developer.refusal.off': 'The developer routes are off here. A super admin turns them on under Settings › System › Developer Routes.',
  'developer.refusal.offLink': 'Open Developer Routes',
  'developer.refusal.notAdmin': 'The developer routes are a super admin\u2019s alone, and this login is not one.',
  'reset.loading': 'Asking the server what the database holds.',
  'reset.unreachable': 'The server did not answer, so there is nothing to reset from here. This page works only where the API is running behind it.',
  'reset.section.database': 'What the database holds now',
  'reset.section.run': 'Run the reset',
  'reset.envKind': 'Environment',
  'reset.installed': 'Install script run',
  'reset.yes': 'Yes',
  'reset.no': 'No',
  'reset.seededDeveloper': 'Seeded developer',
  'reset.noSeededDeveloper': 'None: the install script has not run.',
  'reset.lastReset': 'Last reset',
  'reset.never': 'Never',
  'reset.counts.companies': 'Companies',
  'reset.counts.people': 'People',
  'reset.counts.employments': 'Employments',
  'reset.counts.users': 'Users',
  'reset.warning': 'The reset empties every table, so everything typed since the date above goes with it. It takes several seconds.',
  'reset.button': 'Reset to Fixtures',
  'reset.confirm.subject': 'everything in the test database: every company, person, employment and user typed since the last reset, after which install, the reference seed and the fixture load run again',
  'reset.confirm.phrase': 'reset to fixtures',
  // Decision 6210: Reset to nothing, the changeover test from a blank system.
  'reset.nothing.leaves': 'Reset to Nothing empties every table and leaves the seeded developer and the reference seed, with no fixture cast and no books, so the changeover can be tested from a blank system.',
  'reset.nothing.button': 'Reset to Nothing',
  'reset.nothing.confirm.subject': 'everything in the test database: every company, person, employment and user, after which only install and the reference seed run again, with no fixture cast and no books',
  'reset.nothing.confirm.phrase': 'reset to nothing',
  'reset.running': 'Emptying the database and rerunning install, seed and fixtures. This takes several seconds.',
  'reset.nothing.running': 'Emptying the database and rerunning install and the reference seed. This takes several seconds.',
  'reset.done.title': 'The reset finished',
  'reset.done.at': 'Finished at',
  'reset.done.truncated': 'Tables emptied',
  'reset.done.breakGlass': 'Break-glass password',
  'reset.done.breakGlassFromSecret': 'Set from the host secret named BREAK_GLASS_PASSWORD, and the change-at-next-login flag is cleared.',
  'reset.done.breakGlassDefault': 'Left at the shipped default, because the host set no secret to read.',
  'reset.done.fixtures': 'Fixture rows loaded',
  'reset.done.noFixtures': 'No fixture load ran, so the database holds the install and the reference seed alone.',
  'reset.failed.title': 'The reset did not finish',
  'reset.failed.body': 'The server refused the reset or stopped part way. What the database now holds is whatever the run reached, so read the counts above again before trusting them.',

  // Piece 13: the record pages, the two modals, the review queue and the calendar.
  'modal.dismiss': 'Dismiss',
  'refusal.openAccountingBasis': 'Open Accounting Basis',
  'refusal.openPage': 'Open the Page',
  'record.yes': 'Yes',
  'record.no': 'No',
  'record.tile.empty': 'None yet',
  'record.tile.later': 'This tile arrives with the piece that declares the records it shows.',
  // 🔒 Decision 10370 (building 10240, the owner's S11b): a press on a document row the viewer cannot open.
  // Decision 11340 (T3550): the refused row's button says so to a screen reader and on hover.
  'record.tile.refusedHint': 'You may not open this document.',
  'record.tile.permissionDenied': 'Permission denied: you are not on the account or the Deal’s team, so this document does not open for you.',
  // Piece 13: what a History row says an audited event was. The audit record keeps
  // the identifiers and the capability names; the row on the record keeps the words.
  'history.audit.levelSet': 'Level set to',
  // T728, decision 1500: a link deleted by mistake, followed by what the row said; the reason reads beneath.
  'history.audit.linkDeleted': 'Link deleted',
  // Decision 8960: a Confidential file's release decided at the approval gate, on the record the file sits on.
  'history.audit.releaseApproved': 'Release approved',
  'history.audit.releaseRejected': 'Release rejected',
  'history.audit.releaseTo': 'to',
  // Decision 3071: a worker below the line's level assigned from the Dispatch Board with a reason.
  'history.audit.assignedPastBlock': 'Assigned past a block',
  'history.audit.assignedPastBlockReason': 'Why:',
  // Decision 1500: the positions and contact methods a deleted employment took with it, read after the reason.
  'history.audit.linkDeletedWith': 'Went with it:',
  'history.audit.pill': 'System',
  // 🔒 Decision 1953: a Clean verdict on a file of the record, read off the version, never an audit row.
  'history.fileScanned': '{file} scanned clean',
  'history.fileScannedDetail': 'Version {version}, by {scanner}',
  'history.scan.pill': 'Scan',
  // 🔒 Decision 7420: when a file on the record was encrypted, read off its version.
  'history.fileEncrypted': '{file} encrypted',
  'history.encrypt.pill': 'Encrypted',
  // 🔒 Decision 10840 (T1012): a message tagged to the Deal, its subject, the other party beneath and the time.
  'history.mail.pill': 'Email',
  'history.mail.to': 'To {party}',
  'history.mail.from': 'From {party}',
  'history.mail.noSubject': '(No Subject)',
  'history.audit.times': 'times',
  'history.audit.other': 'Activity recorded',
  'history.audit.overrideRead': 'Opened by somebody whose role can see every record',
  // Decision 5400: a Deal Pack made from the Deal, copied or downloaded.
  'history.audit.dealPackRead': 'Deal Pack made for Claude',
  'history.audit.customFieldChanged': 'Custom fields changed',
  'history.audit.hrDetailsRevealed': 'HR details were revealed',
  'history.audit.locationResolved': 'Merged into another address',
  'history.audit.addressSuiteMoved': 'Suite moved from the address to its site',
  'history.audit.addressVerifiedByUser': 'Address verified by hand',
  'history.audit.trashRestored': 'Restored after deletion',
  'history.audit.reminderSent': 'Reminder sent',
  'history.audit.packageDeleted': 'Package deleted',
  'history.audit.certificateRemoved': 'Certificate removed',
  'history.audit.certificateMoved': 'Certificate moved',
  'history.audit.heldCreditApplied': 'Held credit applied',
  'history.audit.heldCreditRefunded': 'Held credit refunded',
  'history.audit.approvalMailed': 'Approval request mailed',
  'history.audit.approvalReminderMailed': 'Approval reminder mailed',
  'history.audit.approvalRouted': 'Approval routed',
  'history.audit.approvalVia.editCleared': 'An edit moved its figures, so the hold was cleared until it is submitted again',
  'history.audit.itemRepriced': 'Repriced from a price list',
  'history.audit.itemRepricingOneOff': 'Repriced on its own',
  'history.audit.priceListAccepted': 'Price list accepted',
  'history.audit.itemAccountsChanged': 'Accounting codes changed',
  'history.audit.assetHierarchyChanged': 'Asset hierarchy changed',
  // Decision 1604: a field changed by Edit selected on the Items or Companies list.
  'history.audit.bulkEdited': 'Changed in a bulk edit',
  // Decision 5180: the record's line naming the texted card it was created from.
  'history.audit.textedCardTaken': 'Created from a texted card',
  // Decision 7700: the line a person's name corrected to title case reads, the name before and after beneath.
  'history.audit.personNameCorrected': 'Name corrected to title case',
  // Decision 10310 (T884): the two rows an approved Bill's History draws when its order moves underneath it, and the sentence beneath each.
  'history.audit.billPurchaseOrderChanged': 'Purchase order changed after approval',
  'history.audit.billReceivedShort': 'Received short of the bill',
  'bill.afterApproval.lineMoved': '{order}, {line}: {moves}',
  'bill.afterApproval.quantityMoved': 'quantity {from} to {to}',
  'bill.afterApproval.priceMoved': 'price {from} to {to}',
  'bill.afterApproval.receivedShort': '{order}, {line}: billed {billed}, received {received}, {short} short',
  // Decision 10310: the open-2050 report's two kinds of row, read in its Row column.
  'reports.openClearing.openBalance': 'Open balance',
  'reports.openClearing.receivedShort': 'Received short of the bill',
  // T3543: the open-2050 report's column headings, with the Row and Short of the Bill columns that decision 10310 added.
  'reports.openClearing.column.vendor': 'Vendor',
  'reports.openClearing.column.item': 'Item',
  'reports.openClearing.column.row': 'Row',
  'reports.openClearing.column.balance': 'Open Balance',
  'reports.openClearing.column.short': 'Short of the Bill',
  'reports.openClearing.column.oldest': 'Oldest Posting',
  'reports.openClearing.column.documents': 'Documents',
  'history.audit.bulkEditedNobody': 'nobody',
  'history.audit.bulkEditedNoImage': 'no image',
  // Decision 7790 (T3157): an attribute value set in a bulk edit that had none before reads as not set.
  'history.audit.bulkEditedNoAttribute': 'not set',
  // Decision 8780 (T3275): a supplier added in a bulk edit reads none before it.
  'history.audit.bulkEditedNoSupplier': 'none',
  // Piece 26, decision 1221: an invoice issued with its commission unpriced, and priced once a rate was approved, on the invoice and its Deal.
  'history.audit.commissionUnpriced': 'Invoice {invoice} issued with its commission unpriced',
  'history.audit.commissionPriced': 'Commission on invoice {invoice} priced',
  // Piece 18, decision 1301: a change to a Deal's contacts, team, authority list, lists, Budget or Timing, as its History reads it.
  'history.audit.dealContactAdded': '{person} was added to the contacts',
  'history.audit.dealContactEnded': '{person} was ended as a contact',
  'history.audit.dealContactMadePrimary': '{person} was made the Primary contact',
  'history.audit.dealTeamAdded': '{person} was added to the Deal Team as {role}',
  'history.audit.dealTeamEnded': '{person} was ended on the Deal Team',
  'history.audit.dealTeamRoleChanged': '{person}’s Deal Team role was changed to {role}',
  'history.audit.dealTeamShareChanged': '{person}’s commission share was changed',
  'history.audit.dealTeamRoleAndShareChanged': '{person}’s Deal Team role was changed to {role}, and their commission share was changed',
  'history.audit.dealAuthorityAdded': '{person} was added to Decision Making as {role}',
  'history.audit.dealAuthorityRoleChanged': '{person}’s part in Decision Making was changed to {role}',
  'history.audit.dealAuthorityNoteChanged': 'The Decision Making note on {person} was changed',
  'history.audit.dealAuthorityRemoved': '{person} was removed from Decision Making',
  'history.audit.dealBomAttached': 'The bill of materials {bom} was attached',
  'history.audit.dealBomDetached': 'The bill of materials {bom} was detached',
  'history.audit.dealBudgetSaved': 'The Budget was saved',
  'history.audit.dealTimingSaved': 'The Timing was saved',
  'history.audit.dealSomebody': 'Somebody',
  // Piece 18, decision 1351: the Deal's own field edits and stage moves, as its History reads them.
  'history.audit.dealNameChanged': 'The name was changed from {from} to {to}',
  'history.audit.dealValueChanged': 'The value was changed from {from} to {to}',
  'history.audit.dealAccountChanged': 'The Account was changed from {from} to {to}',
  'history.audit.dealTypeChanged': 'The type was changed from {from} to {to}',
  'history.audit.dealCloseDateChanged': 'The expected close date was changed',
  'history.audit.dealEdited': 'The Deal was edited',
  'history.audit.dealStageMoved': 'The stage moved from {from} to {to}',
  'history.audit.dealStageWon': 'The Deal was marked Won',
  'history.audit.dealStageLost': 'The Deal was marked Lost, with the reason {reason}',
  'history.audit.dealStageLostNoReason': 'The Deal was marked Lost',
  'history.audit.dealStageReopened': 'The Deal was reopened at {to}',
  // Piece 18, decision 1482: Clone, as the History of the Deal cloned from reads it.
  'history.audit.dealCloned': 'The Deal was cloned as {deal}',
  // Piece 9, decision 1487: a Deal's creation, as its History reads it.
  'history.audit.dealCreated': 'The Deal was created by {person}',
  'history.audit.dealCreatedFromClone': 'The Deal was created by {person} as a clone of {deal}',
  'history.audit.and': 'and',
  // Piece 22, decision 1696 (T928): a Quote's own saves and its floor acknowledgements, as its History reads them.
  'history.audit.documentCreated': 'Created',
  'history.audit.documentEdited': 'Edited',
  'history.audit.documentFieldMoved': '{field} from {from} to {to}',
  'history.audit.documentFieldChanged': '{field} changed',
  'history.audit.documentFloorAcknowledged': 'Floor acknowledged on {floor}, {shortfall} short',
  'history.document.total': 'total',
  'history.document.lines': 'lines',
  'history.document.paymentTerms': 'payment terms',
  'history.document.quoteDate': 'quote date',
  'history.document.expiryDate': 'good-till date',
  'history.document.displayMode': 'display mode',
  'history.document.billTo': 'bill-to',
  'history.document.shipTo': 'ship-to',
  'history.document.deal': 'deal',
  'history.document.shipmentPreference': 'shipping',
  'history.document.customerNotes': 'customer notes',
  'history.document.termsOverride': 'terms',
  'history.document.customerPo': 'customer PO number',
  'history.document.documentFloor': 'the document floor',
  'employmentModal.stepOne': 'Add a person',
  'employmentModal.stepTwo': 'Their Employment Here',
  'employmentModal.next': 'Next',
  'employmentModal.back': 'Back',
  'employmentModal.firstName': 'First name',
  'employmentModal.lastName': 'Last name',
  'employmentModal.title': 'Title',
  'employmentModal.workEmail': 'Work email',
  'employmentModal.workPhone': 'Work phone',
  'employmentModal.extension': 'Extension',
  'employmentModal.stepOnePerson': 'Where they work',
  'employmentModal.company': 'Company',
  'employmentModal.pickCompany': 'Pick a company…',
  'employmentModal.hireInstead': 'Joining our own company? Somebody employed here is hired through HR, where the level is set beside the department.',
  'employmentModal.hireLink': 'Open the Hire Window',
  'employmentModal.thisCompany': 'This company',
  'employmentModal.thisPerson': 'This person',
  'employmentModal.duplicates': 'Somebody similar is already here:',
  'employmentModal.alreadyHere': 'This person already works at this company; open that employment instead of making a second one.',
  'duplicate.title': 'Something Similar Is Already Here',
  'duplicate.subtitle': 'Nothing was merged and nothing was blocked. Decide which record you want.',
  'duplicate.lead': 'Compare the two, then open the existing one or keep both.',
  'duplicate.personTitle': 'There Is a Person with the Same Name',
  'duplicate.personSubtitle': 'Is this the same person, or somebody else?',
  'duplicate.personLead': 'Two records for one person is a tidy-up. One record for two people is not, so say “Somebody else” unless you are sure.',
  'duplicate.thisOne': 'What you are adding',
  'duplicate.alreadyHere': 'Already on file',
  'duplicate.at': 'At',
  'duplicate.lastActive': 'Last active',
  'duplicate.openExisting': 'Open the existing one',
  'duplicate.keepBoth': 'Keep Both',
  'duplicate.thisPerson': 'This person',
  'duplicate.somebodyElse': 'Somebody else',
  'siteModal.stepOne': 'Which Building',
  'siteModal.stepTwo': 'The Occupancy',
  'siteModal.subject': 'A new site',
  'siteModal.address': 'Street address',
  'siteModal.locality': 'City',
  'siteModal.administrativeArea': 'State',
  'siteModal.postalCode': 'Postal code',
  'siteModal.suite': 'Suite',
  'siteModal.name': 'Site name',
  'siteModal.note': 'First note',
  'siteModal.nearMatch': 'A suite at this building looks close to that one without matching it.',
  'review.title': 'Address Needs Review',
  'review.subtitle': 'Buildings a provider could not confirm, buildings whose typed address matches another, and buildings whose address lines still carry a suite.',
  'review.city': 'City',
  'review.verification': 'Verification',
  'review.sites': 'Occupants',
  'review.duplicates': 'Matching buildings',
  'review.suitePart': 'Suite in the address lines',
  'review.suiteQueue': 'The address lines still carry {part}, and the one-time move did not give it to a site. Several sites may run here, the one running site may hold a suite already, none may run, or the move failed and the deploy log names the building. Set it in the Suite field of the site that occupies this building, then take it off the address.',
  'location.suiteRefusal': "{part} is a suite, and a suite belongs to the site that occupies the building. Type it in that site's Suite field and keep the building's address lines for the street and the building (Tower, Building, Wing).",
  'review.resolve': 'Keep the Other and Retire This One',
  'review.empty': 'Nothing needs review. A building lands here when a provider cannot confirm it or when its address matches another.',
  'calendar.title': 'Calendar',
  'calendar.subtitle': 'Linked to Locations and Contacts',
  'calendar.reload': 'Reload',
  'calendar.empty': 'Nothing is booked. Add an event from a record page.',
  'newRecord.displayName': 'Display name',
  'newRecord.isCustomer': 'Customer',
  'newRecord.isVendor': 'Vendor',
  'record.logContact': '+ Log Contact',
  'record.action.more': 'More ▾',
  'item.attribute.editTitle': 'Edit Attribute',
  'item.attribute.edit': 'Edit attribute',
  'item.attribute.remove': 'Remove Attribute',
  'item.attribute.removeTitle': 'Remove this attribute?',
  'item.attribute.removeBody': 'The value comes off the Item. The attribute type stays on its category and can be added again.',
  'item.attribute.removeConfirm': 'Remove',
  // 🔒 The Item page's Transactions tile (T1254; decision 1940): every line naming the Item, newest first.
  'itemTransactions.legend': 'Show Transactions',
  'itemTransactions.filter.all': 'All',
  'itemTransactions.filter.sales': 'Sales',
  'itemTransactions.filter.purchases': 'Purchases',
  'itemTransactions.filter.stock': 'Stock',
  'itemTransactions.count': '{shown} of {total} transactions',
  'itemTransactions.empty': 'Nothing names this Item yet.',
  'itemTransactions.emptyFilter': 'No transactions of this kind.',
  'itemTransactions.showMore': 'Show More',
  'itemTransactions.column.date': 'Date',
  'itemTransactions.column.document': 'Document',
  'itemTransactions.column.account': 'Account',
  'itemTransactions.column.quantity': 'Qty',
  'itemTransactions.column.unitPrice': 'Unit Price',
  'itemTransactions.column.status': 'Status',
  'itemTransactions.noDate': 'No date',
  'itemTransactions.withheld': 'Withheld',
  'itemTransactions.moreFailed': 'The next transactions did not load. Try Show more again.',
  'itemTransactions.inTransit': 'In transit',
  'itemTransactions.reason.receipt': 'Receipt',
  'itemTransactions.reason.shipment': 'Shipment',
  'itemTransactions.reason.consumption': 'Fitted on a Job',
  'itemTransactions.reason.consumptionReturn': 'Returned from a Job',
  'itemTransactions.reason.creditReturn': 'Customer Return',
  'itemTransactions.reason.creditReturnDropShip': 'Customer Return, Drop Ship',
  'itemTransactions.reason.transfer': 'Transfer',
  'itemTransactions.reason.adjustment': 'Count Adjustment',
  'itemTransactions.reason.issue': 'Issued',
  'itemTransactions.reason.issueReturn': 'Issue Returned',
  'record.noteText': 'What happened',
  'record.record': 'Record',
  'record.tile.checkOut': 'Check out',
  'record.tile.end': 'End',
  'deceased.title': 'Record as Deceased',
  'deceased.notify': 'This will notify',
  'deceased.notifyNobody': 'This will notify nobody, because nobody inside the company has a recorded interaction with this person, and',
  'deceased.person': 'person and',
  'deceased.people': 'people and',
  'deceased.end': 'end',
  'deceased.employment': 'employment',
  'deceased.employments': 'employments',
  'deceased.confirm': 'Save as Deceased',
  'companyDelete.action': 'Delete company',
  'companyDelete.company': 'the Company',
  'companyDelete.recoverable': 'The Company goes to the trash and can be restored from Settings.',
  'companyDelete.site': 'site',
  'companyDelete.sites': 'sites',
  'companyDelete.and': 'and',
  'companyDelete.employment': 'employment',
  'companyDelete.employments': 'employments',
  'companyDelete.endToday': 'end today and stay ended, each reopened separately if that was not intended.',
  'companyDelete.openDeal': 'open Deal stays',
  'companyDelete.openDeals': 'open Deals stay',
  'companyDelete.dealsStay': 'as they are.',
  'companyDelete.previewFailed': 'The linked sites, employments and Deals could not be counted, so Delete is not offered.',
  'trash.companyNote': 'Employments and sites the delete ended stay ended; reopen each separately if that was not intended.',
  // Decision 7710 (T3140): the Person's Delete, beside the Company's.
  'personDelete.action': 'Delete person',
  'personDelete.person': 'the Person',
  'personDelete.recoverable': 'The Person goes to the trash and can be restored from Settings.',
  'personDelete.employmentEnds': 'employment ends today, or on its own first day if that is later, and stays ended; reopen it separately if that was not intended.',
  'personDelete.employmentsEnd': 'employments end today, or on their own first day if that is later, and stay ended; reopen each separately if that was not intended.',
  'personDelete.previewFailed': 'The employments and logins that would end could not be counted, so Delete is not offered.',
  'personDelete.loginEnds': 'login ends with its employment.',
  'personDelete.loginsEnd': 'logins end with their employments.',
  'personDelete.noLogin': 'No login ends.',
  'personDelete.approval': 'open approval',
  'personDelete.approvals': 'open approvals',
  'personDelete.assignment': 'work-order assignment',
  'personDelete.assignments': 'work-order assignments',
  'personDelete.stay': 'stay as they are, and every document and note keeps naming the Person.',
  'personDelete.and': 'and',
  'personDelete.refusalLabel': 'Delete is not offered:',
  'personDelete.openDeal': 'open Deal',
  'personDelete.openDeals': 'open Deals',
  'personDelete.dealsHint': '{name} owns {count}. Choose who takes them before deleting; a Won or Lost Deal keeps its owner.',
  'personDelete.successor': 'Successor',
  'personDelete.successorEntity': 'person employed here',
  'personDelete.successorPick': 'Choose a successor for the open Deals first.',
  'personDelete.dealsGo': 'go to {name}; a Won or Lost Deal keeps its owner.',
  // 🔒 The Person delete's refusals and the hand-on's reason (T3461), sentences the Edit window and the audit trail show.
  'document.line.unitInactive': 'Line {line}: {unit} is not an active unit; choose another.',
  'personDelete.refuse.notFound': 'No person {id} was found.',
  'personDelete.refuse.signedIn': '{name} is the person signed in, so this delete is refused; ask another person to delete them.',
  'personDelete.refuse.breakGlass': '{name} holds the break-glass login, which cannot be deleted.',
  'personDelete.refuse.root': '{name} is the root of the Organization\'s chart; name another root in Settings before deleting them.',
  'personDelete.refuse.onlyAdmin': '{name} is the only administrator; give the Admin role to another person before deleting them.',
  'personDelete.refuse.approverOne': '{name} is named as the approver for {types} while 1 document awaits a decision; hand the approval to another person before deleting them.',
  'personDelete.refuse.approverMany': '{name} is named as the approver for {types} while {count} documents await a decision; hand the approval to another person before deleting them.',
  'personDelete.refuse.dealsOne': '{name} owns 1 open Deal, the first \u201c{first}\u201d; choose a successor to take them before deleting {name}.',
  'personDelete.refuse.dealsMany': '{name} owns {count} open Deals, the first \u201c{first}\u201d; choose a successor to take them before deleting {name}.',
  'personDelete.refuse.successorElsewhere': 'The successor for {name}\'s open Deals is somebody employed here today; choose another person.',
  'personDelete.refuse.successorSelf': '{name} cannot succeed themselves; choose another person to take their open Deals.',
  'personDelete.refuse.roleAbsent': 'The seeded {role} team role is absent; run the install.',
  'personDelete.handedReason': 'Handed to {successor} when {name} was deleted.',
  'trash.personNote': 'Employments the delete ended stay ended and each login stays off; reopen each separately if that was not intended.',
  'trash.heldNote': 'Restore writes back the rows its deletion took, the Work Orders a Deal took with it included.',
  'trash.visitNote': 'Restore writes back the visit with its time, notes and photos, on its job.',
  'trash.jobNote': 'The invoices its deletion voided stay Void and the entries it reversed stay reversed; raise them again by hand if the job is restored.',
  'history.audit.personDeleted': 'Deleted',
  // 🔒 Decision 12700: a Deal or a Work Order deleted reads its name and what went with it beneath, on the record and on its account.
  'history.audit.dealDeleted': 'Deal deleted',
  'history.audit.workOrderDeleted': 'Work Order deleted',
  'history.audit.visitDeleted': 'Visit deleted',
  'history.audit.confidentialityRoleGranted': 'Confidentiality role granted',
  'history.audit.confidentialityRoleEnded': 'Confidentiality role ended',
  'deceased.previewFailed': 'The people to notify could not be counted, so the save did not run.',
  'record.tile.remove': 'Remove',
  'record.tile.removeTag': 'Remove',
  'record.tile.open': 'Open',
  // A row's own reach, drawn beside its value: the address a maps search opens, the address a message goes to, the line a dialer calls (2026-09-14).
  'record.tile.openInMaps': 'Open in Maps',
  'record.tile.emailAddress': 'Send an email to this address',
  'record.tile.callNumber': 'Call this number',
  // A person row with more than one line opens the others behind a chevron beside its Call or its Email (T214; decision 0692).
  'record.tile.chooseNumber': 'Choose which number to call',
  'record.tile.chooseAddress': 'Choose which address to write to',
  'record.tile.present': 'Present',
  // 🔒 T3097 (decision 7480): the Employments tile as a timeline, a title the position never named, and the list's name.
  'jobTimeline.untitled': 'Employment',
  'jobTimeline.label': 'Employment history',
  'record.tile.editEmployment': 'Edit this employment',
  'employmentModal.editTitle': 'Edit This Employment',
  'employmentModal.editSubtitle': 'Each title held here with its days, and the work email and work phone on this job.',
  'employmentModal.saveEdit': 'Save',
  'employmentModal.clearedNote': 'Clearing an address or a number ends that row; the history it kept stays readable.',
  // Decision 6446: the window lists an employment's positions, oldest first, where its title changed.
  'employmentModal.positions': 'Titles held, oldest first',
  'employmentModal.startDate': 'Start date',
  'employmentModal.endDate': 'Last day',
  'employmentModal.endDateNote': 'The latest title’s last day is the day this job ended; correcting it does not reopen the job.',
  'employmentModal.positionsNote': 'The latest title is the one the person card shows, and the first title’s start is the day this job began.',
  'employmentModal.present': 'Present',
  'employmentModal.lastDayPresent': 'Last day: Present, while this job is open.',
  'employmentModal.addPosition': '+ Add Position',
  'employmentModal.removePosition': 'Remove Position',
  'employmentModal.endLead': 'Last day worked',
  'employmentModal.endHint': 'End employment closes this job on the day above.',
  'employmentModal.end': 'End Employment',
  'employmentModal.endTitle': 'End this employment?',
  'employmentModal.endConsequence': 'The job closes on the last day above and the row stays on the tile as history. Any login on this employment is deactivated, its sessions end and any invitation still waiting is revoked. Reopening the job is a new employment, not an undo.',
  'employmentModal.endConfirm': 'End the Employment',
  // Decision 11740 (T3608): the hand-off the End Employment window asks for when the job approves for any department.
  'employmentModal.handOffLead': 'Approvals Hand-off',
  'employmentModal.handOffHint': 'This person approves for the departments below. Name who takes each one from today; the person they report to is filled in where there is one.',
  'employmentModal.handOffAll': 'All Departments',
  'employmentModal.handOffFor': 'Successor for {department}',
  'employmentModal.handOffPick': 'Choose a successor',
  'employmentModal.handOffLoading': 'Reading the departments this job approves for…',
  'employmentModal.handOffFailed': 'The departments this job approves for did not load ({reason}), so ending now hands every one of them up the chain automatically.',
  'employmentModal.handOffEmpty': 'A box left empty is handed off by the chain automatically, to the first active person up it.',
  'occupancyModal.title': 'Edit This Occupancy',
  'occupancyModal.subtitle': 'The site\u2019s name and suite, and the days this company occupied the building.',
  'occupancyModal.name': 'Site name',
  'occupancyModal.suite': 'Suite',
  'occupancyModal.startDate': 'Start date',
  'occupancyModal.endDate': 'Last day',
  'occupancyModal.endDateNote': 'A correction to the day this occupancy ended; it does not reopen it.',
  'occupancyModal.endLead': 'Last day occupied',
  'occupancyModal.endHint': 'End occupancy closes it on the day above.',
  'occupancyModal.end': 'End Occupancy',
  'occupancyModal.endTitle': 'End this occupancy?',
  'occupancyModal.endConsequence': 'The occupancy closes on the last day above. The row stays on both tiles as history, and every document already pointing at this site keeps pointing at it. Occupying the building again is a new site, not an undo.',
  'occupancyModal.endConfirm': 'End the Occupancy',
  'occupancyModal.edit': 'Edit this occupancy',
  'contactModal.remove': 'Remove This Line',
  'contactModal.removeTitle': 'Remove this contact detail?',
  'contactModal.removeConsequence': 'The line stops showing on the tile from today. It is end-dated rather than deleted, so which number we called in March stays answerable.',
  'contactModal.removeConfirm': 'Remove the Line',
  'record.clearFlag': 'Clear this flag',
  'record.removeTag': 'Remove this tag',
  // Decision 2261: the pill's menu and the record's own note on its tag.
  'record.tagMenu': 'Tag menu',
  'record.tagNote.add': 'Add Note',
  'record.tagNote.edit': 'Edit Note',
  'record.tagNote.title': 'Tag Note',
  'record.tagNote.label': 'Note',
  'record.tagNote.save': 'Save Note',
  'record.tagNote.hint': 'The note belongs to this record\'s tag only. It shows when a person points at the pill. Up to 500 characters; a blank note removes it.',
  // The record banner's contact column and the person's photograph (decision 0616).
  'record.contactColumn': 'Contact details',
  'record.call': 'Call',
  'record.email': 'Email',
  'record.openWeb': 'Open',
  // 🔒 One human-English name per contact kind, Title Case, drawn bold at the head of the stacked contact row (decision 0623).
  'contact.kind.workEmail': 'Work Email',
  'contact.kind.personalEmail': 'Personal Email',
  'contact.kind.desk': 'Desk Phone',
  'contact.kind.mobile': 'Mobile',
  'contact.kind.fax': 'Fax',
  'contact.kind.other': 'Other',
  'contact.primary': 'Primary',
  'person.photo.title': 'Person photo',
  'person.photo.pick': 'Choose a photo',
  'person.photo.hint': 'The picture is cropped to a circle in the banner and stored no larger than 512 pixels a side, never enlarged.',
  'person.photo.save': 'Save photo',
  'person.photo.remove': 'Remove photo',
  'person.photo.none': 'No photo yet, so the banner draws the initials.',
  'person.photo.pickFirst': 'Choose a photo before saving.',
  'person.photo.alt': 'Person photo',
  // The company's own logo, in the same picture slot and through the same modal (T170).
  'company.termsSet.label': 'Terms Set',
  'company.termsSet.classDefault': 'Each Class Default',
  'company.termsSet.chosen': 'Client\u2019s Choice',
  'company.termsSet.none': 'Class Default',
  'company.termsSet.edit': 'Choose Terms Set',
  'company.termsSet.title': 'Terms Set for {name}',
  'company.termsSet.hint': 'Every document to this client prints the set chosen here. Class default prints each document\u2019s class default. A confidentiality addition prints either way.',
  'company.termsSet.field': 'Terms Set',
  'company.termsSet.useDefault': 'Class Default',
  'company.logo.title': 'Company logo',
  'company.logo.pick': 'Choose a logo',
  'company.logo.hint': 'The picture is cropped to a circle in the banner and stored no larger than 512 pixels a side, never enlarged.',
  'company.logo.save': 'Save logo',
  'company.logo.remove': 'Remove logo',
  'company.logo.none': 'No logo yet, so the banner draws the company glyph.',
  'company.logo.pickFirst': 'Choose a logo before saving.',
  'company.logo.alt': 'Company logo',
  // What the picture slot and its modal say whichever record they are on.
  'location.photo.title': 'Location photo',
  'location.photo.pick': 'Choose a photo',
  'location.photo.hint': 'The picture is cropped to a circle in the banner and stored no larger than 512 pixels a side, never enlarged.',
  'location.photo.save': 'Save photo',
  'location.photo.remove': 'Remove photo',
  'location.photo.none': 'No photo yet, so the banner draws the building glyph.',
  'location.photo.pickFirst': 'Choose a photo before saving.',
  'location.photo.alt': 'Location photo',
  'record.picture.open': 'Set the picture',
  // 🔨 The circle around it already carries the record's name, so the picture itself names what it is and not who.
  'record.picture.alt': 'Record picture',
  'record.picture.notImage': 'Pick a PNG, JPEG, WebP or GIF image.',
  'record.picture.storedAt': 'Stored at',
  // The drag-to-place and zoom step the window takes after a picture is picked (decision 0624).
  'record.picture.place': 'Place the picture',
  'record.picture.placeHint': 'Drag the picture to move it, and use the slider to zoom. Arrow keys nudge it; plus and minus zoom.',
  'record.picture.frame': 'Picture placement',
  'record.picture.zoom': 'Zoom',
  'record.tile.laterShort': 'Arrives with a later piece',
  'record.hireHint': 'Somebody employed here is hired through HR.',
  'tagModal.title': 'Add a Tag',
  'tagModal.subtitle': 'Existing labels are offered first; a new one is created on save.',
  'tagModal.label': 'Tag',
  'tagModal.save': 'Add Tag',
  'tagModal.suggestions': 'Labels already in use',
  'tagModal.preview': 'How the tag will look',
  'tagModal.reusedColour': 'That label already exists and keeps its own category and color.',
  // Decision 2260: a new label asks its category; decision 2261: the note on this record's tag.
  'tagModal.category': 'Category',
  'tagModal.fromCategory': 'A tag in a category takes the category\'s color and icon.',
  'tagModal.note': 'Note',
  'tagModal.noteHint': 'Optional. It belongs to this record\'s tag and shows when a person points at the pill.',
  // The eight tag swatches (piece 1). The name is announced rather than drawn,
  // so the choice is not carried by colour alone.
  'tag.colour.legend': 'Color',
  // Decision 2260: the category icon picker's legend, and what a tag with no category is called.
  'tag.icon.legend': 'Icon',
  'tag.noCategory': 'No category',
  'tag.other': 'Other',
  // T1581 (screen E): a list's Tags menu, by category, then by tag; ticks in one category mean either, across categories all.
  'tagFilter.button': 'Tags',
  'tagFilter.label': 'Filter by Tag',
  'tagFilter.any': 'Any',
  'tagFilter.or': 'or',
  'tagFilter.and': 'and',
  'tagFilter.clear': 'Clear All',
  'tagFilter.empty': 'No tag is on a record in this list yet.',
  'tagFilter.tagged': 'tagged',
  'tag.colour.slate': 'Slate',
  'tag.colour.blue': 'Blue',
  'tag.colour.teal': 'Teal',
  'tag.colour.green': 'Green',
  'tag.colour.amber': 'Amber',
  'tag.colour.orange': 'Orange',
  'tag.colour.red': 'Red',
  'tag.colour.purple': 'Purple',
  'contactModal.title': 'Add a Way to Reach Them',
  'contactModal.subtitle': 'One dated row per address or line, on the employment it belongs to.',
  'contactModal.kind': 'Kind',
  // 🔨 The row's own word for the line — work, mobile, desk — free text and never dialling instructions (T147, 2026-09-21).
  'contactModal.label': 'Label',
  'contactModal.labelPlaceholder': 'work, mobile, desk',
  'contactModal.value': 'Address or number',
  'contactModal.extension': 'Extension',
  'contactModal.primary': 'Primary for this kind',
  'contactModal.save': 'Add',
  'contactModal.editTitle': 'Correct This Contact Detail',
  'contactModal.editSubtitle': 'A correction to the same row; a line that changed is added as a new one.',
  'contactModal.saveEdit': 'Save',
  'contactModal.edit': 'Correct',
  'contactModal.noEmployment': 'This person holds no active employment, and a contact method belongs to one.',
  // 🔨 The line is the job's, never the person's (decision 0170), so the window says which job and offers the choice where there is one (decision 0593).
  'contactModal.job': 'Job this line belongs to',
  'contactModal.belongsTo': 'This line belongs to the job',
  'contactModal.jobHeadline': 'headline job',
  'phone.country': 'Country',
  'phone.number': 'Number',
  'phone.extension': 'Extension',
  'phone.extensionPlaceholder': 'ext.',
  'harness.sample.phoneOtherNote': 'Other takes the whole number after the plus, for a country the table does not seed.',
  'harness.sample.phoneExtensionNote': 'The extension is its own field, drawn only where the record has a column for it.',
  'vcard.action': 'Import vCard',
  'vcard.title': 'Import Contacts from a vCard',
  'vcard.subtitle': 'One file or many, one card or many, vCard 3.0 or 4.0. Nothing is written until Import.',
  'vcard.file': 'vCard files',
  'vcard.choose': 'Choose one or more .vcf files to see what they would do.',
  'vcard.reading': 'Reading the cards…',
  'vcard.empty': 'No cards were found in those files.',
  'vcard.column.name': 'Name',
  'vcard.column.company': 'Company',
  'vcard.column.email': 'Email',
  'vcard.column.phone': 'Phone',
  'vcard.column.address': 'Address',
  'vcard.column.action': 'Action',
  'vcard.column.reason': 'Why',
  'vcard.action.create': 'Create',
  'vcard.action.update': 'Update',
  'vcard.action.choose': 'Choose',
  'vcard.action.skip': 'Skip',
  'vcard.company.new': 'new',
  'vcard.company.own': 'our own',
  'vcard.import': 'Import',
  'vcard.importing': 'Importing…',
  'vcard.done': 'Done',
  'vcard.report.created': 'created',
  'vcard.report.updated': 'updated',
  'vcard.report.skipped': 'skipped',
  'vcard.report.companies': 'companies created',
  'vcard.report.sites': 'sites created',
  // The duplicate choice, the address and the multi-file preview (decision 0617).
  'vcard.choice.create': 'Create New',
  'vcard.choice.merge': 'Merge',
  'vcard.choice.done': 'The import has already run; the choice cannot be changed.',
  'vcard.choose.row': 'Choose Create new or Merge for this card.',
  'vcard.choose.waiting': 'card(s) match somebody already here and need an answer before anything is imported.',
  'vcard.choose.blocked': 'A card matches somebody already here. Choose Create new or Merge on every warned row first.',
  'vcard.match.at': 'at',
  'vcard.site.new': 'new site',
  'vcard.site.match': 'a site already here',
  'vcard.site.none': 'no company to hold it',
  'customFieldsModal.title': 'Custom Fields',
  'customFieldsModal.subtitle': 'The definitions that apply to this record, each validated by its kind on save.',
  'customFieldsModal.save': 'Save Values',
  'customFieldsModal.none': '—',
  'customFieldsModal.companyEntity': 'Company',
  'customFieldsModal.personEntity': 'Person',
  'customFieldsModal.refused': 'Nothing was saved. Fix the fields named below and save again.',
  'credit.limitIsDefault': '(default)',
  // Decision 3280 (T2269): the basis in words, and the warning an order against an account over its limit or on hold carries.
  'credit.basis.receivablesOnly': 'open invoices only',
  'credit.basis.receivablesPlusOpenOrders': 'open invoices and open sales orders',
  'credit.warning.hold': '{company} is on Credit Hold.',
  'credit.warning.over': '{who} is over its credit limit: {exposure} owed against a limit of {limit}, counting {basis}.',
  'credit.warning.itToo': 'It',
  'credit.warning.status': 'Its credit status is {status}.',
  'credit.warning.talk': 'Have the talk with the customer before this order goes further.',
  // Decision 3280: the Account standing tile's Exposure row past the limit.
  'credit.overLimit': 'Over the limit',
  'creditModal.title': 'Account Standing',
  'creditModal.subtitle': 'Our terms with this account: how they pay, how much credit they carry and whether they are on hold.',
  'creditModal.paymentTerms': 'Payment terms',
  'creditModal.creditLimit': 'Credit limit',
  'creditModal.creditStatus': 'Credit status',
  'creditModal.usesDefault': 'Leave blank to use the organization default,',
  'creditModal.usesDefaultUnknown': 'Leave blank to use the organization default.',
  'creditModal.holdNotice': 'Hold blocks new sales orders and new shipments. It never blocks fire, life-safety or warranty work, and only a person can set it.',
  'creditModal.overLimitNotice': 'A credit limit only ever warns. Going over it rides the approval a document already needs and blocks nothing on its own.',
  'creditModal.save': 'Save',
  'uploadModal.recordSubtitle': 'The file is tagged to this record and shows in its Files tile.',
  'checkIn.refused': 'The check-in was refused:',
  'checkIn.noEmployment': 'A check-in is written against your own employment, and the frame names none for you.',
  // T112: a web check-in asks the browser where you are, and a refusal checks in without it.
  'checkIn.noCoordinates': 'Checked in without your location, so this visit shows no distance from the site.',
  'shell.bannerKinds': 'Record classifications',
  'shell.dashboard.welcome': 'Welcome back,',
  'shell.arrangementNotSaved': 'Your dashboard arrangement could not be saved; it will look as before on the next visit.',
  'shell.dashboard.tiles': 'Add or Remove Tiles',
  'shell.dashboard.tilesTitle': 'Dashboard Tiles',
  'shell.dashboard.tilesSubtitle': 'Tick a tile to show it on your dashboard; untick it to remove it. Nobody else’s dashboard changes.',
  'shell.dashboard.notBuilt': 'not built yet',
  'shell.dashboard.allHidden': 'Every tile is removed. Open Add or remove tiles to bring one back.',
  // T725: a role carrying no tiles, an outside login's among them, is told so rather than told it removed them.
  'shell.dashboard.noTiles': 'This role has no dashboard tiles.',
  'edit.title': 'Edit',
  'edit.save': 'Save Changes',
  'edit.required': 'These are required:',
  // T2248: a change request whose asked-for fields are not on the form builds nothing to save, and says so instead of closing.
  'edit.requestNotApplied': 'The request could not be applied. None of the fields it asks about can be changed on this form.',
  // 🔒 Decision 3082 (T2246): a sealed number reads on file or not, and Change opens the box for a new one; the held number is never shown.
  'records.sealed.onFile': 'On file',
  'records.sealed.notRecorded': 'Not recorded',
  'records.sealed.change': 'Change',
  // 🔒 Decision 7890: a vendor's Tax ID is the W-9 profile's number, so the Edit window points at the tile that changes it.
  'records.sealed.onVendorTile': 'A vendor\'s Tax ID is changed on its Vendor tile, from the W-9.',
  'siteModal.company': 'Company',
  'siteModal.needsCompany': 'A site names the company occupying the building; choose one first.',
  'siteModal.existing': 'Existing building',
  'siteModal.orNew': 'Or enter a building that is not on file yet:',
  'siteModal.picked': 'The site occupies the building you picked.',
  'siteModal.typeInstead': 'Type a New One Instead',
  // Piece 18: the three Settings screens of the Deal.
  'settings.deals.quickRules': 'Quick Deal Rules',
  'settings.deals.quickRulesHint': 'A Quick Deal above the threshold requires approval rather than being blocked; the window is how long an owner’s action waits before it escalates to their manager.',
  // T3744: Discount Scope on Settings › CRM › Pipeline Rules (decision 0097).
  'settings.deals.discountScopeTitle': 'Discount Scope',
  'settings.deals.discountScope': 'Discount applies on',
  'settings.deals.discountScopeHint': 'Footer takes one discount for the whole document. Line adds a Discount column to every line of a Quote, Sales Order and Invoice. Documents already saved keep the discount they carry.',
  'settings.deals.discountFooter': 'The footer',
  'settings.deals.discountLine': 'Each line',
  'settings.deals.threshold': 'Quick Deal approval threshold ($)',
  'settings.deals.thresholdHint': 'Blank holds nothing for approval. Set it from the report on real Quick Deal values by rep.',
  'settings.deals.escalationWindow': 'Owner-absence escalation window (hours)',
  'settings.deals.pricingDefaults': 'Pricing Defaults',
  'settings.deals.pricingHint': 'When a vendor’s price is too old to quote from, and what to do on an urgent deal instead. An older price sends the deal for a fresh quote; an urgent deal raises it by the rate. A category can set its own numbers; a blank uses the organization’s, shown in gray.',
  'settings.deals.stalenessDays': 'Price-staleness window (days)',
  'settings.deals.projectionRate': 'Cost projection rate (% per year)',
  'settings.deals.categoryGrid': 'Category overrides',
  'settings.deals.windowColumn': 'Window (days)',
  'settings.deals.rateColumn': 'Rate (%)',
  'settings.deals.default': 'default',
  'settings.deals.wholeDays': 'the staleness window is a whole number of days',
  'settings.deals.stageActions': 'Stage Actions',
  'settings.deals.stageActionsHint': 'When a Deal reaches a stage or ticks a sub-stage, for one deal type or any, create a to-do for a role or notify a role. A rule fires once per Deal, never for a sub-stage marked not applicable, and every save is audited.',
  'settings.deals.when': 'When a',
  'settings.deals.deal': 'Deal',
  'settings.deals.anyType': 'Deal of any type',
  'settings.deals.reaches': 'reaches',
  'settings.deals.ticks': 'ticks',
  'settings.deals.createToDo': 'create a to-do for',
  'settings.deals.notify': 'notify',
  'settings.deals.dueIn': 'due in',
  'settings.deals.businessDays': 'business days',
  'settings.deals.fired': 'Fired',
  'settings.deals.editRule': 'Edit',
  'settings.deals.ruleNotSaved': 'That rule was not saved.',
  'settings.deals.times': 'time(s)',
  'settings.deals.active': 'Active',
  'settings.deals.noRules': 'No rules yet.',
  'settings.deals.newRule': '+ Add Rule',
  'settings.deals.stage': 'Stage',
  'settings.deals.subStage': 'Sub-stage',
  'settings.deals.anySubStage': 'On reaching the stage',
  'settings.deals.dealType': 'Deal type',
  'settings.deals.any': 'Any',
  'settings.deals.action': 'Action',
  'settings.deals.role': 'Role',
  'settings.deals.needsRole': 'A rule targets a role; choose one.',
  'settings.deals.title': 'Title',
  'settings.deals.titleHint': 'The title takes {deal}, {customer} and {value}; nothing else is a token.',
  'settings.deals.dueOffset': 'Due offset (business days)',
  'settings.deals.saveRule': 'Add a Rule',
  'dev.pieceEighteen': 'Deal, BOM and Quote lines',
  'dev.page.deals.lookFor': 'Each rule reads as one sentence with its Active switch; a title with a token other than {deal}, {customer} or {value} is refused at save. Pricing defaults shows 90 in gray beside a blank category window and refuses 0.',
  // Piece 19: the Deal page, its progress panel, its StageCard, its dialogs and the board.
  'deal.progress.now': 'Now',
  'deal.progress.urgent': 'Urgent',
  'deal.progress.board': 'Pipeline board',
  'deal.progress.na': 'N/A',
  'deal.progress.due': 'due',
  'deal.progress.panel': 'Deal progress',
  'deal.progress.stageOf': 'Stage',
  'lead.progress.panel': 'Lead progress',
  'record.panel.collapse': 'Collapse the progress panel',
  'record.panel.expand': 'Expand the progress panel',
  'deal.progress.escalates': 'Past threshold: added to the owner’s To-Do list and flagged to their manager.',
  // The floor's working, off the page since 2026-09-14 and offered to a super admin alone.
  'deal.floor.working': 'Show floor working',
  'deal.floor.workingHide': 'Hide floor working',
  'deal.floor.workingLoading': 'Reading how the floor was calculated…',
  'deal.floor.workingFailed': 'The floor working could not be read.',
  'deal.card.bant': 'BANT checklist',
  'deal.card.mark': 'Mark',
  'deal.card.complete': 'Complete',
  'deal.card.notApplicable': 'Mark N/A',
  'deal.card.applicable': 'Applies',
  'deal.card.notes': 'Notes',
  'deal.card.due': 'Due',
  'deal.card.save': 'Save',
  'deal.card.done': 'done',
  'deal.card.linkQuote': 'Open the Quote',
  'deal.card.linkSalesOrder': 'Open the Sales Order',
  /** 🔒 A stage card with nothing recorded folds to its name and these words; an empty step reads Not recorded (decision 6702). */
  'deal.card.nothingRecorded': 'nothing recorded yet',
  'deal.card.notRecorded': 'Not recorded',
  'deal.card.systemDriven': 'system-driven, a side effect of a real document',
  'deal.card.blocked': 'Not complete:',
  'deal.card.decision': 'The prospect decides: Won or Lost. Days here count from when the Deal reached Decision.',
  'deal.card.lostNote': 'This Deal is Lost. Reopen it to a stage to work it again; the loss stays on record.',
  'deal.card.back': 'Back to',
  'deal.card.earlier': 'completed; still editable',
  'deal.card.show': 'Show',
  // Decision 6001 (T2778): Set Meeting on the Intent card, the ask on Mark Intent Complete, and the Authority row's quick buttons.
  'deal.meeting.set': 'Set Meeting',
  'deal.meeting.booked': 'Meeting set for',
  'deal.meeting.subject': 'Discovery Visit',
  'deal.meeting.askTitle': 'No Meeting Is Set',
  'deal.meeting.askBody': 'Intent is where the Discovery visit is set. Set the meeting now, or tick the box where it came up on the sales call.',
  'deal.meeting.salesCall': 'It came up on the sales call',
  'deal.meeting.complete': 'Mark Intent Complete',
  'deal.meeting.needSalesCall': 'Tick the box when the meeting came up on the sales call, or set the meeting first.',
  'deal.meeting.updatePhone': 'If this app does not show that box, update it from the store and try again.',
  'deal.documents.title': 'Documents',
  'deal.documents.none': 'No documents yet.',
  'deal.documents.noneOf': 'No',
  'deal.documents.or': 'or',
  'deal.documents.yet': 'yet.',
  'deal.card.of': 'of',
  'deal.card.completed': 'Completed',
  'deal.card.daysAtStage': 'days at this stage',
  'deal.card.oneDayAtStage': '1 day at this stage',
  'deal.card.bomOne': 'bill of materials',
  'deal.card.bomMany': 'bills of materials',
  'deal.card.rfqOne': 'RFQ',
  'deal.card.hide': 'Hide',
  'deal.card.bomLines': 'BOM lines',
  'deal.card.rfqs': 'RFQs',
  'deal.card.noBomLines': 'No lines yet; add a bill of materials below and type its lines on its own page.',
  'deal.card.noRfqs': 'No RFQ sent yet; Request for quotation below sends the first one.',
  'deal.card.boms': 'Bills of materials',
  'deal.card.upload': 'Upload the negotiated terms',
  'deal.card.uploadHint': 'Drop the file here or choose one; it is tagged to this Deal and shows in its Files tile.',
  'deal.card.decisionClock': 'At Decision for',
  'deal.card.days': 'days',
  'deal.card.age': 'Age',
  // 🔒 A stage-card row's tagged notes, under a chevron (decision 0725).
  // 🔒 Each Intent row's completion rule, one line under its name (T252; decision 0724).
  'deal.card.rule.budget': 'Done when a Target is saved.',
  'deal.card.rule.authority': 'Done when a person who decides is tagged, or when ticked. Mark Complete needs a note only when nobody is tagged.',
  'deal.card.rule.need': 'Done when ticked. Mark Complete also needs a note.',
  'deal.card.rule.timing': 'Done when an Expected quote is saved.',
  'deal.card.oneDay': '1 day',
  'deal.card.colSku': 'SKU',
  'deal.card.colName': 'Item',
  'deal.card.colQuantity': 'Qty',
  'deal.card.colUnit': 'Unit',
  'deal.card.colCost': 'Cost',
  'deal.card.colNumber': 'Number',
  'deal.card.colVendor': 'Vendor',
  'deal.card.colSent': 'Sent',
  'deal.card.colDue': 'Answer due',
  'deal.card.colStatus': 'Status',
  // The RFQ's own row on the Evaluation card, with its vendors folded under it (decision 0609).
  'deal.card.rfqAsked': 'asked',
  'deal.card.rfqAnswered': 'answered',
  'deal.card.showVendors': 'Show vendors',
  'deal.card.hideVendors': 'Hide vendors',
  'deal.card.colAnswered': 'Answered',
  'deal.card.noVendors': 'No vendor has been asked yet.',
  // 🔒 The Intent card's Authority row: the people who decide, each with the part they play and a note (decision 0618).
  'deal.authority.label': 'Who decides',
  'deal.authority.add': 'Add Person',
  'deal.authority.edit': 'Edit',
  'deal.authority.clear': 'Clear tag',
  'deal.authority.tag': 'Authority',
  'deal.authority.empty': 'Nobody named yet. Add the people on the customer’s side who decide, and what each of them does.',
  'deal.authority.title': 'Add to Authority',
  'deal.authority.editTitle': 'Edit Authority',
  'deal.authority.person': 'Person, from the customer',
  'deal.authority.entity': 'Person',
  'deal.authority.role': 'Part in the decision',
  'deal.authority.note': 'Note about this person',
  'deal.authority.noteHint': 'What this person does in the decision. The row’s own note above is the summary of the whole committee.',
  'deal.authority.needsPerson': 'Choose the person; an authority row names somebody.',
  'deal.authority.needsRole': 'Choose the part they play in the decision.',
  'deal.authority.noCandidates': 'Nobody left to offer: the customer’s people are either on this list already or not on the customer’s record yet. Add them to the customer first.',
  'deal.authority.left': 'no longer at the customer',
  // 🔒 The kind line's origin and source pills (decision 0722).
  'deal.classification.pipeline': 'Pipeline',
  'deal.classification.quickDeal': 'Quick Deal',
  'deal.classification.emergency': 'Emergency',
  'deal.classification.fromLead': 'From a lead',
  'deal.classification.direct': 'Direct',
  'deal.budget.target': 'Target',
  'deal.budget.maximum': 'Maximum',
  'deal.budget.save': 'Save Budget',
  'deal.budget.history': 'Budget history',
  'deal.budget.none': 'No budget saved yet.',
  'deal.budget.earlierNote': 'Note written before the budget had its own figures',
  'deal.budget.by': 'by',
  'deal.budget.unknownPerson': 'someone',
  'deal.timing.expectedQuote': 'Expected quote',
  'deal.timing.expectedStart': 'Expected start',
  'deal.timing.expectedFinish': 'Expected finish',
  'deal.timing.save': 'Save Dates',
  'deal.timing.none': 'No dates set yet.',
  'deal.timing.earlierNote': 'Note written before Timing had its own dates',
  'deal.timing.quoteShort': 'Quote',
  'deal.timing.startShort': 'Start',
  'deal.timing.finishShort': 'Finish',
  'deal.timing.withTime': 'Times of day',
  'deal.banner.timing': 'Timing',
  'deal.banner.value': 'Deal Value',
  // 🔒 The Deal value's source and the budget warning (decision 0726).
  'deal.banner.budgetTooLow': 'Budget Too Low',
  'deal.nextBanner.urgent': 'Urgent:',
  'deal.nextBanner.lead': 'The next action on this Deal is',
  'deal.nextBanner.budget': 'The next action on this Deal is to talk with the customer about the budget, because the Deal value is above the Budget Maximum.',
  'deal.nextBanner.due': 'due',
  'deal.nextBanner.wasDue': 'which was due',
  'deal.nextBanner.noDue': 'and it has no due date.',
  'deal.nextBanner.oneMore': 'One more action is open, and Deal Progress lists it.',
  'deal.nextBanner.moreOpen': 'more actions are open, and Deal Progress lists them.',
  'deal.nextBanner.two': 'Two',
  'deal.nextBanner.three': 'Three',
  'deal.nextBanner.four': 'Four',
  'deal.nextBanner.five': 'Five',
  'deal.nextBanner.six': 'Six',
  'deal.nextBanner.seven': 'Seven',
  'deal.nextBanner.eight': 'Eight',
  'deal.nextBanner.nine': 'Nine',
  'deal.nextAction.talkBudget': 'Talk with the customer about the budget',
  'deal.quote.draft': 'Draft Quote',
  // Piece 18, decisions 2290 and 2390: the Parts form New Deal draws for the Parts type.
  'deal.parts.formHint': 'A parts order saves open at Evaluation with a Draft Quote holding its lines. The Deal is won when the customer accepts that Quote.',
  'deal.parts.pickAccount': 'Choose the account first; its people and its sites follow.',
  'deal.parts.askedBy': 'Who asked',
  'deal.parts.askedByEntity': 'contact',
  'deal.parts.owner': 'Owner',
  'deal.parts.ownerEntity': 'person employed here',
  'deal.parts.ownerYou': 'You own this Deal unless you pick somebody else.',
  'deal.parts.expectedDate': 'Expected date',
  'deal.parts.expectedDateHint': 'The day the customer wants the parts. It can drive the sale, so ask for it.',
  'deal.parts.lines': 'Parts',
  'deal.parts.shipTo': 'Ship to',
  'deal.parts.customerPo': 'Customer PO number',
  'deal.parts.customerPoHint': 'The customer’s own purchase order number, where they gave one. It goes on the Quote and on the Sales Order.',
  'deal.parts.inTheirWords': 'In their words',
  'deal.parts.inTheirWordsHint': 'What the customer said they need. Left empty, the lines you add stand as the need.',
  'deal.parts.noLines': 'a parts order carries at least one part from the catalog',
  'deal.parts.freeTextLine': 'part {part} names no catalog Item; pick one in place of the typed line',
  'deal.parts.defaultName': 'Parts for {account}: {item}',
  'deal.parts.defaultNameMore': 'Parts for {account}: {item} and {count} more',
  'deal.parts.authorityNote': 'Asked for the parts when the order was taken.',
  'deal.parts.quoteBand': 'Quote',
  'deal.parts.quoteStep.draft': 'Draft',
  'deal.parts.quoteStep.sent': 'Sent',
  'deal.parts.quoteStep.accepted': 'Accepted',
  'deal.parts.quoteHint': 'The Deal is won when the customer accepts this Quote.',
  'deal.parts.quoteNone': 'No Quote stands on this Deal yet. Draft Quote writes one to add the parts to.',
  'deal.quote.latest': 'Newest quote',
  'deal.proposal.report': 'Proposal report',
  'deal.proposal.none': 'No proposal report yet. Make Report on the Discovery card writes it.',
  'deal.invoice.draft': 'Draft (no number yet)',
  'deal.invoice.unnumbered': 'Unnumbered invoice',
  'deal.action.won': 'Won',
  'deal.action.lost': 'Mark Lost',
  'deal.action.reopen': 'Reopen at Intent',
  // Piece 18, decision 1482: Clone a Deal, its window and the chip the new Deal carries.
  'deal.action.clone': 'Clone',
  // 🔒 Decision 12700 (T3820): Delete under More, for a Deal made by mistake that holds no document.
  'deal.action.delete': 'Delete Deal',
  'deal.delete.title': 'Delete Deal',
  'deal.delete.confirm': 'Delete',
  'deal.delete.reading': 'Reading what the Deal holds…',
  'deal.delete.lead': 'Delete {name}? It leaves every list and search, and History on its account names the deletion.',
  'deal.delete.goes': 'Its {notes}, {files} and {contacts} go with it, the files to the Trash.',
  'deal.delete.unread': 'What the Deal holds could not be read, so nothing is deleted.',
  'deal.delete.anyway': 'Delete Anyway',
  'deal.delete.anywayLead': 'As a super administrator you may delete it anyway, to the Trash: {subject}.',
  // Piece 19, decision 5400: the Deal Pack under the Deal's More, on the web and the phone.
  'deal.action.more': 'More ▾',
  'deal.pack.copy': 'Copy for Claude',
  'deal.pack.download': 'Download Deal Pack',
  'deal.pack.copied': 'The Deal Pack is copied. Paste it into Claude.',
  'deal.pack.copyFailed': 'The Deal Pack was not copied.',
  'deal.pack.downloadFailed': 'The Deal Pack was not downloaded.',
  'deal.clone.title': 'Clone Deal',
  'deal.clone.account': 'Account',
  'deal.clone.accountEntity': 'Company',
  'deal.clone.keepContacts': 'Keep contacts',
  'deal.clone.keepTeam': 'Keep team',
  'deal.clone.contactsElsewhere': 'Contacts stay behind: each one is employed at the Deal’s own Account, not at the one chosen.',
  'deal.clone.explain': 'The copy starts at Intent with copies of the bills of materials, priced again from today’s Items, the skills, the custom fields and the Need and Timing answers. Documents, notes, files, Budget and Authority are never copied.',
  'deal.clone.confirm': 'Clone',
  'deal.clone.copyOf': 'Copy of {name}',
  // The sentence a copied Need note opens with, naming the Deal it came from; the cloner authors the copy (decision 1482).
  'deal.clone.needCopiedFrom': 'Copied from {name}: ',
  'deal.clone.from': 'Cloned from {name}',
  'deal.action.earlierMonth': 'Earlier Month',
  'deal.action.board': 'Board',
  'files.release.button': 'Release',
  'deal.action.approve': 'Approve Quick Deal',
  'deal.action.reject': 'Reject Quick Deal',
  'deal.approval.rejected': 'Not booked as Quick: the Deal is back on the pipeline at Intent. Finish the full form.',
  'deal.gate.opened': 'Opened at the gate the card was dropped on; the stage moves only from its own button.',
  'deal.lost.title': 'Mark Deal Lost',
  'deal.lost.reason': 'Reason',
  'deal.lost.competitor': 'Competitor',
  'deal.lost.competitorEntity': 'Company',
  'deal.lost.detail': 'Detail',
  'deal.lost.needsReason': 'Choose the reason; a Deal at Lost with no reason is not a state.',
  'deal.lost.confirm': 'Mark Lost',
  'deal.lost.quotes': 'Open Quotes',
  'deal.lost.quotesHint': 'Each open Quote closes in the same save as the loss.',
  'deal.lost.quotesUnread': 'Open Quotes are not listed because this login cannot read Quotes; they stay as they are.',
  'deal.lost.closesDeclined': 'This will decline Quote {number} ({total}).',
  'deal.lost.closesVoid': 'This will void Quote {number} ({total}).',
  'deal.lost.closeAs': 'Close {number} As',
  'deal.lost.outcome.declined': 'Declined',
  'deal.lost.outcome.void': 'Void',
  'deal.team.title': 'Add Team Member',
  'deal.team.member': 'Person, by their employment',
  'deal.team.entity': 'Employment',
  'deal.team.role': 'Role',
  'deal.team.shareToggle': 'Commission share',
  'deal.team.share': 'Share of the owner’s commission (%)',
  'deal.team.shareHint': 'Off by default. A share splits the owner’s own commission for this Deal and never adds a second payout.',
  'deal.team.ownerRemainder': 'The owner keeps the remainder of the commission:',
  'deal.team.ownerRemainderHint': 'A split is set from the member’s own row: open a member with the pencil and turn on their commission share.',
  'deal.team.shareHidden': 'The commission share is not shown to your role, so it is left as it stands.',
  'deal.team.needsBoth': 'A team row names a person and a role.',
  'deal.team.edit': 'Edit team member',
  'deal.owner.action': 'Change Owner',
  'deal.owner.title': 'Change Owner',
  'deal.owner.member': 'New owner',
  'deal.owner.confirm': 'Make {name} the owner of this Deal? The team’s owner row moves to them.',
  'deal.owner.needsOne': 'Pick somebody employed here to take the Deal.',
  'deal.team.editTitle': 'Edit Team Member',
  'deal.contact.title': 'Add Contact',
  'deal.contact.person': 'Contact, by their employment',
  'deal.contact.primary': 'Primary contact',
  'deal.contact.atAccount': 'at the Account',
  'deal.contact.elsewhere': 'not linked to the Account',
  'deal.contact.needsPerson': 'Choose the contact.',
  'deal.bom.urgent': 'Urgent: project a stale price instead of sending it to RFQ',
  'deal.bom.exportPdf': 'Export PDF',
  'deal.bom.exportXlsx': 'Export Excel',
  'deal.bom.exportFailed': 'The bill of materials could not be exported.',
  'deal.bom.valueRaised': 'Deal value raised to',
  'deal.bom.valueRaisedReason': 'to meet the floor',
  'deal.bom.needsDividerName': 'Every group divider carries a name.',
  'deal.rfq.action': 'Request for Quotation',
  'deal.rfq.vendor': 'Vendor',
  'deal.rfq.vendorEntity': 'Vendor',
  'deal.rfq.contact': 'Send to',
  'deal.rfq.noContacts': 'This vendor has nobody with a work email on file; type the address.',
  'deal.rfq.typedAddress': 'Another address',
  'deal.rfq.email': 'Email address',
  'deal.rfq.lines': 'Lines to quote',
  'deal.rfq.suggested': 'Suggested for this vendor',
  'deal.rfq.tickAll': 'Tick All',
  'deal.rfq.tickNone': 'Clear',
  'deal.rfq.noLines': 'Tick at least one line.',
  'deal.rfq.dueOn': 'Responses due',
  'deal.rfq.message': 'Message to the vendor',
  'deal.rfq.attachments': 'The email carries the request as a PDF and as an Excel sheet the vendor fills in.',
  'deal.rfq.send': 'Send RFQ',
  'deal.rfq.needsVendor': 'Pick the vendor and where to send it.',
  'deal.rfq.badAddress': 'That is not an email address.',
  'deal.rfq.sent': 'sent to',
  'deal.rfq.sendFailed': 'was issued, and the email did not go; it is in the failure queue',
  'deal.rfq.emptyBom': 'Add lines to the bill of materials, or to the Quote where the Deal has none, before asking for quotes.',
  'deal.rfq.tile.sent': 'sent',
  'deal.rfq.tile.due': 'due',
  'deal.bom.needsItem': 'Every line names an Item; a free-text row has no place on a bill of materials.',
  'deal.bom.save': 'Save BOM',
  'deal.bom.discard': 'Discard Changes',
  'deal.bom.empty': 'No lines yet. Add a row and choose the Item.',
  // Decision 0862: the bill of materials' printed sheet.
  'bom.print.deal': 'Deal',
  'bom.print.printed': 'Printed',
  'bom.print.totalCost': 'Total cost',
  'deal.bom.saved': 'Bill of materials saved.',
  'deal.bom.source': 'Source',
  'deal.bom.source.hint': 'Buy puts the line on the purchase order; From Stock takes it off our shelf and picks it to the Deal.',
  'deal.bom.onHand': 'On hand',
  'deal.bom.stockSite': 'Shelf',
  'deal.bom.stockSite.none': 'No site holds it',
  'deal.bom.reserved': 'Reserved',
  'deal.bom.short': 'Short',
  'deal.bom.picked': 'Picked',
  'deal.bom.at': 'at',
  // Decision 3322: Pick to Deal, the window that issues a list's From Stock parts to the Deal.
  'deal.bom.pick.open': 'Pick to Deal',
  'deal.bom.pick.title': 'Pick to Deal',
  'deal.bom.pick.hint': 'Parts taken off our shelf for the Deal, from the list’s From Stock lines',
  'deal.bom.pick.to': 'Parts picked to',
  'deal.bom.pick.act': 'Pick Parts',
  'deal.bom.pick.quantity': 'Quantity to pick',
  'deal.bom.pick.of': 'of',
  'deal.bom.pick.none': 'No line of this list is taken from stock.',
  'deal.bom.pick.nothing': 'Type a quantity on at least one line.',
  'deal.bom.pick.nothingToPick': 'Nothing on this list is left to pick to the Deal.',
  'deal.bom.pick.done': 'Parts picked to the Deal.',
  'deal.bom.pick.removalRefused': 'This line has {picked} of {sku} picked to {deals}, so it is not removed; bring the parts back with Return Stock first.',
  'deal.bom.pick.removalRefused.theDeal': 'the Deal',
  'deal.bom.lost': 'A Lost Deal’s bill of materials is kept for reference and not edited.',
  // Decision 1466: a list locks with its Deal's outcome, and a costed change after the Quote went out owes a document.
  'deal.bom.done': 'Every Deal this bill of materials is on is Won or Lost, so it is kept for reference and not edited.',
  'deal.bom.won': 'Every Deal this bill of materials is on is Won or Lost, so it is kept for reference and not edited. Won: {deals}.',
  'deal.bom.costChange.title': 'This Save Owes a Document',
  'deal.bom.costChange.intro': 'The list’s cost moves after a Quote drawn from it went out, so the save makes what each Deal is owed:',
  'deal.bom.costChange.cost': 'Cost {before} → {after}',
  'deal.bom.costChange.quoteRevision': '{deal}: opens Quote {opens}, the revision after {standing}, for you to re-price.',
  'deal.bom.costChange.refused': '{deal}: {sentence}',
  'deal.bom.costChange.refusedHint': 'A document this save owes cannot be made, so the save does not go through as it stands.',
  'deal.bom.costChange.confirm': 'Save and make them',
  'deal.bom.costChange.override': 'Save without the document',
  'deal.bom.costChange.overrideLabel': 'Reason to save without the document',
  'deal.bom.costChange.overrideHint': 'You hold the override. Type why, and the save makes no document; the reason and your name stay on the list.',
  'deal.bom.costChange.opened': 'Opened Quote',
  'deal.bom.costChange.overridden': 'Saved without a document; the reason stays on the list.',
  'deal.bom.costChange.line': 'The cost changed from {before} to {after} after {standing} went out, by {actor} on {date}.',
  'deal.bom.costChange.lineNoCost': 'The cost changed after {standing} went out, by {actor} on {date}.',
  'deal.bom.costChange.lineMade': 'It made {documents}.',
  'deal.bom.costChange.lineOverride': 'Saved without a document: {reason}',
  // The Deal's bills of materials, each on its own page (decision 0565).
  'deal.boms.add': 'Add BOM',
  'deal.boms.name': 'Name',
  // 🔨 The list says what it is for in the builder's own words (decision 0609).
  'deal.boms.description': 'Description',
  'deal.boms.descriptionPlaceholder': 'What this list is for',
  'deal.boms.saveDetails': 'Save',
  'deal.boms.editDetails': 'Edit name and description',
  'deal.boms.detailsSaved': 'The bill of materials was saved.',
  'deal.boms.noDescription': 'No description yet.',
  // The row's own fold: the list's table is under the row and opens from here (decision 0609).
  'deal.boms.showLines': 'Show lines',
  'deal.boms.hideLines': 'Hide lines',
  'deal.boms.needsName': 'A bill of materials carries a name.',
  'deal.boms.added': 'Bill of materials added.',
  'deal.boms.empty': 'No bills of materials yet. Add one and give it a name.',
  'deal.boms.lines': 'lines',
  'deal.boms.oneLine': '1 line',
  'deal.boms.onDeal': 'Linked to Deal',
  'deal.boms.notAttached': 'Not attached to a Deal',
  'deal.boms.moreDeals': '{name} and {n} more',
  'deal.boms.status': 'Status',
  'deal.boms.statusSaved': 'The status was saved.',
  'deal.boms.new': 'New Bill of Materials',
  'deal.boms.attachExisting': 'Attach Existing',
  'deal.boms.attachToDeal': 'Attach to a Deal',
  'deal.boms.pickList': 'Bill of materials',
  'deal.boms.pickDeal': 'Deal',
  'deal.boms.entity': 'Bill of Materials',
  'deal.boms.dealEntity': 'Deal',
  'deal.boms.detach': 'Detach',
  'deal.boms.edit': 'Edit this list',
  'deal.boms.detached': 'The bill of materials was detached from this Deal.',
  'deal.boms.attached': 'The bill of materials was attached.',
  'deal.boms.attach': 'Attach',
  'deal.boms.pickFirst': 'Pick one from the list first.',
  'deal.boms.deal': 'Deal',
  'deal.boms.lineCount': 'Lines',
  'deal.boms.loadFailed': 'The bill of materials could not be read.',
  'deal.boms.doneEditing': 'Done',
  'files.release.title': 'Release File',
  'files.release.action': 'Release',
  'files.release.reason': 'Reason',
  'files.release.needsTarget': 'Choose the company to release to.',
  'deal.release.noTargets': 'No company to release to yet: the Account and the contacts’ companies are the targets.',
  'files.release.done': 'Released to',
  // Decision 8960: a Confidential file's release waits for an approver, said before the press and after it.
  'files.release.waitsForApproval': 'This file is Confidential, so its release waits for approval. The company sees nothing until an approver says yes.',
  'files.release.sentForApproval': 'Sent for approval: the release to',
  // 🔒 Decision 10340: the release owner's sentence, one per rule that named the owner; the window and the server's refusal both draw it.
  'files.release.owner.dealOwner': 'Releasing this file is for the deal owner, {name}, or a manager above them on the reporting line.',
  'files.release.owner.accountRep': 'Releasing this file is for the account’s assigned rep, {name}, or a manager above them on the reporting line.',
  'files.release.owner.recordOwner': 'Releasing this file is for the person who created its record, {name}, or a manager above them on the reporting line.',
  'files.release.noTargets': 'This file has no company to release to. It releases only to the account it sits on, and none is on record.',
  'files.release.owner.capability': 'Nobody owns this file’s record, so releasing it is for a role holding “{capability}” ({key}).',
  'deal.board.title': 'Pipeline board',
  'deal.board.empty': 'No deals here.',
  'deal.board.emptyAction': 'Open the first opportunity with + New Deal.',
  'deal.board.days': 'days in stage',
  'deal.board.closes': 'Closes',
  'deal.new.typeHeading': 'What kind of Deal',
  'deal.new.tile.parts': 'Parts',
  'deal.new.tile.partsHint': 'Parts a customer asked for: the account, who asked and the parts. Saves with a Draft Quote.',
  'deal.new.tile.service': 'Service',
  'deal.new.tile.serviceHint': 'A service call: the need, the site and when. Emergency books the job at once.',
  'deal.new.tile.project': 'Project',
  'deal.new.tile.projectHint': 'A priced scope walked through the pipeline, with its bill of materials.',
  'deal.new.back': 'Back',
  'deal.new.openSaved': 'Open the Deal',
  'deal.new.textThenCarryOn': 'The Deal is already saved, so edits made now are not applied. Press Save to carry on to the Deal.',
  'deal.new.emergencyRefused': 'An emergency is booked from the Service form with Emergency turned on, which books its job in the same save.',
  'deal.board.partsRefused': 'A Parts deal goes from Evaluation to Won when its Quote is accepted, so it is not opened at Discovery, Negotiation or Decision.',
  'deal.board.dropHint': 'Drag a card to another column to open the Deal at that gate; the stage moves only from its own button.',
  'deal.edit.floorReason': 'Override reason',
  'deal.edit.floorHint': 'The value is below the floor. Give the reason to save it anyway; the override is recorded with your name.',
  'dev.pieceNineteen': 'Deal page — Deal Progress, StageCard and the document lists',
  'dev.page.deal.lookFor': 'Deal Progress with the NOW pill on the current stage and the red ring with STALE at threshold; the StageCard with Intent as BANT; the floor beside the value with its arithmetic; the board at /deals?view=board where a drop opens the gate and moves nothing.',
  // Piece 20: the Lead page, its queue, the follow-up view and the seven CRM settings screens.
  'lead.queue.all': 'All leads',
  'lead.queue.held': 'Held',
  'lead.queue.unrouted': 'In no territory',
  // Decision 5180: the texted-card offer on the Leads page.
  'lead.textedCard.title': 'Texted Cards',
  'lead.textedCard.from': 'Texted card from',
  'lead.textedCard.createLead': 'Create Lead',
  'lead.textedCard.createPerson': 'Create Person at Company',
  'lead.textedCard.dismiss': 'Dismiss',
  'lead.textedCard.personLead': 'The card was texted from this number. Choose the company this person works at.',
  'lead.textedCard.personCompany': 'The company came from the texting number or from the card. Change it if it is wrong.',
  'lead.textedCard.taken': 'Created from the texted card.',
  'lead.textedCard.dismissed': 'The texted card was dismissed.',
  'lead.textedCard.failed': 'The texted card could not be updated. Try again.',
  // Piece 10, decision 1486: the Companies list's four queues, one list read by kind.
  'company.queue.all': 'All companies',
  'company.queue.customers': 'Customers',
  'company.queue.vendors': 'Vendors',
  'company.queue.manufacturers': 'Manufacturers',
  'lead.stage.awareness': 'Awareness',
  'lead.stage.interest': 'Interest',
  'lead.action.callAttempt': 'Log Call Attempt',
  'lead.action.convertCompany': 'Convert to Company',
  'lead.action.notFit': 'Mark Not a Fit',
  'lead.action.convertDeal': 'Convert to Deal',
  'lead.action.hot': 'Mark Hot',
  'lead.action.release': 'Release',
  'lead.action.block': 'Block',
  'lead.action.redirect': 'Redirect',
  'lead.action.dispatch': 'Dispatch',
  'lead.action.more': 'More ▾',
  'lead.match.company': 'Suggested company:',
  'lead.match.person': 'Suggested person:',
  'lead.match.link': 'Link to This',
  'lead.match.createNew': 'Create New',
  'lead.card.notes': 'Why this is true',
  'lead.card.save': 'Save note',
  'lead.card.complete': 'Complete',
  'lead.card.blocked': 'Not yet:',
  'lead.card.derived': 'read from the Company’s NDA',
  'lead.card.satisfied': 'satisfied',
  'lead.card.negotiationOpen': 'Negotiation is the last stage; Convert to Deal once its items are met, or use the override after enough call attempts.',
  'lead.card.negotiationDone': 'Negotiation is complete; Convert to Deal opens the Deal at Intent.',
  'lead.card.back': 'Back to',
  'lead.card.ahead': 'takes input now; completing it still needs the stage before it complete',
  'lead.card.upload': 'Upload the signed file',
  'lead.card.uploadHint': 'Drop the file here or choose one; it is tagged to this Lead and shows in its Files tile.',
  'lead.card.removeFile': 'Remove',
  'lead.card.replaceFile': 'Replace',
  'lead.notFit.title': 'Mark Not a Fit',
  'lead.notFit.reason': 'Reason',
  'lead.notFit.needsReason': 'Give the reason: a lead discarded without one teaches the intake nothing.',
  'lead.notFit.confirm': 'Mark Not a Fit',
  'lead.convert.title': 'Convert to Deal',
  'lead.convert.name': 'Deal name',
  'lead.convert.type': 'Deal type',
  'lead.convert.value': 'Deal value',
  'lead.convert.overrideHint': 'Negotiation is not complete. The override is offered because enough call attempts are logged; give the reason and the unmet items are recorded.',
  'lead.convert.overrideReason': 'Override reason',
  'lead.convert.notOffered': 'Negotiation is not complete and the override is not offered yet; log more call attempts first.',
  'lead.convert.needsReason': 'The override needs a typed reason.',
  'lead.convert.confirm': 'Convert',
  'lead.redirect.title': 'Redirect to Another Territory',
  'lead.redirect.territory': 'Territory',
  'lead.redirect.general': 'General queue (no territory)',
  'lead.redirect.reason': 'Reason (optional)',
  'lead.redirect.confirm': 'Redirect',
  'lead.dispatch.title': 'Dispatch to a Salesperson',
  'lead.dispatch.rep': 'Salesperson',
  'lead.dispatch.repEntity': 'Employment',
  'lead.dispatch.needsRep': 'Pick the salesperson who works this lead.',
  'lead.dispatch.confirm': 'Dispatch',
  'lead.role.title': 'Add an Account Role',
  'lead.role.role': 'Role',
  'lead.role.person': 'Person (by employment)',
  'lead.role.salesPerson': 'Sales Person',
  'lead.role.needsRole': 'Pick the role.',
  'lead.role.needsPerson': 'Pick the person.',
  'lead.role.hint': 'Sales Person writes the account’s assigned rep; every other role is a dated label that grants no access.',
  'lead.role.confirm': 'Add Role',
  'lead.role.qualifying': 'Only people whose current position is this job role are offered:',
  'lead.role.noneQualify': 'Nobody’s current position holds this role’s job role, so nobody can be offered for it:',
  'settings.leads.qualifyingJobRole': 'Qualifying job role',
  'settings.leads.anyJobRole': 'Any job role',
  'nda.queue.title': 'NDAs in Review',
  'nda.queue.hint': 'Every agreement uploaded and not yet activated. Answer its questions in order; activation is its own step and names its effect first.',
  'nda.queue.empty': 'Nothing is waiting for review.',
  'nda.queue.upload': '+ Upload NDA',
  /** Decision 9861 (T3455): the queue's two views, and the Person page's agreement rows. */
  'nda.queue.view': 'Which Agreements',
  'nda.queue.view.review': 'In Review',
  'nda.queue.view.active': 'Active',
  'nda.queue.emptyActive': 'No agreement is active.',
  'person.agreement.label': 'Confidentiality Agreement',
  'person.agreement.from': 'from {date}',
  'nda.queue.col.counterparty': 'Counterparty',
  'nda.queue.col.scope': 'Scope',
  'nda.queue.col.status': 'Status',
  'nda.queue.col.effective': 'Effective',
  'nda.queue.col.blockers': 'Questions Left',
  'nda.queue.col.uploaded': 'Uploaded',
  'nda.cover.title': 'Accounts that require cover',
  'nda.cover.hint': 'Every company whose NDA requires our people to be covered before they open its documents, and who is covered today.',
  'nda.cover.empty': 'No account requires cover.',
  'nda.cover.covered': 'covered',
  'nda.modal.title': 'Upload NDA',
  'nda.modal.scope': 'What the agreement covers',
  'nda.modal.scopeCompany': 'Every Deal of the Company',
  'nda.modal.scopeDeal': 'One Deal',
  'nda.modal.scopePerson': 'One of our people (the personal NDA behind a Confidentiality Role)',
  'nda.modal.counterparty': 'Counterparty company',
  'nda.modal.counterpartyEntity': 'Company',
  'nda.modal.deal': 'Deal',
  'nda.modal.person': 'Person',
  'nda.modal.effective': 'Effective date',
  'nda.modal.expiry': 'Expiry date',
  'nda.modal.demanded': 'Demanded: the customer requires an NDA and no signed copy is on file yet',
  'nda.modal.demandedHint': 'A demand requires cover from today and enables no release. Attach the signed copy when it arrives.',
  'nda.modal.file': 'Signed copy (PDF)',
  'nda.modal.fileType': 'File type',
  'nda.modal.needsCounterparty': 'Name the counterparty company.',
  'nda.modal.needsDeal': 'Pick the Deal the agreement covers.',
  'nda.modal.needsPerson': 'Pick the person the agreement covers.',
  'nda.modal.needsEffective': 'Enter the effective date.',
  'nda.modal.needsFileType': 'Pick a file type for the signed copy.',
  'nda.modal.needsFile': 'Attach the signed copy, or mark the agreement Demanded.',
  'nda.modal.uploading': 'Uploading the signed copy…',
  'nda.review.title': 'NDA Review',
  'nda.review.hint': 'Eight questions, answered in order from the document. Six block activation; survival defaults to perpetual and return-or-destroy is optional.',
  'nda.review.answered': 'Answered',
  'nda.review.open': 'Open',
  'nda.review.yes': 'Yes',
  'nda.review.no': 'No',
  'nda.review.months': 'Months after expiry (blank is perpetual)',
  'nda.review.recipients': 'Other companies that may receive documents',
  'nda.review.recipientsHint': 'Add each company; the counterparty itself is always permitted.',
  'nda.review.addRecipient': 'Add',
  'nda.review.remove': 'Remove',
  'nda.review.activation': 'Activation',
  'nda.review.activationHint': 'Activation begins both effects at once. What it changes:',
  'nda.review.blocked': 'Questions still open:',
  'nda.review.activate': 'Activate',
  'nda.review.activated': 'The agreement is active.',
  'nda.review.revoke': 'Revoke',
  'nda.review.endObligation': 'End the Obligation',
  'nda.review.reason': 'Reason',
  'nda.review.signedCopy': 'Signed copy',
  'nda.review.noSignedCopy': 'No signed copy attached',
  'nda.review.effects': 'Effects',
  'nda.review.requiresCover': 'requires cover',
  'nda.review.enablesRelease': 'enables release',
  'nda.review.noEffects': 'none yet; the answers set them',
  /** 🔒 Decision 9680: an employment agreement's own answers, read on its card, and the line that says why it carries no questionnaire. */
  'nda.review.employment.hint': 'This is an employee’s agreement, written from its upload. Its answers below are the review, so it has no questionnaire.',
  'nda.review.employment.dateSigned': 'Date Signed',
  'nda.review.employment.confidentiality': 'Confidentiality Clause',
  'nda.review.employment.nonSolicit': 'Non-Solicit',
  'nda.review.employment.nonSolicitMonths': '{months} months after the employment ends',
  'nda.review.employment.returnOrDestroy': 'Returned or Destroyed',
  'nda.review.employment.unanswered': 'Not answered',
  /** 🔒 Decision 9680: the Cover Required page a refused file opens on, and its Request Cover. */
  'cover.title': 'Cover Required',
  'cover.account': 'Account',
  'cover.person': 'Signed In As',
  'cover.file': 'File',
  'cover.request': 'Request Cover',
  'cover.requested': 'HR has been asked to upload the employment agreement for {person}.',
  'cover.requestedNone': 'Nobody holds the HR pages permission, so no request was raised. Ask an administrator.',
  'cover.covered': 'You are covered for this file now. Open it again.',
  'cover.open': 'Open the File',
  /** 🔒 Decision 9680: the Employments tile's second act, an HR holder's upload of the employment forms. */
  'employment.documents': 'Employment Documents',
  'nda.review.back': 'Back to the Queue',
  // Decision 1517: the review is a wizard, one question a step, with a step rail and a ninth Activate step.
  'nda.review.step.counterparty': 'Counterparty',
  'nda.review.step.requiresCover': 'Cover required',
  'nda.review.step.enablesRelease': 'Release permitted',
  'nda.review.step.scope': 'Scope',
  'nda.review.step.dates': 'Dates',
  'nda.review.step.survival': 'Survival',
  'nda.review.step.permittedRecipients': 'Permitted recipients',
  'nda.review.step.returnOrDestroy': 'Return or destroy',
  'nda.review.step.activate': 'Activate',
  'nda.review.steps': 'Review steps',
  'nda.review.step': 'Step',
  'nda.review.of': 'of',
  'nda.review.question': 'Question',
  'nda.review.required': 'Required',
  'nda.review.optional': 'Optional',
  'nda.review.confirmNext': 'Confirm and Next',
  'nda.review.previous': 'Back',
  'nda.review.skip': 'Skip, It Is Optional',
  'nda.review.chooseFirst': 'Choose an answer first.',
  'nda.review.firstStep': 'This is the first question.',
  'nda.review.detected': 'Detected from the document:',
  'nda.review.detectedConfirm': 'Confirm it or correct it.',
  'nda.review.nothingDetected': 'Nothing was detected; read the answer from the signed copy.',
  'nda.review.confirmedAs': 'Confirmed:',
  'nda.review.perpetual': 'Perpetual',
  'nda.review.monthsAfter': 'months after expiry',
  'nda.review.noRecipients': 'The counterparty only',
  'nda.review.inactive': 'This agreement is no longer active, so nothing here can be changed.',
  'hygiene.title': 'Follow-Up View',
  'hygiene.runNow': 'Run the Nightly Pass Now',
  'hygiene.ran': 'Follow-up items raised:',
  'hygiene.empty': 'No customer sites yet; the view fills as sites are added.',
  'hygiene.col.company': 'Company',
  'hygiene.col.site': 'Site',
  'hygiene.col.rep': 'Rep',
  'hygiene.col.days': 'Days Since Contact',
  'hygiene.col.threshold': 'Threshold · Source · Class',
  'hygiene.col.state': 'State',
  'hygiene.col.suppress': 'Suppress',
  'hygiene.noRep': 'No rep',
  'hygiene.never': 'never',
  'hygiene.reason': 'Reason',
  'hygiene.suppress': 'Suppress',
  'hygiene.noSite.title': 'Companies with no site',
  'hygiene.noSite.hint': 'A data-quality row, not a contact row: the clock lives on the site, so add one to start it.',
  'hygiene.daysSinceCreated': 'days since created',
  'hygiene.addClock': '+ Add Clock',
  'hygiene.suppressed.title': 'Suppressed, with reasons',
  'hygiene.reassigned.title': 'Leads of a departed rep, not yet contacted by the new one',
  'hygiene.endedOn': 'employment ended',
  'hygiene.state.current': 'Current',
  'hygiene.state.stale': 'Stale',
  'hygiene.state.suppressed': 'Suppressed',
  'hygiene.state.deferred': 'Deferred (absence)',
  'hygiene.state.lapsed': 'Absence lapsed',
  'hygiene.state.reassigned': 'Reassigned, not yet contacted',
  'hygiene.state.noContact': 'Never contacted',
  'hygiene.absences.title': 'Absences',
  'hygiene.absences.hint': 'A contact who is away defers the follow-up until they are back. An absence read from an auto-reply waits here for somebody to confirm it.',
  'hygiene.absences.empty': 'No absence is on file.',
  'hygiene.addAbsence': '+ Add Absence',
  'hygiene.absence.kind.away': 'Away',
  'hygiene.absence.kind.extended': 'Extended',
  'hygiene.absence.kind.doNotContact': 'Do Not Contact',
  'hygiene.absence.confirm': 'Confirm',
  'hygiene.absence.unconfirmed': 'Awaiting Confirmation',
  'hygiene.absence.confirmed': 'Confirmed',
  'hygiene.absence.label.source': 'Source',
  'hygiene.absence.label.reason': 'Reason',
  'hygiene.absence.byHand': 'entered by hand',
  'hygiene.absence.detected': 'read from an auto-reply',
  'hygiene.absence.from': 'from',
  'hygiene.absence.to': 'back',
  'hygiene.absence.noReturn': 'no return date',
  'hygiene.absence.title': 'Add Absence',
  'hygiene.absence.person': 'Person',
  'hygiene.absence.personEntity': 'Person',
  'hygiene.absence.fromDate': 'From',
  'hygiene.absence.toDate': 'Expected Back',
  'hygiene.absence.reason': 'Reason',
  'hygiene.absence.needsPerson': 'Pick the person who is away.',
  'hygiene.absence.needsFrom': 'Say the day the absence starts.',
  'hygiene.absence.badDate': 'A date on an absence is a calendar day.',
  'hygiene.absence.endsBeforeStart': 'The expected return is before the day the absence starts.',
  'settings.leads.followUp': 'Follow-Up Rules',
  'settings.leads.followUpHint': 'How many days a customer site can go without contact before somebody is reminded to follow up, for each kind of relationship. A company’s own setting, or a tag, wins over these.',
  'settings.leads.customerDays': 'Customer (days)',
  'settings.leads.prospectDays': 'Prospect (days)',
  'settings.leads.vendorDays': 'Vendor-only (days)',
  'settings.leads.otherDays': 'Other (days)',
  'settings.leads.graceDays': 'Absence grace (days past the expected return)',
  'settings.leads.runHour': 'Nightly pass hour (0–23, Organization zone)',
  'settings.leads.overrideAttempts': 'Call attempts before the conversion override is offered',
  'settings.leads.overrideHint': 'Convert to Deal over an incomplete Negotiation is offered once this many call attempts are logged.',
  'settings.leads.routing': 'Website Lead Routing',
  'settings.leads.routingHint': 'How enquiries from your website’s form arrive. List the websites allowed to send them, choose the department whose head gets each new lead, and set the limits that keep floods and spam out.',
  'settings.leads.allowlist': 'Allowed origins, one per line',
  'settings.leads.allowlistHint': 'For example https://www.example.com. The list starts empty, so nothing is accepted until you add one.',
  'settings.leads.dispatchDepartment': 'Dispatch department',
  'settings.leads.perIp': 'Posts per source address per hour',
  'settings.leads.global': 'Posts across the endpoint per hour',
  'settings.leads.fieldCap': 'Bytes per field',
  'settings.leads.payloadCap': 'Bytes per post',
  'settings.leads.minimumFill': 'Minimum fill seconds (faster flags, never refuses)',
  'settings.leads.dedupWindow': 'Dedup window (hours)',
  'settings.leads.releaseRate': 'Release rate that surfaces a rule (%)',
  'settings.leads.captchaSite': 'Turnstile site key',
  'settings.leads.captchaSecret': 'Turnstile secret',
  'settings.leads.captchaUnconfigured': 'No CAPTCHA is configured. Until a Turnstile secret is entered, nothing checks the sender and every website lead is held for review rather than accepted clean.',
  'settings.leads.stageQuestions': 'Stage Questions',
  'settings.leads.stageQuestionsHint': 'The questions a new lead answers at each early stage, each with an example of a good answer. Change the wording here; a lead already answered keeps its own. Only a super admin can change them.',
  'settings.leads.addStageQuestionButton': '+ Add Stage Question',
  'settings.leads.addStageQuestionTitle': 'Add a Stage Question',
  'settings.leads.questionStage': 'Stage',
  'settings.leads.questionLabel': 'What must be true',
  'settings.leads.questionPlaceholder': 'Example of a good answer',
  'settings.leads.questionInactive': 'Inactive',
  'settings.leads.questionBlank': 'A question keeps its wording and an example of a good answer; type both.',
  'settings.leads.roleTypes': 'Company Role Types',
  'settings.leads.roleTypesHint': 'The roles a person can hold at a customer’s company, such as Decision Maker. A role is a label and gives no access; a role in use is turned off rather than deleted.',
  'settings.leads.salesPersonNote': 'Shown in the Account Roles panel; reads and writes the account’s assigned rep, never a link row.',
  'settings.leads.roleName': 'Role name',
  'settings.leads.addRoleTypeButton': '+ Add Role Type',
  'settings.leads.addRoleTypeTitle': 'Add a Role Type',
  'settings.leads.blocklist': 'Lead Blocklist',
  'settings.leads.blocklistEmpty': 'No verdicts yet. Use {button} to block or allow an email address, a domain or an address range.',
  'settings.leads.blocklistHint': 'Senders whose website enquiries are always blocked or always let through, before any scoring. Allow wins over every other rule for its domain, and every change is recorded.',
  'settings.leads.verdictKind': 'Verdict',
  'settings.leads.block': 'Block',
  'settings.leads.allow': 'Allow',
  'settings.leads.matchType': 'Match',
  'settings.leads.matchValue': 'Email address, domain or IP address range',
  'settings.leads.verdictReason': 'Reason',
  'settings.leads.addVerdictButton': '+ Add Verdict',
  'settings.leads.addVerdictTitle': 'Add a Verdict',
  'settings.leads.matches': 'matches',
  'settings.leads.retire': 'Retire',
  'settings.leads.heldReport': 'Held leads by rule',
  'settings.leads.heldReportHint': 'Releases over holds per rule; a rule releasing above the threshold is surfaced for removal. Threshold:',
  'settings.leads.held': 'held',
  'settings.leads.released': 'released',
  'settings.leads.surfaced': 'surfaced for removal',
  'settings.leads.territories': 'Territories',
  'settings.leads.territoriesEmpty': 'No territories yet. Use {button} to give one an owner and a start date, then give it criteria to place accounts.',
  'settings.leads.territoriesHint': 'The areas each rep covers, by state, county, postal code, distance or named account. A territory only suggests who owns an account; a person still decides, and redrawing a territory moves no account.',
  'settings.leads.territoryName': 'Name',
  'settings.leads.addTerritoryButton': '+ Add Territory',
  'settings.leads.addTerritoryTitle': 'Add a Territory',
  'settings.leads.owner': 'Owner (employment at our own Company)',
  'settings.leads.noOwner': 'No owner',
  'settings.leads.ownerEnded': 'owner’s employment ended',
  'settings.leads.priority': 'Priority',
  'settings.leads.startDate': 'Effective from',
  'settings.leads.criterionKind': 'Criterion',
  'settings.leads.stateCode': 'State code',
  'settings.leads.county': 'County',
  'settings.leads.postalFrom': 'Postal from',
  'settings.leads.postalTo': 'Postal to',
  'settings.leads.latitude': 'Latitude',
  'settings.leads.longitude': 'Longitude',
  'settings.leads.radius': 'Radius (miles)',
  'settings.leads.companyId': 'Company id (named account)',
  'settings.leads.addCriterion': 'Add Criterion',
  'settings.leads.removeCriterion': 'Remove',
  'settings.leads.coverage': 'Coverage and realignment',
  'settings.leads.checkIns': 'Check-Ins',
  'settings.leads.checkInsHint': 'How long a check-in keeps the place it was made from, and how far from its site a check-in may be before it reads in doubt.',
  'settings.leads.leadsInNoTerritory': 'leads in the general queue',
  'settings.leads.unassigned': 'in no territory',
  'settings.leads.placed': 'placed by hand; a redraw never moves it',
  'settings.leads.realign': 'outside its rep’s territory; nothing is reassigned',
  'settings.leads.goals': 'Sales Goals',
  'settings.leads.goalsEmpty': 'No sales goals yet. Use {button} to set a target for a rep or a manager’s team.',
  'settings.leads.goalsHint': 'Sales targets for each rep or each manager’s team, over a month, a quarter or a year. Progress shows against where they would be on pace.',
  'settings.leads.addGoalButton': '+ Add Sales Goal',
  'settings.leads.addGoalTitle': 'Add a Sales Goal',
  'settings.leads.holder': 'Rep, or the manager for a team goal',
  'settings.leads.teamGoal': 'Team goal (the manager’s reports)',
  'settings.leads.metric': 'Metric',
  'settings.leads.period': 'Period',
  'settings.leads.onDate': 'A date inside the period',
  'settings.leads.target': 'Target',
  'settings.leads.throughPeriod': 'through the period',
  'settings.leads.toGoal': 'to goal',
  'settings.leads.pace.ahead': 'ahead of pace',
  'settings.leads.pace.on': 'on pace',
  'settings.leads.pace.behind': 'behind pace',
  'settings.leads.costWithheld': 'attainment is withheld: a margin figure shows only to a viewer who may see cost and margin',
  'settings.leads.memberSum': 'members’ goals sum to',
  'settings.leads.gap': 'gap',
  'settings.leads.team.unallocated': 'unallocated quota',
  'settings.leads.team.overCommitted': 'the team carries more than asked',
  'settings.leads.team.matched': 'matched',
  'settings.leads.removeGoal': 'Remove',
  // The Expanded card's one action and the Event modal's Linked To and call number (2026-09-10).
  'event.openInMaps': 'Open in Maps',
  'event.joinMeeting': 'Join Meeting',
  'event.callNow': 'Call Now',
  'event.linkedTo': 'Linked to',
  'event.countryCode': 'Country code',
  'event.callNumberHint': 'Stored with its country code; +1 unless you pick another.',
  'dev.pieceTwenty': 'Lead funnel, conversion, account hygiene, sales goals',
  'dev.page.leads.lookFor': 'The Lead queue with Held and In no territory filters; a Lead page with three pills, the submitted text beside a suggested match with Link to this and Create new, three StageCards over the seeded wording, and Log Call Attempt, Convert to Company and Mark Not a Fit in the header; the follow-up view at /leads/hygiene; seven CRM settings entries.',
  // Piece 9, decision 0344: the To-Do page and the My to-dos tile.
  'todos.title': 'To-Dos',
  'todos.subtitle': 'Your open items, and your role’s',
  'todos.empty': 'Nothing is open. A record or a rule raises the next one.',
  'todos.mine': 'Mine',
  'todos.role': 'My role’s',
  'todos.due': 'Due',
  'todos.noDue': 'No due date',
  'todos.done': 'Done',
  'todos.snooze': 'Snooze',
  'todos.snoozeUntil': 'Snooze until',
  'todos.dismiss': 'Dismiss',
  'todos.dismissReason': 'Reason for dismissing',
  'todos.openRecord': 'Open the record',
  'todos.closesOnRecord': 'Closes on the record it points at',
  'todos.completed': 'Marked done',
  'todos.snoozed': 'Snoozed',
  'todos.dismissed': 'Dismissed',
  'todos.tile.empty': 'No open to-dos',
  'todos.tile.seeAll': 'See all to-dos',
  'todos.tile.open': 'open',
  // Piece 9, decision 0345: Share on the file rows, the default expiry, and Scan confirm on the record page.
  'file.share': 'Share',
  // Decision 8960: a Deal's file whose newest open release waits for approval, or was rejected.
  'file.release.pending': 'Pending Approval',
  'file.release.rejected': 'Release Rejected',
  // 🔒 Rows T2779, T2794: the Company's Files tile lists the files of its Deals, Employments and Asset Nodes under a toggle in its header (decision 6041).
  'file.dealFiles.show': 'Show Related Files',
  'file.dealFiles.hide': 'Hide Related Files',
  // 🔒 Decision 1953: the Files tile row says when the newest version was scanned and by which scanner, or that its scan waits.
  'file.scan.scanned': 'Scanned {when} by {scanner}',
  'file.scan.scannedUnnamed': 'Scanned {when}',
  'file.scan.pending': 'Scan pending',
  'share.expiryDefault': 'Left blank, the link takes the default expiry from Settings › Documents › File Release.',
  'scan.modalTitle': 'Confirm the Scan',
  'scan.modalHint': 'Rotation and crop were detected and are never applied silently; confirm or correct them before the image is processed.',
  'scan.loading': 'Reading the detection…',
  'scan.complete': 'The processed version is saved.',
  'scan.close': 'Close',
  // Piece 22: the Quote's page — the lines on the Item Table, the version history and the footer's one primary action.
  'quote.lines.empty': 'No lines yet. Add one, pick its Item and Save.',
  'quote.lines.save': 'Save lines',
  'quote.lines.discard': 'Discard',
  'quote.lines.saved': 'Lines saved.',
  'quote.lines.needsItem': 'Every line names an Item or carries a description of its own.',
  'quote.lines.frozen': 'This revision is frozen. Duplicate the quote to change its lines.',
  'quote.lines.total': 'Total',
  // The two blocks the Quote's own document page heads for itself (decision 0470).
  'quote.lines.title': 'Lines',
  // Decision 1466: the Quote's page says its bill of materials changed after the revision went out, until a later revision is sent.
  'quote.bomChanged': 'The bill of materials {bom} changed after {standing} went out, on {date}. Sending the next revision clears this line.',
  'quote.versions.title': 'Version history',
  'quote.lines.sheet': 'priced by',
  'quote.versions.empty': 'Nothing has been sent yet, so there is no copy to compare.',
  'quote.versions.compare': 'Compare',
  'quote.versions.from': 'Earlier copy',
  'quote.versions.to': 'Later copy',
  'quote.versions.same': 'The two copies say the same thing.',
  'quote.versions.header': 'Header',
  'quote.versions.line': 'Line',
  'quote.versions.textChanged': 'the wording changed between the two copies',
  'quote.versions.added': 'added',
  'quote.versions.removed': 'removed',
  'quote.versions.sentBy': 'sent by',
  'quote.versions.close': 'Close Comparison',
  'quote.action.send': 'Send',
  'quote.action.markAccepted': 'Mark as Accepted',
  'quote.action.extendExpiry': 'Extend Expiry',
  'quote.action.convert': 'Convert to Sales Order',
  'quote.action.openSalesOrder': 'Open Sales Order',
  'quote.action.decline': 'Mark as Declined',
  'quote.action.void': 'Void',
  'quote.action.duplicate': 'Duplicate',
  'quote.send.title': 'Send the Quote',
  'quote.send.to': 'To',
  'quote.send.toHint': 'Left blank, it goes to the deal’s primary contact, or the account’s.',
  'quote.send.message': 'Message',
  'quote.send.sentTo': 'Sent to',
  'quote.send.failed': 'The quote moved to Sent and the mail was refused; it is in the failure queue.',
  'document.delivery.title': 'Delivered',
  'document.delivery.previousTitle': 'The last send of this document',
  'document.delivery.sending': 'The mail host has not yet confirmed this send for every recipient.',
  'document.delivery.sent': 'The mail host accepted this send for every recipient, and its Sent copy is filed.',
  'document.delivery.failed': 'The mail host could not deliver this send to every recipient.',
  'document.delivery.unconfirmed': 'This send has waited more than an hour for the mail host, so check the addresses and the mailbox.',
  'document.delivery.recipient': 'Recipient',
  'document.delivery.status': 'Status',
  'document.delivery.time': 'Time',
  'document.delivery.tls': 'TLS',
  'document.delivery.noRows': 'The mail host has not answered for any recipient yet.',
  'document.delivery.bouncedNoReason': 'The receiving host sent the message back without giving a reason.',
  'document.delivery.recorded': "Recorded by the test environment's fake host; nothing was delivered",
  'history.audit.sentAnyway': 'It was sent past the stale-date warning.',
  'mail.conversation.andMore': 'and {n} more',
  'mail.conversation.switch': 'Conversation View',
  'mail.conversation.count': 'messages',
  'mail.conversation.title': 'Conversation',
  'mail.conversation.expand': 'Show this message',
  'mail.conversation.collapse': 'Hide this message',
  'mail.conversation.current': 'This message is open above.',
  'mail.conversation.loading': 'The message is being read.',
  'document.staleSend.sentence': 'This {document} is dated {date}, {days} days before today, and this is its first send. The customer counts its terms from that date.',
  'document.staleSend.quote': 'quote',
  'document.staleSend.salesOrder': 'sales order',
  'document.staleSend.purchaseOrder': 'purchase order',
  'document.staleSend.invoice': 'invoice',
  'document.staleSend.sendAnyway': 'Send Anyway',
  'document.staleSend.issueAnyway': 'Issue Anyway',
  'document.staleSend.changeDate': 'Change the Date',
  'document.staleSend.daysProblem': 'The warning’s days are a whole number from 0 to 365.',
  'settings.staleSend.title': 'Stale Send Warning',
  'settings.staleSend.hint': 'A document dated more than this many days before today warns on its first send, naming the date and the gap, and offers Send Anyway and Change the Date. A resend warns nothing.',
  'settings.staleSend.days': 'Days',
  'quote.accept.title': 'Mark as Accepted',
  'quote.accept.body': 'Records the customer’s answer against the revision on screen, and marks its deal Won. Converting to a Sales Order is a second act, offered here.',
  'quote.accept.convert': 'Convert to a Sales Order now',
  'quote.accept.convertHint': 'Left unticked, the conversion waits on the Convert button and the deal owner’s to-do.',
  'quote.accept.accepted': 'Accepted.',
  'quote.accept.converted': 'Accepted, and converted to sales order',
  'quote.accept.offered': 'Accepted. Converting is on the deal owner’s to-do list.',
  'quote.convert.converted': 'Converted to sales order',
  'quote.convert.title': 'Convert to a Sales Order',
  'quote.convert.body': 'The accepted revision becomes a Draft Sales Order with the same lines.',
  'quote.customerPo.number': 'Customer’s purchase order number (optional)',
  'quote.customerPo.hint': 'Written onto the Sales Order. Leave it empty if the customer has not sent one yet; the order asks for it before it releases.',
  'quote.void.title': 'Void the Quote',
  'quote.void.body': 'Void records the company withdrawing the quote. The customer’s own answer is Mark as Declined, never this.',
  'quote.void.reason': 'Reason',
  'quote.void.voided': 'Voided.',
  'quote.void.act': 'Void the Quote',
  'quote.expiry.title': 'Extend the Expiry',
  'quote.expiry.date': 'Good till',
  'quote.expiry.body': 'An expired quote is live again the moment its Good till date moves past today.',
  'quote.expiry.extended': 'Good till',
  'quote.decline.title': 'Mark as Declined',
  'quote.decline.body': 'Records that the customer said no. Voiding is the company withdrawing the quote instead.',
  'quote.decline.declined': 'Declined recorded.',
  'quote.duplicate.duplicated': 'Duplicated as',
  // Piece 22: the New Quote modal, opened from a Company or a Deal with the record filled in.
  'quote.new.account': 'Account',
  'quote.new.deal': 'Deal',
  'quote.new.billTo': 'Bill to',
  'quote.new.shipTo': 'Ship to',
  'quote.new.customerPo': 'Customer PO number',
  'quote.new.notChosen': 'Not chosen',
  'quote.new.needsBillTo': 'A quote names the site it bills to.',
  'quote.new.shipToHint': 'A customer with one site ships there; with two, picking one for somebody is a guess.',
  'quote.new.quoteDate': 'Quote date',
  'quote.new.expiryDate': 'Good till',
  'quote.new.expiryHint': 'Left blank, it is thirty days after the quote date.',
  'quote.new.paymentTerms': 'Payment terms',
  'quote.new.displayMode': 'Display mode',
  'quote.new.taxExempt': 'Tax exempt on the site this ships to',
  'quote.new.create': 'Create quote',
  'quote.new.created': 'Quote created',
  'quote.recipients.heading': 'Recipients',
  'quote.recipients.selectAll': 'Select All',
  'quote.recipients.addNew': 'Add New',
  'quote.recipients.noAddress': 'no address',
  'quote.recipients.none': 'This account has no contacts yet, so add one to say who the Quote goes to.',
  'quote.new.noSites': 'This account has no open site, so there is nowhere to bill until one is added here.',
  'quote.new.chooseAccount': 'Pick the account this quote is for; its sites and terms fill in once you do.',
  'quote.new.accountEntity': 'account',
  'quote.new.readFailed': 'The account’s sites and terms could not be read, so this quote cannot be saved yet.',
  'quote.new.generateFromBom': 'Generate Lines from the BOM',
  'quote.floor.label': 'Under the deal floor',
  'quote.floor.acknowledge': 'Acknowledge the Floor',
  'quote.floor.title': 'Acknowledge the Deal Floor',
  'quote.floor.body': 'Say why this quote goes out below its deal’s floor. The reason is kept with the quote, and a deeper cut asks again.',
  'quote.floor.reason': 'Reason',
  'quote.floor.acknowledgedBy': 'Acknowledged by',
  'quote.floor.lineAcknowledged': 'acknowledged',
  'quote.floor.lineLabel': 'Under a line floor',
  'quote.floor.lineTitle': 'Acknowledge the line floor',
  'quote.floor.lineBody': 'Say why this line goes out below the floor of the BOM rows it names. The reason is kept with the line, and a deeper cut asks again.',
  'quote.floor.acknowledgedDone': 'The floor is acknowledged.',
  'quote.floorWarning.title': 'Below the Floor',
  'quote.floorWarning.body': 'This quote is priced under its floor. Sending it for approval asks the approver to accept the price and give the reason. Fixing it closes this window and writes nothing.',
  'quote.floorWarning.sendAnyway': 'Send for Approval Anyway',
  'quote.floorWarning.fix': 'Fix the Quote',
  'quote.new.bomFailed': 'The deal’s bill of materials could not be read, so no lines were generated.',
  'quote.new.bomEmpty': 'This deal’s bill of materials has no lines yet, so there is nothing to generate from.',
  'quote.new.bomAlready': 'Every line on the deal’s bill of materials is already on this quote.',
  'deal.quotes.empty': 'No quote has been raised on this deal yet.',
  // Piece 9, decision 0345: the three Settings › Security pages.
  'settings.security.screening.title': 'Screening Tuning',
  'settings.security.screening.hint': 'How suspicious website leads and incoming mail are scored. Each warning sign adds its weight when it shows up, and the two thresholds decide when a lead is noted or held. Change a weight and press Preview to see how past leads would have scored.',
  'settings.security.screening.channel': 'Channel',
  'settings.security.screening.noteThreshold': 'Note threshold',
  'settings.security.screening.holdThreshold': 'Hold threshold',
  'settings.security.screening.savePolicy': 'Save Thresholds',
  'settings.security.screening.signals': 'Signals',
  'settings.security.screening.weight': 'Weight',
  'settings.security.screening.enabled': 'Enabled',
  'settings.security.screening.off': 'Off',
  'settings.security.screening.unconfigured': 'unconfigured',
  'settings.security.screening.preview': 'Preview the Change',
  'settings.security.screening.previewLine': 'stored results re-scored',
  'settings.security.screening.previewChanged': 'would change outcome',
  'settings.security.screening.previewExcluded': 'excluded',
  // Piece 9, decisions 0822 and 0823 (T271): Settings › System › System Errors, in the proposal's own words.
  'settings.category.system': 'System',
  'settings.system.errors.title': 'System Errors',
  'settings.system.errors.hint': 'Errors the application met that nobody handled, on the server and in people’s browsers, with repeats grouped together. Open one to see where it happened; personal details were removed before it was kept.',
  'settings.system.errors.from': 'From',
  'settings.system.errors.to': 'To',
  'settings.system.errors.reference': 'Error reference',
  'settings.system.errors.search': 'Search',
  'settings.system.errors.searchNeedsChange': 'Change a date, a source or a reference to search',
  // T3701: the Source filter; the phone posts as a browser, so its rows are named Phone by their route and user agent.
  'settings.system.errors.source': 'Source',
  'settings.system.errors.sourceAll': 'All sources',
  'settings.system.errors.sourceServer': 'Server',
  'settings.system.errors.sourceBrowser': 'Browser',
  'settings.system.errors.sourcePhone': 'Phone',
  'settings.system.errors.sourceRefusal': 'Refusal',
  'settings.system.errors.refusalsHidden': 'Refusals hidden. Choose Refusal under Source to list every refusal a person met.',
  'settings.system.errors.export': 'Export for Repair',
  'settings.system.errors.exportHint': 'Export for repair downloads the ticked groups, or every group listed when none is ticked, as one sanitized file to paste into a repair session.',
  'settings.system.errors.empty': 'No system error in this range.',
  'settings.system.errors.pick': 'Include in the export',
  'settings.system.errors.open': 'Open',
  'settings.system.errors.close': 'Close',
  'settings.system.errors.resolve': 'Resolve',
  'settings.system.errors.statusOpen': 'Open',
  'settings.system.errors.statusResolved': 'Resolved',
  // T363 (decision 0998): a browser fault naming nothing of ours, which the open count leaves out.
  'settings.system.errors.statusOutside': 'Outside the application',
  'settings.system.errors.outsideHint': 'No frame of the application is in this fault, so a script the browser or one of its extensions put into the page threw it. There is nothing here to repair.',
  'settings.system.errors.count': 'Occurrences',
  'settings.system.errors.firstSeen': 'First seen',
  'settings.system.errors.lastSeen': 'Last seen',
  'settings.system.errors.occurrences': 'Occurrences, newest first',
  'settings.system.errors.stack': 'Stack',
  'settings.system.errors.noStack': 'No stack was recorded.',
  'settings.system.errors.request': 'Request',
  'settings.system.errors.version': 'Version',
  'settings.system.errors.repeats': 'repeats folded in',
  'settings.system.errors.resolvedNotice': 'Resolved. The group reopens by itself if the fault happens again.',
  // Piece 9, decision 1530 (T794): Settings › System › Server info, its three tabs and the Server tab's four cards.
  'settings.system.serverInfo.title': 'Server Info',
  'settings.system.serverInfo.intro': 'How the server is doing: which outside services answer, what it runs on, its latest log lines and its slowest requests in the last hour, one tab each.',
  'settings.system.serverInfo.tabs': 'Part of the server info to show',
  'settings.system.serverInfo.tab.server': 'Server',
  'settings.system.serverInfo.tab.log': 'API Log',
  'settings.system.serverInfo.tab.scanner': 'Scanner Log',
  'settings.system.serverInfo.tab.timings': 'Request Timings',
  'settings.system.server.refresh': 'Refresh',
  'settings.system.server.readAt': 'Read at {when}',
  'settings.system.server.empty': 'The server sent no figures. Press Refresh to ask again.',
  'settings.system.server.none': '—',
  'settings.system.server.application': 'Application',
  'settings.system.server.host': 'Host',
  'settings.system.server.database': 'Database',
  'settings.system.server.services': 'Services',
  'settings.system.server.commit': 'Commit',
  'settings.system.server.environment': 'Environment',
  'settings.system.server.enforcement': 'Enforcement',
  'settings.system.server.started': 'Started',
  'settings.system.server.uptime': 'Uptime',
  'settings.system.server.uptimeDays': '{days} d {hours} h',
  'settings.system.server.uptimeHours': '{hours} h {minutes} min',
  'settings.system.server.uptimeMinutes': '{minutes} min',
  'settings.system.server.node': 'Node version',
  'settings.system.server.processMemory': 'Process memory',
  'settings.system.server.processMemoryValue': '{resident} GB resident, heap {heapUsed} GB of {heapTotal} GB',
  'settings.system.server.hostMemory': 'Memory',
  'settings.system.server.freeOf': '{free} GB free of {total} GB',
  'settings.system.server.cpus': 'Processors',
  'settings.system.server.load': 'Load, 1, 5 and 15 minutes',
  'settings.system.server.disk': 'Disk',
  'settings.system.server.timeZone': 'Time zone',
  'settings.system.server.dbVersion': 'PostgreSQL version',
  'settings.system.server.dbSize': 'Size',
  'settings.system.server.sizeGb': '{size} GB',
  'settings.system.server.sizeMb': '{size} MB',
  'settings.system.server.dbConnections': 'Open connections',
  'settings.system.server.pool': 'The API’s pool',
  'settings.system.server.poolValue': '{size} in the pool: {open} open, {busy} busy, {idle} idle',
  'settings.system.server.servicesMoved': 'What answers the file store, the scanner and every other outside service, and whether each answered, is on the Status tab.',
  'settings.system.server.openStatus': 'Open the Status tab',
  'settings.system.serverInfo.tab.status': 'Status',
  'settings.system.status.intro': 'Every outside service the application talks to, what answers it on this installation, and whether it answered when asked. A built-in stand-in answers everything and does nothing outside.',
  'settings.system.status.checkAll': 'Check All',
  'settings.system.status.empty': 'The server named no services. Press Check All to ask again.',
  'settings.system.status.list': 'Outside services',
  'settings.system.status.answering': 'What answers',
  'settings.system.status.live': 'Live check',
  'settings.system.status.seam.database': 'Database',
  'settings.system.status.seam.objectStorage': 'Object storage',
  'settings.system.status.seam.malwareScan': 'File scanning',
  'settings.system.status.seam.outgoingMail': 'Outgoing mail',
  'settings.system.status.seam.mailbox': 'Mailbox',
  'settings.system.status.seam.textMessages': 'Text messages',
  'settings.system.status.seam.phoneCalls': 'Phone calls',
  'settings.system.status.seam.payments': 'Payments',
  'settings.system.status.seam.bankFeed': 'Bank feed',
  'settings.system.status.seam.maps': 'Map service',
  'settings.system.status.seam.addressCheck': 'Address check',
  'settings.system.status.seam.booksSource': 'Books source',
  'settings.system.status.seam.signIn': 'Sign-in',
  'settings.system.status.seam.eSignature': 'E-signature',
  'settings.system.status.seam.pdfRenderer': 'PDF rendering',
  'settings.system.status.seam.payroll': 'Payroll',
  'settings.system.status.seam.ai': 'AI seam',
  'settings.system.status.seam.enrichmentPeopleData': 'People-data lookups',
  'settings.system.status.seam.enrichmentWebsite': 'Website lookups',
  'settings.system.status.seam.enrichmentSearch': 'Web search lookups',
  'settings.system.status.seam.keyStore': 'Key store',
  'settings.system.status.vendor.postgres': 'PostgreSQL',
  'settings.system.status.vendor.s3': 'The S3-compatible store',
  'settings.system.status.vendor.clamav': 'ClamAV',
  'settings.system.status.vendor.stripe': 'Stripe',
  'settings.system.status.vendor.plaid': 'Plaid',
  'settings.system.status.vendor.stripeFinancialConnections': 'Stripe Financial Connections',
  'settings.system.status.vendor.googleMaps': 'Google Maps',
  'settings.system.status.vendor.openStreetMap': 'OpenStreetMap',
  'settings.system.status.vendor.zohoBooks': 'Zoho Books',
  'settings.system.status.vendor.imap': 'The IMAP mail host',
  'settings.system.status.vendor.chromium': 'Headless Chromium',
  'settings.system.status.vendor.hostKey': 'The host’s own secret key',
  'settings.system.status.vendor.passwords': 'Password sign-in on this installation',
  'settings.system.status.vendor.anthropic': 'Anthropic',
  'settings.system.status.vendor.peopleDataLabs': 'People Data Labs',
  'settings.system.status.vendorAt': '{vendor} at {address}',
  'settings.system.status.vendorFromEnvironment': '{vendor} at {address}, from the environment',
  'settings.system.status.standIn': 'Built-in stand-in',
  'settings.system.status.off': 'Off',
  'settings.system.status.standInLine': 'Using the built-in stand-in: {meaning}',
  'settings.system.status.offLine': 'Off: {meaning}',
  'settings.system.status.standInMeaning.database': 'nothing is kept after a restart.',
  'settings.system.status.standInMeaning.objectStorage': 'files are held in memory and are lost when the server restarts.',
  'settings.system.status.standInMeaning.malwareScan': 'every upload is passed as clean and nothing is scanned.',
  'settings.system.status.standInMeaning.outgoingMail': 'mail is recorded here and nothing is sent.',
  'settings.system.status.standInMeaning.mailbox': 'the mailboxes are scripted here and no real mail is read.',
  'settings.system.status.standInMeaning.textMessages': 'text messages are recorded here and nothing is sent.',
  'settings.system.status.standInMeaning.phoneCalls': 'calls are recorded here and no phone rings.',
  'settings.system.status.standInMeaning.payments': 'payments go to a pretend processor and no money moves.',
  'settings.system.status.standInMeaning.bankFeed': 'statement lines come from a script, not from a bank.',
  'settings.system.status.standInMeaning.maps': 'places are found from a fixed list, not a map service.',
  'settings.system.status.standInMeaning.addressCheck': 'every address with a street line reads as verified, real or not.',
  'settings.system.status.standInMeaning.booksSource': 'records come from a script, not from a books package.',
  'settings.system.status.standInMeaning.signIn': 'nobody signs in, and every request is the seeded developer.',
  'settings.system.status.standInMeaning.eSignature': 'envelopes go nowhere and nobody is asked to sign.',
  'settings.system.status.standInMeaning.pdfRenderer': 'a printed document is a plain stand-in page, not the template drawn by a browser.',
  'settings.system.status.standInMeaning.payroll': 'nothing reaches a payroll service; exports are recorded here.',
  'settings.system.status.standInMeaning.ai': 'nothing is sent to a language model.',
  'settings.system.status.standInMeaning.enrichment': 'nothing is looked up outside the building.',
  'settings.system.status.standInMeaning.keyStore': 'a key sealed now cannot be opened after the server restarts.',
  'settings.system.status.standInMeaning.keyStoreSeeded': 'keyed from a fixed public seed, so a key sealed now opens after the server restarts, and protects nothing.',
  'settings.system.status.offMeaning.payments': 'no card or bank payment is taken online.',
  'settings.system.status.offMeaning.bankFeed': 'no bank is connected, so statements are imported by hand.',
  'settings.system.status.offMeaning.maps': 'no map is drawn and no address is placed.',
  'settings.system.status.offMeaning.addressCheck': 'no address is checked against a map service.',
  'settings.system.status.offMeaning.booksSource': 'nothing is read from an outside books package.',
  'settings.system.status.offMeaning.ai': 'nothing is sent to a language model.',
  'settings.system.status.offMeaning.enrichment': 'nothing is looked up outside the building.',
  'settings.system.status.offMeaning.other': 'nothing answers it.',
  'settings.system.status.check.answered': 'Answered {ago}',
  'settings.system.status.check.notAnswered': 'Not answered',
  'settings.system.status.check.notChecked': 'Not checked',
  'settings.system.status.check.checking': 'Checking…',
  'settings.system.status.why.standIn': 'nothing outside answers, so there is nothing to ask.',
  'settings.system.status.why.off': 'the service is switched off.',
  'settings.system.status.why.noCheck': 'no cheap check exists, so it is tried the next time it is used.',
  'settings.system.status.ago.now': 'just now',
  'settings.system.status.ago.minute': '1 minute ago',
  'settings.system.status.ago.minutes': '{count} minutes ago',
  'settings.system.status.ago.hour': '1 hour ago',
  'settings.system.status.ago.hours': '{count} hours ago',
  'settings.system.status.ago.day': '1 day ago',
  'settings.system.status.ago.days': '{count} days ago',
  'settings.system.status.line.answered': '{vendor}, answered {ago}',
  'settings.system.status.line.answeredSignatures': '{vendor}, signatures {signatures} of {day}, answered {ago}',
  'settings.system.status.line.answeredSignaturesUndated': '{vendor}, signatures {signatures}, answered {ago}',
  'settings.system.status.scanner.waitingNone': 'No upload is waiting for a scan.',
  'settings.system.status.scanner.waitingOne': '1 upload is waiting for a scan.',
  'settings.system.status.scanner.waitingMany': '{count} uploads are waiting for a scan.',
  'settings.system.status.scanner.stale': 'The signatures are more than {days} days old, so the daily update may have stopped.',
  'settings.system.status.scanner.noSignatures': 'The scanner names no signature date, so nothing says its daily update ran.',
  'settings.system.status.line.notAnswered': '{vendor} is not answering',
  'settings.system.status.line.checking': '{vendor}, checking…',
  'settings.system.status.line.notChecked': '{vendor}, not checked',
  // Piece 9, decision 1528 (T792): the API log tab.
  'settings.system.log.holds': 'The last {count} lines the API wrote since it last started, newest first. The container’s own log keeps the rest.',
  'settings.system.scannerLog.holds': 'The last {count} lines the malware scanner and its updater wrote to their own log files, newest first.',
  'settings.system.scannerLog.unmounted': 'The malware scanner’s log files are not mounted on this server, so there is nothing to show here.',
  'settings.system.log.level': 'Level',
  'settings.system.log.levelEvery': 'Every level',
  'settings.system.log.levelWarn': 'Warnings and errors',
  'settings.system.log.levelError': 'Errors',
  'settings.system.log.search': 'Search the lines',
  'settings.system.log.export': 'Export',
  'settings.system.log.every': 'Every 10 seconds',
  'settings.system.log.empty': 'No line matches. Change the level or the search, or press Refresh.',
  'settings.system.log.level.log': 'Log',
  'settings.system.log.level.warn': 'Warning',
  'settings.system.log.level.error': 'Error',
  'settings.security.audit.title': 'Audit Viewer',
  // 🔒 Decision 2771 (T1626): Settings › History, every change to an Organization setting, newest first.
  'settings.history.title': 'History',
  'settings.history.hint': 'Every change made to a setting, newest first: who made it, when, and the value before and after. The Audit Viewer holds the rest of the record.',
  'settings.history.empty': 'No setting has been changed yet. A change saved on any Settings page is listed here.',
  'settings.history.capped': 'The newest 200 changes are listed here. The Audit Viewer holds every change before them.',
  'settings.history.when': 'When',
  'settings.history.who': 'Who',
  'settings.history.setting': 'Setting',
  'settings.history.was': 'Was',
  'settings.history.now': 'Now',
  'settings.history.field': 'Field',
  'settings.history.view': 'Every change',
  'settings.history.order': 'Listed newest first, the order the changes were made',
  'settings.history.notSet': 'Not set',
  'settings.history.hidden': 'Hidden',
  'settings.history.on': 'On',
  'settings.history.off': 'Off',
  'settings.history.from': 'from',
  'settings.history.unnamed': 'A setting no page names',
  'settings.history.somebody': 'Somebody no longer on record',
  'settings.security.audit.hint': 'Everything anybody opened or changed, newest first. Filter by who, what and when; nothing here can be edited or removed.',
  'settings.security.audit.actor': 'Actor',
  'settings.security.audit.entityType': 'Entity type',
  'settings.security.audit.entityId': 'Entity id',
  'settings.security.audit.action': 'Action',
  'settings.security.audit.from': 'From',
  'settings.security.audit.to': 'To',
  'settings.security.audit.search': 'Search',
  'settings.security.audit.empty': 'No audit rows match. Widen the dates or clear a filter.',
  'settings.security.audit.rows': 'rows',
  'settings.security.nda.title': 'Shared Under NDA',
  'settings.security.nda.hint': 'Every file currently released on an account whose NDA requires cover, grouped by the record it is tagged to, with who released it and when.',
  'settings.security.nda.empty': 'No file is currently released under an NDA.',
  'settings.security.nda.releasedTo': 'released to',
  'settings.security.nda.by': 'by',
  'settings.security.nda.cover': 'covered by',
  'settings.security.nda.untagged': 'Untagged',
  // Piece 9, decision 0346: the Scheduled jobs card.
  'settings.security.jobs.title': 'Scheduled Jobs',
  'settings.security.jobs.hint': 'Each job runs once a day at its hour in the Organization’s zone and reports its run even with nothing due. Run now runs the same job at once.',
  'settings.security.jobs.hour': 'Run hour',
  'settings.security.jobs.lastRun': 'Last run',
  'settings.security.jobs.never': 'never',
  'settings.security.jobs.runNow': 'Run Now',
  'settings.security.jobs.ran': 'ran',
  'settings.security.jobs.save': 'Save hour',

  // 🔨 What each nightly job is called on screen, and one line saying what it
  // does (decision 0472). The job's own name is a key the runner and the audit
  // row read, and a person never sees it.
  'jobs.closeOpenCheckIns.label': 'Close open check-ins',
  'jobs.closeOpenCheckIns.purpose': 'Closes any site visit still showing as open from the day before, and raises a to-do for each one it closed.',
  'jobs.ageAuditReads.label': 'Clear old read history',
  'jobs.ageAuditReads.purpose': 'Removes the records of who opened which record, once they are older than the retention window.',
  'jobs.purgeQuarantine.label': 'Delete quarantined files',
  'jobs.purgeQuarantine.purpose': 'Scans again every upload still waiting for its virus scan, asks the scanner how it is, and deletes the contents of files the virus scan blocked, thirty days after they were blocked.',
  'jobs.reviewNdaDemands.label': 'Chase unsigned agreements',
  'jobs.reviewNdaDemands.purpose': 'Finds non-disclosure agreements asked for more than thirty days ago with nothing signed, and tells whoever asked.',
  'jobs.runReminders.label': 'Send reminders',
  'jobs.runReminders.purpose': 'Sends the reminders and follow-ups that fall due today.',
  'jobs.runLedgerTieOuts.label': 'Check the ledger adds up',
  'jobs.runLedgerTieOuts.purpose': 'Compares each sub-ledger against the general ledger and reports any difference it finds.',
  'jobs.runPostingRederivation.label': 'Re-check recent postings',
  'jobs.runPostingRederivation.purpose': 'Works out the last month of ledger entries again, and names any that no longer match what was posted.',
  'jobs.runFinanceCharges.label': 'Charge late payment interest',
  'jobs.runFinanceCharges.purpose': 'Adds finance charges to customer invoices that are past their due date.',
  'jobs.rewindowNexusCounters.label': 'Update sales tax thresholds',
  'jobs.rewindowNexusCounters.purpose': 'Recounts sales by state, so the point at which each state needs a return stays current.',
  'jobs.generateSalesTaxFilings.label': 'Prepare sales tax returns',
  'jobs.generateSalesTaxFilings.purpose': 'Builds the sales tax returns that are due for each state.',
  // Decisions 11020 and 11520: the recurring Draft Bills and Draft journals.
  'jobs.generateRecurringDrafts.label': 'Raise recurring bills and journals',
  'jobs.generateRecurringDrafts.purpose': 'Raises a Draft Bill for each recurring bill that has come due, and a Draft journal for each recurring journal; it posts nothing itself.',
  'recurring.title': 'Recurring',
  'recurring.empty': 'Nothing recurs yet. Open a posted Bill or a Manual Journal and press Make Recurring.',
  'recurring.about': 'A recurring Bill raises a Draft Bill each period, and a recurring Manual Journal raises a Draft journal dated the period. Nothing posts by itself.',
  'recurring.make': 'Make Recurring',
  'recurring.makeTitle': 'Make Recurring',
  'recurring.makeHint': 'The first run is one step after today. Each run raises a Draft for a person to review.',
  'recurring.frequency': 'Frequency',
  'recurring.frequency.monthly': 'Monthly',
  'recurring.frequency.quarterly': 'Quarterly',
  'recurring.frequency.annual': 'Annual',
  'recurring.endOn': 'End On',
  'recurring.endOnHint': 'Optional. A template raises nothing past this day.',
  'recurring.save': 'Make Recurring',
  'recurring.made': 'This now recurs. Accounting › Recurring lists it.',
  'recurring.source.bill': 'Bill',
  'recurring.source.journalEntry': 'Manual Journal',
  'recurring.nextRun': 'Next run',
  'recurring.endsOn': 'Ends',
  'recurring.ended': 'Ended',
  'recurring.end': 'End',
  'recurring.end.subject': 'the recurring template for',
  'recurring.end.cannotRestart': 'it raises nothing more, and an ended template cannot be started again; make the source recurring afresh to resume',
  'recurring.endedNotice': 'Ended. It raises nothing more.',
  'recurring.sourceGone': 'document removed',
  'recurring.runFailed': 'The last run could not raise {template}, next run {nextRun}: its source is gone. End it, or restore the source.',
  'recurring.draftNumber': '{vendorNumber} · {period}',
  'jobs.pullBankFeeds.label': 'Import bank transactions',
  'jobs.pullBankFeeds.purpose': 'Pulls new transactions from every connected bank and card account.',
  // 🔒 Decision 3801: the daily pull of what changed in Zoho Books, which reads nothing while sync mode is off.
  'jobs.pullZohoBooksChanges.label': 'Pull changes from the old books',
  'jobs.pullZohoBooksChanges.purpose': 'While the daily pull is on, asks Zoho Books what changed since its last pull and writes it here, and stops after the end date.',
  'jobs.runLowStock.label': 'Check stock against thresholds',
  'jobs.runLowStock.purpose': 'Raises a to-do for every item and site that has fallen to its threshold, and never a purchase order.',
  'jobs.raiseForm1099Reminder.label': 'Ask accounting to confirm the 1099 threshold',
  // Decision 10710: the text with no receipt after two days is aged to Unknown.
  'jobs.sweepUnansweredTexts.label': 'Age unanswered texts',
  'jobs.sweepUnansweredTexts.purpose': 'Marks an outgoing text that has had no delivery receipt for two days as Unknown, so it stops reading Sent for ever.',
  'jobs.raiseForm1099Reminder.purpose': 'Raises one to-do each January so the filing threshold for the new tax year is confirmed rather than assumed.',
  // 🔒 Decision 0638 (T116, T171): the one name each document kind goes by, Title Case, read through documentNames.ts and nowhere else.
  'documentName.new': 'New',
  // 🔒 Decision 1790: the standard abbreviation a control too narrow for the full name shows, read through documentAbbreviation and nowhere else.
  'documentAbbreviation.purchaseOrder': 'PO',
  'documentAbbreviation.workOrder': 'WO',
  'documentAbbreviation.salesOrder': 'SO',
  'documentAbbreviation.rfq': 'RFQ',
  'documentAbbreviation.bom': 'BOM',
  'documentName.draft': 'Draft',
  'documentName.edit': 'Edit',
  'documentName.quote': 'Quote',
  'documentName.salesOrder': 'Sales Order',
  'documentName.invoice': 'Invoice',
  'documentName.purchaseOrder': 'Purchase Order',
  'documentName.workOrder': 'Work Order',
  'documentName.bill': 'Bill',
  'documentName.creditNote': 'Credit Note',
  'documentName.vendorCredit': 'Vendor Credit',
  'documentName.changeOrder': 'Change Order',
  'documentName.rfq': 'Request for Quotation',
  'documentName.bom': 'Bill of Materials',
  'documentName.payment': 'Payment',
  'documentName.shipment': 'Shipment',
  'documentName.package': 'Package',
  'documentName.stockTransfer': 'Stock Transfer',
  'documentName.stockCount': 'Stock Count',
  'documentName.stockRevaluation': 'Stock Revaluation',
  'documentName.expenseReport': 'Expense Report',
  'documentName.journalEntry': 'Journal Entry',
  'documentName.check': 'Check',
  'documentName.statement': 'Statement',
  'documentName.packingSlip': 'Packing Slip',
  'documentName.paymentReceipt': 'Payment Receipt',
  'documentName.mergeRequest': 'Merge Request',
  'documentName.fileRelease': 'File Release',
  'documentNames.quote': 'Quotes',
  'documentNames.salesOrder': 'Sales Orders',
  'documentNames.invoice': 'Invoices',
  'documentNames.purchaseOrder': 'Purchase Orders',
  'documentNames.workOrder': 'Work Orders',
  'documentNames.bill': 'Bills',
  'documentNames.creditNote': 'Credit Notes',
  'documentNames.vendorCredit': 'Vendor Credits',
  'documentNames.changeOrder': 'Change Orders',
  'documentNames.rfq': 'Requests for Quotation',
  'documentNames.bom': 'Bills of Materials',
  'documentNames.payment': 'Payments',
  'documentNames.shipment': 'Shipments',
  'documentNames.package': 'Packages',
  'documentNames.stockTransfer': 'Stock Transfers',
  'documentNames.stockCount': 'Stock Counts',
  'documentNames.stockRevaluation': 'Stock Revaluations',
  'documentNames.expenseReport': 'Expense Reports',
  'documentNames.journalEntry': 'Journal Entries',
  'documentNames.check': 'Checks',
  'documentNames.statement': 'Statements',
  'documentNames.packingSlip': 'Packing Slips',
  'documentNames.paymentReceipt': 'Payment Receipts',
  'documentNames.mergeRequest': 'Merge Requests',
  'documentNames.fileRelease': 'File Releases',
  // Piece 22: Settings › Accounting › Document Number Series.
  'settings.numberSeries.title': 'Document Number Series',
  'settings.numberSeries.hint': 'How each kind of document is numbered. Each line shows the next number it will issue; open one with its pencil to change its format. A change applies to new documents only, and the next number can only move forward.',
  'settings.numberSeries.prefix': 'Prefix',
  'settings.numberSeries.separator': 'Separator',
  'settings.numberSeries.dateComponent': 'Date',
  'settings.numberSeries.padding': 'Digits',
  'settings.numberSeries.nextNumber': 'Next number',
  'settings.numberSeries.resetPolicy': 'Reset',
  'settings.numberSeries.allocateAt': 'Numbered at',
  'settings.numberSeries.active': 'Active',
  'settings.numberSeries.inactive': 'Inactive',
  'settings.numberSeries.preview': 'Next',
  'settings.numberSeries.period': 'Counting in',
  'settings.numberSeries.date.none': 'No date',
  'settings.numberSeries.date.yy': 'Two-digit year',
  'settings.numberSeries.date.yyyy': 'Year',
  'settings.numberSeries.date.yyyymm': 'Year and month',
  'settings.numberSeries.reset.never': 'Never',
  'settings.numberSeries.reset.annually': 'Every year',
  'settings.numberSeries.reset.monthly': 'Every month',
  'settings.numberSeries.at.creation': 'Creation',
  'settings.numberSeries.at.firstSend': 'First send',
  'settings.numberSeries.at.print': 'Print',
  'settings.numberSeries.inactiveHint': 'An inactive series refuses every new document of its type until it is turned back on.',
  'settings.numberSeries.overrides': 'Numbers issued by hand',
  'settings.numberSeries.overridesHint': 'A number typed by hand is recorded here with who issued it and why, and it never moves the counter backwards. A number in the series’ own format moves the counter past it; one outside the format leaves the counter alone.',
  'settings.numberSeries.overrideType': 'Document type',
  'settings.numberSeries.currentNumber': 'Its number today',
  'settings.numberSeries.issuedNumber': 'Number to issue',
  'settings.numberSeries.reason': 'Why',
  'settings.numberSeries.issue': 'Issue by Hand',
  'settings.numberSeries.issued': 'Issued',
  'settings.numberSeries.noOverrides': 'No number has been issued by hand.',
  'settings.numberSeries.noOverridable': 'No document type takes a hand-issued number yet; each arrives with its own screens.',
  'settings.numberSeries.by': 'by',
  'settings.numberSeries.on': 'on',
  'settings.numberSeries.was': 'was',
  // Piece 22: the three Settings › Accounting values entries.
  'settings.autoConversion.title': 'Auto-Conversion Rules',
  'settings.autoConversion.hint': 'Nothing is created silently when a Quote is accepted. For each deal type, this names the next step offered — as a to-do and a button — when the Deal reaches Won: nothing, a draft Sales Order, or a Sales Order created and sent. An invoice number is never taken automatically.',
  'settings.autoConversion.project': 'Project deals',
  'settings.autoConversion.parts': 'Parts deals',
  'settings.autoConversion.service': 'Service deals',
  'settings.autoConversion.mode.off': 'Off — nothing is offered',
  'settings.autoConversion.mode.createDraft': 'Offer a draft Sales Order',
  'settings.autoConversion.mode.createAndSend': 'Offer a Sales Order, created and sent',
  'settings.precision.hint': 'How many decimals a quantity shows on a document, trailing zeros trimmed. Quantities are stored to two decimals whatever is shown here, and that is not configurable.',
  'settings.precision.decimals': 'Decimals shown on a quantity',
  'settings.creditLimit.title': 'Credit Limit Default',
  'settings.creditLimit.hint': 'The credit limit for every customer who has none of their own. A customer with a blank limit gets this number, never unlimited credit and never zero; a customer’s own limit is set on their Company record.',
  'settings.creditLimit.amount': 'Default credit limit',
  // Piece 22: Settings › Accounting › Approval Rules and Approval Tiers (piece 15 specifies both; decision 0192).
  'settings.approvals.rules.title': 'Approval Rules',
  'settings.approvals.rules.hint': 'Who is allowed to approve what: for a document type and an amount band, the step in the chain and the approver — a role, a named person, the head of a department, or the requester’s own manager. Bands within one type and step never overlap; the overlap is refused when you save, never met at approval time. Only a super admin edits a rule, and every change is recorded.',
  'settings.approvals.chainDepth': 'Management chain depth (0 = entire chain)',
  'settings.approvals.chainDepthHint': 'How far up the management chain a manager’s standing authority to approve a subordinate’s document reaches. 0 reaches the entire chain. A change is recorded and applies to no approval already made.',
  'settings.approvals.band.from': 'from',
  'settings.approvals.band.to': 'to',
  'settings.approvals.band.andUp': 'and up',
  'settings.approvals.step': 'step',
  'settings.approvals.orgWide': 'organization-wide',
  'settings.approvals.selfApproval': 'submitter may approve their own',
  'settings.approvals.active': 'Active',
  'settings.approvals.editRule': 'Edit',
  'settings.approvals.needsApprover': 'Name who approves this step: choose the role or the person, or pick a department for its head.',
  'settings.approvals.ruleNotSaved': 'That rule was not saved.',
  'settings.approvals.ruleHintMin': 'The starting amount is zero or more. Enter it in dollars.',
  'settings.approvals.ruleHintMax': 'The ceiling sits below the starting amount. Raise it, or leave it blank for no ceiling.',
  'settings.approvals.ruleHintStep': 'The step is a whole number from 1 up.',
  'settings.roleNotSaved': 'That role was not saved.',
  'settings.changedBy': 'Last changed by',
  // Decision 0699 (T208): Settings › Documents › Envelopes, the profiles the template editor's envelope overlay offers.
  'settings.envelopes.title': 'Envelopes',
  'settings.envelopes.hint': 'The window envelopes you mail documents in, so a print template can be checked against them. Each says where its windows sit and how the sheet folds to go in; hide one to stop offering it.',
  'settings.envelopes.windowHint': 'A window’s place is where the cutout falls on the folded sheet, in inches from the sheet’s top left corner. An envelope with neither window ticked shows nothing of the sheet, as an announcement envelope does.',
  'settings.envelopes.name': 'Name',
  'settings.envelopes.sheet': 'Sheet',
  'settings.envelopes.fold': 'Fold',
  'settings.envelopes.width': 'Width (in)',
  'settings.envelopes.height': 'Height (in)',
  'settings.envelopes.shown': 'Shown in the template editor',
  'settings.envelopes.hidden': 'hidden',
  'settings.envelopes.noWindows': 'No window',
  'settings.envelopes.add': '+ Add Envelope',
  'settings.neverChanged': 'No change recorded since this was made.',
  'settings.approvals.noRules': 'No rule yet: every document of this type lands Not required, unless a tier holds it.',
  'settings.approvals.newRule': '+ Add Rule',
  'settings.approvals.documentType': 'Document type',
  'settings.approvals.department': 'Department',
  'settings.approvals.minAmount': 'From ($)',
  'settings.approvals.maxAmount': 'Up to ($, blank for no ceiling)',
  'settings.approvals.stepOrder': 'Step',
  'settings.approvals.approverKind': 'Approver',
  'settings.approvals.kind.systemRole': 'A role',
  'settings.approvals.kind.employment': 'A named person',
  'settings.approvals.kind.departmentHead': 'The head of the department',
  'settings.approvals.kind.requesterManager': 'The requester’s manager',
  'settings.approvals.role': 'Role',
  'settings.approvals.person': 'Person',
  'settings.approvals.allowSelf': 'The submitter may approve their own',
  'settings.approvals.saveRule': 'Add a Rule',
  'settings.approvals.approverOf': 'approved by',
  'settings.approvals.headOf': 'the head of',
  'settings.approvals.tiers.title': 'Approval Tiers',
  // Decision 10631 (T938): the Delegations section, where an approver names cover for their own approvals.
  'settings.approvals.delegations.title': 'Delegations',
  'settings.approvals.delegations.hint': 'Name a person who approves for you while you are away. Your approvals go to them from the first day through the last, then come back to you.',
  'settings.approvals.delegations.newCover': '+ Add Cover',
  'settings.approvals.delegations.addCover': 'Add Cover',
  'settings.approvals.delegations.person': 'Covered By',
  'settings.approvals.delegations.firstDay': 'First Day',
  'settings.approvals.delegations.lastDay': 'Last Day',
  'settings.approvals.delegations.lastDayHint': 'Leave the last day empty for cover with no end.',
  'settings.approvals.delegations.department': 'Department',
  'settings.approvals.delegations.allDepartments': 'All Departments',
  'settings.approvals.delegations.gave': '{name} covers your approvals',
  'settings.approvals.delegations.holds': 'You cover the approvals of {name}',
  'settings.approvals.delegations.from': 'From {start}',
  'settings.approvals.delegations.span': '{start} to {end}',
  'settings.approvals.delegations.end': 'End',
  'settings.approvals.delegations.ended': 'Cover ended; today is its last day.',
  'settings.approvals.delegations.removed': 'Cover removed before it began.',
  'settings.approvals.delegations.notSaved': 'The cover was not saved',
  'settings.approvals.delegations.none': 'Nobody covers your approvals, and you cover nobody’s.',
  'settings.approvals.delegations.noJob': 'You signed in through no job, so you have no approvals to cover.',
  'settings.approvals.delegations.pickPerson': 'Name the person who covers you and the first day.',
  'settings.approvals.tiers.hint': 'How much each person can sell or buy without asking. A document within all its submitter’s limits, on value, margin and discount, needs no approval; a blank limit is not checked, and somebody with no tier asks every time.',
  'settings.approvals.tiers.memberNote': 'Placing a person on a tier is an HR action, recorded on their employment, and it applies from their next submission.',
  'settings.approvals.tiers.name': 'Name',
  'settings.approvals.tiers.rank': 'Rank',
  'settings.approvals.tiers.maxValue': 'Up to ($)',
  'settings.approvals.tiers.minMargin': 'Margin at least (%)',
  'settings.approvals.tiers.maxDiscount': 'Discount at most (%)',
  'settings.approvals.tiers.noRail': 'no rail',
  'settings.approvals.tiers.upTo': 'up to',
  'settings.approvals.tiers.marginAtLeast': 'margin at least',
  'settings.approvals.tiers.discountAtMost': 'discount at most',
  'settings.approvals.tiers.members': 'On this tier',
  'settings.approvals.tiers.noMembers': 'Nobody yet.',
  'settings.approvals.tiers.addMember': 'Place a Person',
  'settings.approvals.tiers.remove': 'Remove',
  'settings.approvals.tiers.newTier': '+ Add Tier',
  'settings.approvals.tiers.saveTier': 'Add a Tier',
  'settings.approvals.tiers.placed': 'Placed on the tier',
  'settings.approvals.tiers.removed': 'Removed from the tier',
  'settings.approvals.tiers.noTiers': 'No tier yet: every submission routes through the rules.',
  // Piece 10, decision 0079: the org chart, its two actions and the reporting-line exceptions queue.
  'org.title': 'Org Chart',
  'org.subtitle': 'Who reports to whom, as at a date. Drag a person onto their new manager, or use Move person.',
  'org.asAt': 'Chart as at',
  'org.unplaced': 'Unplaced',
  'org.unplacedHint': 'Nobody is above these people, so nothing they raise routes anywhere.',
  'org.exceptionsLink': 'Open the reporting line exceptions',
  'org.readOnly': 'Changing a reporting line needs the org chart permission.',
  'org.readOnly.what': 'the org chart',
  // T2164: the one read-only sentence every page draws where the viewer may read and may not change.
  'readOnly.note': 'You have read-only access to {what}.',
  'org.empty': 'No positions are in force on this date.',
  'org.reports': 'reports',
  'org.movePerson': 'Move Person',
  'org.moveTitle': 'Move Person',
  'org.newManager': 'New manager',
  'org.noManager': 'Nobody',
  'org.effectiveDate': 'Effective date',
  'org.department': 'Department',
  'org.level': 'Level',
  'org.noLevel': 'No Level',
  'org.reason': 'Reason',
  'org.replacementHead': 'Replacement department head',
  'org.moveSaved': 'Moved',
  'org.pastDateNote': 'A past date is accepted and rewrites nothing: the position in force ends the day before, and a new dated position opens.',
  'org.changeHead': 'Change Department Head',
  'org.headTitle': 'Change Department Head',
  'org.head': 'Department head',
  'org.headSaved': 'Department head changed',
  'org.headTasks': 'approval rules named the outgoing head and now await confirmation',
  'org.departments': 'Departments',
  'org.departmentsHint': 'The same badge marks a head on their own row in the tree above.',
  'org.headOfPrefix': 'Head of',
  'org.collapseAll': 'Collapse All',
  'org.expandAll': 'Expand All',
  'org.noHead': 'No head',
  'org.exceptionsTitle': 'Reporting Line Exceptions',
  'org.exceptionsSubtitle': 'Every person whose reporting line does not reach the top, and every line a departure re-pointed.',
  'org.exceptionsEmpty': 'Every reporting line reaches the top.',
  'org.openChart': 'Open the org chart',
  'org.positionHistory': 'Position history',
  // T726, decision 1495: Set level, the pencil on the Person page's Employments row for a position at our own Company.
  'employment.level.set': 'Set level',
  'employment.level.title': 'Set Level',
  'employment.level.hint': 'The level prices this worker’s labor. Saving opens a new dated position carrying it, the way a move does, and keeps the manager and the department.',
  // T728, decision 1500: Delete this link, the administrator's correction of a link made by mistake.
  'link.delete.label': 'Delete this link',
  'link.delete.title': 'Delete This Link',
  'link.delete.hint': 'Delete a link only when it was never true. A link that was true and stopped is closed with an end date instead. The link is removed for good, and the reason stays on the record’s History.',
  'link.delete.reason': 'Why this link was a mistake',
  'link.delete.confirm': 'Delete Link',
  'link.delete.done': 'The link is deleted.',
  // Piece 22: the Pending Approvals queue at /approvals (piece 8 names the queue; piece 12 registers it).
  'approvals.title': 'Pending Approvals',
  'approvals.subtitle': 'Documents awaiting approval — yours to decide, and everyone else’s',
  'approvals.empty': 'Nothing is waiting. A document outside its submitter’s rails lands here when it is submitted.',
  'approvals.mine': 'Awaiting your decision',
  'approvals.others': 'Awaiting somebody else',
  'approvals.unroutable': 'Held for an administrator',
  'approvals.from': 'from',
  'approvals.step': 'step',
  // Decision 8960: a File Release's row opens the record its file sits on.
  'approvals.openRecord': 'Open the record',
  'approval.reason.label': 'Approval',
  'approval.standing.pending': 'Pending approval',
  'approval.standing.held': 'held for an administrator',
  'approval.standing.approved': 'Approved',
  'approval.standing.rejected': 'Rejected',
  'approval.submitForApproval': 'Submit for approval',
  'approval.submitted': 'Submitted for approval. It goes out once it is approved.',
  'approvals.approve': 'Approve',
  'approvals.reject': 'Reject',
  'approvals.rejectReason': 'Reason for rejecting',
  'approvals.approved': 'Approved',
  'approvals.floor.title': 'Approve Below the Floor',
  'approvals.floor.body': 'Say why this quote is approved below its floor. The reason is kept with the quote, and the quote is approved in the same act.',
  'approvals.floor.reason': 'Reason',
  'approvals.floor.confirm': 'Acknowledge and Approve',
  'approvals.rejected': 'Rejected, back to Draft with the reason',
  'approvals.unnumbered': 'unnumbered',
  'approvals.onlyTypes': 'The list holds the document types that have their screens today, and the Dashboard tile counts the same documents this list holds.',
  'approvals.documentType': 'Document type',
  'approvals.tile.caption': 'documents awaiting approval',
  'approvals.tile.open': 'Open approvals queue',
  'approvals.tile.yours': 'yours to decide',
  'approvals.tile.empty': 'Nothing is waiting on anybody.',
  // Piece 22: the Purchase Order, the RFQ's comparison and modal additions, the Receive/Ship Confirmation, the vendor reps and the RFQ chase defaults.
  'deal.rfq.acknowledge': 'Send these lines anyway',
  'deal.rfq.alarmAfter': 'Alarm after (working days)',
  'deal.rfq.chaseEvery': 'Chase every (working days)',
  'deal.rfq.chaseHint': 'Blank reads the defaults under Settings › Accounting › RFQ Chase Defaults.',
  'deal.rfq.confirmRepeat': 'Ask this vendor again',
  'deal.rfq.needsAcknowledgement': 'Acknowledge the unassigned lines, or untick them.',
  'deal.rfq.needsRepeatConfirmation': 'Confirm asking this vendor again, or change the vendor or the lines.',
  'deal.rfq.repeat': 'Already asked',
  'deal.rfq.repeatHint': 'This vendor has already been asked about some of these lines on this deal. Send it again for a chase, a changed specification or a stale price.',
  'deal.rfq.unassigned': 'Unassigned lines',
  'deal.rfq.unassignedHint': 'No vendor supplies, reps or has sold us these; the request goes out anyway once you acknowledge it.',
  'po.action.approve': 'Approve',
  'po.action.confirmShipment': 'Confirm Shipment',
  'po.action.createBill': 'Create Bill',
  'po.action.close': 'Close',
  'po.action.receiveUnsent': 'Receive Before Sending',
  'po.receive.unsentRefusal': 'The order has not been sent to the vendor.',
  'po.receive.overrideReason': 'Why these goods arrived before the order was sent',
  'po.receive.overrideHint': 'An administrator records this, for goods that arrived on a phone order. The reason is kept on the order’s history.',
  'po.receive.unsentWarning': 'Goods were received against this order before it was sent to the vendor. The receipt stands; add a note saying why the order never went.',
  'po.receive.overrideContinue': 'Continue to Receive',
  'po.receive.closedRefusal': 'This purchase order is Closed, so it receives nothing and confirms no shipment. Raise a new order for a late delivery.',
  'po.edit.closedRefusal': 'This purchase order is Closed, so it is not edited. Raise a new order for anything still to come.',
  'po.revision.notSent': 'Not sent to the vendor',
  'po.revision.label': 'revision',
  'po.revision.sent': 'Sent to the vendor',
  'po.revision.sentRevision': 'Sent to the vendor · revision',
  'po.revision.draft': 'Draft revision',
  'po.revision.vendorHolds': 'the vendor holds revision',
  'po.send.receivedRefusal': 'A purchase order that has received material, or whose drop shipment is confirmed, is not sent again; the vendor already holds the order it delivered against',
  'po.action.pdf': 'View PDF',
  'sheet.failed': 'The sheet did not open.',
  'sheet.blocked': 'The browser blocked the sheet’s tab. Allow pop-ups for this site and press again.',
  'po.action.receive': 'Receive',
  'po.action.reject': 'Reject',
  'po.action.resubmit': 'Resubmit for Approval',
  'po.action.send': 'Send to Vendor',
  'po.action.void': 'Void',
  'po.approval.approved': 'Approved',
  'po.approval.notRequired': 'No approval needed',
  'po.approval.pending': 'Pending approval',
  'po.approval.rejected': 'Rejected',
  'po.approvedNotice': 'Approved.',
  'po.confirmedNotice': 'Shipment confirmed.',
  'po.emptyLines': 'The order carries no lines.',
  'po.escalation': 'Approval',
  'po.fact.createdBy': 'Ordered by',
  'po.fact.deal': 'Deal',
  'po.fact.receiveAt': 'Receive at',
  'po.fact.rfq': 'From RFQ',
  'po.fact.salesOrder': 'Sales order',
  'po.fact.shipTo': 'Ship to',
  'po.fact.vendor': 'Vendor',
  'po.lines.received': 'received',
  'po.lines.title': 'Lines',
  // The order's own page, in the two modes decision 0409 gives every accounting document.
  'po.receipts.title': 'Lines and Receipts',
  'po.form.deliveryAddress': 'Delivery address',
  'po.form.destinationLocations': 'Locations',
  'po.form.destinationCustomer': 'Customer',
  'po.form.changeDestination': 'Change Destination',
  'po.form.number': 'Purchase Order #',
  'po.form.numberOnSave': 'Allocated from the series when the order is saved',
  // 🔒 Decision 9600 (T3428): the one-time tax exempt form on a Purchase Order, its Region 7 notice and its Settings page.
  'po.print.exemptNotice': 'Tax exempt: unit exemption certificate attached',
  'po.form.exemptForm': 'Tax Exempt Form',
  'po.exempt.transactionLine': 'Purchase Order {number}',
  // 🔒 The refusals of the one-time form (T3447), sentences the Settings page, the order's save and its Download all show.
  'po.exempt.unavailable': 'A one-time tax exempt form cannot be filled here; choose None.',
  'po.exempt.noState': 'The ship-to site\u2019s address names no state, so no one-time tax exempt form can be filled; choose None.',
  'po.exempt.noSalesTax': '{state} has no general sales tax, so no one-time tax exempt form is filled there; choose None.',
  'po.exempt.noForm': '{state} has no state form the application can fill as a one-time certificate; choose None.',
  'po.exempt.reasonClaims': 'The {form} one-time certificate for {state} claims Resale or Manufacturing.',
  'po.exempt.noNumber': 'Settings \u203a Tax \u203a Our Exemption holds no exemption number for {state}; add it there, or choose None.',
  'po.exempt.noSigner': 'Settings \u203a Tax \u203a Our Exemption names nobody to sign the form; add the signer there, or choose None.',
  'po.exempt.numbersNotList': 'the exemption numbers are a list of lines, each a state code then the number',
  'po.exempt.numbersBadLine': '\u201c{line}\u201d is not a state code then a number, such as PA 12345678',
  'po.exempt.numbersBadState': '{code} is not a state code',
  'po.exempt.numbersTwice': '{state} is named twice',
  'po.exempt.signerNotText': 'the signer is a line of text',
  'po.exempt.signerTooLong': 'the signer\u2019s name and title are each at most {limit} characters',
  'po.exempt.reasonSetting': 'the default reason is Resale or Manufacturing',
  'po.exempt.field.sellerName': 'vendor\u2019s name',
  'po.exempt.field.sellerStreet': 'vendor\u2019s street',
  'po.exempt.field.sellerCity': 'vendor\u2019s city',
  'po.exempt.field.sellerState': 'vendor\u2019s state',
  'po.exempt.field.sellerZip': 'vendor\u2019s ZIP code',
  'po.exempt.field.purchaserName': 'organization\u2019s name',
  'po.exempt.field.purchaserStreet': 'organization\u2019s street',
  'po.exempt.field.purchaserCity': 'organization\u2019s city',
  'po.exempt.field.purchaserState': 'organization\u2019s state',
  'po.exempt.field.purchaserZip': 'organization\u2019s ZIP code',
  'po.exempt.field.number': 'exemption number',
  'po.exempt.field.date': 'date signed',
  'po.exempt.field.reasonText': 'reason for the exemption',
  'po.exempt.field.signer': 'signer\u2019s name and title',
  'po.exempt.field.transaction': 'purchase order line',
  'po.exempt.badCharacter': 'The {field} holds \u201c{character}\u201d, which the form\u2019s lettering cannot draw; change it to plain letters, or choose None.',
  'po.exempt.cutShort': 'The form\u2019s {field} box holds {limit} characters, so \u201c{value}\u201d was cut short.',
  'po.form.exemptNone': 'None',
  'po.form.exemptOneTime': 'One-time Certificate',
  'po.form.exemptReason': 'Exemption Reason',
  'po.form.exemptState': 'State',
  'po.form.exemptHint': 'The {form} form for {state}, filled from Settings › Tax › Our Exemption and sent with the order as its second attachment.',
  'po.form.exemptNeedsSite': 'The state is read from the ship-to site’s address once one is chosen.',
  'settings.ourExemption.title': 'Our Exemption',
  'settings.ourExemption.hint': 'What a Purchase Order’s one-time tax exempt form prints for us as the purchaser. The legal name and address are the organization’s own company’s.',
  'settings.ourExemption.numbers': 'Exemption Numbers by State',
  'settings.ourExemption.numbersHint': 'One line per state: its two-letter code, then the number, such as PA 12345678.',
  'settings.ourExemption.signerName': 'Signer’s Name',
  'settings.ourExemption.signerTitle': 'Signer’s Title',
  'settings.ourExemption.reason': 'Default Reason',
  'po.print.termsStock': 'Please confirm the order and the delivery date, quoting the purchase order number on the packing slip and the invoice.',
  'po.print.termsDropShip': 'Please ship directly to the address above and send the shipping confirmation, with the date and the tracking reference, quoting the purchase order number.',
  'po.lines.total': 'Total',
  'po.method.dropShip': 'Drop ship',
  'po.method.stock': 'Stock',
  'po.new.hint': 'Every line clearing its margin floor creates the order approved; a line short of it, or with nothing to resell, sends the order for approval.',
  'po.new.lines': 'Lines',
  'po.new.method': 'Fulfillment',
  'po.new.methodDropShip': 'Drop ship — sent to the customer',
  'po.new.methodStock': 'Stock — received at our site',
  'po.new.needsLines': 'Add at least one line with an item and a quantity.',
  'po.new.needsSalesOrder': 'Pick the sales order the drop ship is for.',
  'po.new.needsSite': 'Pick the site that receives the order.',
  'po.new.needsVendor': 'Pick the vendor.',
  'po.new.noShipTo': 'no ship-to address',
  'po.new.salesOrder': 'Sales order',
  'po.new.salesOrderEntity': 'Sales order',
  'po.new.salesOrderHint': 'A drop ship names the sales order it ships against and goes to that order’s address.',
  'po.new.save': 'Create purchase order',
  'po.new.shipTo': 'Receiving site',
  'po.new.vendor': 'Vendor',
  'po.new.vendorEntity': 'Vendor',
  'po.notFound': 'This purchase order could not be loaded.',
  'po.pill.notReceived': 'Not Received',
  'po.pill.partiallyReceived': 'Partially Received',
  'po.pill.received': 'Received',
  'po.pill.void': 'Void',
  'po.receivedNotice': 'Receipt recorded.',
  'po.reject.reason': 'Reason',
  'po.reject.title': 'Reject Purchase Order',
  'po.rejectedNotice': 'Rejected; the submitter has a to-do.',
  'po.rejection': 'Rejected because',
  'po.resubmittedNotice': 'Resubmitted for approval.',
  // 🔒 Decision 3141: the purchase order's question at issue, for each item a line pays more for than its item file says.
  'po.priceRise.title': 'Purchase Price Above the Item File',
  'po.priceRise.intro': 'This order pays more for these items than their item file says. Answer each before the order goes.',
  'po.priceRise.pays': 'this order pays',
  'po.priceRise.file': 'the item file says',
  'po.priceRise.question': 'Adjust the item’s purchase price to this order’s price?',
  'po.priceRise.yes': 'Yes',
  'po.priceRise.no': 'No',
  'po.priceRise.salesPrice': 'New sales price',
  'po.priceRise.sellsToday': 'It sells today at',
  'po.priceRise.salesHint': 'The price offered is the item’s normal margin on this order’s price, or today’s price where that is higher; a price under its minimum margin is refused.',
  'po.priceRise.answerFirst': 'Answer the purchase price question for each item first.',
  'po.priceRise.checking': 'Checking this order’s prices against the item file.',
  'po.priceRise.failed': 'The prices could not be checked against the item file. Close this window and open it again.',
  'po.send.email': 'Email address',
  'po.send.message': 'Message to the vendor',
  'po.send.send': 'Send',
  'po.send.sent': 'Sent to the vendor.',
  'po.send.title': 'Send Purchase Order',
  'po.shipments.none': 'No shipment confirmed yet.',
  'po.shipments.title': 'Shipments',
  'po.void.hint': 'The number stays and the order reads Void. An order with a receipt against it is not voided.',
  'po.void.reason': 'Reason',
  'po.void.title': 'Void Purchase Order',
  'po.voidedNotice': 'Voided.',
  'po.closedNotice': 'Closed. The order stays on the list as Closed.',
  'po.working': 'Show margin working',
  'po.workingFailed': 'The working could not be read.',
  'po.workingHide': 'Hide margin working',
  'po.workingTitle': 'Margin working',
  'rfq.addVendor.hint': 'The vendor gets its own email with this request’s lines and never sees who else was asked.',
  'rfq.addVendor.title': 'Add Vendor',
  'rfq.edit.title': 'Edit Request',
  'rfq.edit.hint': 'The day answers are due, how often silent vendors are chased and when you are alarmed. The vendors already asked are not mailed again.',
  'rfq.edit.save': 'Save',
  'rfq.award.hint': 'A purchase order is raised from this vendor’s priced lines and routed through approval; the request closes as awarded.',
  'rfq.award.needsSite': 'Pick the site that receives the order.',
  'rfq.award.save': 'Raise Purchase Order',
  'rfq.award.shipTo': 'Receive at',
  'rfq.award.title': 'Award',
  'rfq.page.addVendor': '+ Add Vendor',
  'rfq.page.alarm': 'Alarm after',
  'rfq.page.awaiting': 'Awaiting',
  'rfq.page.award': 'Award',
  'rfq.page.awarded': 'Awarded as',
  'rfq.page.bomPrice': 'BOM price',
  'rfq.page.bomUpdated': 'BOM lines repriced:',
  'rfq.page.chase': 'Chase every',
  'rfq.page.days': 'd',
  'rfq.page.deal': 'Deal',
  'rfq.page.declined': 'Declined',
  'rfq.page.default': 'default',
  'rfq.page.due': 'Responses due',
  'rfq.page.expired': 'Expired',
  'rfq.page.expires': 'expires',
  'rfq.page.issued': 'Issued',
  'rfq.page.lead': 'lead',
  'rfq.page.line': 'Line',
  'rfq.page.loadFailed': 'This request could not be loaded.',
  'rfq.page.lowest': 'lowest',
  'rfq.page.min': 'min',
  'rfq.page.noPrice': '—',
  'rfq.page.noVendors': 'No vendor has been asked yet.',
  'rfq.page.of': 'of',
  'rfq.page.pdf': 'PDF',
  'rfq.page.priced': 'priced',
  'rfq.page.qty': 'Qty',
  'rfq.page.quote': 'Quote',
  'rfq.page.received': 'Received',
  'rfq.page.record': 'Record Response',
  'rfq.page.sheet': 'Excel sheet',
  'rfq.page.total': 'Total priced',
  'rfq.page.useForBom': 'Use for BOM Pricing',
  'rfq.page.vendors': 'Vendors',
  'rfq.page.workingDays': 'working days',
  'rfq.response.declined': 'The vendor declined to quote',
  'rfq.response.expiresOn': 'Quote expires on',
  'rfq.response.hint': 'Leave a line blank the vendor did not price; eight of ten is an ordinary answer.',
  'rfq.response.leadTime': 'Lead time (days)',
  'rfq.response.minimum': 'Minimum quantity',
  'rfq.response.needsLine': 'Price at least one line, or mark the vendor as declined.',
  'rfq.response.notes': 'Vendor notes',
  'rfq.response.save': 'Save Response',
  'rfq.response.title': 'Record Response',
  'rfq.response.unitPrice': 'Unit price',
  'rfq.response.vendorQuote': 'Vendor quote number',
  'settings.accounting.alarmAfter': 'Alarm after (working days)',
  'settings.accounting.chaseEvery': 'Chase every (working days)',
  'settings.accounting.rfqChase': 'RFQ Chase Defaults',
  'settings.accounting.rfqChaseHint': 'Working days between chases to a silent vendor, and working days after issue before the requester is alarmed; a request can name its own.',
  'ship.confirm.carrier': 'Carrier',
  'ship.confirm.hint': 'The date the goods left, the carrier and the tracking reference. A drop ship closes here; nothing enters our stock.',
  'ship.confirm.hintStock': 'The date the goods left, the carrier, the tracking reference and the site they left from.',
  'ship.confirm.needsDate': 'Enter the date the goods left.',
  'ship.confirm.needsOrigin': 'Pick the site the goods left.',
  'ship.confirm.needsTracking': 'Enter the tracking reference.',
  'ship.confirm.origin': 'Shipped from',
  'ship.confirm.save': 'Confirm shipment',
  'ship.confirm.shippedOn': 'Shipped on',
  'ship.confirm.title': 'Ship Confirmation',
  'ship.confirm.tracking': 'Tracking reference',
  'ship.receive.date': 'Received on',
  'ship.receive.hint': 'Each line offers what is left to receive. Change it to what arrived; a partial receipt is ordinary, and the rest stays open.',
  'ship.receive.needsLine': 'Enter a quantity on at least one line.',
  'ship.receive.note': 'Note',
  'ship.receive.quantity': 'Received now',
  'ship.receive.remaining': 'left',
  'ship.receive.save': 'Record receipt',
  'ship.receive.title': 'Receive',
  'vendorRep.add': '+ Add Category',
  'vendorRep.category': 'Category',
  'vendorRep.hint': 'A vendor is asked about anything in a category, and every category beneath it, when a request for quotation goes out.',
  'vendorRep.inactive': 'off',
  'vendorRep.none': 'This vendor represents no category yet.',
  'vendorRep.remove': 'Stop',
  'vendorRep.restore': 'Restore',
  'vendorRep.title': 'Categories Represented',
  // Piece 22, decision 0388: the three Settings › Accounting entries the Work Order bills through.
  'settings.travel.title': 'Travel Billing',
  'settings.travel.hint': 'Travel is billed portal to portal from a company site, at twice the one-way drive time, rounded up once. A drive time older than the staleness window is looked up again before it is billed.',
  'settings.travel.rounding': 'Round the round trip up to (minutes)',
  'settings.travel.staleness': 'Look a cached drive time up again after (days)',
  'settings.travel.origin': 'Dispatch origin when no assigned worker names one',
  'settings.travel.originHint': 'The shop a job dispatches from, used where no assigned worker carries a dispatch origin of their own. A job raised with neither has no origin at all, so name one before the first work order.',
  'settings.travel.noOrigin': 'No site named',
  'settings.followUp.title': 'Follow-Up Reason Codes',
  'settings.followUp.hint': 'Why a second trip happened, and whether it bills. A code in use is deactivated, never deleted, and a single work order can override its billable answer with a reason.',
  'settings.followUp.addButton': '+ Add Reason Code',
  'settings.followUp.addTitle': 'Add a Reason Code',
  'settings.followUp.code': 'Code',
  'settings.followUp.label': 'What it means',
  'settings.followUp.billable': 'Bills the customer',
  'settings.followUp.absorbed': 'Absorbed',
  'settings.followUp.billableShort': 'bills the customer',
  'settings.followUp.absorbedShort': 'we absorb it',
  'settings.followUp.makeBillable': 'Make Billable',
  'settings.followUp.makeAbsorbed': 'Make Absorbed',
  'settings.labour.title': 'Labor and Skill Levels',
  'settings.labour.levelsHint': 'A level bills at the sales price of the labor item it names, so a level with no item prices nobody. The customer pays for the person sent, whatever level the job asked for.',
  'settings.labour.level': 'Level',
  'settings.labour.levelName': 'Name',
  'settings.labour.labourItem': 'Labor item',
  'settings.labour.noLabourItem': 'No item — this level prices nobody',
  'settings.labour.rate': 'Rate',
  'settings.labour.addLevel': 'Add a Level',
  'settings.labour.addLevelButton': '+ Add Level',
  'settings.labour.levelsTitle': 'Skill Levels',
  'settings.labour.addSkillButton': '+ Add Skill',
  'settings.labour.addSkillTitle': 'Add a Skill',
  'settings.labour.skills': 'Skills',
  'settings.labour.skillsHint': 'The kinds of work sold, added as the business learns to sell new ones. A skill carries no rate: the rate is the level, not the skill.',
  'settings.labour.skillName': 'Skill name',
  'settings.labour.skillDescription': 'Description',
  'settings.labour.certification': 'Certification Required Before Dispatch',
  // The certification-blocks read answered with nothing to preview; it kept this wording when a tile's empty line became "None yet" (decision 1443).
  'settings.labour.certificationBlocksNone': 'Nothing here yet.',
  'settings.labour.certificationOn': 'Requires certification',
  // T2364: the Knowledge Base quiz that proves a skill, chosen on the skill's row by its article's title.
  'settings.labour.provingQuiz': 'Proving quiz',
  'settings.labour.noProvingQuiz': 'None',
  // Decision 0464: the capabilities beneath a skill, typed on the same screen as the skill itself.
  'settings.labour.subSkills': 'Sub-skills',
  'settings.labour.subSkillsHint': 'What somebody who does this work can actually do — the languages, the platforms, the dialects. A job asks for them by line, and a worker who holds none of one is warned about at dispatch and never refused.',
  'settings.labour.subSkillName': 'Sub-skill name',
  'settings.labour.addSubSkill': 'Add',
  'settings.labour.noSubSkills': 'None yet',
  'settings.labour.retireSubSkill': 'Retire',
  'settings.labour.restoreSubSkill': 'Bring Back',
  'settings.labour.modifiersHint': 'How the priority and after-hours premiums combine, and how much the after-hours premium is. A factor of 1.5 means one and a half times, in every installation.',
  'settings.labour.composition': 'How the premiums combine',
  'settings.labour.afterHours': 'After-hours premium (a factor)',
  'settings.labour.blocksTitle': 'Turning this on blocks these assigned workers',
  'settings.labour.blocksNone': 'Nobody assigned today is blocked by turning this on.',
  'settings.labour.blocksConfirm': 'Turn It on Anyway',
  'settings.labour.blocksCancel': 'Leave It Off',
  // Piece 22: the Work Order page — its footer per status, its lines with the workers as chips, the parts and the travel line.
  'workOrder.action.schedule': 'Schedule',
  'workOrder.action.submit': 'Submit for Approval',
  'workOrder.action.start': 'Start Work',
  'workOrder.action.complete': 'Complete',
  'workOrder.action.close': 'Close',
  'workOrder.action.closeWithoutBilling': 'Close Without Billing',
  'workOrder.action.backToDraft': 'Back to Draft',
  'workOrder.action.createFollowUp': 'Create Follow-Up',
  // 🔒 Decision 12700 (T3820): the cancel writes Void, the piece's own cancel, under the words a person looks for.
  'workOrder.action.void': 'Cancel Work Order',
  'workOrder.action.delete': 'Delete',
  'workOrder.action.hold': 'Put on Hold',
  'workOrder.action.resume': 'Resume',
  'workOrder.line.add': 'Add a Labor Line',
  'workOrder.line.description': 'What the line is',
  'workOrder.line.level': 'Level required',
  'workOrder.line.hours': 'Hours',
  'workOrder.line.addWorker': 'Assign a Worker',
  'workOrder.line.worker': 'Worker',
  'workOrder.line.noWorkers': 'Nobody assigned yet',
  'workOrder.line.remove': 'Remove',
  'workOrder.line.cost': 'cost',
  'workOrder.line.complete': 'Mark Complete',
  'workOrder.line.notComplete': 'Mark Not Complete',
  'workOrder.line.reason': 'Why it did not finish',
  'workOrder.line.missingSkill': 'The skill the scope never named',
  'workOrder.line.unassignable': 'no level on their position, so they cannot be assigned',
  'workOrder.line.mismatchHigher': 'above the level asked for',
  'workOrder.line.mismatchLower': 'below the level asked for',
  'workOrder.line.notBillable': 'not billable',
  // 🔨 A reason code reads as a sentence, never as its stored member (found in review, 2026-09-15).
  'workOrder.line.reasonRequiresHigherSkillLevel': 'Needed a higher skill level',
  'workOrder.line.reasonMissingPart': 'A part was missing',
  'workOrder.line.reasonNoSiteAccess': 'No access to the site',
  'workOrder.line.reasonRanOutOfDaylight': 'Ran out of daylight',
  'workOrder.line.reasonWrongEquipment': 'The wrong equipment was on the van',
  // The three situations of decision 0246, drawn as what happened rather than as a letter.
  'workOrder.line.situationA': 'diagnosed and stopped',
  'workOrder.line.situationB': 'started and handed over',
  'workOrder.line.situationC': 'did the whole job at the level sent',
  // Decision 0464: what the visit needs, and who on it does not hold one.
  'workOrder.line.subSkillsRequired': 'Needs',
  'workOrder.line.subSkillAdd': 'Add a Sub-Skill This Line Needs',
  'workOrder.line.subSkillPick': 'Sub-skill',
  'workOrder.line.subSkillRemove': 'Remove',
  'workOrder.line.subSkillNone': 'No sub-skill named for this line',
  'workOrder.line.subSkillMissingPhrase': 'is not recorded as able to do',
  'workOrder.line.subSkillOpenPerson': 'Open Their Page',
  // 🔒 Decision 2282: a worker short of a certification the job's checklists ask for.
  'workOrder.line.trainingMissingPhrase': 'holds no current certification the checklist asks for:',
  'workOrder.material.none': 'No parts fitted yet',
  'workOrder.travel.recompute': 'Look the Drive Up Again',
  'workOrder.travel.override': 'Enter It by Hand',
  'workOrder.travel.minutes': 'Round-trip minutes',
  'workOrder.travel.reason': 'Why the figure is being overridden',
  'workOrder.travel.unresolved': 'Unresolved — the invoice will refuse until this is answered',
  // T3819 (piece 29): what the lookup found and how it was priced, under the figure.
  'workOrder.travel.from': 'From {place}',
  'workOrder.travel.to': 'To {place}',
  'workOrder.travel.distance': '{miles} mi one way',
  'workOrder.travel.basis': 'The {minutes} min above is the round trip, out and back',
  'workOrder.travel.basisCovered': 'No minutes are billed: the Agreement covers travel, and the drive was {oneWay} minutes one way',
  'workOrder.travel.basisCoveredNoLookup': 'No minutes are billed: the Agreement covers travel',
  'workOrder.travel.basisCapped': 'The {minutes} min above is the Agreement’s cap, and the drive was {oneWay} minutes one way',
  'workOrder.travel.basisCappedNoLookup': 'The {minutes} min above is the Agreement’s cap',
  'workOrder.travel.basisLookup': 'The {minutes} min above is the round trip: twice the {oneWay}-minute one-way drive, rounded up',
  'workOrder.travel.lookedUp': 'Looked up {when}',
  'workOrder.travel.lookedUpThrough': 'Looked up {when} through {service}',
  'workOrder.travel.outsideCalls': 'Open Outside Calls',
  'workOrder.travel.enteredByHand': 'Entered by hand by {person}, {when}',
  'workOrder.travel.enteredByUnknown': 'a person the record does not name',
  'workOrder.travel.replacedLookup': 'It replaced the looked-up {minutes} min',
  'workOrder.travel.replaced': 'It replaced the earlier {minutes} min',
  'workOrder.travel.reasonLine': 'Why: {reason}',
  'workOrder.travel.rule': 'Travel Billing: {rate} an hour, the round trip rounded up to the next {increment} min, so {minutes} min bills as {hours} h',
  'workOrder.travel.ruleNoRounding': 'Travel Billing: {rate} an hour, so {minutes} min bills as {hours} h',
  'workOrder.travel.minimum': 'The labor Item has a minimum of {hours} h, which counts travel and labor together',
  'workOrder.travel.billed': 'Billed to the customer',
  'workOrder.travel.billedCapped': 'Billed to the customer, capped by the Agreement at {minutes} min',
  'workOrder.travel.absorbedAgreement': 'Absorbed: the Agreement covers travel',
  'workOrder.travel.absorbedFollowUp': 'Absorbed: the follow-up reason is not billable',
  'workOrder.schedule.title': 'Book the Visit',
  'workOrder.schedule.start': 'Starts',
  'workOrder.schedule.end': 'Ends',
  'workOrder.onSite.title': 'Who is on site',
  'workOrder.onSite.none': 'No worker is assigned to this Work Order yet. Assign one on a line or when booking the visit.',
  'workOrder.onSite.checkIn': 'Check In',
  'workOrder.onSite.checkOut': 'Check Out',
  'workOrder.onSite.in': 'Checked in',
  'workOrder.onSite.elsewhere': 'Checked in elsewhere, at',
  'workOrder.onSite.notIn': 'Not checked in',
  'workOrder.onSite.noLocation': 'no location shared',
  'workOrder.onSite.withLocation': 'Checked in with your location.',
  'workOrder.onSite.refused': 'The check-in was refused:',
  'workOrder.onSite.gateTitle': 'Gate Sign-In',
  'workOrder.onSite.gateHint': 'This site runs gate sign-in, so the worker signs in on the gate device with their own clock PIN. The gate writes the billed arrival and the check-in together.',
  'workOrder.onSite.device': 'Gate device',
  'workOrder.onSite.pin': 'The worker’s clock PIN',
  'workOrder.onSite.noDevice': 'No gate device is registered at this site, so the gate cannot take an entry here yet.',
  'workOrder.schedule.workers': 'Assigned workers',
  'workOrder.schedule.workerEntity': 'Worker',
  'workOrder.schedule.workersHint': 'The workers are sent for every line of this Work Order and join each line’s workers, beside anybody a line already has. A visit booked with workers is sent to them at once.',
  'workOrder.followUp.title': 'Create a Follow-Up',
  'workOrder.followUp.reason': 'Why the second trip',
  'workOrder.followUp.lines': 'The unfinished lines carry over, each one level up.',
  'workOrder.void.title': 'Cancel Work Order',
  'workOrder.void.reason': 'Why it is being canceled',
  'workOrder.void.keep': 'Keep the Job',
  // 🔒 Decision 12700 (T3820): Delete on a Work Order made by mistake, before its visit goes to a crew.
  'workOrder.delete.title': 'Delete This Work Order',
  'workOrder.delete.lead': 'Delete {number}? It leaves every list, its unstarted visits and notes go with it, and History on its account names the deletion.',
  'workOrder.delete.confirm': 'Delete',
  'workOrder.delete.reading': 'Reading what the job carries…',
  'workOrder.delete.unread': 'What the job carries could not be read, so nothing is deleted.',
  'workOrder.delete.anyway': 'Delete Anyway',
  'workOrder.delete.anywayLead': 'As a super administrator you may delete it anyway, to the Trash: {subject}.',
  'workOrder.visits.deleteAnyway': 'Delete Anyway',
  'workOrder.visits.deleteAnywayReading': 'Reading what the visit carries…',
  'workOrder.visits.deleteAnywayUnread': 'Delete Anyway could not read what this visit carries; reload the page to try again.',
  'workOrder.hold.title': 'Put This Job on Hold',
  'workOrder.hold.reason': 'What the job is waiting on',
  'workOrder.hold.note': 'A note for whoever picks it up (optional)',
  'workOrder.hold.reasonParts': 'Parts',
  'workOrder.hold.reasonCustomer': 'Customer',
  'workOrder.hold.reasonWeather': 'Weather',
  'workOrder.hold.reasonAccess': 'Access',
  'workOrder.hold.banner': 'On hold for {reason}, held {duration}',
  'workOrder.hold.minutes': '{n} min',
  'workOrder.hold.hours': '{n} h',
  'workOrder.hold.days': '{n} d',
  'workOrder.new.action': '+ New Work Order',
  'workOrder.new.customer': 'Customer',
  'workOrder.new.site': 'Site the work happens at',
  'workOrder.new.deal': 'Deal it belongs to',
  'workOrder.new.dealNotTheirs': 'That Deal is not one of this customer’s. Pick one of theirs or leave it empty.',
  'workOrder.new.agreement': 'Agreement it is covered by',
  'workOrder.new.start': 'Scheduled start (leave empty until it is booked)',
  'workOrder.new.noneYet': 'None',
  'workOrder.new.startInvalid': 'The scheduled start is not a date and a time.',
  'workOrder.rateWorking': 'How This Was Priced',
  'workOrder.rateWorkingHide': 'Hide How This Was Priced',
  // Piece 23: the Chart of Accounts, the Manual Journal, the Accounting periods and the blocked-postings queue.
  'accounting.subtitle': 'Accounting',
  'accounting.chart.title': 'Chart of Accounts',
  'accounting.chart.new': '+ New Account',
  'accounting.chart.empty': 'No account matches.',
  'accounting.chart.hint': 'Grouped by type. An inactive account hides from new entry, keeps its postings and is never deleted.',
  'accounting.chart.column.number': 'Number',
  'accounting.chart.column.name': 'Name',
  'accounting.chart.column.subtype': 'Subtype',
  'accounting.chart.column.section': 'Cash Flow',
  'accounting.chart.column.balance': 'Balance',
  'accounting.chart.column.active': 'Active',
  'accounting.yes': 'Yes',
  'accounting.no': 'No',
  'accounting.account.newTitle': 'New Account',
  'accounting.account.number': 'Number',
  'accounting.account.name': 'Name',
  'accounting.account.type': 'Type',
  'accounting.account.parent': 'Parent account',
  'accounting.account.noParent': 'No parent',
  'accounting.account.subtype': 'Subtype',
  'accounting.account.section': 'Cash flow section',
  'accounting.account.chooseSubtype': 'Choose a subtype',
  'accounting.account.chooseSection': 'Choose a section',
  'accounting.account.contra': 'Contra account: presented by its own sign, never as a negative balance',
  'accounting.account.control': 'Control account: every line against it names a Company',
  'accounting.account.active': 'Is Active',
  'accounting.account.description': 'Description',
  'accounting.account.save': 'Save',
  'accounting.account.saved': 'Saved',
  'accounting.account.balance': 'Balance',
  'accounting.account.recent': 'Recent postings',
  'accounting.account.noPostings': 'Nothing has posted to this account yet.',
  'accounting.account.deleteNote': 'An account is never deleted. Turn Is Active off to hide it from new entry; its postings stay.',
  'accounting.account.subAccounts': 'Sub-accounts',
  'accounting.account.back': '← Chart of Accounts',
  'accounting.account.inactive': 'Inactive',
  // 🔒 Set Opening Balance on an account's page (decision 10120; T3480): one entry, the account against 3100 Retained Earnings.
  'accounting.opening.act': 'Set Opening Balance',
  'accounting.opening.post': 'Post',
  'accounting.opening.date': 'Opening date',
  'accounting.opening.amount': 'Opening balance',
  'accounting.opening.side': 'Side',
  'accounting.opening.debit': 'Debit',
  'accounting.opening.credit': 'Credit',
  'accounting.opening.hint': 'One entry: this account on the side shown, and 3100 Retained Earnings on the other. The date is offered as the day before the books open.',
  'accounting.opening.negativeHint': 'A negative figure posts on the other side, as an overdrawn bank account opens.',
  'accounting.opening.needsDate': 'Choose the opening date first.',
  'accounting.opening.needsAmount': 'Enter a figure other than zero first.',
  'accounting.opening.edit': 'Edit',
  'accounting.opening.posted': 'Opening balance posted as',
  // 🔨 A subtype, a cost basis, a cost-of-goods election and a credit status read as words, never as their stored spelling (found in review, 2026-09-15).
  'accounting.detailType.currentAsset': 'Current asset',
  'accounting.detailType.fixedAsset': 'Fixed asset',
  'accounting.detailType.otherAsset': 'Other asset',
  'accounting.detailType.currentLiability': 'Current liability',
  'accounting.detailType.longTermLiability': 'Long-term liability',
  'accounting.detailType.equityDetail': 'Equity',
  'accounting.detailType.income': 'Income',
  'accounting.detailType.costOfGoodsSold': 'Cost of goods sold',
  'accounting.detailType.operatingExpense': 'Operating expense',
  'accounting.detailType.otherIncome': 'Other income',
  'accounting.detailType.otherExpense': 'Other expense',
  'accounting.costBasis.fifo': 'FIFO — first in, first out',
  'accounting.costBasis.weightedAverage': 'Weighted average',
  'accounting.cogsTiming.atPayment': 'When the invoice is paid',
  'accounting.cogsTiming.atSale': 'When the sale is made',
  'credit.status.good': 'Good standing',
  'credit.status.watch': 'Watch',
  'credit.status.hold': 'On hold',
  'accounting.journal.title': 'Manual Journal',
  'accounting.journal.hint': 'A direct double-entry adjustment no document generates — depreciation, an accrual, a correction. Debits must equal credits before Post is offered, and a posted entry is never edited: it is reversed.',
  'accounting.journal.date': 'Entry date',
  'accounting.journal.memo': 'Memo',
  'accounting.journal.timing': 'Cash timing',
  'accounting.journal.timingImmediate': 'Immediate — in the cash view',
  'accounting.journal.timingAccrualOnly': 'Accrual only — never in cash',
  'accounting.journal.timingHint': 'Chosen here, never defaulted: an entry no document backs cannot be re-timed by a settlement.',
  'accounting.journal.reason': 'Reason',
  'accounting.journal.reasonHint': 'Required when a line reaches 3100 Retained Earnings: that is a prior-period adjustment.',
  'accounting.journal.reversing': 'Reversing entry: write the opposite entry on a future date',
  'accounting.journal.reverseOn': 'Reverse on',
  'accounting.journal.account': 'Account',
  'accounting.journal.chooseAccount': 'Choose an account',
  // T3713: the cash timing's own placeholder; it had borrowed the account's.
  'accounting.journal.chooseTiming': 'Choose when cash moves',
  // T3531: the read notice's words for a report whose tree named no section.
  'import.compare.notice.noSection': 'no section',
  'accounting.journal.debit': 'Debit',
  'accounting.journal.credit': 'Credit',
  'accounting.journal.company': 'Company',
  'accounting.journal.item': 'Item',
  'accounting.journal.companyHint': 'Required on a control account, 1100 and 2000.',
  'accounting.journal.lineDescription': 'Line description',
  'accounting.journal.addLine': '+ Add Line',
  'accounting.journal.removeLine': 'Remove',
  'accounting.journal.difference': 'Out of balance by',
  'accounting.journal.balanced': 'Balanced',
  'accounting.journal.post': 'Post',
  'accounting.journal.posted': 'Posted',
  'accounting.journal.recent': 'Recent entries',
  'accounting.journal.description': 'Description',
  'accounting.journal.reverse': 'Reverse',
  'accounting.journal.reversedBy': 'reversed by',
  'accounting.journal.reverses': 'reverses',
  'accounting.journal.noEntries': 'Nothing has posted yet.',
  'accounting.journal.lines': 'lines',
  // T137: who created the entry and when, read off the columns the posting stores.
  'accounting.journal.createdBy': 'Created by',
  // T3699: a Draft's saver is whoever saved it last.
  'accounting.journal.lastSavedBy': 'Last saved by',
  'accounting.journal.postWhyDifference': 'Post is available when the debits equal the credits.',
  'accounting.journal.postWhyLines': 'Post is available with at least two filled lines.',
  'accounting.journal.postWhyMemo': 'Post is available once the memo is typed.',
  'accounting.journal.postWhyTiming': 'Post is available once the cash timing is chosen.',
  'accounting.journal.postWhyReverseOn': 'Post is available once the Reversing date is chosen.',
  // Decision 0760 (T138): the person's own reference beside the entry number the series gives.
  'accounting.journal.reference': 'Reference number',
  // Decision 0761 (T139): the Manual Journal as a list and a document page.
  'accounting.journal.listTitle': 'Manual Journals',
  'accounting.journal.fact.number': 'Journal number',
  'accounting.journal.fact.date': 'Journal date',
  'accounting.journal.fact.notes': 'Notes',
  'accounting.journal.fact.timing': 'Cash timing',
  'accounting.journal.fact.reverses': 'Reverses',
  'accounting.journal.fact.reversedBy': 'Reversed by',
  'accounting.journal.lines.title': 'Journal Details',
  'accounting.journal.lines.none': 'This entry carries no lines.',
  'accounting.journal.newTitle': 'New Manual Journal',
  'accounting.journal.saveDraft': 'Save as Draft',
  'accounting.journal.saveDraftEdits': 'Save Draft',
  'accounting.journal.draftSaved': 'Draft saved. It posts nothing until Post is pressed.',
  'accounting.journal.draftHint': 'A Draft posts nothing to the books. Post moves the account balances, dated the entry date above; the date can be changed until then.',
  'accounting.journal.draftReversingHint': 'A Draft does not keep a Reversing date; tick Reversing when you post it.',
  'accounting.journal.reverse.title': 'Reverse this journal?',
  'accounting.journal.reverse.consequence': 'A reversing entry is posted today with every debit and credit swapped, and the two are linked. The original is never edited.',
  'accounting.journal.reversed': 'Reversed',
  'accounting.journal.void': 'Void',
  'accounting.journal.void.title': 'Void this Draft?',
  'accounting.journal.void.consequence': 'The Draft keeps its number and reads Void. It posts nothing, and its lines can no longer be edited.',
  // The Void entry's header: its last saver is the voider.
  'accounting.journal.voided': 'Voided by',
  'accounting.periods.title': 'Accounting Periods',
  'accounting.periods.aboutHeading': 'Accounting periods',
  'accounting.periods.hint': 'One row per month of the fiscal year. Periods close in order; a closed month refuses any posting dated into it until an Admin reopens it with a reason. Closing writes nothing to the ledger.',
  'accounting.periods.close': 'Close',
  'accounting.periods.reopen': 'Reopen',
  'accounting.periods.reopenReason': 'Reason for reopening',
  'accounting.periods.closed': 'Closed',
  'accounting.periods.reopened': 'Reopened',
  'accounting.periods.on': 'on',
  'accounting.periods.by': 'by',
  'accounting.periods.fiscalYear': 'FY',
  'accounting.periods.reopenReport': 'Reopen report',
  'accounting.periods.reopenReportHint': 'Every reopen, with its reason and who did it. The reopen path is a control weakness by design; this list is its price.',
  'accounting.periods.noReopens': 'No period has been reopened.',
  'accounting.blocked.title': 'Postings Blocked by a Closed Period',
  'accounting.blocked.hint': 'A scheduled posting a closed month refused, never re-dated into the open one. Reopen the month with a reason, run the job again, then mark the row resolved here.',
  'accounting.blocked.resolve': 'Mark Resolved',
  'accounting.blocked.resolved': 'Resolved',
  'accounting.blocked.empty': 'Nothing is blocked.',
  // Piece 23, decision 0407: the ledger's self-checks — the drift queue, and the working its route answers.
  'accounting.drift.title': 'Ledger Drift to Resolve',
  'accounting.drift.hint': 'Where the books and the documents behind them disagree. Nothing here is corrected automatically: a correcting entry posted by a job would balance the books and hide its cause. Read the two figures, fix what caused them, then resolve the row with a reason.',
  'accounting.drift.empty': 'The ledger ties out. Nothing disagrees.',
  'accounting.drift.documents': 'On the documents',
  'accounting.drift.ledger': 'In the ledger',
  'accounting.drift.difference': 'Difference',
  'accounting.drift.firstEntry': 'The figures parted at',
  'accounting.drift.reason': 'Why this is resolved',
  'accounting.drift.resolve': 'Resolve',
  'accounting.drift.resolved': 'Resolved',
  'accounting.drift.runNow': 'Run the Tie-Outs Now',
  'accounting.drift.ranNow': 'Tie-outs run',
  // Piece 23 (2026-09-16): an account's two modes (decision 0430), opening a period (decision 0431),
  // what a period is in the page's own words, and the journal's two money columns.
  'accounting.account.edit': 'Edit Account',
  'accounting.account.cancelEdit': 'Cancel',
  'accounting.account.details': 'Account details',
  'accounting.account.contraLabel': 'Contra account',
  'accounting.account.controlLabel': 'Control account',
  'accounting.periods.what': 'An accounting period is one month of your fiscal year. It is the month a journal entry belongs to, decided by the entry date on the document and not by the day somebody keyed it.',
  'accounting.periods.whatClosing': 'Closing a month freezes it. Nothing further posts into that month, so a report of it reads the same next quarter as it does today.',
  'accounting.periods.openNext': 'Open the Next Period',
  'accounting.periods.openNextHint': 'A month appears in this list on its own, the first time anything posts into it. Open the next period writes the month after the last one below, so it is here to be closed in order.',
  'accounting.periods.cadence': 'How long a period runs',
  'accounting.periods.cadenceHint': 'Changing this re-cuts the periods, so it is refused once any period has been closed.',
  'accounting.periods.cadenceChanged': 'Periods now run',
  'accounting.periods.closeTitle': 'Close This Period',
  'accounting.periods.closeAnyway': 'Close the period',
  'accounting.periods.closing': 'Closing…',
  'accounting.periods.nothingOpen': 'Nothing dated in this period is still open. Closing it locks the dates against any further posting.',
  'accounting.periods.openItemsWarning': 'Some things dated inside this period are still open. Closing it does not delete them — it stops anything further being posted into these dates.',
  'accounting.periods.openItemsHint': 'Finish or re-date them first, or close now and reopen the period if one of them has to be posted.',
  'accounting.periods.openedPeriod': 'Opened',
  'accounting.journal.entryTotals': 'Entry totals',
  'accountJournal.title': 'Account Journal',
  'accountJournal.none': 'Nothing has posted to the ledger from this document yet.',
  'accountJournal.total': 'Total',
  'accountJournal.reversal': 'Reversal',
  // Decision 0584: the Settings entries about one subject are one page with a
  // section each, so the titles below name the page rather than the setting.
  'settings.group.basis': 'Accounting Basis',
  'settings.group.basisIntro': 'Which basis reports open on, what inventory is costed at, and when cash-basis cost of goods is recognized.',
  'settings.group.approvals': 'Approvals',
  'settings.group.approvalsIntro': 'Which documents need an approval, and the money bands that decide who gives it.',
  // Decision 0707 (T228): one Settings entry for sales tax, its five sections in the order a state's position is worked through.
  'settings.group.salesTax': 'Sales Tax',
  'settings.group.salesTaxIntro': 'Where we are registered, each state\u2019s rate and what it taxes, when a nexus warning is raised, and the exemption forms we offer.',
  'settings.group.siteRequirements': 'Site Requirements',
  'settings.group.siteRequirementsIntro': 'The requirement types every site is judged against, and the requirements set on a company\u2019s own sites.',
  'settings.group.fileTypes': 'File Types and Blocked Extensions',
  'settings.group.fileTypesIntro': 'Each document type with its classification, and the extensions an upload is refused for.',
  'settings.group.customerCredit': 'Customer Credit',
  'settings.group.customerCreditIntro': 'The credit limit a company falls back to, and what a late invoice is charged.',
  'settings.group.expenses': 'Expenses',
  'settings.group.expensesIntro': 'Each expense category with the account it posts to, and the per-mile rate a mileage claim pays.',
  'settings.group.emailTemplates': 'Email Templates and Footer',
  'settings.group.emailTemplatesIntro': 'The template each document type is sent with, and the legal footer every outbound message carries.',
  'settings.group.websiteLeads': 'Website Leads',
  'settings.group.websiteLeadsIntro': 'Where a lead from the website is routed, and the senders a lead is refused from.',
  // Decision 2790 (T1610): seventeen Settings entries about one job are five rules pages and a section.
  'settings.group.documentRules': 'Document Rules',
  'settings.group.documentRulesIntro': 'What is offered when a quote is won, when a stale date warns before a send, how often a vendor is chased for a reply, and when a credit note may carry a line that cannot be returned.',
  'settings.group.paymentRules': 'Payment Rules',
  'settings.group.paymentRulesIntro': 'How far a bill may differ from its order, what counts as a bank match, which way check numbers run, and what a credit card payment adds.',
  'settings.group.dispatchRules': 'Dispatch Rules',
  'settings.group.dispatchRulesIntro': 'The windows an emergency call is approved and answered in, how long a changed visit stays quiet before its workers are told, and how travel is billed.',
  'settings.group.pipelineRules': 'Pipeline Rules',
  'settings.group.pipelineRulesIntro': 'The amount above which a Quick Deal needs approval and how long an absent owner’s action waits, how many days a site may go without contact before a follow-up is due, and whether a discount is one figure in the footer or a column on each line.',
  'settings.group.hrRules': 'HR Rules',
  'settings.group.hrRulesIntro': 'Where a complaint goes when it cannot stay inside the company, how long an HR document is kept, and the day counts the HR reminders run on.',
  // Piece 23: the five Settings › Accounting entries this piece owns.
  'settings.category.accounting': 'Accounting',
  'settings.accounting.notSet': 'Not set',
  'settings.accounting.inForce': 'In force now:',
  'settings.accounting.from': 'from',
  'settings.accounting.effectiveFrom': 'Effective from',
  'settings.accounting.remove': 'Remove',
  'settings.accounting.noHistory': 'Nothing has been set yet.',
  'settings.accounting.chooseMethod': 'Choose a method',
  'settings.accounting.nonReturnable': 'Non-Returnable Line on a Credit Note',
  'settings.accounting.nonReturnableHint': 'What happens when a credit note credits a line that is not returnable. Warn asks for a reason and an authorizer against the note; Refuse blocks the line, and the line is edited on the source document instead. Both leave the same evidence, so changing this never changes what is recoverable.',
  'settings.accounting.cogsTiming': 'Cash-basis COGS timing',
  'settings.accounting.cogsTimingTitle': 'Cash-Basis COGS Timing',
  'settings.accounting.cogsTimingHint': 'On the cash basis, whether inventory is deducted when it is paid for or when it is sold. It ships unset on purpose: this is a tax position, and the return preparer answers it. While it is unset the cash view refuses to show cost of goods rather than guessing.',
  'settings.accounting.defaultBasis': 'Default reporting basis',
  'settings.accounting.defaultBasisTitle': 'Default Reporting Basis',
  'settings.accounting.defaultBasisHint': 'The basis a financial statement opens on. Every statement carries its own Cash and Accrual toggle, so this is only where they start; postings are made once, on the accrual basis, and the cash view is derived from them.',
  'settings.accounting.costBasis': 'Cost basis',
  'settings.accounting.costBasisTitle': 'Cost Basis',
  'settings.accounting.costBasisHint': 'How the cost of stock sold is worked out: first in, first out, or the average cost. It starts unset, and stock cannot leave until it is chosen, because a cost booked by a method nobody chose is wrong for good. A change applies from its own date and changes nothing already booked.',
  'settings.accounting.financeCharge': 'Finance Charge',
  'settings.accounting.financeChargeHint': 'The share of an unpaid balance charged every thirty days, and who is told after the fourth period. Both start empty, and nothing is charged until a rate is set. Several states cap commercial interest, so take advice on the lawful figure.',
  'settings.accounting.rate': 'Rate per period (%)',
  'settings.accounting.escalation': 'Escalation target (employment)',
  'settings.accounting.escalationHint': 'The employment the fourth period raises a to-do for. After that period nothing further is charged on that invoice.',
  'settings.accounting.escalationEntity': 'person',
  'settings.accounting.escalationNobody': 'Nobody yet',
  'settings.accounting.escalationClear': 'Clear the Target',
  // Piece 25: the Sales Tax Rate Table and the Nexus Notification Thresholds, the two Settings entries this piece builds.
  'settings.tax.rateTable': 'Sales Tax Rate Table',
  // Decision 0646: the mark on a rate the seed wrote, until a person enters the state's own figure over it.
  'settings.tax.seededMark': 'Seeded starting point: the statewide base rate published for 2025. Confirm it against the state before filing.',
  // Decision 0708 (T230): one line per section; the rest is the Sales tax page's help topic.
  'settings.tax.rateTableHint': 'The rate in force for each state. Open a state\u2019s arrow for the rates it replaced.',
  'settings.tax.checkForUpdates': 'Check for Updates',
  'settings.tax.state': 'State',
  'settings.tax.chooseState': 'Choose a state',
  'settings.tax.rate': 'Rate (%)',
  'settings.tax.localRange': 'Documented local range',
  'settings.tax.useTaxOnLocal': 'Use tax on local',
  'settings.tax.source': 'Source',
  'settings.tax.effectiveFrom': 'Effective from',
  'settings.tax.addRate': 'Add a Rate',
  'settings.tax.addRateButton': '+ Add Rate',
  'settings.tax.rateColumn': 'Rate',
  'settings.tax.until': 'until',
  'settings.tax.showEarlier': 'Show the rows this one replaced',
  'settings.tax.hideEarlier': 'Hide the rows this one replaced',
  'settings.tax.seededPill': 'Seeded \u2014 Confirm',
  'settings.tax.statesWithoutRate': 'States with no rate entered',
  // Decision 0741 (T284): the register is one table of every state, grouped, each opening in place.
  'settings.tax.states.hint': 'Every state is listed, the ones we are registered in first; the rest stand folded under their count until you open that group. Open a state to see everything about it: its facts across the top, then its rates, what it taxes, its forms, its returns and its review lines.',
  'settings.tax.states.registered': 'Registered',
  'settings.tax.states.notRegistered': 'Not registered',
  'settings.tax.states.basisColumn': 'Basis',
  'settings.tax.states.frequencyColumn': 'Filing',
  'settings.tax.states.open': 'Show this state\u2019s details',
  'settings.tax.states.close': 'Hide this state\u2019s details',
  // Decision 2793 (T2037): a seeded rate reads on the collapsed line as quiet text, the pill stays in the Rates box.
  'settings.tax.states.unconfirmed': 'unconfirmed',
  // Decision 2793: the Not registered group starts folded behind its count.
  'settings.tax.states.groupOpen': 'Show the states we are not registered in',
  'settings.tax.states.groupClose': 'Hide the states we are not registered in',
  'settings.tax.states.unconfirmedNote': 'Unconfirmed means the rate is the seeded starting point and nobody has entered the state\u2019s own figure over it. Open the state to confirm it.',
  'settings.tax.states.taxesTitle': 'What This State Taxes',
  'settings.tax.states.taxesHint': 'One treatment per tax category, from the day it started. A seeded answer is the research\u2019s starting point, to be confirmed against the state. A Default one is charged at the standard rate and is on the review report below until somebody confirms it.',
  'settings.tax.states.noStatewideTaxRules': 'No statewide sales tax, so the state taxes none of these and no answer is kept.',
  'settings.tax.states.note': 'Note (optional)',
  'settings.tax.states.customRate': 'Rate (%)',
  'settings.tax.states.answerSource': 'Source (optional)',
  'settings.tax.states.answerVerified': 'Verified on (empty is today)',
  'settings.tax.states.answerLaw': 'Law citation (optional)',
  'settings.tax.states.seededAnswer': 'Seeded from the research of 2026-09-25. Confirm it against the state before filing.',
  'settings.tax.states.unanswered': 'Nobody has answered for this state yet, so it is charged at the standard rate and is on the review report.',
  'settings.tax.states.since': 'since',
  'settings.tax.states.change': 'Change',
  'settings.tax.states.treatment': 'Treatment',
  'settings.tax.states.chooseTreatment': 'Choose a treatment',
  'settings.tax.states.answerFrom': 'From (empty is today)',
  'settings.tax.states.saveAnswer': 'Save the Answer',
  'settings.tax.states.answerSaved': 'The answer is saved from its own date, and the one it replaced is kept.',
  'settings.tax.states.formsTitle': 'Exemption Forms',
  'settings.tax.states.formsHint': 'The state\u2019s own forms, then the two multistate certificates. A hidden form leaves the forms library and the certificate window.',
  'settings.tax.states.noStatewideTax': 'No statewide sales tax, so no exemption form is needed.',
  'settings.tax.states.noOwnForm': 'No form of this state\u2019s own is in the library. The state publishes:',
  'settings.tax.states.multistate': 'Multistate',
  'settings.tax.states.registrationTitle': 'Registration',
  'settings.tax.states.notRegisteredYet': 'Not registered in this state.',
  'settings.tax.states.register': 'Register in This State',
  'settings.tax.states.liabilityAccount': 'Liability account',
  'settings.tax.states.agency': 'Pays',
  'settings.tax.states.accountNumber': 'State account number',
  'settings.tax.states.dueRule': 'Due',
  'settings.tax.states.dueRuleText': 'months after the period ends, on day',
  'settings.tax.states.sales': 'Sales in the window',
  'settings.tax.states.threshold': 'Published threshold',
  'settings.tax.states.earlierRegistration': 'Earlier registration',
  'settings.tax.states.ratesTitle': 'Rates',
  'settings.tax.states.ratesHint': 'The state\u2019s rate first, then each county, town and district rate charged on top of it. Each line is a dated series, and its arrow opens the rates it replaced.',
  'settings.tax.states.noRate': 'No rate is entered for this state.',
  'settings.tax.states.basisAccrual': 'Accrual',
  'settings.tax.states.basisCash': 'Cash',
  'settings.tax.states.basisAccrualWithBadDebtCredit': 'Accrual, bad-debt credit',
  'settings.tax.states.unreachable': 'This part could not be read. The rest of the state stands.',
  // Decision 1524 (T787): a state's open view, one facts band across the top and five tabs under it, and the count pill on its closed row.
  'settings.tax.states.attention': 'to settle',
  'settings.tax.states.attentionReturns': 'returns outstanding',
  'settings.tax.states.attentionReview': 'review lines unsettled',
  // Decision 1780 (T1077): Set up a state, six steps each read from the registration's own rows.
  'settings.tax.setup.title': 'Set Up a State',
  'settings.tax.setup.hint': 'The six things a state needs before its returns can be filed. Each step reads what is saved, so a step done elsewhere shows as done here.',
  'settings.tax.setup.fact': 'Setup',
  'settings.tax.setup.open': 'Set Up',
  'settings.tax.setup.left': 'of 6 steps left',
  'settings.tax.setup.allDone': 'Every step is done.',
  'settings.tax.setup.saved': 'The registration is saved.',
  'settings.tax.setup.save': 'Save',
  'settings.tax.setup.register.title': 'Register with the state and enter its account number',
  'settings.tax.setup.register.body': 'Register on the state revenue department\u2019s own site first. The account number it gives you goes on every return.',
  'settings.tax.setup.register.none': 'We are not registered in this state yet.',
  'settings.tax.setup.register.noStatewide': 'This state levies no statewide sales tax, so there is nothing to register.',
  'settings.tax.setup.register.since': 'Registered since',
  'settings.tax.setup.register.noAccount': 'no state account number entered yet.',
  'settings.tax.setup.register.account': 'State account number',
  'settings.tax.setup.frequency.title': 'Set how often returns are filed',
  'settings.tax.setup.frequency.body': 'The state tells you the frequency when you register. The due rule is the months after the period ends and the day of that month.',
  'settings.tax.setup.frequency.none': 'No filing frequency is set, so no return is created for this state.',
  'settings.tax.setup.frequency.files': 'Files',
  'settings.tax.setup.frequency.due': 'due',
  'settings.tax.setup.frequency.monthsAfter': 'months after the period ends, on day',
  'settings.tax.setup.frequency.held': 'A return already stands for this registration, so its frequency stays as it is. The due rule can still change.',
  'settings.tax.setup.register.accountBlank': 'Type the account number the state gave you, then press Save.',
  // Decision 10090 (T3500): the refusal a delete meets, and the kinds it counts.
  'attributeUse.refusal': '{name} is used by {words}; retire it instead.',
  'attributeUse.cameIntoUse': '{name} came into use while it was being deleted; retire it instead.',
  'attributeUse.join': '{list} and {last}',
  'attributeUse.separator': ', ',
  'attributeUse.item': '{count} item',
  'attributeUse.items': '{count} items',
  'attributeUse.category': '{count} category',
  'attributeUse.categories': '{count} categories',
  'attributeUse.assetComponent': '{count} asset component',
  'attributeUse.assetComponents': '{count} asset components',
  'attributeUse.maintenanceSchedule': '{count} maintenance schedule',
  'attributeUse.maintenanceSchedules': '{count} maintenance schedules',
  'attributeUse.importMapping': '{count} import mapping',
  'attributeUse.importMappings': '{count} import mappings',
  'attributeUse.record': '{count} record',
  'attributeUse.records': '{count} records',
  'settings.tax.setup.register.accountUnchanged': 'The account number is already saved. Change it to save a new one.',
  'settings.tax.setup.frequency.blank': 'Choose how often the state wants the return, then press Save.',
  'settings.tax.setup.frequency.waits': 'Register with the state on step 1 first.',
  'settings.tax.setup.rate.title': 'Confirm the state rate',
  'settings.tax.setup.rate.body': 'Check the state rate against the state\u2019s own site and add it on the Rates tab. A confirmed rate drops the starting-point mark.',
  'settings.tax.setup.rate.none': 'The rate table carries no state rate in force today.',
  'settings.tax.setup.rate.seeded': 'is still the starting point the setup wrote, and nobody has confirmed it.',
  'settings.tax.setup.rate.confirmed': 'is confirmed.',
  'settings.tax.setup.rate.open': 'Open the Rates',
  'settings.tax.setup.taxes.title': 'Answer what the state taxes',
  'settings.tax.setup.taxes.body': 'Answer each category below with the pencil at its end. A Default answer is charged at the standard rate and stays on the review report until somebody answers it.',
  'settings.tax.setup.taxes.unsettled': 'categories read Default or have no answer.',
  'settings.tax.setup.taxes.of': 'of',
  'settings.tax.setup.taxes.done': 'Every category has an answer.',
  'settings.tax.setup.local.title': 'Settle the local rates',
  'settings.tax.setup.local.body': 'Each location below is checked against the address service as the list opens. Use the corrected address or keep the one typed, and either confirms the location\u2019s tax jurisdiction. Where the state\u2019s local rates are all in the table, a location needs only its county.',
  'settings.tax.setup.local.enterCounty': 'Open the location and pick its county; the location settles once it has one.',
  'settings.tax.setup.local.noCounty': 'locations have no county entered.',
  'settings.tax.setup.local.unconfirmed': 'locations have no confirmed tax jurisdiction.',
  'settings.tax.setup.local.estimated': 'locations have an address whose house number is only estimated.',
  'settings.tax.setup.local.done': 'Every location in this state is settled.',
  'settings.tax.setup.local.coverageNone': 'The state has no local sales tax.',
  'settings.tax.setup.local.coveragePartial': 'The table holds some of the state\u2019s local rates.',
  'settings.tax.setup.local.coverageComplete': 'The table holds all of the state\u2019s local rates.',
  'settings.tax.setup.local.untaxed': 'Invoices that charged no tax on a day the registration covers:',
  'settings.tax.setup.local.listEmpty': 'No location in this state waits on its county or a confirmation, and no invoice charged no tax.',
  'settings.tax.setup.local.rowNoCounty': 'No county entered; it pays the state rate alone until one is.',
  'settings.tax.setup.local.rowUnconfirmed': 'Nobody has confirmed its tax jurisdiction.',
  'settings.tax.setup.local.rowEstimated': 'Its house number is only estimated; press Verified by User on the Location before local tax is looked up.',
  'settings.tax.setup.local.rowConfirmed': 'Jurisdiction confirmed on',
  'settings.tax.setup.local.rowSettled': 'Settled.',
  // Decision 2220: a row the address service offers nothing for draws an × that confirms it as typed; an address used in another state leaves the list.
  'settings.tax.setup.local.noCorrection': 'No correction offered',
  'settings.tax.setup.local.unlistedArea': 'The offered address is in “{area}”, a place this list does not know, so it was not used. Open the Location and enter the address yourself.',
  'settings.tax.setup.local.leftState': 'Its address now names another state, so it left this state\u2019s list:',
  'settings.tax.setup.local.tryAgain': 'Try Again',
  'settings.tax.setup.local.verifyAgain': 'Verify Again',
  'settings.tax.setup.local.invoicesTitle': 'Invoices That Charged No Tax',
  'settings.tax.setup.nexus.title': 'Read the nexus warning',
  'settings.tax.setup.nexus.body': 'The warning watches our sales in the state against its published threshold. A crossing is accepted on the register, and never dismissed.',
  'settings.tax.setup.nexus.none': 'No threshold row yet. Enter the state\u2019s published threshold so the warning can fire.',
  'settings.tax.setup.nexus.crossed': 'The threshold was crossed on',
  'settings.tax.setup.nexus.notAccepted': 'and nobody has accepted the crossing.',
  'settings.tax.setup.nexus.done': 'A threshold is set and no crossing is waiting.',
  'settings.tax.setup.nexus.accept': 'Accept the Crossing',
  // Decision 1781 (T1078): Exempt customer, four steps under a site's Tax exempt certificates tile.
  'site.exemptSteps.title': 'Exempt Customer Steps',
  'site.exemptSteps.noState': 'The site\u2019s address names no state, so no exemption form can be named yet.',
  'site.exemptSteps.noCertificate': 'No certificate is recorded for this site\u2019s state yet.',
  'site.exemptSteps.waits.state': 'Give the site an address that names its state first.',
  'site.exemptSteps.waits.certificate': 'Record the certificate on step 2 first.',
  'site.exemptSteps.edit': 'Edit the Certificate',
  'site.exemptSteps.findForm.title': 'Find the state\u2019s exemption form',
  'site.exemptSteps.findForm.body': 'The forms library keeps the state\u2019s own form and the multistate ones. The customer fills it in and signs it.',
  'site.exemptSteps.findForm.form': 'The form for',
  'site.exemptSteps.findForm.is': 'is',
  'site.exemptSteps.findForm.none': 'The forms library lists no form of its own for',
  'site.exemptSteps.requestOrRecord.title': 'Request it, or record one received',
  'site.exemptSteps.requestOrRecord.body': 'Request a certificate, below, mails a contact a link to upload the signed form. A form already in hand is recorded here.',
  'site.exemptSteps.requestOrRecord.recorded': 'A certificate is recorded, effective from',
  'site.exemptSteps.requestOrRecord.open': 'Open request links waiting for the upload:',
  'site.exemptSteps.requestOrRecord.none': 'No request link is open and no certificate is recorded.',
  'site.exemptSteps.checkFields.title': 'Check the fields read off the form',
  'site.exemptSteps.checkFields.body': 'Compare the form type, the reason and the start date with the signed form, in the certificate window.',
  'site.exemptSteps.checkFields.read': 'Read:',
  'site.exemptSteps.checkFields.from': 'from',
  'site.exemptSteps.checkFields.missing': 'The certificate still needs:',
  'site.exemptSteps.checkFields.formType': 'its form type',
  'site.exemptSteps.checkFields.reason': 'its exemption reason',
  'site.exemptSteps.confirm.title': 'Confirm it covers this site',
  'site.exemptSteps.confirm.body': 'Saving the certificate window confirms the certificate. An upload exempts nothing until a person has confirmed it.',
  'site.exemptSteps.confirm.reviewed': 'A person has confirmed the certificate, so it exempts this site\u2019s sales in its state.',
  'site.exemptSteps.confirm.awaiting': 'The certificate is awaiting review, so it exempts nothing yet.',
  // Decision 1782 (T1079): File and pay a return, four steps for one return period.
  'settings.tax.returnSteps.title': 'File and Pay a Return',
  'settings.tax.returnSteps.open': 'File and Pay',
  'settings.tax.returnSteps.hint': 'The four things one return period needs, from its unsettled charges to the payment. Each step reads the return as it is saved.',
  'settings.tax.returnSteps.review.title': 'Review the period\u2019s unsettled charges',
  'settings.tax.returnSteps.review.body': 'A charge nobody settled is on the return at the standard rate. Settle each line on the state\u2019s Review tab before you file.',
  'settings.tax.returnSteps.review.lines': 'lines of this state wait on the review report.',
  'settings.tax.returnSteps.review.none': 'Nothing in this state is waiting for review.',
  'settings.tax.returnSteps.review.past': 'The return has been filed, so its review is over.',
  'settings.tax.returnSteps.review.open': 'Open the Review',
  'settings.tax.returnSteps.figures.title': 'Read the return\u2019s figures',
  'settings.tax.returnSteps.figures.body': 'These are the figures the state\u2019s return form asks for. Recompute reads the period again from the ledger and the issued invoices.',
  'settings.tax.returnSteps.figures.gross': 'Gross sales',
  'settings.tax.returnSteps.figures.taxable': 'taxable',
  'settings.tax.returnSteps.figures.taxDue': 'tax due',
  'settings.tax.returnSteps.figures.stale': 'The return states nothing while the period had sales, so recompute it.',
  'settings.tax.returnSteps.figures.recompute': 'Recompute',
  'settings.tax.returnSteps.figures.recomputed': 'The return\u2019s figures are read again.',
  'settings.tax.returnSteps.file.title': 'Record the filing with its confirmation number',
  'settings.tax.returnSteps.file.body': 'File the return on the state\u2019s own site, then record the day and the confirmation number it gave back.',
  'settings.tax.returnSteps.file.filed': 'Filed on',
  'settings.tax.returnSteps.file.confirmation': 'confirmation',
  'settings.tax.returnSteps.file.notYet': 'Not filed yet. The return is due on',
  'settings.tax.returnSteps.pay.title': 'Record the payment from the bank account',
  'settings.tax.returnSteps.pay.body': 'Record the remittance once the money has left. It pays the state\u2019s agency and moves the return to Paid.',
  'settings.tax.returnSteps.pay.paid': 'Paid on',
  'settings.tax.returnSteps.pay.notYet': 'No payment is recorded against this return yet.',
  'settings.tax.returnSteps.file.waits': 'Recompute the return\u2019s figures on step 2 first.',
  'settings.tax.returnSteps.pay.waits': 'Mark the return filed with its confirmation number on step 3 first.',
  'settings.tax.view.facts': 'This state at a glance',
  'settings.tax.view.registration': 'Registration',
  'settings.tax.view.registeredSince': 'Registered since',
  'settings.tax.view.account': 'account',
  'settings.tax.view.noAccountNumber': 'no state account number entered',
  'settings.tax.view.nextDue': 'next return due',
  'settings.tax.view.noneDue': 'no return waiting to be filed',
  'settings.tax.view.payable': 'Liability account and agency',
  'settings.tax.view.stateRate': 'State rate',
  'settings.tax.view.ofThreshold': 'of the threshold',
  'settings.tax.view.stageWarning': 'Warning',
  'settings.tax.view.stageCritical': 'Critical',
  'settings.tax.view.review': 'Review',
  'settings.tax.view.reviewNone': 'Nothing to settle.',
  'settings.tax.view.tabs': 'This state\u2019s details',
  'settings.tax.view.tabRates': 'Rates',
  'settings.tax.view.tabTaxes': 'What it taxes',
  'settings.tax.view.tabForms': 'Forms',
  'settings.tax.view.tabReturns': 'Returns',
  'settings.tax.view.tabReview': 'Review',
  'settings.tax.view.returnsHint': 'Every return period for this state, paid ones included, with what is due on it and how late it is.',
  'settings.tax.view.returnsNone': 'No return period has been created for this state yet.',
  'settings.tax.view.returnsNotRegistered': 'We are not registered in this state, so no return is owed.',
  'settings.tax.view.reviewHint': 'These are this state\u2019s charges nobody has settled yet. Each line opens the place where it is settled.',
  'settings.tax.view.reviewEmpty': 'Nothing in this state is waiting for review.',
  'settings.tax.view.answerIt': 'Answer It',
  'settings.tax.view.addTheRate': 'Add the Rate',
  'settings.tax.view.openLocations': 'Open the locations',
  'settings.tax.view.openInvoice': 'Open',
  'settings.tax.view.wouldCarry': 'Would carry',
  'settings.tax.view.stateColumn': 'State',
  'settings.tax.view.periodColumn': 'Period',
  'settings.tax.view.dueColumn': 'Due',
  'settings.tax.view.statusColumn': 'Status',
  'settings.tax.view.taxDueColumn': 'Tax due',
  'settings.tax.view.lateColumn': 'Days late',
  'settings.tax.view.actsColumn': 'Acts',
  // Decision 1020 (T288 part 2): the Local rates part of a state's open line, its two windows, and the coverage the rate window now asks.
  'settings.tax.local.addJurisdiction': '+ Add Jurisdiction',
  'settings.tax.local.levelState': 'State',
  'settings.tax.record.source': 'Source',
  'settings.tax.record.verified': 'Verified',
  'settings.tax.record.effective': 'Effective',
  'settings.tax.record.law': 'Law',
  'settings.tax.record.none': 'No source on record',
  'settings.tax.local.addRate': '+ Add Rate',
  'settings.tax.local.levelCounty': 'County',
  'settings.tax.local.levelMunicipality': 'Municipality',
  'settings.tax.local.levelSpecialDistrict': 'Special district',
  'settings.tax.local.noRateToday': 'No rate in force today',
  'settings.tax.local.from': 'from',
  'settings.tax.local.until': 'until',
  'settings.tax.local.scheduled': 'scheduled',
  'settings.tax.local.withState': 'with the state',
  'settings.tax.local.verified': 'verified',
  'settings.tax.local.inactive': 'Inactive',
  'settings.tax.local.districtNotMatched': 'A special district matches no address yet, so nothing is charged at this rate.',
  'settings.tax.local.coverage': 'Coverage',
  'settings.tax.local.coverageNone': 'None',
  'settings.tax.local.coveragePartial': 'Partial',
  'settings.tax.local.coverageComplete': 'Complete',
  'settings.tax.local.coverageNoneMeans': 'Only the state\u2019s rate is charged here, and no ship-to is reported for local tax.',
  'settings.tax.local.coveragePartialMeans': 'Some of the state\u2019s local taxes are held here. What matches is charged, and each ship-to is on the review report until somebody confirms its jurisdiction on its site.',
  'settings.tax.local.coverageCompleteMeans': 'Every local tax in the state is held here, so a ship-to matching none of them owes no local tax.',
  'settings.tax.local.coverageWhere': 'It changes with a new state rate, added from the tile\u2019s + Add rate.',
  'settings.tax.local.coverageUnknown': 'No state rate stands today, so no coverage is read.',
  'settings.tax.local.emptyNone': 'No local jurisdiction is entered for this state, and none is needed: only the state\u2019s rate is charged.',
  'settings.tax.local.emptyPartial': 'No local jurisdiction is entered for this state yet. A ship-to here pays the state\u2019s rate alone and is on the review report until somebody confirms its jurisdiction.',
  'settings.tax.local.emptyComplete': 'No local jurisdiction is entered for this state, so a ship-to here owes no local tax.',
  'settings.tax.local.emptyUnknown': 'No local jurisdiction is entered for this state.',
  'settings.tax.local.addJurisdictionTitle': 'Add a Local Jurisdiction',
  'settings.tax.local.addRateTitle': 'Add a Local Rate',
  'settings.tax.local.addRateFor': 'Add a rate for',
  'settings.tax.local.level': 'Level',
  'settings.tax.local.chooseLevel': 'Choose a level',
  'settings.tax.local.county': 'County',
  'settings.tax.local.chooseCounty': 'Choose a county',
  'settings.tax.local.countiesUnreachable': 'The county list could not be read, so a County cannot be picked here.',
  'settings.tax.local.noCountyLeft': 'Every county of this state is already a jurisdiction.',
  'settings.tax.local.name': 'Name',
  'settings.tax.local.rate': 'Rate (%)',
  'settings.tax.local.effectiveFrom': 'Effective from (empty is today)',
  'settings.tax.local.source': 'Source',
  'settings.tax.local.verifiedOn': 'Verified on (empty is the day it takes effect)',
  'settings.tax.local.clearsConfirmations': 'Saving puts every site in this state whose jurisdiction was confirmed back on the review report until it is confirmed again.',
  'settings.tax.local.jurisdictionSaved': 'The jurisdiction is saved with its first rate.',
  'settings.tax.local.rateSaved': 'The rate is saved from its own date, and the one it replaced is kept.',
  'settings.tax.localCoverage': 'Local coverage',
  'settings.tax.localCoverageKeep': 'Keep the coverage in force',
  // Piece 25, found in review 2026-09-15: the rule row form, without which the rule table could never be written.
  'settings.tax.rules': 'What each state taxes',
  'settings.tax.rulesHint': 'One dated answer per tax category per state. A pairing with no answer is taxed.',
  'settings.tax.category': 'Tax category',
  'settings.tax.chooseCategory': 'Choose a category',
  'settings.tax.taxable': 'Taxable in this state',
  'settings.tax.taxableYes': 'taxable',
  'settings.tax.taxableNo': 'not taxable',
  'settings.tax.addRule': 'Add the rule',
  'settings.tax.addRuleButton': '+ Add rule',
  'settings.tax.taxableColumn': 'Taxable',
  'settings.tax.nexusTitle': 'Nexus Notification Thresholds',
  'settings.tax.nexusHint': 'A Warning is raised at this share of a state\u2019s published threshold, and a Critical at the crossing itself.',
  'settings.tax.warningPercent': 'Warning at (% of threshold)',
  'settings.tax.criticalRecipient': 'Critical email goes to',
  'settings.tax.criticalRecipientUnset': 'Not set, so no email is sent',
  'settings.tax.criticalRecipientEdit': 'Edit under Documents Inbox rules',
  'settings.tax.superAdminOnly': 'This value is a super admin\u2019s to change.',
  // Piece 25, decision 0425: the tax register in the Accounting area.
  // Decision 0707: the register's own heading, now it is the first section of the Sales tax page.
  'accounting.tax.registerTitle': 'Sales Tax Register',
  'accounting.tax.hint': 'Where we are registered, what each state\u2019s own counters say against its published threshold, the returns each registration owes, and the answers nobody has given yet.',
  'accounting.tax.crossingsHint': 'A state whose threshold we have crossed and nobody has accepted the crossing on. Collection is owed from the crossing date forward, and this is never dismissed silently.',
  'accounting.tax.crossedOn': 'crossed on',
  'accounting.tax.acknowledge': 'Accept the Crossing',
  'accounting.tax.acknowledged': 'The crossing is accepted, and who accepted it is on the state\u2019s own row.',
  'accounting.tax.statesHint': 'Each state we are watching, its rolling counters, and the published test they are measured against.',
  'accounting.tax.statesEmpty': 'No state carries a threshold or any sales yet. Enter each state\u2019s published threshold before a warning can fire.',
  'accounting.tax.transactions': 'transactions',
  'accounting.tax.since': 'since',
  'accounting.tax.threshold': 'threshold',
  'accounting.tax.noThreshold': 'no published threshold entered, so nothing can warn for this state',
  'accounting.tax.registrationsHint': 'Every registration we hold or have held, with the liability account it opened and the agency a remittance pays.',
  'accounting.tax.registeredOn': 'Registered on',
  'accounting.tax.closedOn': 'closed on',
  'accounting.tax.close': 'Close the Registration',
  'accounting.tax.registrationOpened': 'The registration is open, with its own liability account and its own agency record.',
  'accounting.tax.registrationClosed': 'The registration is closed. The row stays, so a return already filed still resolves to it.',
  'accounting.tax.openHint': 'A state cannot be registered until its rate is in the Sales Tax Rate Table, because the return and the liability account both rest on it.',
  'accounting.tax.state': 'State',
  'accounting.tax.chooseState': 'Choose a state',
  'accounting.tax.basis': 'When the tax becomes due',
  'accounting.tax.frequency': 'Filing frequency',
  'accounting.tax.frequencyNotSet': 'frequency not set',
  'accounting.tax.dueMonthOffset': 'Due months after the period ends',
  'accounting.tax.dueDayOfMonth': 'Due on day of month',
  'accounting.tax.open': 'Open a Registration',
  'accounting.tax.newRegistration': 'New Registration',
  'accounting.tax.enterThreshold': 'Enter a State’s Threshold',
  'accounting.tax.filingsHint': 'Each return not yet paid, in every state, with what is due on it and how late it is. A paid return is read in its state\u2019s Returns tab and in Reports \u203a Tax \u203a Filings due. A registration with no frequency set is shown rather than skipped.',
  'accounting.tax.generateFilings': 'Cut the Returns Due',
  'accounting.tax.filingsGenerated': 'The returns due were cut. A second run the same day cuts nothing.',
  'accounting.tax.dueOn': 'due',
  'accounting.tax.daysLate': 'days late',
  'accounting.tax.unmappedHint': 'Each registered state with a charge nobody has settled yet: a category on Default or unanswered, no rate in force, or a location with no county or no confirmed jurisdiction. Open a state to read its lines in its Review tab.',
  'accounting.tax.unmappedEmpty': 'Nothing is waiting for review.',
  // Decision 1524 (T787): the two page-level lists and the crossings carry a heading each, in sentence case.
  'accounting.tax.crossingsTitle': 'Crossings to Accept',
  'accounting.tax.returnsDueTitle': 'Returns Due',
  'accounting.tax.reviewTitle': 'Review Report',
  'accounting.tax.reviewLines': 'lines to settle',
  // T3467: the Review report's sentences, written by the tax service and drawn as they stand (decision 1780).
  'tax.review.stateRate': 'the state rate',
  'tax.review.noRate': '{state} is registered and the rate table carries no rate in force today; every line shipping there is taxed at nothing',
  'tax.review.unanswered': 'Nobody has answered how {state} taxes {category}, so each line is charged at the standard rate until somebody does',
  'tax.review.default': '{state} answers {category} Default, so each line is charged at the standard rate until somebody confirms the answer',
  'tax.review.defaultNote': '{state} answers {category} Default, so each line is charged at the standard rate until somebody confirms the answer: {note}',
  'tax.review.estimatedOne': '{state} carries local taxes and {locations} has an address whose house number is only estimated; a document shipping there is held and charged the state rate alone until each is Verified by User',
  'tax.review.estimatedMany': '{state} carries local taxes and {locations} have an address whose house number is only estimated; a document shipping there is held and charged the state rate alone until each is Verified by User',
  'tax.review.noCountyOne': '{state} has local sales tax by county and {locations} has no county entered; each is charged the state rate alone',
  'tax.review.noCountyMany': '{state} has local sales tax by county and {locations} have no county entered; each is charged the state rate alone',
  'tax.review.unconfirmedOne': '{state} carries local taxes the table holds only some rows for, and {locations} has no confirmed tax jurisdiction; each is charged the state rate and what it matched',
  'tax.review.unconfirmedMany': '{state} carries local taxes the table holds only some rows for, and {locations} have no confirmed tax jurisdiction; each is charged the state rate and what it matched',
  'tax.review.unnumbered': 'an unnumbered invoice',
  'tax.review.invoiceSubject': 'Invoice {number}',
  'tax.review.untaxed': "Issued {day} to a ship-to in {state} and charged no tax, on a day {state}'s registration covers",
  'accounting.tax.openState': 'Open this state',
  'accounting.tax.openReview': 'Open Its Review Lines',
  // Piece 25, found in review 2026-09-15: the two acts the page named and drew no control for.
  'accounting.tax.thresholdHint': 'A state\u2019s published threshold is two numbers and a window, and until one is entered no warning and no crossing can fire for that state.',
  'accounting.tax.salesThreshold': 'Sales threshold ($)',
  'accounting.tax.transactionThreshold': 'Transaction count threshold',
  'accounting.tax.measurementPeriod': 'Measured over',
  'accounting.tax.thresholdFrom': 'In force from',
  'accounting.tax.setThreshold': 'Enter the Threshold',
  'accounting.tax.thresholdSaved': 'The threshold is entered, and the state\u2019s counters were read against it straight away.',
  'accounting.tax.markFiled': 'Filed',
  'accounting.tax.markFiledTitle': 'Mark This Return Filed',
  'accounting.tax.markFiledConfirm': 'Record It as Filed',
  'accounting.tax.markFiledHint': 'The confirmation the state gave back is what proves the return was filed, years later, to somebody who was not there.',
  'accounting.tax.confirmationRef': 'Confirmation from the state',
  'accounting.tax.filedOn': 'Filed on',
  'accounting.tax.filedNotice': 'The return is Filed, with its confirmation and its date.',
  // Decision 0506: Filed to Paid, the remittance the state was actually paid.
  'accounting.tax.remit': 'Record Remittance',
  'accounting.tax.remitTitle': 'Record What Was Paid to the State',
  'accounting.tax.remitConfirm': 'Record the Remittance',
  'accounting.tax.remitHint': 'This writes one payment out to the state\u2019s agency, clears the state\u2019s own liability account by that amount, and moves the return to Paid. It cannot be undone from here.',
  'accounting.tax.remitAccount': 'Paid from',
  'accounting.tax.chooseAccount': 'Choose the bank account',
  'accounting.tax.remitAccountNone': 'No bank account is set up yet, so nothing can be paid from here.',
  'accounting.tax.remitAmount': 'Amount paid ($)',
  'accounting.tax.remitPaidOn': 'Paid on',
  'accounting.tax.remitReference': 'Reference from the agency',
  'accounting.tax.remitNotice': 'The return is Paid, and the payment to the agency stands on the Payments list.',
  // The missing rate, resolved where the person hit it (decisions 0426, 0442).
  'accounting.tax.rateNeededTitle': 'This State Has No Rate Yet',
  'accounting.tax.rateNeededHint':
    'Enter the rate the state publishes and the date it took effect. It is written to the Sales Tax Rate Table, and the registration you were opening is then opened for you.',
  'accounting.tax.rateNeededConfirm': 'Save the Rate and Register',
  'accounting.tax.rateNeededState': 'State',
  'accounting.tax.rateNeededRate': 'State rate (%)',
  'accounting.tax.rateNeededSource': 'Where the rate came from',
  'accounting.tax.rateNeededFrom': 'In force from',
  'accounting.tax.rateNeededSourceHint':
    'Name the publication or page the figure was read off, so the next person can check it without hunting. The table never refreshes itself.',
  // Piece 22, the Sales Order's own page: its edit form, its printed sheet and the one action per status its footer carries (decision 0409).
  'salesOrder.new.account': 'Account',
  'salesOrder.new.accountEntity': 'account',
  'salesOrder.new.billTo': 'Bill to',
  'salesOrder.new.shipTo': 'Ship to',
  'salesOrder.new.notChosen': 'Not chosen',
  'salesOrder.new.displayMode': 'Display mode',
  'salesOrder.new.customerPo': 'Customer PO number',
  'salesOrder.new.customerPoHint': 'The reference the customer issued. It is needed before release, not before the order is raised.',
  'salesOrder.new.paymentTerms': 'Payment terms',
  'salesOrder.new.chooseAccount': 'Pick the account this order is for; its sites and terms fill in once you do.',
  'salesOrder.new.readFailed': 'The account’s sites and terms could not be read, so this order cannot be saved yet.',
  'salesOrder.new.needsBillTo': 'A sales order names the site it bills to.',
  'salesOrder.new.needsLines': 'A sales order carries at least one line.',
  'salesOrder.new.noSites': 'This account has no open site, so there is nowhere to bill until one is added here.',
  /* Found in review, 2026-09-22: a Sales Order converted from a Quote holds the Quote's two sites. */
  'salesOrder.edit.sitesFromQuote': 'These sites came from the accepted quote and are not changed on this order; raise a revised quote to change them.',
  'salesOrder.form.number': 'Number',
  'salesOrder.form.numberOnSave': 'Taken from the series when the order is saved',
  'salesOrder.fact.account': 'Account',
  'salesOrder.fact.deal': 'Deal',
  'salesOrder.fact.quote': 'Converted from',
  'salesOrder.fact.customerPo': 'Customer PO',
  'salesOrder.fact.contractValue': 'Contract value',
  'salesOrder.fact.invoiced': 'Invoiced',
  'salesOrder.fact.created': 'Created',
  // Decision 2630 (T1177): a Draft's control is Send, with Mark as Sent behind its chevron.
  'salesOrder.action.send': 'Send',
  'salesOrder.action.recordCustomerPo': 'Record the Customer PO Number',
  'salesOrder.action.waiveCustomerPo': 'Waive the Customer PO',
  'salesOrder.action.release': 'Release',
  'salesOrder.action.invoice': 'Raise an Invoice',
  'salesOrder.action.changeOrder': 'Raise a Change Order',
  // Decision 0863 (T282): the order raises its purchase order and its Work Order from its title bar.
  'salesOrder.raise.purchaseOrder': 'Raise a Purchase Order',
  'salesOrder.raise.workOrder': 'Raise a Work Order',
  'salesOrder.raise.opened': 'Opened',
  'salesOrder.raise.purchaseOrderOpened': 'Purchase order',
  'salesOrder.raise.workOrderOpened': 'Work order',
  'salesOrder.raise.raise': 'Raise',
  'salesOrder.invoice.title': 'Raise an Invoice',
  'salesOrder.invoice.charges': 'Shipping charges not yet billed',
  'salesOrder.invoice.chargesHint': 'Each ticked charge is added to the invoice as a line on 4200 Shipping Revenue. Untick one to leave it for a later invoice.',
  'salesOrder.invoice.raise': 'Raise Invoice',
  'salesOrder.raise.vendor': 'Vendor',
  'salesOrder.raise.site': 'Our site',
  'salesOrder.raise.siteHint': 'Stock here is used first; the purchase order is received here unless it drop-ships.',
  'salesOrder.raise.dropShip': 'Drop ship to the order’s ship-to',
  'salesOrder.raise.noDropShip': 'This order ships nowhere, so it cannot be drop-shipped.',
  'salesOrder.raise.shortTitle': 'Short of stock',
  'salesOrder.raise.chooseSite': 'Choose the site to see what is short.',
  'salesOrder.raise.noneShort': 'Nothing on this order is short of stock at that site.',
  'salesOrder.raise.fromStock': ' ({count} from stock)',
  'salesOrder.raise.serviceTitle': 'The service lines the job takes',
  'salesOrder.raise.noService': 'This order carries no service line, so there is no labor to raise a job for.',
  'salesOrder.raise.budget': 'Budget',
  'salesOrder.raise.hours': 'h',
  'salesOrder.raise.needsVendor': 'Choose the vendor.',
  'salesOrder.action.void': 'Void',
  'salesOrder.send.title': 'Mark This Order as Sent',
  'salesOrder.send.hint': 'Nothing is mailed from here. The order is frozen as sent, and the customer’s copy is the numbers on it now.',
  // Decision 2630 (T1177): the Sales Order's send window, which mails the order's own PDF.
  'salesOrder.mail.title': 'Send This Order',
  'salesOrder.mail.hint': 'Left blank, it goes to the account’s Accounts Payable mailbox, else the ticked recipients of the quote this order came from, else the deal’s contacts, else the account’s. The order’s PDF is attached.',
  'salesOrder.mail.resendHint': 'The copy the customer holds is mailed again. Nothing on the order moves.',
  'salesOrder.customerPo.title': 'Record the Customer’s Purchase Order Number',
  'documentFiles.title': 'Files',
  'documentFiles.none': 'Nothing is attached to this document yet.',
  'documentFiles.upload': '↑ Upload a File',
  'documentFiles.verified': 'Verified',
  // T1741 (decision 2713): the Files tab's filter, over each file's name and the name its served version is stored under.
  'documentFiles.filter': 'Filter Files',
  'documentFiles.noMatch': 'No file here matches that name.',
  // Decision 1663 (T1072): the messages tagged to a document, beneath its uploaded files.
  'documentFiles.fromMail': 'From mail',
  'documentFiles.fromMail.received': 'Received',
  'documentFiles.fromMail.sent': 'Sent',
  'documentFiles.fromMail.noSubject': '(No Subject)',
  'documentFiles.fromMail.blocked': 'held by the malware scan',
  // Decision 1662 (T1070): the PDF view's rail, as tabs beside the sheet.
  'document.rail.legend': 'Beside the sheet',
  'document.rail.notes': 'Notes',
  'document.rail.files': 'Files',
  'document.rail.journal': 'Journal',
  'document.rail.history': 'History',
  // The two panels every document page carries beneath its own blocks (decision 0470).
  'documentNotes.title': 'Internal Notes',
  'documentNotes.none': 'None yet',
  'documentHistory.title': 'History',
  'documentHistory.none': 'Nothing has happened on this document yet.',
  'salesOrder.customerPo.number': 'Customer PO number',
  'salesOrder.customerPo.recorded': 'The customer’s purchase order number is recorded.',
  'salesOrder.waive.title': 'Waive the Customer Purchase Order Requirement',
  'salesOrder.waive.reason': 'Why the order releases without the number',
  'salesOrder.waive.authoriser': 'Who authorized it',
  'salesOrder.waive.authoriserEntity': 'person',
  'salesOrder.waive.needsAuthoriser': 'Choose who authorized the waiver first.',
  'salesOrder.waive.waived': 'The requirement is waived and the waiver is recorded against the order.',
  'salesOrder.changeOrder.title': 'Raise a Change Order against this order',
  'salesOrder.changeOrder.reason': 'Why the scope moved',
  'salesOrder.changeOrder.description': 'What changed',
  'salesOrder.changeOrder.raised': 'The change order is raised and routed.',
  'salesOrder.changeOrders.title': 'Change orders',
  'salesOrder.changeOrders.none': 'No change order has been raised against this order.',
  // Piece 22, decision 0475: the change order is a document page of its own, and its lines are typed on it.
  'changeOrder.fact.order': 'Sales order',
  'changeOrder.fact.account': 'Account',
  'changeOrder.fact.sequence': 'Change order number on this job',
  'changeOrder.fact.total': 'Amount',
  'changeOrder.fact.created': 'Raised',
  'changeOrder.lines.title': 'Lines',
  'changeOrder.lines.total': 'Amount',
  'changeOrder.lines.hint': 'A quantity below zero takes scope away; the signed order keeps its own lines either way.',
  'changeOrder.reason': 'Why the scope moved',
  'changeOrder.description': 'What changed',
  'changeOrder.needsReason': 'A change order names why the scope moved.',
  'changeOrder.needsDescription': 'A change order describes what changed.',
  'changeOrder.needsLines': 'A change order carries at least one line, and no line moves a quantity of zero.',
  'changeOrder.needsOrder': 'A change order is raised against a sales order, and none was named.',
  'changeOrder.readFailed': 'This change order could not be read, so it cannot be saved yet.',
  'changeOrder.saved': 'The change order is saved and routed.',
  'changeOrder.save': 'Save the change order',
  'changeOrder.backToOrder': 'Back to the Sales Order',
  // Decision 0504: the three acts a change order is worked from its own page with.
  'changeOrder.action.withdraw': 'Withdraw',
  'changeOrder.action.resubmit': 'Resubmit for Approval',
  'changeOrder.withdraw.title': 'Withdraw This Change Order',
  'changeOrder.withdraw.hint': 'A withdrawn change order is fixed for good and stops counting toward the contract value. Raise another one if the scope moves again.',
  'changeOrder.withdraw.reason': 'Why it is being withdrawn',
  'changeOrder.withdrawn': 'The change order is withdrawn.',
  'changeOrder.approved': 'The change order is approved.',
  'changeOrder.rejected': 'The change order is rejected.',
  'changeOrder.resubmitted': 'The change order is resubmitted for approval.',
  'salesOrder.void.title': 'Void This Sales Order',
  'salesOrder.void.reason': 'Why it is voided',
  'salesOrder.void.hint': 'The order and its number stay; every open stock claim it held is released.',
  'salesOrder.voided': 'The order is voided.',
  'salesOrder.released': 'The order is released.',
  'salesOrder.lines.title': 'Lines',
  'salesOrder.lines.total': 'Total',
  'salesOrder.branches.title': 'Parts and labor',
  'salesOrder.branches.parts': 'Parts',
  'salesOrder.branches.labour': 'Labor',
  'salesOrder.branches.markNotApplicable': 'Mark N/A',
  'salesOrder.branches.undoNotApplicable': 'Undo N/A',
  'salesOrder.branches.markedNotApplicable': 'The branch is marked N/A and counts as complete.',
  'salesOrder.branches.undoneNotApplicable': 'The branch is open again and follows its documents.',
  'salesOrder.print.terms': 'This order acknowledges the scope and prices agreed. Changes to it are raised as change orders.',
  // Piece 22, the Invoice's own page: its edit form, its printed sheet and the one action per status its footer carries (decision 0409).
  'sourceLink.order.title': 'Sales Order this Invoice is for',
  'sourceLink.quote.title': 'Quote this order came from',
  'sourceLink.none': 'Not linked to one.',
  'sourceLink.attach': 'Link One',
  'sourceLink.change': 'Change',
  'sourceLink.unlink': 'Unlink',
  'sourceLink.draftOnly': 'The link is set while the document is still a draft. Past that it stays as it is, because an invoice’s approval is worked out from it.',
  'invoice.new.account': 'Account',
  'invoice.new.accountEntity': 'account',
  'invoice.new.billTo': 'Bill to',
  'invoice.new.invoiceDate': 'Invoice date',
  'invoice.new.paymentTerms': 'Payment terms',
  'invoice.new.dueDate': 'Due',
  'invoice.new.chooseAccount': 'Pick the account this invoice bills; its sites and terms fill in once you do.',
  'invoice.new.readFailed': 'The account’s sites and terms could not be read, so this invoice cannot be saved yet.',
  'invoice.new.needsBillTo': 'An invoice names the site it bills to.',
  'invoice.new.needsLines': 'An invoice carries at least one line.',
  'invoice.new.noSites': 'This account has no open site, so there is nowhere to bill until one is added here.',
  'invoice.form.number': 'Number',
  'invoice.form.numberOnIssue': 'Taken from the series when the invoice is issued',
  'invoice.form.salesOrder': 'Against sales order',
  'invoice.openOrders.title': 'Open sales orders for this account',
  'invoice.openOrders.add': 'Add to Invoice',
  'invoice.openOrders.dismiss': 'Not Now',
  'invoice.openOrders.added': 'The uninvoiced remainder of that order is on this invoice.',
  'invoice.openOrders.nothingLeft': 'Every line of that order is already invoiced.',
  'invoice.fact.account': 'Account',
  'invoice.fact.salesOrder': 'Sales order',
  // Decision 0502: the job this invoice billed, off the record's own column.
  'invoice.fact.workOrder': 'Work order',
  'invoice.fact.invoiceDate': 'Invoice date',
  'invoice.fact.dueDate': 'Due',
  'invoice.fact.terms': 'Terms',
  'invoice.fact.paid': 'Paid',
  'invoice.fact.openBalance': 'Open balance',
  'invoice.action.issue': 'Issue',
  'invoice.action.dispute': 'Mark as Disputed',
  'invoice.action.clearDispute': 'Clear the Dispute',
  'invoice.action.writeOff': 'Write Off',
  'invoice.action.void': 'Void',
  'invoice.issue.title': 'Issue This Invoice',
  'invoice.issue.hint': 'The number is taken from the series now, the receivable is posted, and the frozen copy is written. Nothing is emailed; Send mails it.',
  // Decision 2630 (T1177): the Invoice's Send, beside Issue, and its window.
  'invoice.action.send': 'Send',
  'invoice.mail.title': 'Send This Invoice',
  'invoice.mail.issueHint': 'Sending issues it first: the number is taken, the receivable is posted and the frozen copy is written.',
  'invoice.mail.resendHint': 'The issued copy is mailed again. Nothing is issued twice.',
  'invoice.mail.toHint': 'Left blank, it goes to the account’s Accounts Receivable mailbox, else the ticked recipients of the quote behind its order, else the deal’s contacts, else the account’s. The invoice’s PDF is attached.',
  'invoice.issued': 'The invoice is issued and numbered.',
  // Piece 26, decision 1221: the pill beside the status while the invoice's commission waits for a rate.
  'invoice.pill.unpricedCommission': 'Unpriced Commission',
  'invoice.dispute.title': 'Mark This Invoice as Disputed',
  'invoice.dispute.reason': 'What the customer disputed',
  'invoice.disputed': 'The invoice is marked as disputed; the finance charge and the reminders stop.',
  'invoice.disputeCleared': 'The dispute is cleared.',
  'invoice.writeOff.title': 'Write Off What This Invoice Still Owes',
  'invoice.writeOff.reason': 'Why it is written off',
  'invoice.writeOff.hint': 'A written-off invoice is never voided: the sale happened, and a payment arriving later reverses the write-off first.',
  'invoice.writtenOff': 'The invoice is written off.',
  'invoice.writeOff.date': 'Write-off date',
  'invoice.writeOff.dateNeeded': 'Pick the day the write-off is dated.',
  'invoice.action.undoWriteOff': 'Undo Write-Off',
  'invoice.undoWriteOff.title': 'Undo This Write-Off',
  'invoice.undoWriteOff.reason': 'Why the write-off is undone',
  'invoice.undoWriteOff.hint': 'The invoice goes back to Sent and the write-off is reversed today. Then write it off again on the right day.',
  'invoice.writeOffUndone': 'The write-off was undone, and the invoice is Sent again.',
  'invoice.void.title': 'Void This Invoice',
  'invoice.void.reason': 'Why it is voided',
  'invoice.voided': 'The invoice is voided.',
  'invoice.lines.title': 'Lines',
  'invoice.lines.total': 'Total',
  'invoice.lines.tax': 'Tax',
  'invoice.print.terms': 'Payment is due by the date on this invoice. Please quote the invoice number with your remittance.',
  // 🔒 Decision 9142 (T2367): the gate entries behind the job, printed on the backup under their own heading.
  'invoice.print.gateHeading': 'Gate sign-in at the site: date, arrival, departure and who.',
  'invoice.print.gateOpen': 'not signed out',
  // Piece 22, the Credit Note's own page: its edit form over the invoice it credits, its printed sheet and its footer.
  'creditNote.new.raise': 'Raise the credit note',
  'creditNote.new.invoice': 'Against invoice',
  'creditNote.new.invoiceEntity': 'invoice',
  'creditNote.new.reason': 'Why the credit was given',
  'creditNote.new.creditDate': 'Credit date',
  'creditNote.new.returnsToInventory': 'The goods come back to inventory',
  'creditNote.new.bypass': 'Credit without the goods coming back',
  'creditNote.new.bypassReason': 'Why the credit applies with no return',
  'creditNote.new.bypassAuthoriser': 'Who authorized it',
  'creditNote.new.bypassAuthoriserEntity': 'person',
  'creditNote.new.chooseInvoice': 'Pick the invoice this credit is against; its lines fill in once you do.',
  'creditNote.new.readFailed': 'The invoice’s lines could not be read, so this credit note cannot be saved yet.',
  'creditNote.new.needsLines': 'A credit note credits at least one line; type a quantity against one.',
  'creditNote.lines.title': 'Lines Credited',
  'creditNote.lines.credit': 'Credit',
  'creditNote.lines.ofQuantity': 'of',
  'creditNote.lines.alreadyCredited': 'already credited',
  'creditNote.lines.remaining': 'left to credit',
  'creditNote.lines.fullyCredited': 'fully credited already',
  'creditNote.lines.notReturnable': 'Not returnable',
  'creditNote.lines.tax': 'Tax',
  'creditNote.lines.total': 'Credit',
  'creditNote.form.number': 'Number',
  'creditNote.form.numberOnSave': 'Taken from the series when the note is raised',
  'creditNote.fact.account': 'Account',
  'creditNote.fact.invoice': 'Against invoice',
  'creditNote.fact.creditDate': 'Credit date',
  'creditNote.fact.applied': 'Applied to the invoice',
  'creditNote.fact.heldAsCredit': 'Held as the account’s available credit',
  'creditNote.fact.reason': 'Reason',
  'creditNote.action.receive': 'Receive the Return',
  'creditNote.action.void': 'Void',
  'creditNote.receive.title': 'Receive the Returned Goods',
  'creditNote.receive.site': 'Where the goods came back to',
  'creditNote.receive.sitePlaceholder': 'No site chosen',
  'creditNote.receive.hint': 'The stock movement and the credit are written together, so the two can never disagree.',
  'creditNote.received': 'The return is received and the credit is applied.',
  'creditNote.void.title': 'Void This Credit Note',
  'creditNote.void.reason': 'Why it is voided',
  'creditNote.voided': 'The credit note is voided.',
  'creditNote.fact.refunded': 'Refunded to the Account',
  'creditNote.action.refundHeld': 'Refund the Held Credit',
  'creditNote.refundHeld.title': 'Refund the Held Credit',
  'creditNote.refundHeld.hint': 'Pays the customer back out of the credit this note holds. It records a payment sent to the account against this note.',
  'creditNote.refundHeld.amount': 'Amount to Refund',
  'creditNote.refundHeld.method': 'How the Money Moved',
  'creditNote.refundHeld.date': 'Refund Date',
  'creditNote.refundHeld.reference': 'Reference',
  'creditNote.refundHeld.paidFrom': 'Paid From',
  'creditNote.refundHeld.paidFromNone': 'Not named',
  'creditNote.refundHeld.done': 'The held credit is refunded, and a payment sent to the account records it.',
  'creditNote.issuedAtRaising': 'A credit note is issued when it is raised: one that restocks opens at Pending return, one that does not opens Issued.',
  'creditNote.print.terms': 'This credit note reduces what is owed on the invoice it names. No payment is due against it.',
  // Piece 22, the Payment's own page: Record Payment as a page, the recorded money beneath it, and the three acts its status allows.
  'payment.new.title': 'Record a Payment',
  // Decision 0762 (T123): the Payments list read twice, filtered by the payment's direction.
  'payment.list.received': 'Payments Received',
  // Decision 0765 (T129): the Deal team panel on every document that carries a Deal.
  'dealTeamPanel.title': 'Deal team',
  'dealTeamPanel.frozen': 'As it stood when this document was created,',
  'dealTeamPanel.filled': 'Filled in from the Deal’s team after this document was created, on',
  'dealTeamPanel.none': 'The Deal had nobody on its team when this document was created.',
  'dealTeamPanel.share': 'share',
  'dealTeamPanel.noShare': 'No share',
  'dealTeamPanel.primary': 'Primary',
  'dealTeamPanel.noDeal': 'This job belongs to no Deal, so it carries no Deal team.',
  'payment.list.made': 'Payments Made',
  'payment.new.record': 'Record the payment',
  'payment.new.account': 'Account',
  'payment.new.accountEntity': 'account',
  'payment.new.direction': 'Direction',
  'payment.new.directionReceived': 'Received from the account',
  'payment.new.directionSent': 'Sent to the account',
  'payment.new.paymentDate': 'Payment date',
  'payment.new.method': 'How the money moved',
  'payment.new.reference': 'Reference',
  'payment.new.bankAccount': 'Paid from',
  'payment.new.bankAccountNone': 'Not named',
  'payment.new.bankAccountHint': 'The bank or card account the money left from.',
  'payment.new.bankAccountReceived': 'A payment received names its bank account when it is banked, on the Deposit that banks it.',
  'payment.new.receiptAccount': 'Landed in',
  'payment.new.receiptAccountDefault': '1000 Cash — Operating',
  'payment.new.receiptAccountHint': 'Which account the books credit the money into. Naming the bank account here does not bank it; a Deposit does that.',
  'payment.new.amount': 'Amount',
  'payment.new.surcharge': 'Card surcharge collected',
  'payment.new.chooseAccount': 'Pick the account this money came from; its open invoices fill in once you do.',
  'payment.new.readFailed': 'The account’s open invoices could not be read, so this payment cannot be recorded yet.',
  'payment.new.needsAmount': 'A payment moves an amount above zero; type what arrived.',
  'payment.new.overApplied': 'The amounts applied come to more than the payment; apply less or raise the amount.',
  'payment.new.proRata': 'Spread it across every open invoice by what each still owes',
  'payment.new.unapplied': 'Left unapplied',
  'payment.new.oldestFirst': 'Applied to the oldest invoices first. Change any figure to apply the money yourself.',
  'payment.new.unappliedHint': 'What is left over sits as this account’s available credit; nothing is refused for being unapplied.',
  'payment.new.proRataUnapplied': 'The split is worked out when the payment is recorded, so there is nothing to show here; anything the open invoices cannot take stays as this account’s available credit.',
  'payment.new.sentNoInvoices': 'A payment sent settles a vendor bill, never one of this account’s invoices, so none are offered. Bills arrive with the money-out work.',
  'payment.lines.title': 'Invoices to apply it to',
  'payment.lines.writtenOff': 'written off — a payment here reverses the write-off first',
  'payment.lines.owes': 'still owes',
  'payment.lines.due': 'due',
  'payment.lines.apply': 'Apply',
  'payment.lines.none': 'This account has no invoice waiting to be paid; the whole payment sits as available credit.',
  'payment.heldCredit.title': 'Held Credit',
  'payment.heldCredit.hint': 'Credit from a credit note its invoice could not take. Type what to use; the invoices take the credit before the money.',
  'payment.heldCredit.holds': 'holds',
  'payment.heldCredit.use': 'Use',
  'payment.heldCredit.overUsed': 'The held credit used comes to more than the invoices are taking; use less credit or apply more to the invoices.',
  'payment.heldCredit.overApplied': 'The amounts applied come to more than the payment and the credit used together; apply less or raise the amount.',
  'payment.heldCredit.needsSomething': 'Type what arrived, or use some held credit.',
  'payment.heldCredit.used': 'Credit used',
  'payment.fact.account': 'Account',
  'payment.fact.direction': 'Direction',
  'payment.fact.method': 'Method',
  'payment.fact.paymentDate': 'Payment date',
  'payment.fact.amount': 'Amount',
  'payment.fact.applied': 'Applied',
  'payment.fact.unapplied': 'Available credit',
  'payment.applied.title': 'Applied To',
  'payment.applied.none': 'Nothing is applied yet; the whole payment is this account’s available credit.',
  'payment.applied.dissociated': 'Dissociated',
  'payment.refunds.title': 'Refunded',
  'payment.action.apply': 'Apply to Invoices',
  'payment.action.dissociate': 'Dissociate',
  'payment.action.refund': 'Refund',
  'payment.action.void': 'Void',
  'payment.apply.title': 'Apply This Payment',
  'payment.apply.hint': 'Type what to apply against each invoice; the total can never pass what is left of the payment.',
  'payment.apply.confirm': 'Apply It',
  'payment.applyDone': 'The payment is applied.',
  'payment.dissociate.title': 'Dissociate This Allocation',
  'payment.dissociate.consequence': 'The money stays on the payment and the invoice owes again.',
  'payment.dissociate.confirm': 'Dissociate It',
  'payment.dissociated': 'The allocation is closed; the payment still stands.',
  'payment.refund.title': 'Refund This Payment',
  'payment.refund.method': 'How the money goes back',
  'payment.refund.amount': 'Amount to return',
  'payment.refund.on': 'Refunded on',
  'payment.refund.reason': 'Why it is refunded',
  'payment.refund.authoriser': 'Who authorized it',
  'payment.refund.authoriserEntity': 'person',
  'payment.refund.offRail': 'A refund that goes back a different way from how it arrived names its reason and who authorized it.',
  'payment.refunded': 'The refund is recorded.',
  'payment.void.title': 'Void This Payment',
  'payment.void.reason': 'Why it is voided',
  'payment.voided': 'The payment is voided.',
  'payment.recordedAtSave': 'A payment is recorded when it is saved; there is no draft to come back to.',
  'payment.reference.none': 'Unreferenced',

  // The three stock documents' screens (piece 22; decision 0441).
  'stock.entitySite': 'site',
  'stock.notFound': 'This stock document could not be read; go back to the list and open it again.',
  'stock.raised': 'Raised',
  'stock.cancel': 'Cancel',
  'stock.postsNothingLabel': 'Ledger',
  'stock.transfer.from': 'Stock leaves',
  'stock.transfer.to': 'Stock arrives at',
  'stock.transfer.save': 'Raise the transfer',
  'stock.transfer.despatch': 'Despatch',
  'stock.transfer.receive': 'Receive',
  'stock.transfer.postsNothing': 'A transfer between sites posts nothing to the ledger. The stock leaves at despatch, travels in transit, and arrives when it is received.',
  'stock.transfer.needsFrom': 'Name the site the stock leaves.',
  'stock.transfer.needsTo': 'Name the site the stock arrives at.',
  'stock.transfer.sameSite': 'A transfer moves stock between two sites; these are the same site.',
  'stock.transfer.needsLine': 'A transfer carries at least one item.',
  'stock.count.newHint': 'Each line records what the system expects right now; that figure is never re-read when the count is posted.',
  'stock.count.site': 'Site',
  'stock.count.scope': 'Scope',
  'stock.count.scope.fullSite': 'Full site',
  'stock.count.scope.itemCategory': 'One item category',
  'stock.count.scope.spot': 'Spot check',
  'stock.count.itemsHint': 'Name the items to count, or leave the line blank to count everything stocked at the site.',
  'stock.count.save': 'Start the count',
  'stock.count.saveCounts': 'Save What Was Counted',
  'stock.count.review': 'Move to Review',
  'stock.count.post': 'Post the Count',
  'stock.count.needsSite': 'Name the site whose stock this counts.',
  'stock.count.expected': 'Expected',
  'stock.count.counted': 'Counted',
  'stock.count.variance': 'Variance',
  'stock.count.note': 'Why it differs',
  'stock.count.started': 'Started',
  'stock.count.posted': 'Posted',
  /* 🔒 The block headings the two stock line tables carry (T182); a document page names its line block. */
  'stock.transfer.linesTitle': 'Items Moved',
  'stock.count.linesTitle': 'Items Counted',
  'stock.transfer.noLines': 'This transfer carries no items.',
  'stock.count.noLines': 'This count carries no items.',
  'vendorCredit.new.hint': 'A credit the vendor has granted; it settles that vendor’s bills once it is approved.',
  'vendorCredit.new.vendor': 'Vendor',
  'vendorCredit.new.vendorEntity': 'Company',
  'vendorCredit.new.creditDate': 'Credit date',
  'vendorCredit.new.amount': 'Amount',
  'vendorCredit.new.reason': 'Why the vendor granted it',
  'vendorCredit.new.sourceBill': 'Against the bill',
  'vendorCredit.new.billEntity': 'Bill',
  'vendorCredit.new.save': 'Save credit',
  'vendorCredit.new.saving': 'Saving…',
  'vendorCredit.new.needsVendor': 'A vendor credit names the vendor that granted it.',
  'vendorCredit.new.needsDate': 'A vendor credit carries the date the vendor granted it.',
  'vendorCredit.new.needsAmount': 'A vendor credit carries an amount above zero.',
  'vendorCredit.new.needsReason': 'A vendor credit states why the vendor granted it.',
  // Decision 7580 (T3122): the Vendor Credit's own page.
  'vendorCredit.fact.vendor': 'Vendor',
  'vendorCredit.fact.creditDate': 'Credit date',
  'vendorCredit.fact.amount': 'Amount',
  'vendorCredit.fact.applied': 'Applied',
  'vendorCredit.fact.remaining': 'Left to apply',
  'vendorCredit.fact.sourceBill': 'Against the bill',
  'vendorCredit.fact.reason': 'Why the vendor granted it',
  'vendorCredit.applied.title': 'Applied To',
  'vendorCredit.applied.none': 'Nothing is applied yet; the whole credit is still available for this vendor’s bills.',
  'vendorCredit.applied.dissociated': 'Dissociated',
  'vendorCredit.history.applied': '{credit} applied to {bill}',
  'vendorCredit.apply.act': 'Apply Credit',
  'vendorCredit.apply.pickTitle': 'Apply Credit to a Bill',
  'vendorCredit.apply.pickHint': 'Choose the bill that takes this credit. The next window shows what that bill owes and how much to apply.',
  'vendorCredit.apply.bill': 'Bill',
  'vendorCredit.apply.next': 'Choose Bill',
  'vendorCredit.apply.none': 'This vendor has no posted bill that still owes anything. A credit applies once a bill is submitted and posted.',
  'stock.revaluation.newHint': 'This posts as it is saved; there is no draft to come back to.',
  'stock.revaluation.item': 'Item',
  'stock.revaluation.site': 'Site',
  'stock.revaluation.quantity': 'Quantity',
  'stock.revaluation.was': 'Carried at',
  'stock.revaluation.wasOptional': 'Carried at',
  'stock.revaluation.wasHint': 'Leave this empty to use the cost the stock is already carried at.',
  'stock.revaluation.now': 'Repriced to',
  'stock.revaluation.reason': 'Why it is repriced',
  'stock.revaluation.post': 'Post the revaluation',
  'stock.revaluation.posting': 'Posting…',
  'stock.revaluation.needsItem': 'Name the item whose stock this reprices.',
  'stock.revaluation.needsSite': 'Name the site the stock sits at.',
  'stock.revaluation.needsReason': 'A revaluation states its reason; it is an Accounting and Admin action.',
  'stock.revaluation.quantityUnchanged': 'The quantity on hand is unchanged; only its value moved.',
  /** The Item's stock tile: its shelf lines and the Low Stock pill. */
  'stock.tile.lowAt': 'low at {threshold} or under',
  'stock.tile.reorder': 'reorder {quantity}',
  'stock.tile.onHand': '{quantity} {unit} on hand',
  'stock.tile.lowPill': 'Low Stock',
  /** Piece 23, decision 1142 (T445): Issue Stock, a part picked off a shelf outside any job, and the count's sentence that sends a pick there. */
  'stock.issue.action': 'Issue Stock',
  'stock.issue.title': 'Issue Stock',
  'stock.issue.hint': 'Takes parts off a shelf for a customer or a Deal with no job behind them. It lowers the site’s stock and moves their cost from inventory to cost of goods sold, the way a shipment does.',
  'stock.issue.item': 'Item',
  'stock.issue.site': 'Site',
  'stock.issue.quantity': 'Quantity',
  'stock.issue.reason': 'Why it was taken',
  'stock.issue.deal': 'Deal (optional)',
  'stock.issue.dealEntity': 'Deal',
  'stock.issue.post': 'Issue',
  'stock.issue.posting': 'Issuing…',
  'stock.issue.needsItem': 'Name the item being issued.',
  'stock.issue.needsSite': 'Name the site the part is taken from.',
  'stock.issue.needsQuantity': 'Issue a quantity above zero.',
  'stock.issue.needsReason': 'Say why the part was taken, so the cost has a reason beside it.',
  'stock.issue.done': 'Issued. What is left on the shelf:',
  /** Decision 1609: Return Stock, beside Issue Stock on the three stock lists. */
  'stock.return.action': 'Return Stock',
  'stock.return.title': 'Return Stock',
  'stock.return.hint': 'Puts back parts that Issue Stock took off a shelf. Pick the issue they came from: they go back on its shelf at the cost they left at, and that cost moves from cost of goods sold back to inventory.',
  'stock.return.item': 'Item',
  'stock.return.site': 'Site',
  'stock.return.issue': 'Issue they came from',
  'stock.return.issuePlaceholder': 'Pick an issue',
  'stock.return.issueFirst': 'Name the item and the site to list their issues.',
  'stock.return.issueNone': 'Nothing issued of this item at this site is still out.',
  'stock.return.stillOut': 'still out',
  'stock.return.quantity': 'Quantity',
  'stock.return.post': 'Return',
  'stock.return.posting': 'Returning…',
  'stock.return.needsItem': 'Name the item being returned.',
  'stock.return.needsSite': 'Name the site it was issued from.',
  'stock.return.needsIssue': 'Pick the issue the part is coming back from.',
  'stock.return.needsQuantity': 'Return a quantity above zero.',
  'stock.return.done': 'Returned. What is on the shelf now:',
  'stock.count.countedHint': 'Type the number you count on the shelf, not the change. A part taken off the shelf for a customer or a Deal is recorded with Issue Stock, not here.',

  /** Piece 22, the payment's screens: the payment recorded from the invoice's own page, which opens a Payment on that invoice. */
  'invoice.action.recordPayment': 'Record Payment',
  'invoice.action.raiseCreditNote': 'Raise a Credit Note',
  'invoice.overrun.asked': 'These lines bill past the sales order’s total. Say why and who authorized it beneath the lines, then save again.',
  'invoice.overrun.reason': 'Why the invoices go past the sales order',
  'invoice.overrun.authoriser': 'Who authorized it',
  'invoice.overrun.authoriserEntity': 'person',
  // Decision 1647: the Setup Steps composite's own words, the step's number and its three states.
  'setupSteps.step': 'Step',
  'setupSteps.done': 'Done',
  'setupSteps.current': 'Do this now',
  'setupSteps.later': 'Later',
  // Decision 2850: a guide's heading row, the link that folds it and the count it keeps while folded.
  'setupSteps.heading': 'Setup Steps',
  'setupSteps.hide': 'Hide Steps',
  'setupSteps.show': 'Show Steps',
  'setupSteps.progress': '{done} of {total} done',
  // Decision 1650: Pay this invoice on the portal, its window and the Checkout's return.
  'invoice.pay.action': 'Pay This Invoice',
  'invoice.pay.title': 'Pay This Invoice',
  'invoice.pay.amount': 'Amount to pay',
  'invoice.pay.amountHint': 'The open balance or less.',
  'invoice.pay.how': 'How to pay',
  'invoice.pay.card': 'Card',
  'invoice.pay.bank': 'Bank transfer',
  'invoice.pay.cardType': 'Kind of card',
  'invoice.pay.credit': 'Credit card',
  'invoice.pay.debit': 'Debit card',
  'invoice.pay.prepaid': 'Prepaid card',
  'invoice.pay.surcharge': 'Card surcharge',
  'invoice.pay.total': 'Total to pay',
  'invoice.pay.continue': 'Continue to payment',
  'invoice.pay.opening': 'Opening the payment page…',
  'invoice.pay.secure': 'You type your card or bank details on the payment processor’s own page, never on this one.',
  'invoice.pay.confirming': 'Your payment is being confirmed. This page updates by itself in a moment.',
  'invoice.pay.slow': 'Your payment is still being confirmed. Nothing more is needed from you; reload the page in a few minutes to see it applied.',
  'invoice.pay.paid': 'Payment received. Thank you.',
  'invoice.pay.processing': 'Your bank transfer is on its way. It takes a few business days to settle, and the invoice shows it paid once it has.',
  'invoice.pay.notCompleted': 'The payment was not completed, and nothing was charged.',
  'invoice.pay.unreadable': 'The payment’s state could not be read just now. Reload the page in a moment.',
  /** Piece 22, build issue T35: the parts fitted picker on the Work Order page. */
  'workOrder.material.item': 'Part',
  'workOrder.material.quantity': 'Quantity',
  'workOrder.material.add': 'Add Part',
  'workOrder.material.remove': 'Remove',
  // Decision 0500: the shelf a part comes off, and the refusal where the job names none.
  'workOrder.material.shelf': 'Comes off',
  'workOrder.material.noShelf': 'This job names no origin site and no default dispatch site is set, so a part cannot be fitted from a shelf.',
  /** Piece 22, decision 0436: the second entry on the Send split button, for a document that left by hand. */
  'document.action.markSent': 'Mark as Sent',
  'document.markSent.title': 'Mark This Document as Sent',
  'document.markSent.body': 'The document moves to Sent and its frozen copy is written, exactly as sending does — but nothing is emailed. Use this where it went out by hand, by post, or through the customer’s own portal.',
  'document.markedSent': 'Recorded as sent. Nothing was emailed.',
  // Decision 2630 (T1177): the send window the Sales Order and the Invoice share.
  'document.send.to': 'To',
  'document.send.message': 'Message',
  'document.send.sentTo': 'Sent to',
  'document.send.failed': 'The mail was refused and waits in the failure queue. Nothing else was undone.',
  'document.send.again': 'Send Again',
  /** Piece 26, decisions 0508 and 0509: the report set, the two dashboards and the low-stock run. */
  'reports.title': 'Reports',
  'reports.subtitle': 'Reporting',
  'reports.hint': 'A fixed set of named reports. Every one exports to PDF and to CSV, and nothing here builds a report of its own.',
  'reports.noBuilder': 'The list is fixed. There is no report designer and no field picker; a statement’s own settings can be saved under a name, which defines no new report. If a figure is missing, it is a change to make, not a screen to configure.',
  'reports.empty': 'This report has no rows for the filters it is showing.',
  'reports.back': 'All reports',
  'reports.exportPdf': 'Export to PDF',
  'reports.exportCsv': 'Export to CSV',
  // T350 (decision 1003): the report page's two views, the table and the sheet the PDF prints.
  'reports.view.legend': 'Which view of this report',
  'reports.view.noSheet': 'This report has no printed sheet to show yet.',
  'reports.view.frame': 'Report Sheet',
  'reports.filters': 'Filters',
  // Decision 9620: the account General Ledger detail is narrowed to, and the act that puts every account back.
  'reports.account': 'Account',
  'reports.allAccounts': 'All Accounts',
  'reports.from': 'From',
  'reports.to': 'To',
  'reports.asOf': 'As at',
  'reports.basis': 'Basis',
  'reports.taxYear': 'Tax year',
  'reports.agingThreshold': 'Threshold in days',
  'reports.groupBy': 'Group By',
  // Piece 26 (T853): the site an inventory report is cut to, and the choice that reads every site.
  'reports.site': 'Site',
  'reports.allSites': 'All Sites',
  'reports.run': 'Run',
  'reports.total': 'Total',
  // 🔨 Decision 0880: a statement's lines are its own arrangement, so its columns carry no sort, and the page says why.
  'reports.statementOrder': 'A statement reads in its own order, so its columns do not sort: a sort would put a total among the lines it adds up.',
  'reports.waitsOn': 'This report is not runnable yet',
  // 🔒 Decision 9200 (T2369): the sheet's warning where the range opens before the organization's data-coverage date.
  'reports.coverageShort': 'Data from before {date} was not brought in, so this report is short for the part of its range before that day.',
  /** Piece 26, decision 1140 (T443): a negative line inside a cost or expense section is marked, and the statement's foot says why. */
  'reports.creditMark': '(credit)',
  // Decision 11040 (T1052): Raise Purchase Orders on the Low Stock report, and what the press drafted and left.
  'reports.lowStock.raise': 'Raise Purchase Orders',
  'reports.lowStock.raiseBusy': 'The purchase orders are being drafted.',
  'reports.lowStock.raised': 'Raised Purchase Orders',
  'reports.lowStock.noneDrafted': 'No purchase order was drafted. Each ticked shelf is listed below with the reason.',
  'reports.lowStock.draftedLine': 'Draft to {vendor}, received at {site}; lines: {count}.',
  'reports.lowStock.raisedToMinimum': '{sku} raised from {from} to {to}: {vendor}’s minimum order quantity',
  'reports.lowStock.unraised': 'Not Raised',
  'reports.lowStock.openOrder': 'Open {number}',
  'reports.creditNote': 'A figure marked (credit) inside Cost of Goods Sold or an expense section is an overage, a reversal or a revaluation that credited the account, so it lowers that section\'s total.',
  /** Piece 26, decision 0850 (T126): the Customize Report panel and a statement's saved views. */
  'reports.customise': 'Customize Report',
  'reports.customise.sections': 'Customize Report Sections',
  'reports.customise.general': 'General',
  'reports.customise.columns': 'Show / Hide Columns',
  'reports.customise.dateRange': 'Date Range',
  'reports.customise.preset.today': 'Today',
  'reports.customise.preset.thisWeek': 'This Week',
  'reports.customise.preset.thisMonth': 'This Month',
  'reports.customise.preset.thisQuarter': 'This Quarter',
  'reports.customise.preset.thisYear': 'This Year',
  'reports.customise.preset.lastMonth': 'Last Month',
  'reports.customise.preset.lastQuarter': 'Last Quarter',
  'reports.customise.preset.lastYear': 'Last Year',
  'reports.customise.preset.yearToDate': 'Year To Date',
  'reports.customise.preset.custom': 'Custom',
  'reports.customise.basis': 'Report Basis',
  'reports.customise.basisDefault': 'Default',
  'reports.customise.basisLive': 'Default is the organization’s own basis. Cash reads the same postings timed to when the money moved; nothing is posted twice.',
  'reports.customise.filterAccounts': 'Filter Accounts',
  'reports.customise.withoutZero': 'Accounts without zero balance',
  'reports.customise.allAccounts': 'All accounts',
  'reports.customise.compare': 'Compare',
  'reports.customise.compareWith': 'Compare With',
  'reports.customise.compare.none': 'None',
  'reports.customise.compare.previousPeriods': 'Previous period(s)',
  'reports.customise.compare.previousYears': 'Previous year(s)',
  'reports.customise.count': 'Number of period(s)',
  'reports.customise.latestFirst': 'Arrange periods from latest to oldest',
  'reports.customise.onlyDates': 'This report takes a date range alone.',
  'reports.customise.available': 'Available columns',
  'reports.customise.selected': 'Selected columns',
  'reports.customise.column.account': 'Account',
  'reports.customise.column.accountCode': 'Account Code',
  'reports.customise.column.accountDescription': 'Account Description',
  'reports.customise.column.total': 'Total',
  'reports.customise.column.yearToDate': 'Year to Date',
  'reports.customise.add': 'Add',
  'reports.customise.remove': 'Remove',
  'reports.customise.accountLocked': 'Account is always shown, and first.',
  'reports.customise.needsFigure': 'Choose Total or Year To Date, so the report has a figure to show.',
  'reports.customise.allChosen': 'Every column is chosen.',
  'reports.customise.run': 'Run Report',
  'reports.customise.save': 'Save as Custom Report',
  'reports.customise.saveHint': 'A saved view keeps these settings under a name. It defines no new report.',
  'reports.customise.name': 'Name',
  'reports.customise.share': 'Share with everybody who can read this statement',
  'reports.customise.saveOver': 'Save over the view that is open',
  'reports.customise.saveAction': 'Save',
  'reports.view.label': 'Saved view',
  'reports.view.standard': 'Standard settings',
  'reports.view.sharedBy': 'shared by',
  'reports.view.delete': 'Delete This View',
  'reports.view.unavailable': 'The saved views could not be read, so the statement shows its standard settings.',
  'reports.notice.invoicedNoDeal': 'was invoiced against no Deal, so it belongs to no salesperson and is not in the figures above.',
  'reports.notice.mixedCurrencies': 'Those invoices are not all in this organization’s own currency, so the figure adds up a mixture and is stated in the organization’s currency.',
  'reports.openRecord': 'Open',
  'reports.lowStock.run': 'Check stock now',
  'reports.lowStock.ran': 'Every shelf was judged against its own threshold. Nothing was ordered.',
  /** The Project Performance dashboard. */
  'reports.projectPerformance.title': 'Project Performance',
  'reports.projectPerformance.hint': 'Projects still running: how much of the estimate is gone against how much of the work is done. A job that has finished leaves this screen.',
  'reports.tile.loading': 'Reading the figures…',
  'reports.tile.noFigure': '—',
  'reports.tile.diverged': 'Diverged',
  'reports.tile.divergedCount': 'diverged',
  'reports.tile.projectsRunning': 'projects running',
  'reports.tile.openProjects': 'Open Project Performance',
  'reports.projectPerformance.empty': 'No project is running: nothing here carries a work order that is still open.',
  'reports.projectPerformance.consumed': 'Hours consumed',
  'reports.projectPerformance.complete': 'Work complete',
  'reports.projectPerformance.alarm': 'Diverging',
  'reports.projectPerformance.byLevel': 'Allotted against actual, by level',
  'reports.projectPerformance.causes': 'Where the variance came from',
  'reports.projectPerformance.margin': 'Margin',
  'reports.projectPerformance.quotedMargin': 'Quoted margin',
  'reports.projectPerformance.withheld': 'Cost and margin are not in this answer, because this login does not hold the grant for them.',
  'reports.projectPerformance.trendByType': 'Trend by Project Type',
  'reports.projectPerformance.trendByEstimator': 'Trend by Estimator',
  'reports.projectPerformance.closedByType': 'Closed Projects by Project Type',
  'reports.projectPerformance.closedByEstimator': 'Closed Projects by Estimator',
  'reports.projectPerformance.closedHint': 'Each closed project’s hours as allotted when its order was raised, against the hours its jobs used: the read-back for the next quote.',
  'reports.projectPerformance.closedEmpty': 'No project has closed with its estimate kept yet.',
  /** The Sales Reporting Dashboard. */
  'reports.salesDashboard.title': 'Sales Reporting',
  'reports.salesDashboard.preliminaryNote': 'This design is preliminary and needs more work. Read the figures; do not build a process on the layout.',
  'reports.salesDashboard.revenueFlow': 'Revenue Flow',
  'reports.salesDashboard.weighted': 'Weighted pipeline',
  'reports.salesDashboard.weights': 'The weights used',
  'reports.salesDashboard.byRep': 'Revenue by Sales Rep',
  'reports.salesDashboard.byCategory': 'Revenue by Category',
  'reports.salesDashboard.commission': 'Open the commission report',
  'reports.salesDashboard.conversion': 'conversion',
  // T2362 (decision 9060): the three metric panels piece 20 computes and the dashboard draws.
  'reports.salesDashboard.winLoss': 'Win and Loss',
  'reports.salesDashboard.winLossPeriod': 'Every pipeline deal to date, across the whole organization.',
  'reports.salesDashboard.won': 'Won',
  'reports.salesDashboard.lost': 'Lost',
  'reports.salesDashboard.winRateByCount': 'Win rate by count',
  'reports.salesDashboard.winRateByValue': 'Win rate by value',
  'reports.salesDashboard.unresolved': 'Unresolved',
  'reports.salesDashboard.agedOver': '{count} open over {days} days',
  'reports.salesDashboard.noRate': 'No deal resolved yet',
  'reports.salesDashboard.lossReasons': 'Losses by reason',
  'reports.salesDashboard.noLosses': 'No deal has been lost yet.',
  'reports.salesDashboard.huntingFarming': 'Hunting and Farming',
  'reports.salesDashboard.huntingFarmingPeriod': 'Invoices issued in {year}, across the whole organization. A sale is hunting while its account sits in the {band} band of the account age tiers.',
  'reports.salesDashboard.huntingFarmingNoBand': 'Invoices issued in {year}, across the whole organization. No account age band is in force today, so the split cannot name hunting.',
  'reports.salesDashboard.hunting': 'Hunting',
  'reports.salesDashboard.farming': 'Farming',
  'reports.salesDashboard.noBand': 'No age band',
  'reports.salesDashboard.growth': 'Growth and Acquisition',
  'reports.salesDashboard.growthPeriod': 'Invoices issued in {year} against {previous}, across the whole organization. Growth counts the accounts invoiced in both years, and acquisition is its own figure, never added to it.',
  'reports.salesDashboard.marginGrowth': 'Margin growth',
  'reports.salesDashboard.marginAcquisition': 'Margin from new accounts',
  'reports.salesDashboard.uncostedLines': '{lines} uncosted: a line with no recorded cost counts its whole sale as margin.',
  'reports.salesDashboard.revenueGrowth': 'Revenue growth',
  'reports.salesDashboard.revenueAcquisition': 'Revenue from new accounts',
  'reports.salesDashboard.belowBase': 'no percentage below a base of {base}',
  'reports.salesDashboard.marginWithheld': 'Margin is withheld from this view: it shows only to a viewer who may see cost and margin.',
  /** The words the dashboards join to a figure; a number is never welded into a sentence in the markup. */
  'reports.of': 'of',
  'reports.hours': 'hours',
  'reports.lines': 'lines',
  'reports.allotted': 'allotted',
  'reports.actual': 'actual',
  'reports.variance': 'variance',
  'reports.projects': 'projects',
  'reports.over': 'over',
  'reports.deals': 'deals',
  'reports.invoices': 'invoices',
  'reports.basisDefault': 'Default basis',
  /** Piece 26: the Sales Commission Report, and the two Settings entries this piece builds. */
  'commission.report.title': 'Sales Commission Report',
  'commission.report.hint': 'One line per invoice per recipient. The primary\u2019s line prints as two rows \u2014 base and uplift \u2014 and a team member\u2019s prints base alone, each row carrying the arithmetic that reproduces its own figure. No cost and no margin appears anywhere on it.',
  'commission.report.ownLinesOnly': 'You are seeing your own lines. The whole report is the Accounting and Admin roles\u2019.',
  'commission.report.from': 'From',
  'commission.report.to': 'To',
  'commission.report.employment': 'Recipient (employment)',
  'commission.report.run': 'Run',
  'commission.report.exportPdf': 'Export to PDF',
  'commission.report.exportCsv': 'Export to CSV',
  'commission.report.invoice': 'Invoice',
  'commission.report.customer': 'Customer',
  'commission.report.deal': 'Deal',
  'commission.report.recipient': 'Recipient',
  'commission.report.component': 'Component',
  'commission.report.amount': 'Amount',
  'commission.report.formula': 'Formula',
  'commission.report.floor': 'Floor',
  'commission.report.total': 'Total',
  'commission.report.grandTotal': 'Total for the range',
  'commission.report.empty': 'No invoice in this range earned a commission. An invoice with no Deal earns none.',
  'commission.report.recomputed': 'recomputed',
  'commission.report.unpriced': 'Unpriced',
  'commission.report.unpricedNote': 'A row marked Unpriced belongs to an invoice no commission rate could price on its date. It names the reason, carries no figure, and is left out of every total below.',
  // Decision 0983 (T330): which service offered an address on the Location page.
  'address.source.openstreetmap': 'From OpenStreetMap',
  'address.source.census': 'From the Census Bureau',
  // Decision 7320: Geoapify names itself.
  'address.source.geoapify': 'From Geoapify',
  // Decision 3120: the Postal Service and Google name themselves, and two or more services that agree are named together.
  'address.source.usps': 'From USPS',
  'address.source.google': 'From Google',
  'address.source.agree': '{names} agree',
  'address.sourceName.usps': 'USPS',
  'address.sourceName.google': 'Google',
  'address.sourceName.openstreetmap': 'OpenStreetMap',
  'address.sourceName.census': 'the Census Bureau',
  'address.sourceName.geoapify': 'Geoapify',
  // Decision 3121: the verification line names how many bound services agreed and which.
  'address.agreement.verified': 'Verified by {count} of {asked}: {names}',
  'address.agreement.agreed': '{count} of {asked} agree on this address: {names}',
  'address.agreement.review': 'Needs review: {answered} of {asked} answered',
  'address.agreement.disagree': 'Needs review: {answered} of {asked} answered, and they name different addresses',
  'address.agreement.short': 'Needs review: {count} of {asked} agree, and {needed} must',
  'address.question.unit': 'USPS confirmed the building, and the unit or suite is missing or not one it delivers to. Check the second address line.',
  'address.source.asTyped': 'This service holds the address exactly as typed.',
  // Decision 7430: a low-confidence answer is kept and shown with its confidence and what to check.
  'address.note.numberEstimated': 'It found the street but estimated where the number falls along it. Check the house number on a mailing piece or at the site.',
  'address.note.postalCodeDiffers': 'It places it in postal code {found}, not {typed}.',
  'address.note.postalCodeDiffersUnnamed': 'It places it in a different postal code from the one typed.',
  'address.note.cityDiffers': 'It places it in {found}, not {typed}.',
  'address.note.cityDiffersUnnamed': 'It places it in a different city from the one typed.',
  'address.note.streetNotHeld': 'It does not hold this street. Check the street’s spelling.',
  'address.note.areaOnly': 'It placed only the street or area. Check the house number and the street.',
  'address.note.heading': 'Low confidence',
  // Decision 7460: a street and a postal code confirmed with the house number estimated is Estimated, and a person can verify it by hand.
  'address.estimated': 'The address was confirmed except for the house number. It is marked Estimated.',
  'address.estimatedTyped': 'The address was confirmed except for the house number; it is marked Estimated once saved.',
  'address.estimated.street': 'The street and the postal code are real; the house number is estimated along the street. Check the number.',
  'address.verifiedByUser': 'Verified by User',
  'address.verifiedByUser.hint': 'Press this once the house number is confirmed on a mailing piece or at the site.',
  // Decision 0982 (T328): Edit selected over a list's picked rows, four fields through their single edits.
  'bulkEdit.action': 'Edit Selected',
  'bulkEdit.title': 'Edit Selected',
  'bulkEdit.hint': 'Choose the one field to change and its new value. Each row is saved as if it were edited on its own page, so a row its own page would refuse is refused here and named.',
  'bulkEdit.field': 'Field',
  'bulkEdit.field.salesPrice': 'Sales price',
  'bulkEdit.field.purchasePrice': 'Purchase price',
  'bulkEdit.field.status': 'Item status',
  'bulkEdit.field.salesRep': 'Sales rep',
  // Decision 2890: the Item fields Edit Selected gained, in the Item form's own words.
  'bulkEdit.field.category': 'Category',
  'bulkEdit.field.unit': 'Unit of sale',
  'bulkEdit.field.taxCategory': 'Tax category',
  'bulkEdit.field.salesAccountId': 'Sales account',
  'bulkEdit.field.purchaseAccountId': 'Purchase account',
  'bulkEdit.field.manufacturer': 'Manufacturer',
  'bulkEdit.field.type': 'Item type',
  'bulkEdit.field.defaultMargin': 'Default margin %',
  'bulkEdit.field.minimumMargin': 'Minimum margin %',
  'bulkEdit.field.returnable': 'Returnable',
  'bulkEdit.choose': 'Choose one',
  'bulkEdit.needsValue': 'Choose the new value first.',
  'bulkEdit.review': 'Review',
  'bulkEdit.apply': 'Change Them',
  'bulkEdit.back': 'Back',
  'bulkEdit.done': 'Done',
  'bulkEdit.editAgain': 'Edit Again',
  'bulkEdit.row': 'row',
  'bulkEdit.rows': 'rows',
  'bulkEdit.confirmPrefix': 'Change',
  'bulkEdit.confirmTo': 'to',
  'bulkEdit.confirmOn': 'on',
  // Decision 7790: an attribute edit names how many of the ticked rows carry it, as in 2 of 3 rows.
  'bulkEdit.confirmOf': 'of',
  'bulkEdit.confirmEach': 'Each row is saved on its own, and a row that cannot be saved is listed afterwards with the reason.',
  'bulkEdit.changed': 'changed',
  'bulkEdit.refusedCount': 'refused, each named below with the reason',
  'bulkEdit.notice': 'rows changed by Edit selected',
  // Decision 7640 (T3125): one picture set on, or removed from, every ticked Item.
  'bulkEdit.field.image': 'Image',
  'bulkEdit.image.mode': 'What to do with the image',
  'bulkEdit.image.choose': 'Choose Picture',
  'bulkEdit.image.chooseAnother': 'Choose Another Picture',
  'bulkEdit.image.none': 'No picture is chosen yet. Choose one to set on every ticked Item.',
  'bulkEdit.image.chosen': 'Picture chosen',
  'bulkEdit.image.removed': 'no image',
  'bulkEdit.image.modalTitle': 'Image for the Ticked Items',
  'bulkEdit.image.use': 'Use This Picture',
  // Decision 7790 (T3157): one attribute value set on every ticked Item whose category carries the attribute.
  'bulkEdit.field.attribute': 'Attribute',
  'bulkEdit.attribute.none': 'The catalog has no active attribute type yet. Add one in Settings under Catalog, Attribute Types.',
  'bulkEdit.attribute.reviewing': 'Checking which ticked rows carry this attribute.',
  'bulkEdit.attribute.carrying': 'Rows that carry this attribute and will be changed',
  'bulkEdit.attribute.lacking': 'Rows that do not carry this attribute, because their category does not list it, and will be skipped',
  'bulkEdit.attribute.nobody': 'None of the ticked rows carries this attribute, so nothing would be changed.',
  'bulkEdit.attribute.skipped': 'skipped: its category does not carry this attribute',
  // Decision 8780 (T3275): one supplier added to every ticked Item, at Primary or Active.
  'bulkEdit.field.supplier': 'Supplier',
  'bulkEdit.supplier.hint': 'Each ticked Item gains this vendor on its Sourcing tile from today, with no part number or minimum. An Item that already has the vendor is left as it is, except that Primary makes the vendor its Primary.',
  'bulkEdit.supplier.primaryHint': 'Primary makes this vendor the one a Purchase Order line defaults to, and each Item\u2019s previous Primary becomes Active.',
  'bulkEdit.supplier.blacklisted': 'refused: this vendor is Blacklisted on the Item\u2019s Sourcing tile, so it is never added in a bulk edit',
  'bulkEdit.supplier.notCompany': 'The supplier named is not a company here.',
  'bulkEdit.supplier.namesCompany': 'a bulk edit of a supplier names the company to add',
  'bulkEdit.supplier.statusProblem': 'a supplier added in a bulk edit is {statuses}',
  // Decision 0981 (T326): the rep's pay summary, per imported pay period, naming each recovery with its credit note.
  'commission.paySummary.title': 'Pay summary',
  'commission.paySummary.hint': 'What each pay period imported from payroll paid in commission, and what it took back. A credit note written after its commission was paid is recovered by the next pay period, on a line naming the credit note.',
  'commission.paySummary.period': 'Pay period',
  'commission.paySummary.journal': 'Journal',
  'commission.paySummary.line': 'Line',
  'commission.paySummary.empty': 'No pay period ending in this range has been imported from payroll.',
  'commission.report.recomputedNote': 'A row marked recomputed belongs to an invoice that keeps no calculation of its commission, usually because no commission rate was in force on its date. Its figures come from the rate card as it stands today, so a rate change can move them.',
  'commission.rateTable.title': 'Commission Rate Table',
  // Piece 26, decisions 1220 and 1221: an invoice issued with its commission unpriced, as its warning, its to-do and its link read.
  'commission.unpriced.thisInvoice': 'This invoice',
  'commission.unpriced.invoiceSubject': 'Invoice {invoice}',
  'commission.unpriced.rate': '{subject} was issued, and no approved commission rate is in force for {dealType} deals on {date}, so its commission is unpriced until one is.',
  'commission.unpriced.tier': '{subject} was issued, and no account age band covers an account {age} old on {date}, so its commission is unpriced until one does.',
  'commission.unpriced.other': '{subject} was issued, and its commission is unpriced because {reason}.',
  'commission.unpriced.addRate': 'Add a {dealType} rate in force on that date in the Commission Rate Table, and the commission is priced once the rate is approved.',
  'commission.unpriced.addTier': 'Add a band covering {age} in the Commission Rate Table, and the commission is priced once the band is saved.',
  'commission.unpriced.ask': 'The people who manage commission rates have a to-do to add one, and the commission is priced once it is in force.',
  'commission.unpriced.pending': 'A {dealType} rate from {date} is waiting for a second person\u2019s approval, and the commission is priced once it is approved.',
  'commission.unpriced.fixOther': 'The commission is priced once the Commission Rate Table covers this sale.',
  'commission.unpriced.openRateTable': 'Open Commission Rate Table',
  'commission.unpriced.todoTitle': 'Price the commission on invoice {invoice}',
  'commission.unpriced.todoCloses': 'This to-do closes itself once the commission is priced.',
  'commission.rateTable.hint': 'The commission rate for each deal type, by how long the customer has been an account. A change takes effect from the date you give and keeps the old rate for earlier sales; a new rate counts only once a second person approves it.',
  'commission.rateTable.rates': 'Base rate per deal type',
  'commission.rateTable.tiers': 'Account age bands',
  'commission.rateTable.dealType': 'Deal type',
  'commission.rateTable.chooseDealType': 'Choose a deal type',
  'commission.rateTable.baseRate': 'Base rate (%)',
  'commission.rateTable.startDate': 'In force from',
  'commission.rateTable.addRate': 'Add a Rate',
  'commission.rateTable.addRateButton': '+ Add Rate',
  'commission.rateTable.approve': 'Approve',
  'commission.rateTable.reject': 'Reject',
  'commission.rateTable.rejectReason': 'Reason for rejecting',
  'commission.rateTable.setBy': 'Set by',
  'commission.rateTable.approvedBy': 'Approved by',
  'commission.rateTable.awaitingSecond': 'Awaiting a second person\u2019s approval. You set this row, so you cannot approve it.',
  'commission.rateTable.tierName': 'Band name',
  'commission.rateTable.fromMonths': 'From (months)',
  'commission.rateTable.toMonths': 'To (months, blank runs onward)',
  'commission.rateTable.percentOfFullRate': 'Percent of full rate',
  'commission.rateTable.addTier': 'Add a Band',
  'commission.rateTable.addTierButton': '+ Add Band',
  'commission.rateTable.closeTier': 'Close this band',
  'commission.rateTable.endDate': 'Closes on',
  'commission.rateTable.superseded': 'Superseded',
  'commission.earningCodes.title': 'Earning Code Map',
  'commission.earningCodes.empty': 'No earning codes mapped yet. A mapping pairs a time entry type or a leave plan kind with the payroll code, and the account its hours post to.',
  'commission.earningCodes.hint': 'The payroll earning code each kind of time or pay is sent under. Map every kind before the payroll export: an unmapped one stops the export rather than guessing, so leave is never paid as hours worked.',
  'commission.earningCodes.sourceKind': 'Source',
  'commission.earningCodes.sourceValue': 'Value',
  'commission.earningCodes.code': 'Earning code',
  'commission.earningCodes.chooseCode': 'Choose a code',
  'commission.earningCodes.account': 'Posts to',
  'commission.earningCodes.chooseAccount': 'Choose an account',
  'commission.earningCodes.overtimeEligible': 'Overtime eligible',
  'commission.earningCodes.effectiveFrom': 'In force from',
  'commission.earningCodes.add': 'Map a Source',
  'commission.earningCodes.addButton': '+ Map Source',
  'commission.earningCodes.leaveNeverOvertime': 'A leave code is never overtime eligible: overtime is computed on hours worked, and paid leave is not hours worked.',
  'commission.earningCodes.pulled': 'The codes are pulled from the payroll service and never typed by hand.',
  // Piece 1, decisions 1470 and 1529 (T732, T793): Settings › System › Server info › Request timings.
  'timings.title': 'Request timings',
  'timings.intro': 'The slowest routes of the last hour, slowest first, from the answers the API timed since it started. Every answer on a route that takes a session carries the same split in its Server-Timing header: the database, the object store, the application and the pool\u2019s connection wait.',
  'timings.view': 'Last hour',
  'timings.slowLine': 'An answer slower than {ms} ms also writes a line to the API\u2019s log.',
  'timings.empty': 'No answer has been timed in the last hour.',
  'timings.column.route': 'Route',
  'timings.column.count': 'Answers',
  'timings.column.p50': 'Median (ms)',
  'timings.column.p95': '95th Percentile (ms)',
  'timings.column.max': 'Slowest (ms)',
  'timings.column.dbShare': 'Database (%)',
  'timings.column.storeShare': 'Object Store (%)',
  'timings.column.queries': 'Queries',
  // Decision 1544 (T813): each figure's name on a phone's compact row, where the headings above are not drawn; {value} is the figure.
  'timings.compact.count': 'answers {value}',
  'timings.compact.p50': 'median {value} ms',
  'timings.compact.p95': '95th {value} ms',
  'timings.compact.max': 'slowest {value} ms',
  'timings.compact.dbShare': 'database {value}%',
  'timings.compact.storeShare': 'store {value}%',
  'timings.compact.queries': 'queries {value}',
  'timings.pool.title': 'Connection pool',
  'timings.pool.size': 'Pool size',
  'timings.pool.connections': 'Connections',
  'timings.pool.connectionsValue': '{open} open: {busy} busy, {idle} idle',
  'timings.pool.waitingNow': 'Queries waiting now',
  'timings.pool.waits': 'Waits since the API started',
  'timings.pool.waitsValue': '{waits} waits, {total} ms in all, {over} of them over 10 ms',
  // Decisions 1730 and 1731: the package window, its page and slip, the order's two blocks and the shipment window.
  'package.action.edit': 'Edit',
  'package.action.delete': 'Delete',
  'package.delete.title': 'Delete Package',
  'package.delete.body': 'This deletes the package and its lines. The order keeps what it holds, and the goods can be packed again.',
  'package.action.print': 'Print',
  'package.action.ship': 'Ship',
  'package.page.dimensions': 'Dimensions',
  'package.page.fromOrder': 'From sales order',
  'package.page.lines': 'Packed Lines',
  'package.page.shipment': 'Shipment',
  'package.page.totalQuantity': 'Total quantity',
  'package.print.packageDate': 'Date',
  'package.print.salesOrder': 'Order #',
  'package.print.totalQuantity': 'Quantity',
  'package.saved': 'Package saved.',
  'package.window.date': 'Package date',
  'package.window.dimensionUnit': 'Dimension unit',
  'package.window.editTitle': 'Edit package',
  'package.window.forOrder': 'For sales order',
  'package.window.grossWeight': 'Gross weight',
  'package.window.height': 'Height',
  'package.window.left': 'left',
  'package.window.length': 'Length',
  'package.window.linesHint': 'The quantity packed from each goods line. Each line opens at what it has left to pack.',
  'package.window.loading': 'Loading the order’s lines…',
  'package.window.needsDate': 'Enter the package date.',
  'package.window.needsLine': 'Pack at least one line.',
  'package.window.netWeight': 'Net weight',
  'package.window.newTitle': 'New package',
  'package.window.noGoods': 'This order has no goods lines to pack.',
  'package.window.ordered': 'Ordered',
  'package.window.packedElsewhere': 'packed elsewhere',
  'package.window.quantity': 'Quantity',
  'package.window.retry': 'Try Again',
  'package.window.save': 'Save package',
  'package.window.saveEdit': 'Save changes',
  'package.window.type': 'Package type',
  'package.window.weightUnit': 'Weight unit',
  'package.window.width': 'Width',
  'salesOrder.action.createPackage': 'Create Package',
  'salesOrder.action.createShipment': 'Create Shipment',
  'salesOrder.packages.none': 'Nothing packed yet.',
  'salesOrder.packages.title': 'Packages',
  'salesOrder.shipments.none': 'Nothing shipped yet.',
  'salesOrder.shipments.dropShipped': 'Drop-shipped',
  'salesOrder.shipments.slip': 'Packing slip',
  'salesOrder.shipments.title': 'Shipments',
  'shipment.needsPackage.message': 'To create a shipment, pack the goods first. A shipment carries packages.',
  'shipment.needsPackage.newPackage': 'New Package',
  'shipment.needsPackage.title': 'No Package to Ship',
  'shipment.window.badTrackingUrl': 'The tracking address is a web address, such as https://carrier.example/track/123.',
  'shipment.window.carrierEntity': 'carrier',
  'shipment.window.charges': 'Shipping charges',
  'shipment.window.delivered': 'Shipment already delivered',
  'shipment.window.needsEmail': 'Enter the address the notice goes to.',
  'shipment.window.needsPackage': 'Tick at least one package.',
  'shipment.window.newCarrier': 'New carrier',
  'shipment.window.noticeFailed': 'The notice to the customer did not send.',
  'shipment.window.notify': 'Notify the customer',
  'shipment.window.notifyEmail': 'Notice goes to',
  'shipment.window.packages': 'Packages',
  'shipment.window.save': 'Save Shipment',
  'shipment.window.shipped': 'Shipped as',
  'shipment.window.title': 'New Shipment',
  'shipment.window.trackingUrl': 'Tracking URL',
  'shipment.window.units': 'units',
  // Decision 1732: the What's next banner, its heading, each kind's sentence and each step's button.
  'whatsNext.heading': 'What’s next?',
  'whatsNext.quote.draft': 'Send the quote to the customer.',
  'whatsNext.quote.sent': 'Record the customer’s acceptance once they say yes.',
  'whatsNext.quote.accepted': 'Convert the accepted quote to a sales order.',
  'whatsNext.salesOrder.released': 'Convert the sales order to packages, shipments or invoices.',
  'whatsNext.salesOrder.shipLeft': 'Every line is invoiced; pack and ship what is left.',
  'whatsNext.salesOrder.fromVendor': 'The vendor ships this order straight to the customer; raise the invoice.',
  'whatsNext.salesOrder.invoiceLeft': 'Nothing is left to ship from here; raise the invoice.',
  'whatsNext.salesOrder.vendorShipping': 'Invoiced: the vendor ships the rest straight to the customer.',
  'whatsNext.salesOrder.done': 'Shipped and invoiced: nothing is left to do here.',
  'whatsNext.invoice.draft': 'Send the invoice to the customer.',
  'whatsNext.invoice.open': 'Record the customer’s payment against this invoice.',
  'whatsNext.purchaseOrder.draft': 'Send the purchase order to the vendor.',
  'whatsNext.purchaseOrder.sent': 'Receive the goods as they arrive, and raise the vendor’s bill.',
  'whatsNext.purchaseOrder.received': 'Raise the vendor’s bill for what was received.',
  'whatsNext.bill.open': 'Pay the bill.',
  'whatsNext.creditNote.pendingReturn': 'Receive the returned goods to issue the credit.',
  'whatsNext.package.notShipped': 'Ship the package.',
  'whatsNext.step.send': 'Send',
  'whatsNext.step.accept': 'Mark as Accepted',
  'whatsNext.step.convert': 'Convert to Sales Order',
  'whatsNext.step.createPackage': 'Create Package',
  'whatsNext.step.createShipment': 'Create Shipment',
  'whatsNext.step.raiseInvoice': 'Create Invoice',
  'whatsNext.step.recordPayment': 'Record Payment',
  'whatsNext.step.receive': 'Receive',
  'whatsNext.step.raiseBill': 'Create Bill',
  'whatsNext.step.pay': 'Pay',
  'whatsNext.step.receiveReturn': 'Receive the Return',
  'whatsNext.step.ship': 'Ship',
  'whatsNext.step.newRfq': 'New RFQ',
  'whatsNext.step.markWon': 'Mark Won',
  'whatsNext.step.openProgress': 'Open Deal Progress',
  'whatsNext.deal.decision': 'The prospect decides: mark the Deal Won or Lost.',
  'invoice.pill.opening': 'Opening',
  'bill.pill.opening': 'Opening',
  // Decisions 2971 to 2974 (T2052): a company's mailboxes that are no person's, on the Company page and in the send windows.
  'mailbox.kind.sales': 'Sales',
  'mailbox.kind.accountsReceivable': 'Accounts Receivable',
  'mailbox.kind.accountsPayable': 'Accounts Payable',
  'mailbox.kind.support': 'Support',
  'mailbox.kind.general': 'General',
  'mailbox.kind.other': 'Other',
  'mailbox.modal.addTitle': 'Add Mailbox',
  'mailbox.modal.editTitle': 'Edit Mailbox',
  'mailbox.modal.subtitle': 'An address the company receives mail at that is no person’s, such as its sales or its accounts receivable box.',
  'mailbox.field.kind': 'What it is for',
  'mailbox.field.label': 'Label',
  'mailbox.field.labelPlaceholder': 'US sales',
  'mailbox.field.email': 'Email address',
  'mailbox.save': 'Add Mailbox',
  'mailbox.saveEdit': 'Save Changes',
  'mailbox.edit': 'Edit',
  'mailbox.end': 'End Mailbox',
  'mailbox.endTitle': 'End this mailbox?',
  'mailbox.endConsequence': 'The mailbox is ended today and kept as history; nothing is sent to it after today.',
  'mailbox.endConfirm': 'End Mailbox',
  'mailbox.send.filled': 'To is filled in with the account’s {kind} mailbox; type over it to send elsewhere.',
  // Decision 5450 (row T2750, batch 1): the Discovery card on the web and the phone, and its library sheet.
  'discovery.cardTitle': 'Discovery',
  'discovery.nextAction': 'the Discovery visit',
  'discovery.templatesLead': 'Templates:',
  'discovery.noneAdded': 'none added',
  'discovery.added': 'added',
  'discovery.of': 'of',
  'discovery.questionsAnswered': 'questions answered',
  'discovery.questions': 'questions',
  'discovery.uploads': 'uploads',
  'discovery.noneYet': 'None yet',
  'discovery.photoOne': 'photo',
  'discovery.photoMany': 'photos',
  'discovery.fileOne': 'file',
  'discovery.fileMany': 'files',
  'discovery.addTemplate': '+ Add Template',
  'discovery.sheetTitle': 'Add Template',
  'discovery.sheetHint': 'Tap a name to add it to this Deal.',
  'discovery.sheetEmpty': 'Every active template in the library is on this Deal already.',
  'discovery.empty': 'Add a template to guide the visit: its questions and the photos and files it asks for appear here.',
  'discovery.notAnswered': 'Not answered',
  'discovery.answerPlaceholder': 'Your answer',
  'discovery.saveAnswer': 'Save Answer',
  'discovery.yes': 'Yes',
  'discovery.no': 'No',
  'discovery.addAnotherPhoto': 'Add Another Photo',
  'discovery.addPhoto': 'Add Photo',
  'discovery.addFile': 'Add File',
  'discovery.fileOnWeb': 'added on the web',
  'discovery.tileHintPhone': 'Each tile opens the camera, and the photo is filed under its spot.',
  'discovery.tileHintWeb': 'Each tile opens the upload window with its spot already set.',
  'discovery.saveAsTemplate': 'Save as Template',
  'discovery.saveAsHint': 'Every question and upload point on this Deal joins the library as one new template.',
  'discovery.saveTemplate': 'Save Template',
  'discovery.name': 'Name',
  'discovery.nameNeeded': 'Type a name for the template first.',
  'discovery.description': 'Description',
  'discovery.savedAsTemplate': 'The template joined the library.',
  'discovery.kind.text': 'Text',
  'discovery.kind.number': 'Number',
  'discovery.kind.choice': 'Choice',
  'discovery.kind.yesNo': 'Yes or No',
  'discovery.kind.photo': 'Photo',
  'discovery.kind.file': 'File',
  'discovery.kind.group': 'Group',
  'discovery.kind.list': 'List',
  // Decision 5450: the sentences a template draft, an answer or a Discovery write is refused in.
  'discovery.problem.nameMissing': 'A template has a name.',
  'discovery.problem.nameLong': "A template's name is {max} characters or fewer.",
  'discovery.problem.empty': 'A template asks at least one question or names at least one upload point.',
  'discovery.problem.questionKey': "Question {n}'s key is lower case words joined by a hyphen.",
  'discovery.problem.uploadKey': "Upload point {n}'s key is lower case words joined by a hyphen.",
  'discovery.problem.groupKey': "Group or List {n}'s key is lower case words joined by a hyphen.",
  'discovery.problem.questionLabel': 'Question {n} has a label.',
  'discovery.problem.questionKind': 'Question {n} is Text, Number, Choice or Yes or No.',
  'discovery.problem.questionOptions': 'Question {n} offers at least two options.',
  'discovery.problem.uploadTag': 'Upload point {n} has a tag.',
  'discovery.problem.uploadTagLong': "Upload point {n}'s tag is {max} characters or fewer.",
  'discovery.problem.uploadKind': 'Upload point {n} is a photo or a file.',
  'discovery.problem.uploadHintLong': "Upload point {n}'s description is {max} characters or fewer.",
  'discovery.problem.uploadKindLong': "Upload point {n}'s default photo kind is {max} characters or fewer.",
  'discovery.problem.questionRange': 'Question {n} has a From no higher than its To.',
  'discovery.problem.questionGroup': 'Question {n} stands in a Group or List the template holds.',
  'discovery.problem.uploadGroup': 'Upload point {n} stands in a Group or List the template holds.',
  'discovery.problem.groupHeading': 'Group or List {n} has a heading.',
  'discovery.problem.groupHeadingLong': "Group or List {n}'s heading is {max} characters or fewer.",
  'discovery.problem.groupKind': 'Group or List {n} is a Group or a List.',
  'discovery.problem.groupEmpty': 'Group or List {n} holds at least one question, upload point, Group or List.',
  'discovery.problem.groupParent': 'Group or List {n} stands in a Group or List the template holds, never in itself or in one standing in it.',
  'discovery.problem.groupDepth': 'Group or List {n} stands at most {max} deep.',
  'discovery.problem.groupStart': 'List {n} starts a new Deal with 0 to {max} items.',
  'discovery.answer.range': '{label} takes a number {range}.',
  'discovery.answer.choiceMany': '{label} takes one or more of its options; tick them.',
  'discovery.range.between': 'from {from} to {to}',
  'discovery.range.atLeast': 'of {from} or more',
  'discovery.range.atMost': 'of {to} or less',
  'discovery.addEntry': 'Add {name}',
  'discovery.removeEntry': 'Remove',
  'discovery.removeEntryLabel': 'Remove {name}',
  'discovery.removeEntryTitle': 'Remove {name}?',
  'discovery.removeEntryKeeps': 'Its answers and files, and those of every Group or List standing in it, stay on the Deal as plain notes and files.',
  'discovery.removeTemplateHolds': 'It holds {answers} and {files}. Each answer counts once, and Remove deletes its earlier versions too.',
  'discovery.removeTemplateEmpty': 'It holds no answers and no files.',
  'discovery.removeChoiceLegend': 'Its answers and files',
  'discovery.removeTemplateOnly': 'Remove Template',
  'discovery.keepAnswersFiles': 'Keep Answers and Files',
  'discovery.keepAnswersFilesDetail': 'Keep them on the Deal as plain notes and files',
  'discovery.removeAnswersFiles': 'Remove Answers and Files',
  'discovery.removeAnswersFilesDetail': 'Remove them: the answers are deleted and the files go to the Trash',
  'discovery.answerOne': 'answer',
  'discovery.answerMany': 'answers',
  'discovery.refused.removeChoice': 'Say whether the answers and files stay on the Deal or go with the template.',
  'discovery.refused.templateNotHeld': 'That template is not on this Deal; reload the Deal.',
  'discovery.otherPlaceholder': 'Other, in your words',
  'discovery.pointNote': 'Note on This Spot',
  'discovery.pointNotePlaceholder': 'Anything about this spot the photos do not show',
  'discovery.photoTitle': 'Photo',
  'discovery.photoKind': 'Kind',
  'discovery.photoNote': 'Note',
  'discovery.photoPosition': 'Position',
  'discovery.photoNoKind': 'No kind recorded',
  'discovery.photoNoPosition': 'No position recorded',
  'discovery.saveNote': 'Save Note',
  'discovery.noteUnchanged': 'Change the note first.',
  'discovery.noteSaved': 'The note was saved.',
  'discovery.moreThumbs': '+{count} more',
  'discovery.openPhoto': 'Open {name}',
  'discovery.removePhoto': 'Remove',
  'discovery.removePhotoTitle': 'Remove {name}?',
  'discovery.removePhotoBody': 'The photo leaves this spot and goes to the Trash, where an administrator can restore it.',
  'discovery.removePhotoConfirm': 'Remove Photo',
  'discovery.addAnotherFile': 'Add Another File',
  'discovery.otherLabel': 'Other, in Your Words',
  'discovery.rangeHint': 'A number {range}.',
  'discovery.refused.entryKey': 'An item names its template and its Group or List; add it from the Discovery card.',
  'discovery.refused.entryGone': 'That Group or List is no longer on its template; reload the Deal.',
  'discovery.refused.entryStanding': '{name} is on this Deal already.',
  'discovery.refused.entryNotStanding': 'That item is not on this Deal; reload the Deal.',
  'discovery.refused.listFull': 'A List holds at most {max} items on one Deal.',
  'discovery.answer.missing': '{label} takes an answer; type one before saving.',
  'discovery.answer.number': '{label} takes a number; type digits.',
  'discovery.answer.choice': '{label} takes one of its options; pick one.',
  'discovery.answer.yesNo': '{label} takes Yes or No; pick one.',
  'discovery.refused.stale': 'This template was changed by somebody else since it was opened; reopen it and edit again.',
  'discovery.refused.noTemplate': 'No Discovery template {id} was found.',
  'discovery.refused.saysActive': 'Say whether the template is active.',
  'discovery.refused.nameTaken': 'Another Discovery template is already named {name}; choose another name.',
  'discovery.refused.noDeal': 'No such Deal exists, so its Discovery is not written.',
  'discovery.refused.lost': 'A Lost Deal is reopened to a named stage before its Discovery is written.',
  'discovery.refused.addNamesTemplate': 'Add Template names the template to add; pick one from the library.',
  'discovery.refused.noSuchTemplate': 'No such Discovery template exists, so it is not added.',
  'discovery.refused.deactivated': 'The template {name} is deactivated; reactivate it in Settings before adding it.',
  'discovery.refused.notOffered': 'The template {name} is not offered on a Deal; choose another template.',
  'discovery.refused.answerKey': 'An answer names its template and its question; answer from the Discovery card.',
  'discovery.refused.answerNotHeld': 'That template is not on this Deal; add it before answering its questions.',
  'discovery.refused.questionGone': 'That question is no longer on its template; reload the Deal.',
  'discovery.refused.uploadKey': 'The upload names its file and its spot; upload from a Discovery tile.',
  'discovery.refused.uploadNotHeld': 'That template is not on this Deal; add it before uploading to its spots.',
  'discovery.refused.spotGone': 'That upload point is no longer on its template; reload the Deal.',
  'discovery.refused.spotPhoto': 'That upload point takes a picture; choose an image file.',
  'discovery.refused.fileElsewhere': 'That file is not on this Deal; upload it from the Deal.',
  'discovery.refused.noSections': 'This Deal holds no template yet; add one before saving its sections as a template.',
  'discovery.refused.noRead': 'This identity holds no read on the Deal module.',
  'discovery.refused.adminWrites': 'Discovery templates are written by an administrator.',
  'discovery.refused.adminSaves': 'A Deal’s sections are saved as a template by an administrator, who writes the library.',
  // Decision 5530 (row T2780 batch 1): Ask Claude on the Discovery card, its question list and the sentences it is refused in.
  'askClaude.ask': 'Ask Claude',
  'askClaude.asking': 'Asking Claude…',
  'askClaude.resubmit': 'Resubmit',
  'askClaude.title': 'Open Questions from Claude',
  'askClaude.hint': 'Answer what you can, then press Resubmit: the answers go to Claude with the Deal, and the questions still open come back.',
  'askClaude.answered': 'answered',
  'askClaude.answerLabel': 'Answer',
  'askClaude.notAnswered': 'Not answered',
  'askClaude.clearAnswer': 'Clear Answer',
  'askClaude.dismiss': 'Dismiss Questions',
  'askClaude.dismissTitle': 'Dismiss the questions from Claude?',
  'askClaude.dismissBody': 'Every question on this list is taken off the Deal, with the answers typed against it. Ask Claude again for a new list.',
  'askClaude.dismissConfirm': 'Dismiss',
  'askClaude.none': 'Claude asked no open questions about this Deal.',
  'askClaude.askedSome': 'Claude asked its open questions; answer them below.',
  'askClaude.openSettings': 'Open Settings › Integrations › Anthropic',
  'askClaude.refused.seamOff': 'Claude is not set up, so nothing was sent. An administrator turns it on at Settings › Integrations › Anthropic with a key and a model.',
  'askClaude.refused.capReached': 'This month’s spending cap for outside lookups is reached, so nothing was sent to Claude. An administrator raises it at Settings › Integrations › Anthropic.',
  'askClaude.refused.key': 'Anthropic refused the key held at Settings › Integrations › Anthropic, so no questions came back.',
  'askClaude.refused.rateLimited': 'Anthropic is answering too many calls just now; press Ask Claude again in a minute.',
  'askClaude.refused.unavailable': 'Anthropic did not answer, so no questions came back; press Ask Claude again later.',
  'askClaude.refused.noList': 'Claude’s answer held no list of questions, so the card is unchanged; press Ask Claude again.',
  'askClaude.refused.noQuestion': 'That question is no longer on this Deal; reload the Deal.',
  'askClaude.refused.answerMissing': 'An answer is typed before it is saved; Clear Answer takes one off.',
  'askClaude.refused.answerLong': 'An answer is {max} characters or fewer.',
  // Decisions 6003 and 6004 (T2780 batch 2): Make Report writes the Deal's proposal report from the pack and the answers.
  'makeReport.make': 'Make Report',
  'makeReport.making': 'Making Report…',
  'makeReport.openTitle': 'Questions Are Still Open',
  'makeReport.openBody': 'Questions from Claude still stand open, {count} in all. The report is made from what the Deal holds, and lists each open question as still open.',
  'makeReport.openConfirm': 'Make Report Anyway',
  'makeReport.made': 'The report is made, and filed on the Deal as its Proposal Report.',
  'makeReport.version': 'version',
  'makeReport.madeAt': 'made',
  'makeReport.refused.key': 'Anthropic refused the key held at Settings › Integrations › Anthropic, so no report was made.',
  'makeReport.refused.rateLimited': 'Anthropic is answering too many calls just now; press Make Report again in a minute.',
  'makeReport.refused.unavailable': 'Anthropic did not answer, so no report was made; press Make Report again later.',
  'makeReport.refused.noReport': 'Claude’s answer held no report, so nothing was filed; press Make Report again.',
  'makeReport.refused.openQuestions': 'Questions from Claude are still open; answer them, or confirm the report is made with them open.',
  'makeReport.refused.noPermission': 'Your role does not carry Ask Claude, so nothing was sent. An administrator grants it at Settings › People › Roles.',
  // Decision 5450: Settings › CRM › Discovery Templates.
  'settings.discovery.title': 'Discovery Templates',
  'settings.discovery.add': '+ New Template',
  'settings.discovery.hint': 'The templates a Deal’s Discovery card adds from. Each asks its questions and names the photos and files the visit gathers.',
  'settings.discovery.none': 'No template stands in the library yet.',
  'settings.discovery.inactive': 'Inactive',
  'settings.discovery.questionOne': 'question',
  'settings.discovery.questionMany': 'questions',
  'settings.discovery.uploadOne': 'upload point',
  'settings.discovery.uploadMany': 'upload points',
  'settings.discovery.madeFromDeal': 'made from a Deal',
  'settings.discovery.newTitle': 'New Template',
  'settings.discovery.editTitle': 'Edit Template',
  'settings.discovery.questionsHeading': 'Questions',
  'settings.discovery.uploadsHeading': 'Upload Points',
  'settings.discovery.addQuestion': '+ Add Question',
  'settings.discovery.addUploadPoint': '+ Add Upload Point',
  'settings.discovery.label': 'Label',
  'settings.discovery.kind': 'Kind',
  'settings.discovery.options': 'Options',
  'settings.discovery.optionsHint': 'Each line is one option, offered on the Deal in this order. Drag a line by its grip to move it.',
  'settings.discovery.option': 'Option',
  'settings.discovery.noOptions': 'No option yet. A Choice offers at least two.',
  'settings.discovery.addOption': '+ Add Option',
  'settings.discovery.removeOption': 'Remove',
  'settings.discovery.tag': 'Tag Name',
  'settings.discovery.kindsHint': 'Kinds: Text, Number, Choice with its options, and Yes or No.',
  'settings.discovery.moveUp': 'Move Up',
  'settings.discovery.moveDown': 'Move Down',
  'settings.discovery.first': 'This row is already the first.',
  'settings.discovery.last': 'This row is already the last.',
  'settings.discovery.remove': 'Remove',
  'settings.discovery.noQuestions': 'No question yet.',
  'settings.discovery.noUploads': 'No upload point yet.',
  'settings.discovery.uploadDescription': 'Description',
  'settings.discovery.uploadDescriptionHint': 'Shown under the tag on the tile, such as: This is the breaker or switchgear feeding the panel.',
  'settings.discovery.defaultKind': 'Default Photo Kind',
  'settings.discovery.defaultKindHint': 'The kind each photo taken here carries; blank carries the tag.',
  'settings.discovery.groupsHeading': 'Groups and Lists',
  'settings.discovery.groupsHint': 'A Group holds questions and upload points under one heading. A List is numbered on the Deal, such as Power source 1 and Power source 2, and its Add appends the next. A Group or List stands in another by its Stands In, at most three deep.',
  'settings.discovery.noGroups': 'No Group or List yet.',
  'settings.discovery.addGroup': '+ Add Group or List',
  'settings.discovery.addInnerGroup': '+ Add Group or List Inside',
  'settings.discovery.heading': 'Heading',
  'settings.discovery.startCount': 'Items a New Deal Starts With',
  'settings.discovery.standsIn': 'Stands In',
  'settings.discovery.standsInTemplate': 'The Template Itself',
  'settings.discovery.ownHeading': 'The Template’s Own Questions and Upload Points',
  'settings.discovery.blockEmpty': 'Nothing stands in it yet. Add a question or an upload point here.',
  'settings.discovery.answers': 'Answers',
  'settings.discovery.answersOne': 'One',
  'settings.discovery.answersMany': 'Many',
  'settings.discovery.rangeFrom': 'From',
  'settings.discovery.rangeTo': 'To',
  'settings.discovery.rangeHint': 'A number outside the range is refused; either end may stay blank.',
  'settings.discovery.otherHint': 'An option named Other opens a box for the person’s own words. Many draws a check box per option.',
  'settings.discovery.groupOne': 'group or list',
  'settings.discovery.groupMany': 'groups and lists',
  // T3126, decision 7630: the check stock drawn under a check on screen, and the organization's own blank check per bank account.
  'print.checkStock.payTo': 'Pay to the order of',
  'print.checkStock.dollars': 'Dollars',
  'print.checkStock.memo': 'Memo',
  'print.checkStock.bank': 'Your bank',
  'print.checkStock.screenOnly': 'On the screen the check is drawn over a picture of the stock. The printed sheet carries the six values alone, because the stock is pre-printed.',
  'print.checkStock.account': 'Bank Account',
  'print.checkStock.noAccount': 'No bank account is set up, so the drawn stock names no bank. A bank account is added under Banking.',
  'print.checkStock.intro': 'A scan of a blank check from this account gives the exact look. Without one, a drawn voucher stock stands in.',
  'print.checkStock.drawn': 'No picture is uploaded for this account, so the drawn voucher stock stands in.',
  'print.checkStock.choose': 'Choose a picture of a blank check',
  'print.checkStock.rule': 'A PNG or JPEG picture of the whole sheet, no larger than 2 MB. It is drawn on the screen only and never printed.',
  'print.checkStock.upload': 'Upload Check Stock',
  'print.checkStock.replace': 'Replace Check Stock',
  'print.checkStock.remove': 'Remove Check Stock',
  'print.checkStock.uploaded': 'The check stock is saved.',
  'print.checkStock.removed': 'The check stock is removed, and the drawn stock stands in.',
  'print.checkStock.problem.pick': 'Choose a picture to upload.',
  'print.checkStock.problem.type': 'A check stock picture is a PNG or a JPEG.',
  'print.checkStock.problem.size': 'A check stock picture is no larger than 2 MB.',
  'print.checkStock.problem.account': 'The check stock is uploaded for a bank account that a check can be drawn on.',
  // 🔒 Decision 7620 (T3124): the Storage block on a file's details, a super admin's alone (I1a).
  'file.storage.title': 'Storage',
  'file.storage.key': 'Stored at {key}',
  'file.storage.beforePrefix': 'written before the environment prefix',
  'file.storage.size': 'Version {number} · {size} as recorded',
  'file.storage.storedSize': 'The store holds {size} at this key.',
  'file.storage.missing': 'The store holds no object at this key, so the file does not open. Restore it from a Mirror.',
  'file.storage.sealed': 'Sealed: encrypted under a key of its own.',
  'file.storage.plain': 'Plain: stored unencrypted.',
  'file.storage.hash': 'Content hash {hash}',
  'file.storage.alone': 'Stored once; used by this file alone.',
  'file.storage.shared': 'Stored once; shared by {count} files:',
  'file.storage.hidden': '{count} more you cannot open.',
  'file.storage.mirror': '{name} holds a copy with hash {hash}, copied {when}.',
  'file.storage.mirrorOlder': '{name} holds a copy of other bytes, hash {hash}, copied {when}; the next mirror run copies these.',
  'file.storage.noMirror': 'No Mirror holds a copy yet.',
  // 🔒 Decision 7620 (T3124): the stored-objects card on Storage and Backups, a super admin's alone (I2a).
  'settings.storage.objects.title': 'Stored Objects',
  'settings.storage.objects.hint': 'The quota counts every version at its own size. The store keeps one object for every version with the same bytes, so the difference is the bytes shared.',
  'settings.storage.objects.quota': 'Counted by the quota: {versions} versions, {size}.',
  'settings.storage.objects.distinct': 'Named by those versions: {objects} objects, {size}.',
  'settings.storage.objects.notRead': 'The store has not been read since the server started. Reading it lists every object, which takes longer on a large store.',
  'settings.storage.objects.read': 'Read the Store Now',
  'settings.storage.objects.readAt': 'In the store under {prefix}: {objects} objects, {size}, read {when}.',
  'settings.storage.objects.files': 'Objects a version names: {objects}, {size}.',
  'settings.storage.objects.other': 'Thumbnails, sealed copies, dumps and objects no row names: {objects}, {size}.',
  'settings.storage.objects.shared': 'Shared bytes: {size}, counted by the quota and stored once.',
  'settings.storage.objects.missing': '{count} objects a version names are missing from the store.',
  'settings.storage.objects.foreign': '{count} objects a version names are under another environment’s prefix, read where they are and not listed here.',
  // 🔒 Decision 7620 (T3124): Inspect from a record's banner menu, a super admin's alone (I3a).
  'record.inspect': 'Inspect',
  'record.inspect.title': 'Inspect',
  'record.inspect.id': 'Id {id} in {table}',
  'record.inspect.version': 'Version {version}',
  'record.inspect.noVersion': 'This record carries no version counter.',
  'record.inspect.fromMergeMap': 'Read from the merge map: {count} columns.',
  'record.inspect.fromReferences': 'Read from the database’s own references: {count} columns.',
  'record.inspect.pointers': 'Rows that point at it',
  'record.inspect.pointer': '{table} · {column}: {count}',
  'record.inspect.noPointers': 'No row points at it.',
  'record.inspect.audit': 'Audit rows: {count}',
  'record.inspect.files': 'Files: {files}, with {versions} versions on {objects} stored objects, {size}; {sealed} sealed.',
  'record.inspect.noFiles': 'No file is tagged or linked to it.',
  'record.inspect.object': '{key} · {size} · {seal} · {versions} versions',
  'record.inspect.objectSealed': 'sealed',
  'record.inspect.objectPlain': 'plain',
  'record.inspect.moreObjects': '{count} more stored objects.',
  'record.inspect.hint': 'Counts and keys only; nothing here opens a value or changes a row.',
  // 🔒 Decision 7620 (T3124): a store that cannot be listed says so on the Stored Objects card, never as a fault.
  'settings.storage.objects.failed': 'The store could not be read: {reason} Tried {when}.',
  'settings.storage.objects.failedBucket': 'its bucket {bucket} does not exist.',
  'settings.storage.objects.failedRefused': 'it refused this server’s key or secret.',
  'settings.storage.objects.failedUnreachable': 'it did not answer.',
  'settings.storage.objects.failedTimeout': 'it had not finished listing after {seconds} seconds.',
  // 🔒 Decision 12660 (T3814): the manufacturer lookup's contract, its lifecycle states, its fields and the manifest's refusals.
  'itemLookup.lifecycle.active': 'Active',
  'itemLookup.lifecycle.activeMature': 'Active Mature',
  'itemLookup.lifecycle.endOfLife': 'End of Life',
  'itemLookup.lifecycle.discontinued': 'Discontinued',
  'itemLookup.field.name': 'Name',
  'itemLookup.field.description': 'Description',
  'itemLookup.field.referenceName': 'Reference Name',
  'itemLookup.field.series': 'Series',
  'itemLookup.field.listPrice': 'List Price',
  'itemLookup.field.lifecycle': 'Lifecycle',
  'itemLookup.bundle.notJson': 'The bundle’s manifest.json is not readable JSON, so nothing was staged.',
  'itemLookup.bundle.noItems': 'The bundle’s manifest.json names no items, so nothing was staged.',
  'itemLookup.bundle.tooMany': 'A bundle names at most {max} items; split it into two bundles.',
  'itemLookup.bundle.noId': 'Entry {n} of the manifest names no Item id, so nothing was staged.',
  'itemLookup.bundle.twice': 'The manifest names Item {id} twice, so nothing was staged.',
  'itemLookup.bundle.badFileName': 'The manifest names the file {file} for Item {id}, and a file is named by its name alone: no folder, no “..”, no backslash, no control character, and at most {max} characters, so nothing was staged.',
  'itemLookup.bundle.missingFile': 'The manifest names {file} for Item {id}, and the bundle’s files folder holds no such file, so nothing was staged.',
  'itemLookup.lookUp.noDriver': 'No driver reads {manufacturer}’s product pages yet, so nothing was looked up. Export Lookup Request names this Item for a person or an outside agent to fill.',
  'itemLookup.lookUp.noDriverAddress': 'No driver reads the page at {address} yet, so nothing was looked up. Export Lookup Request names this Item for a person or an outside agent to fill.',
  'itemLookup.lookUp.thisManufacturer': 'this manufacturer',
  // 🔒 Decision 12660: Export Lookup Request on the Items list.
  'itemLookup.request.action': 'Export Lookup Request',
  'itemLookup.request.none': 'Tick the Items the lookup request names first.',
  'itemLookup.request.tooMany': 'A lookup request names at most {max} Items; tick fewer.',
  'itemLookup.request.unknown': 'No Item {id} was found, so no lookup request was made.',
  'itemLookup.request.failed': 'The lookup request could not be made. Try again.',
  'itemLookup.request.saved': 'The lookup request was saved. Fill it as a lookup bundle and import it with Import Lookup Bundle.',
  // 🔒 Decisions 12660, 12661: Import Lookup Bundle and its Step Through.
  'itemLookup.bundle.action': 'Import Lookup Bundle',
  'itemLookup.bundle.title': 'Import Lookup Bundle',
  'itemLookup.bundle.hint': 'A zip holding manifest.json and a files folder, as Export Lookup Request describes. Nothing is written to an Item until you step through it.',
  'itemLookup.bundle.file': 'Bundle',
  'itemLookup.bundle.read': 'Read Bundle',
  'itemLookup.bundle.choose': 'Choose a bundle first.',
  'itemLookup.bundle.tooLarge': 'A lookup bundle is at most {mb} MB; split it into two bundles.',
  'itemLookup.bundle.notZip': 'The bundle could not be read as a zip: it holds {reason}. Nothing was staged.',
  'itemLookup.bundle.noManifest': 'The bundle holds no manifest.json at its top level, so nothing was staged.',
  'itemLookup.bundle.unknownItem': 'The manifest names Item {id}, and no Item here carries that id, so nothing was staged.',
  'itemLookup.run.notFound': 'No lookup run carries that id.',
  'itemLookup.run.closed': 'This lookup run is closed, so nothing more is written from it.',
  'itemLookup.run.noFile': 'The bundle holds no file {file}.',
  'itemLookup.step.decided': 'This step is already accepted or skipped.',
  'itemLookup.step.accepting': 'This step is being accepted. Wait for it to finish.',
  'itemLookup.step.released': 'This step’s accept stood for more than 15 minutes and was let go, so its result was not recorded. Open the step again to see where it stands.',
  'itemLookup.run.accepting': 'A step of this run is being accepted. Close the run when it has finished.',
  'itemLookup.acceptAll.notWalked': 'Accept All waits until every step left has been walked with Next or Back.',
  'itemLookup.refusal.price': '{value} is not a price.',
  'itemLookup.refusal.lifecycle': '{value} is none of Active, Active Mature, End of Life and Discontinued.',
  'itemLookup.refusal.notNumber': '{value} is not a number, and {name} is measured.',
  'itemLookup.refusal.unit': '{unit} is not a unit of the {family} family {name} is measured in.',
  'itemLookup.refusal.kind': 'No File Type here is named {kind}; add it under Settings › File Types, or file the document by hand.',
  'itemLookup.refusal.picture': 'The file is not a PNG, JPEG, WebP or GIF picture.',
  'itemLookup.refusal.choice': '{value} could not be added as a choice of {name}.',
  'itemLookup.refusal.line': 'The line {label} was refused, and the lines after it were not written: {reason}',
  'itemLookup.document.held': 'Filed as {file}',
  'itemLookup.picture.itemImage': 'Item image',
  'itemLookup.picture.gallery': 'Gallery picture',
  'itemLookup.picture.imageHeld': 'An image is set',
  'itemLookup.step.title': 'Step Through',
  'itemLookup.step.of': 'Item {n} of {count}',
  'itemLookup.step.found': 'Found',
  'itemLookup.step.held': 'On the Item',
  'itemLookup.step.take': 'Take',
  'itemLookup.step.nothingHeld': 'Nothing yet',
  'itemLookup.step.same': 'Same',
  'itemLookup.step.newType': 'New Type',
  'itemLookup.step.readByAi': 'Read by AI',
  'itemLookup.step.confidence': '{percent}% confident',
  'itemLookup.step.source': 'Read from',
  'itemLookup.step.back': 'Back',
  'itemLookup.step.next': 'Next',
  'itemLookup.step.accept': 'Accept',
  'itemLookup.step.skip': 'Skip',
  'itemLookup.step.acceptAll': 'Accept All',
  'itemLookup.step.backWhy': 'Back is not open on the first step.',
  'itemLookup.step.nextWhy': 'Next is not open on the last step.',
  'itemLookup.step.acceptAllWhy': 'Accept All opens once every step left has been walked with Next or Back.',
  'itemLookup.step.close': 'Close',
  'itemLookup.step.preview': 'Preview',
  'itemLookup.step.value': 'Value for {label}',
  'itemLookup.step.takeLine': 'Take {label}',
  'itemLookup.step.nothingFound': 'The lookup found nothing for this Item.',
  'itemLookup.step.hint': 'A value the Item already holds is shown beside the found one and changes only where you tick it. Nothing is written until you press Accept.',
  'itemLookup.state.waiting': 'Waiting',
  'itemLookup.state.walked': 'Walked',
  'itemLookup.state.accepted': 'Accepted',
  'itemLookup.state.skipped': 'Skipped',
  'itemLookup.run.done': 'The lookup run is closed: {accepted} accepted and {skipped} skipped.',
  // 🔒 Decision 12660: Look Up Manufacturer on the Item form and page.
  'itemLookup.lookUp.action': 'Look Up Manufacturer',
  'itemLookup.lookUp.title': 'Look Up Manufacturer',
  'itemLookup.lookUp.hint': 'Name the manufacturer and part number, or paste the product page address. What is found opens in the Step Through, and nothing is written until you accept it.',
  'itemLookup.lookUp.manufacturer': 'Manufacturer',
  'itemLookup.lookUp.partNumber': 'Part Number',
  'itemLookup.lookUp.address': 'Product Page Address',
  'itemLookup.lookUp.go': 'Look Up',
  'itemLookup.lookUp.nothingToAsk': 'Name a part number or paste a product page address to look up.',
  'itemLookup.lookUp.badAddress': 'A product page address starts with http:// or https://.',
  'itemLookup.lookUp.missingFile': 'The lookup named the file {file} and did not fetch it, so nothing was staged.',
  'itemLookup.lookUp.failed': 'The lookup stopped before it finished, so nothing was staged. Try again, or use Export Lookup Request for this Item.',
  'itemLookup.lookUp.notHeld': 'The lookup’s files could not be held, so nothing was staged.',
  'itemLookup.lookUp.savedFirst': 'Save or cancel your changes first: the lookup writes to the Item itself.',
};

export const strings: Readonly<typeof STRING_TABLE> = STRING_TABLE;

export type StringKey = keyof typeof strings;

/** Resolve a key to its English text. A missing key is a type error, never a blank. */
export function t(key: StringKey): string {
  return strings[key];
}

/**
 * The template filler (T3225): each `{name}` in a template is replaced in one pass through a function, so a dollar sign in a value
 * stays literal and a value holding another placeholder is never rewritten. A name with no value, or an undefined one, keeps its placeholder, as `t()` leaves it.
 */
export function fill(template: string, values: Readonly<Record<string, string | number>>): string {
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = Object.prototype.hasOwnProperty.call(values, name) ? values[name] : undefined;
    // A key given an undefined value (the types forbid it) keeps its placeholder, as the pasted copies did, and never prints the word undefined.
    return value === undefined ? whole : String(value);
  });
}

/**
 * The member segment of a message key. A whole all-caps member is lower-cased whole (`RFQ` gives `rfq`),
 * any other member lower-cases its first letter only (`SalesOrder` gives `salesOrder`).
 */
export function keySegment(member: string): string {
  return /^[A-Z0-9]+$/.test(member) ? member.toLowerCase() : member.charAt(0).toLowerCase() + member.slice(1);
}
